import { parseISO } from 'date-fns';
import { CoreTypes, ApplicationSettings } from '@nativescript/core';
import * as Geolocation from '@nativescript/geolocation';

// src/utils/IntlPolyfill.ts
function getSystemTimeZone() {
  try {
    if (typeof Intl !== "undefined" && Intl.DateTimeFormat) {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
  } catch (e) {
  }
  return "America/Los_Angeles";
}
function createDateTimeFormat(locale, options) {
  try {
    if (typeof Intl !== "undefined" && Intl.DateTimeFormat) {
      return new Intl.DateTimeFormat(locale, options);
    }
  } catch (e) {
  }
  return null;
}
function getTimeZoneOffset(date, timeZone) {
  const formatter = createDateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  });
  if (!formatter) {
    return 0;
  }
  try {
    const parts = formatter.formatToParts(date);
    const tzDate = new Date(
      parseInt(parts.find((p) => p.type === "year")?.value || "0"),
      parseInt(parts.find((p) => p.type === "month")?.value || "1") - 1,
      parseInt(parts.find((p) => p.type === "day")?.value || "1"),
      parseInt(parts.find((p) => p.type === "hour")?.value || "0"),
      parseInt(parts.find((p) => p.type === "minute")?.value || "0"),
      parseInt(parts.find((p) => p.type === "second")?.value || "0")
    );
    return (tzDate.getTime() - date.getTime()) / 6e4;
  } catch (e) {
    return 0;
  }
}

// src/models/ROLocation.ts
var defaultLocation = {
  id: "default-uuid",
  lat: 45.515232,
  lng: -122.678207,
  timeZone: getSystemTimeZone(),
  name: "Portland, OR",
  country: "United States",
  city: "Portland",
  index: 0,
  elevation: 100
};

// src/models/RODailyWeather.ts
var CloudCoverTrend = /* @__PURE__ */ ((CloudCoverTrend2) => {
  CloudCoverTrend2["Increasing"] = "increasing";
  CloudCoverTrend2["Decreasing"] = "decreasing";
  CloudCoverTrend2["Stable"] = "stable";
  CloudCoverTrend2["Indeterminate"] = "indeterminate";
  return CloudCoverTrend2;
})(CloudCoverTrend || {});

// src/utils/constants.ts
var INCHES_TO_MM = 25.4;
var MILES_TO_KM = 1.60934;
var FEET_TO_METERS = 1 / 3.281;
var INVALID_DOUBLE = NaN;
var INVALID_INT = Number.MIN_SAFE_INTEGER;
var invalidDoubleValue = -999.99;
var invalidIntValue = -999;
var LIGHT_RAIN_THRESHOLD = 0.01;
var MODERATE_RAIN_THRESHOLD = 0.1;
var HEAVY_RAIN_THRESHOLD = 0.3;
var STEADY_TEMP_THRESHOLD = 5;
var SPIPPIN_LOCATION = { latitude: 45.535544, longitude: -123.139224 };
var TEST_LOCATIONS = {
  SPIPPIN: SPIPPIN_LOCATION,
  DPIPPIN: { latitude: 45.42147, longitude: -122.8392 },
  JPIPPIN: { latitude: 45.53203, longitude: -122.8095 }
};

// src/models/RODailyWeatherExtremes.ts
function createDefaultExtremes() {
  const distantPast = /* @__PURE__ */ new Date(0);
  return {
    temperature: INVALID_DOUBLE,
    temperatureTimestamp: distantPast,
    dewPoint: INVALID_DOUBLE,
    dewPointTimestamp: distantPast,
    humidity: INVALID_INT,
    humidityTimestamp: distantPast,
    windSpeed: INVALID_INT,
    windSpeedTimestamp: distantPast,
    windGust: INVALID_INT,
    windGustTimestamp: distantPast,
    pressure: INVALID_INT,
    pressureTimestamp: distantPast,
    rainRate: void 0,
    rainRateTimestamp: void 0,
    rainHour: void 0,
    rainHourTimestamp: void 0
  };
}

// src/models/MainWeatherInfo.ts
function createDefaultDailyWeather() {
  return {
    maxTemperatureWeek: 0,
    minTemperatureWeek: 0,
    todayMaxTemperature: 0,
    todayMinTemperature: 0,
    currentIndex: 0,
    currentDayName: "",
    currentDayForecast: "",
    currentDayForecastShort: "",
    tomorrowDayName: "",
    tomorrowDayForecast: "",
    tomorrowDayForecastShort: "",
    todayRain: 0,
    todaySnow: 0,
    todayIce: 0,
    todayWindSpeed: 0,
    todayWindDirection: "",
    todayHumidity: 0,
    todayHumidityLow: 0,
    todayUVIndex: 0,
    days: []
  };
}
var MainWeatherInfo = class {
  constructor() {
    // Location
    this.location = defaultLocation;
    this.locationName = "";
    this.isLocationUSA = false;
    this.timeZone = null;
    this.timeZoneIdentifier = "";
    this.timeZoneOffsetFromGMT = 0;
    // Station Info
    this.stationId = "";
    this.isRainOwlsStation = false;
    this.stationFormattedTime = "";
    this.currentObservationTimestamp = null;
    this.currentObservationSource = null;
    this.availabilityError = null;
    // Data Source (for fallback tracking)
    // 'openmeteo' = primary source, 'nws_fallback' = NWS used as fallback
    this.dataSource = "openmeteo";
    // Current Weather - Core
    this.temperature = -999;
    this.apparentTemperature = -999;
    this.dewPoint = -999;
    this.humidity = -999;
    this.pressure = -999;
    this.windSpeed = -999;
    this.windDirection = -999;
    this.cloudCover = -999;
    this.weatherCode = -999;
    this.isDay = true;
    // Precipitation
    this.rainRate = 0;
    this.rainDay = 0;
    this.rain1hr = 0;
    this.rain24hr = 0;
    this.rainStorm = -999.99;
    this.rainYesterday = 0;
    this.rainMonth = 0;
    this.rainYear = 0;
    // Snow
    this.snowRate = 0;
    this.snowDepth = 0;
    // Daily Extremes
    this.dailyMinimums = createDefaultExtremes();
    this.dailyMaximums = createDefaultExtremes();
    this.yesterdayMinimums = createDefaultExtremes();
    this.yesterdayMaximums = createDefaultExtremes();
    // Forecast Data
    this.hours = { currentIndex: 0, maxTemperature: 0, minTemperature: 0, temperatureColors: [], hours: [] };
    this.dailyWeather = createDefaultDailyWeather();
    this.currentDayForecastDetails = "";
    // Weather Alerts
    this.alerts = [];
    // Loading States
    this.isFirebaseLoading = false;
    this.isSynopticLoading = false;
    this.isOMHourlyLoading = false;
    this.isOMDailyLoading = false;
    this.isNWSAlertsLoading = false;
  }
  /**
   * Check if all loading is complete
   */
  get isLoading() {
    return this.isFirebaseLoading || this.isSynopticLoading || this.isOMHourlyLoading || this.isOMDailyLoading || this.isNWSAlertsLoading;
  }
  /**
   * Check if there are any active alerts
   */
  get hasAlerts() {
    return this.alerts.length > 0;
  }
  /**
   * Get the most severe alert (for prominent display)
   */
  get mostSevereAlert() {
    if (this.alerts.length === 0) return null;
    const severityOrder = ["Extreme", "Severe", "Moderate", "Minor", "Unknown"];
    return this.alerts.reduce((most, current) => {
      const mostIndex = severityOrder.indexOf(most.severity);
      const currentIndex = severityOrder.indexOf(current.severity);
      return currentIndex < mostIndex ? current : most;
    });
  }
  /**
   * Check if any core weather data is valid
   */
  get hasValidData() {
    return this.temperature !== -999 && this.weatherCode !== -999;
  }
};

// src/models/OpenMeteo/OpenMeteo.ts
function createInvalidOpenMeteoData() {
  return {
    lat: -999,
    lng: -999,
    current: void 0,
    hourly: void 0,
    unitsCurrent: void 0,
    elevation: -999,
    utcOffset: 0,
    timezone: "",
    timezoneAbbr: ""
  };
}
var OpenMeteoModelType = /* @__PURE__ */ ((OpenMeteoModelType2) => {
  OpenMeteoModelType2["BEST_MATCH"] = "best_match";
  OpenMeteoModelType2["ECMWF_IFS04"] = "ecmwf_ifs04";
  OpenMeteoModelType2["METNO_NORDIC"] = "metno_nordic";
  OpenMeteoModelType2["GFS_GLOBAL"] = "gfs_global";
  OpenMeteoModelType2["GFS_HRRR"] = "gfs_hrrr";
  OpenMeteoModelType2["JMA_MSM"] = "jma_msm";
  OpenMeteoModelType2["ICON_GLOBAL"] = "icon_global";
  OpenMeteoModelType2["ICON_EU"] = "icon_eu";
  OpenMeteoModelType2["ICON_D2"] = "icon_d2";
  OpenMeteoModelType2["GEM_GLOBAL"] = "gem_global";
  OpenMeteoModelType2["GEM_REGIONAL"] = "gem_regional";
  OpenMeteoModelType2["GEM_HRDPS_CONTINENTAL"] = "gem_hrdps_continental";
  OpenMeteoModelType2["METEOFRANCE_ARPEGE_WORLD"] = "meteofrance_arpege_world";
  OpenMeteoModelType2["METEOFRANCE_ARPEGE_EUROPE"] = "meteofrance_arpege_europe";
  OpenMeteoModelType2["METEOFRANCE_AROME_FRANCE"] = "meteofrance_arome_france";
  OpenMeteoModelType2["ECMWF_IFS"] = "ecmwf_ifs";
  return OpenMeteoModelType2;
})(OpenMeteoModelType || {});

// src/models/OpenMeteo/OpenMeteoCurrent.ts
function createInvalidOpenMeteoCurrent() {
  return {
    time: -1,
    temperature: -999,
    temperatureApparent: -999,
    humidity: -1,
    windBearing: -1,
    windSpeed: -999,
    pressure: -999,
    rainRate: -999,
    snowRate: -999,
    weatherCode: -1,
    isDay: -1,
    cloudCover: -1
  };
}
function createInvalidOpenMeteoCurrentUnits() {
  return {
    time: "",
    interval: "",
    temperature_2m: "",
    relative_humidity_2m: "",
    apparent_temperature: "",
    is_day: "",
    precipitation: "",
    rain: "",
    showers: "",
    snowfall: "",
    weather_code: "",
    cloud_cover: "",
    pressure_msl: "",
    freezing_level_height: "",
    surface_pressure: "",
    wind_speed_10m: "",
    wind_direction_10m: "",
    wind_gusts_10m: "",
    dew_point_2m: ""
  };
}

// src/models/OpenMeteo/OpenMeteoHourly.ts
function createInvalidOpenMeteoHourly() {
  return {
    time: [],
    temperaturesBEST: [],
    temperaturesICON: [],
    temperaturesNBM: void 0,
    temperaturesECMWF: [],
    humiditiesBEST: [],
    humiditiesICON: [],
    humiditiesNBM: void 0,
    humiditiesECMWF: [],
    dewPointsBEST: [],
    dewPointsICON: [],
    dewPointsNBM: void 0,
    dewPointsECMWF: [],
    popsBEST: [],
    popsICON: [],
    popsNBM: void 0,
    popsECMWF: [],
    rainAccumulationsBEST: [],
    rainAccumulationsICON: [],
    rainAccumulationsNBM: void 0,
    rainAccumulationsECMWF: [],
    precipitationsBEST: [],
    precipitationsICON: [],
    precipitationsNBM: void 0,
    precipitationsECMWF: [],
    showerAccumulationsBEST: [],
    showerAccumulationsICON: [],
    showerAccumulationsNBM: void 0,
    showerAccumulationsECMWF: [],
    snowAccumulationsBEST: [],
    snowAccumulationsICON: [],
    snowAccumulationsNBM: void 0,
    snowAccumulationsECMWF: [],
    weatherCodesBEST: [],
    weatherCodesICON: [],
    weatherCodesNBM: void 0,
    weatherCodesECMWF: [],
    cloudsBEST: [],
    cloudsICON: [],
    cloudsNBM: void 0,
    cloudsECMWF: [],
    windSpeedsBEST: [],
    windSpeedsICON: [],
    windSpeedsNBM: void 0,
    windSpeedsECMWF: [],
    windGustsBEST: [],
    windGustsICON: [],
    windGustsNBM: void 0,
    windGustsECMWF: [],
    windDirectionsBEST: [],
    windDirectionsICON: [],
    windDirectionsNBM: void 0,
    windDirectionsECMWF: [],
    isDaysBEST: [],
    temp850hPaBEST: [],
    temp850hPaICON: [],
    temp850hPaECMWF: [],
    temp925hPaBEST: [],
    temp925hPaICON: [],
    temp925hPaECMWF: [],
    temp700hPaBEST: [],
    temp700hPaICON: [],
    temp700hPaECMWF: []
  };
}

// src/models/NationalWeatherService/NWSEndPointsModel.ts
function createInvalidNWSWeatherProperties() {
  return {
    id: "",
    type: "",
    properties: createInvalidNWSWeatherPoints()
  };
}
function createInvalidNWSWeatherPoints() {
  return {
    forecastOffice: "",
    gridId: "",
    gridX: -1,
    gridY: -1,
    forecast: "",
    forecastHourly: "",
    forecastGridData: "",
    observationStations: "",
    forecastZone: "",
    county: "",
    fireWeatherZone: "",
    timeZone: ""
  };
}

// src/models/NationalWeatherService/NWSAlertsModel.ts
function createInvalidNWSAlerts() {
  return {
    updated: "",
    features: [createInvalidNWSAlertsFeature()]
  };
}
function createInvalidNWSAlertsFeature() {
  return {
    type: "",
    properties: createInvalidNWSAlertProperties()
  };
}
function createInvalidNWSAlertProperties() {
  return {
    id: "",
    onset: void 0,
    expires: void 0,
    ends: void 0,
    status: "",
    messageType: "",
    category: "",
    severity: "",
    urgency: "",
    event: "",
    senderName: "",
    headline: "",
    description: ""
  };
}

// src/models/NationalWeatherService/NWSHourlyGridModel.ts
function createInvalidNWSForecastHourlyGrid() {
  return {
    properties: {
      updateTime: "",
      validTimes: "",
      temperature: { uom: "unit:degC", values: [] },
      dewPoint: { uom: "unit:degC", values: [] },
      apparentTemperature: { uom: "unit:degC", values: [] },
      relativeHumidity: { uom: "unit:percent", values: [] },
      windChill: { uom: "unit:degC", values: [] },
      skyCover: { uom: "unit:percent", values: [] },
      pop: { uom: "unit:percent", values: [] },
      qpf: { uom: "unit:mm", values: [] },
      snow: { uom: "unit:mm", values: [] },
      ice: { uom: "unit:mm", values: [] },
      windSpeed: { uom: "unit:km_h-1", values: [] },
      windGust: { uom: "unit:km_h-1", values: [] },
      windDirection: { uom: "unit:degree_(angle)", values: [] },
      weather: { values: [] }
    }
  };
}

// src/models/Synoptic/SynopticStationModel.ts
function createInvalidSynopticSummary() {
  return {
    numberOfObjects: -1,
    responseCode: -1,
    responseMessage: "Invalid"
  };
}
function createInvalidSynopticSummaryModel() {
  return {
    summary: createInvalidSynopticSummary()
  };
}
function transformSynopticResponse(rawData) {
  if (!rawData.STATION || !Array.isArray(rawData.STATION)) {
    return { stations: [createInvalidSynopticStation()] };
  }
  const stations = rawData.STATION.map((station) => {
    const transformObservation = (obs) => {
      const transformValue = (val) => {
        if (!val) return void 0;
        return {
          value: val.value,
          dateTime: val.date_time
          // date_time → dateTime
        };
      };
      return {
        temperature: transformValue(obs.air_temp_value_1),
        dewPoint: transformValue(obs.dew_point_temperature_value_1d),
        windSpeed: transformValue(obs.wind_speed_value_1),
        windDirection: transformValue(obs.wind_direction_value_1),
        windGust: transformValue(obs.wind_gust_value_1),
        pressure: transformValue(obs.pressure_value_1d),
        humidity: transformValue(obs.relative_humidity_value_1),
        precip24hr: transformValue(obs.precip_accum_24_hour_value_1),
        precip1hr: transformValue(obs.precip_accum_1_hour_value_1),
        snowInterval: transformValue(obs.snow_interval_value_1),
        snowDepth: transformValue(obs.snow_depth_value_1),
        snowAccumulation: transformValue(obs.snow_accumulation_value_1),
        snow24hr: transformValue(obs.snow_accum_24_hour_value_1),
        weatherSummary: transformValue(obs.weather_summary_value_1d),
        weatherConditionCode: transformValue(obs.weather_cond_code_value_1)
      };
    };
    const transformMinMax = (minmax) => {
      if (!minmax) return void 0;
      const transformMinMaxValue = (val) => {
        if (!val) return void 0;
        return {
          minValues: val.value_min_local || [],
          maxValues: val.value_max_local || [],
          minTimestamp: val.datetime_min_local || [],
          maxTimestamp: val.datetime_max_local || []
        };
      };
      return {
        temperatureMinMax: transformMinMaxValue(minmax.air_temp_value_1),
        humidityMinMax: transformMinMaxValue(minmax.relative_humidity_value_1),
        dewPointMinMax: transformMinMaxValue(minmax.dew_point_temperature_value_1d),
        windSpeedMinMax: transformMinMaxValue(minmax.wind_speed_value_1),
        windGustMinMax: transformMinMaxValue(minmax.wind_gust_value_1),
        pressureMinMax: transformMinMaxValue(minmax.pressure_value_1d)
      };
    };
    return {
      stid: station.STID,
      name: station.NAME,
      shortName: station.SHORTNAME,
      latitude: station.LATITUDE,
      longitude: station.LONGITUDE,
      elevation: station.ELEVATION,
      elevationDem: station.ELEV_DEM,
      timeZone: station.TIMEZONE,
      distance: station.DISTANCE,
      observations: transformObservation(station.OBSERVATIONS || {}),
      minmax: transformMinMax(station.MINMAX)
    };
  });
  return { stations };
}
function transformSynopticSummary(rawData) {
  if (!rawData.SUMMARY) {
    return createInvalidSynopticSummaryModel();
  }
  return {
    summary: {
      numberOfObjects: rawData.SUMMARY.NUMBER_OF_OBJECTS ?? -1,
      responseCode: rawData.SUMMARY.RESPONSE_CODE ?? -1,
      responseMessage: rawData.SUMMARY.RESPONSE_MESSAGE ?? "Invalid"
    }
  };
}
function createInvalidSynopticStation() {
  const invalidObsDouble = {
    value: -999.99,
    dateTime: "Invalid"
  };
  const invalidObsString = {
    value: "Invalid",
    dateTime: "Invalid"
  };
  const invalidMinMaxValue = {
    minValues: [],
    maxValues: [],
    minTimestamp: [],
    maxTimestamp: []
  };
  return {
    stid: "Invalid",
    name: "Invalid",
    shortName: void 0,
    latitude: "0.0",
    longitude: "0.0",
    elevation: void 0,
    elevationDem: void 0,
    timeZone: "Invalid",
    distance: -9999,
    observations: {
      temperature: invalidObsDouble,
      dewPoint: invalidObsDouble,
      windSpeed: invalidObsDouble,
      windDirection: invalidObsDouble,
      windGust: invalidObsDouble,
      pressure: invalidObsDouble,
      humidity: invalidObsDouble,
      precip24hr: invalidObsDouble,
      precip1hr: invalidObsDouble,
      snowInterval: invalidObsDouble,
      snowDepth: invalidObsDouble,
      snowAccumulation: invalidObsDouble,
      snow24hr: invalidObsDouble,
      weatherSummary: invalidObsString,
      weatherConditionCode: invalidObsDouble
    },
    minmax: {
      temperatureMinMax: invalidMinMaxValue,
      humidityMinMax: invalidMinMaxValue,
      dewPointMinMax: invalidMinMaxValue,
      windSpeedMinMax: invalidMinMaxValue,
      windGustMinMax: invalidMinMaxValue,
      pressureMinMax: invalidMinMaxValue
    }
  };
}

// src/models/Synoptic/SynopticPrecipitationModel.ts
var STORM_RESET_DRY_HOURS = 12;
function parseOptionalNumber(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : void 0;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) return void 0;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : void 0;
  }
  return void 0;
}
function createInvalidSynopticPrecipitationModel() {
  return { stations: [] };
}
function transformSynopticPrecipitationResponse(rawData) {
  if (!rawData.STATION || !Array.isArray(rawData.STATION)) {
    return createInvalidSynopticPrecipitationModel();
  }
  return {
    stations: rawData.STATION.map((station) => {
      const precipitation = Array.isArray(station.OBSERVATIONS?.precipitation) ? station.OBSERVATIONS.precipitation.map((interval) => {
        const total = parseOptionalNumber(interval.total);
        return {
          ...total !== void 0 ? { total } : {},
          firstReport: interval.first_report,
          lastReport: interval.last_report,
          count: interval.count,
          interval: interval.interval,
          reportType: interval.report_type
        };
      }) : [];
      return {
        stid: station.STID,
        name: station.NAME,
        latitude: station.LATITUDE,
        longitude: station.LONGITUDE,
        timeZone: station.TIMEZONE,
        distance: station.DISTANCE,
        observations: { precipitation }
      };
    })
  };
}
function analyzeSynopticPrecipitation(model, stationId, fallbackTimeZone = "UTC") {
  const hasPrecipitationIntervals = (candidate) => (candidate.observations?.precipitation.length ?? 0) > 0;
  const station = (stationId ? model.stations.find(
    (candidate) => candidate.stid === stationId && hasPrecipitationIntervals(candidate)
  ) : void 0) ?? model.stations.find(hasPrecipitationIntervals);
  const intervals = station?.observations?.precipitation ?? [];
  if (intervals.length === 0) return void 0;
  const timeZone = station?.timeZone || fallbackTimeZone;
  const parsed = intervals.map((interval) => {
    const total = interval.total;
    const lastReport = interval.lastReport;
    if (total === void 0 || !Number.isFinite(total) || total < 0 || !lastReport) {
      return void 0;
    }
    const timestamp = parseSynopticDate(lastReport, timeZone);
    if (!timestamp) return void 0;
    return { total, timestamp, timestampString: lastReport };
  }).filter((value) => value !== void 0).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const latest = parsed[parsed.length - 1];
  if (!latest) return void 0;
  const todayKey = dateKey(latest.timestamp, timeZone);
  const yesterdayKey = dateKey(new Date(startOfDay(latest.timestamp, timeZone).getTime() - 864e5), timeZone);
  const todayIntervals = parsed.filter((interval) => dateKey(interval.timestamp, timeZone) === todayKey);
  const yesterdayIntervals = parsed.filter((interval) => dateKey(interval.timestamp, timeZone) === yesterdayKey);
  const dailyMaximums = {};
  const dailyHourly = {};
  for (const interval of parsed) {
    const key = dateKey(interval.timestamp, timeZone);
    dailyHourly[key] ?? (dailyHourly[key] = []);
    dailyHourly[key].push({
      value: interval.total,
      timestamp: interval.timestamp,
      timestampString: formatLocalTimestamp(interval.timestamp, timeZone)
    });
    const current = dailyMaximums[key];
    if (!current || interval.total > current.value) {
      dailyMaximums[key] = {
        value: interval.total,
        timestamp: interval.timestamp,
        timestampString: formatLocalTimestamp(interval.timestamp, timeZone)
      };
    }
  }
  return {
    dailyTotal: sum(todayIntervals),
    stormTotal: stormTotal(parsed),
    latestHourly: latest.total,
    latestHourlyTimestamp: latest.timestamp,
    todayMaxHourly: maxHourly(todayIntervals, timeZone),
    yesterdayMaxHourly: maxHourly(yesterdayIntervals, timeZone),
    dailyMaximums,
    dailyHourly
  };
}
function sum(intervals) {
  if (intervals.length === 0) return void 0;
  return round(intervals.reduce((total, interval) => total + interval.total, 0));
}
function stormTotal(intervals) {
  if (intervals.length === 0) return void 0;
  const rainIntervals = intervals.filter((interval) => interval.total > 0);
  const latestRain = rainIntervals[rainIntervals.length - 1];
  const latest = intervals[intervals.length - 1];
  if (!latestRain || !latest) return 0;
  const dryHoursSinceLastRain = (latest.timestamp.getTime() - latestRain.timestamp.getTime()) / 36e5;
  if (dryHoursSinceLastRain >= STORM_RESET_DRY_HOURS) {
    return 0;
  }
  let total = latestRain.total;
  let newerRain = latestRain;
  for (let index = rainIntervals.length - 2; index >= 0; index -= 1) {
    const interval = rainIntervals[index];
    const dryGapHours = (newerRain.timestamp.getTime() - interval.timestamp.getTime()) / 36e5;
    if (dryGapHours >= STORM_RESET_DRY_HOURS) break;
    total += interval.total;
    newerRain = interval;
  }
  return round(total);
}
function maxHourly(intervals, timeZone) {
  if (intervals.length === 0) return void 0;
  const max = intervals.reduce((best, interval) => interval.total > best.total ? interval : best);
  return {
    value: max.total,
    timestamp: max.timestamp,
    timestampString: formatLocalTimestamp(max.timestamp, timeZone)
  };
}
function parseSynopticDate(value, timeZone) {
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(value)) {
    const iso = Date.parse(value);
    if (Number.isFinite(iso)) return new Date(iso);
  }
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
  if (!match) return void 0;
  const [, year, month, day, hour, minute, second] = match.map(Number);
  const utc = Date.UTC(year, month - 1, day, hour, minute, second);
  const offsetMinutes = timeZoneOffsetMinutes(new Date(utc), timeZone);
  return new Date(utc - offsetMinutes * 6e4);
}
function startOfDay(date, timeZone) {
  const parts = localDateTimeParts(date, timeZone);
  return parseSynopticDate(
    `${parts.year}-${parts.month}-${parts.day}T00:00:00`,
    timeZone
  ) ?? date;
}
function dateKey(date, timeZone) {
  const parts = localDateTimeParts(date, timeZone);
  return `${parts.year}-${parts.month}-${parts.day}`;
}
function formatLocalTimestamp(date, timeZone) {
  const parts = localDateTimeParts(date, timeZone);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
}
function localDateTimeParts(date, timeZone) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  });
  const parts = Object.fromEntries(
    formatter.formatToParts(date).filter((part) => part.type !== "literal").map((part) => [part.type, part.value])
  );
  return parts;
}
function timeZoneOffsetMinutes(date, timeZone) {
  const parts = localDateTimeParts(date, timeZone);
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
  return (asUtc - date.getTime()) / 6e4;
}
function round(value) {
  return Math.round(value * 100) / 100;
}

// src/models/Firebase/FirebaseCurrentWeather.ts
function createInvalidFirebaseCurrentWeather() {
  return {
    humidity: void 0,
    humidityIndoor: void 0,
    pressure: void 0,
    solarPower: void 0,
    temperature: void 0,
    temperatureIndoor: void 0,
    voltageWeather: void 0,
    voltageWind: void 0,
    windDirection: void 0,
    windSpeed: void 0,
    rain: void 0,
    rainMonth: void 0,
    rainRate: void 0,
    rainStorm: void 0,
    rainTips: void 0,
    rainYear: void 0,
    rainYesterday: void 0,
    timestamp: void 0,
    currentRainTimestamp: void 0
  };
}

// src/models/Firebase/FirebaseExtremes.ts
function createInvalidFirebaseExtremesValues() {
  return {
    temperature: void 0,
    temperatureTimestamp: void 0,
    humidity: void 0,
    humidityTimestamp: void 0,
    pressure: void 0,
    pressureTimestamp: void 0,
    windSpeed: void 0,
    windSpeedTimestamp: void 0,
    rainRate: void 0,
    rainRateTimestamp: void 0,
    rainHour: void 0,
    rainHourTimestamp: void 0
  };
}
function createInvalidFirebaseExtremes() {
  return {
    date: void 0,
    minimums: createInvalidFirebaseExtremesValues(),
    maximums: createInvalidFirebaseExtremesValues(),
    hourly: void 0
  };
}

// src/services/EnsembleForecast.ts
var ENSEMBLE_MODELS = {
  ecmwf_ifs025: {
    name: "ECMWF IFS",
    resolution: "0.25\xB0",
    members: 51,
    maxDays: 15
  },
  gfs05: { name: "GFS GEFS", resolution: "0.5\xB0", members: 31, maxDays: 30 }
};
var HOUR = 36e5;
var VARIABLES = [
  "temperature_850hPa",
  "temperature_700hPa",
  "temperature_2m",
  "precipitation",
  "rain",
  "snowfall",
  "surface_pressure"
];
function ensembleRequestUrl(latitude, longitude, model, endpoint = "https://ensemble-api.open-meteo.com/v1/ensemble") {
  const url = new URL(endpoint);
  for (const [key, value] of Object.entries({
    latitude,
    longitude,
    models: model,
    hourly: VARIABLES.join(","),
    temperature_unit: "celsius",
    precipitation_unit: "mm",
    timezone: "auto",
    timeformat: "unixtime",
    past_days: 1,
    // Precipitation uses interval ends: include the following midnight.
    forecast_days: ENSEMBLE_MODELS[model].maxDays + 1
  }))
    url.searchParams.set(key, String(value));
  return url.toString();
}
var record = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : {};
var finite = (value) => typeof value === "number" && Number.isFinite(value);
function normalizeEnsemble(raw, model, fetchedAt = Date.now()) {
  const data = record(raw), hourly = record(data.hourly), units = record(data.hourly_units);
  const rawTimes = hourly.time;
  if (!Array.isArray(rawTimes) || !rawTimes.length || rawTimes.some(
    (t, i) => !finite(t) || i > 0 && t - rawTimes[i - 1] !== 3600
  )) {
    throw new Error("Ensemble response has no valid hourly timeline.");
  }
  const timeZone = typeof data.timezone === "string" ? data.timezone : "UTC";
  new Intl.DateTimeFormat("en", { timeZone }).format(0);
  const series = (variable, id, unit, nonnegative = false) => {
    const key = id === 0 ? variable : `${variable}_member${String(id).padStart(2, "0")}`;
    const values = hourly[key];
    if (!Array.isArray(values)) return rawTimes.map(() => null);
    if (units[key] !== unit)
      throw new Error(`Unexpected ensemble unit for ${variable}.`);
    return rawTimes.map(
      (_, i) => finite(values[i]) && (!nonnegative || values[i] >= 0) ? values[i] : null
    );
  };
  const members = Array.from(
    { length: ENSEMBLE_MODELS[model].members },
    (_, id) => {
      const pressure = series("surface_pressure", id, "hPa");
      return {
        id,
        surfacePressure: pressure,
        temperatureSurface: series("temperature_2m", id, "\xB0C"),
        temperature700: series("temperature_700hPa", id, "\xB0C").map(
          (t, i) => pressure[i] !== null && pressure[i] > 700 ? t : null
        ),
        precipitation: series("precipitation", id, "mm", true),
        rain: series("rain", id, "mm", true),
        snowfall: series("snowfall", id, "cm", true),
        // Unknown or below-ground pressure levels are not usable upper-air guidance.
        temperature850: series("temperature_850hPa", id, "\xB0C").map(
          (t, i) => pressure[i] !== null && pressure[i] > 850 ? t : null
        )
      };
    }
  );
  if (!members.some(
    (m) => m.precipitation.some(finite) || m.temperature850.some(finite) || m.temperature700.some(finite) || m.temperatureSurface.some(finite)
  )) {
    throw new Error("No ensemble member data is available for this location.");
  }
  return {
    model,
    timeZone,
    members,
    times: rawTimes.map((t) => t * 1e3),
    fetchedAt,
    latitude: finite(data.latitude) ? data.latitude : 0,
    longitude: finite(data.longitude) ? data.longitude : 0,
    elevation: finite(data.elevation) ? data.elevation : null
  };
}
function ensembleStatistics(values) {
  const sorted = values.filter(finite).sort((a, b) => a - b);
  const quantile = (p) => {
    if (!sorted.length) return null;
    const index = (sorted.length - 1) * p, low = Math.floor(index);
    return sorted[low] + (sorted[Math.ceil(index)] - sorted[low]) * (index - low);
  };
  return {
    count: sorted.length,
    mean: sorted.length ? sorted.reduce((sum2, n) => sum2 + n, 0) / sorted.length : null,
    median: quantile(0.5),
    p10: quantile(0.1),
    p90: quantile(0.9)
  };
}
function ensemblePeriods(timeZone, days, interval, now = Date.now(), timelineOrigin = 0) {
  const format = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23"
  });
  const local = (time) => {
    const p = Object.fromEntries(
      format.formatToParts(time).map((p2) => [p2.type, p2.value])
    );
    return { date: `${p.year}-${p.month}-${p.day}`, hour: Number(p.hour) };
  };
  const today = local(now).date;
  const groups = /* @__PURE__ */ new Map();
  const phase = timelineOrigin % HOUR;
  const first = Math.floor((now - phase) / HOUR) * HOUR + phase - 48 * HOUR;
  for (let end = first; end <= first + (days + 5) * 24 * HOUR; end += HOUR) {
    const part = local(end - HOUR / 2);
    if (part.date < today) continue;
    const hour = Math.floor(part.hour / interval) * interval, key = `${part.date}:${hour}`;
    const existing = groups.get(key);
    groups.set(key, {
      key,
      date: part.date,
      hour,
      start: existing?.start ?? end - HOUR,
      end
    });
  }
  const dates = [...new Set([...groups.values()].map((p) => p.date))].slice(
    0,
    days
  );
  return [...groups.values()].filter((p) => dates.includes(p.date));
}
function ensembleWindowTotals(forecast, quantity, start, end) {
  const first = forecast.times.indexOf(start + HOUR);
  const expected = Math.round((end - start) / HOUR);
  if (first < 0 || expected <= 0 || forecast.times[first + expected - 1] !== end)
    return forecast.members.map(() => null);
  return forecast.members.map((member) => {
    const values = member[quantity].slice(first, first + expected);
    return values.length === expected && values.every(finite) ? values.reduce((sum2, n) => sum2 + n, 0) : null;
  });
}
var ENSEMBLE_POP_THRESHOLD_MM = 0.254;
function ensemblePrecipitationAgreement(totals) {
  const valid = totals.filter(finite);
  const wet = valid.filter(
    (value) => value >= ENSEMBLE_POP_THRESHOLD_MM
  ).length;
  return {
    wet,
    count: valid.length,
    percent: valid.length ? 100 * wet / valid.length : null
  };
}
function ensembleMatrix(forecast, quantity, periods) {
  const columns = periods.map(
    (period) => ensembleWindowTotals(forecast, quantity, period.start, period.end)
  );
  return {
    rows: forecast.members.map((member, i) => ({
      id: member.id,
      values: columns.map((column) => column[i] ?? null)
    })),
    statistics: columns.map(ensembleStatistics),
    precipitationAgreement: periods.map(
      (period, i) => ensemblePrecipitationAgreement(
        quantity === "precipitation" ? columns[i] : ensembleWindowTotals(
          forecast,
          "precipitation",
          period.start,
          period.end
        )
      )
    )
  };
}
function ensembleTemperatureSeries(member, level) {
  return level === "surface" ? member.temperatureSurface : level === "700" ? member.temperature700 : member.temperature850;
}
function ensembleAutoTemperatureLevel(forecasts, start, end) {
  for (const [level, ceiling, pressure] of [
    ["850", 1e3, 880],
    ["700", 2500, 730]
  ]) {
    if (forecasts.length && forecasts.every(
      (f) => f.elevation !== null && f.elevation < ceiling && f.members.some(
        (m) => f.times.some(
          (t, i) => t >= start && t <= end && ensembleTemperatureSeries(m, level)[i] != null
        )
      ) && f.members.every(
        (m) => f.times.every(
          (t, i) => t < start || t > end || m.surfacePressure[i] == null || m.surfacePressure[i] >= pressure
        )
      )
    ))
      return level;
  }
  return "surface";
}
function ensembleWindowSummary(forecast, start, end, level, periods = [{ start, end }]) {
  const selected = periods.filter((p) => p.start >= start && p.end <= end);
  const totals = (quantity) => {
    const columns = selected.map((p) => ensembleWindowTotals(forecast, quantity, p.start, p.end)).filter((column) => column.some(finite));
    return {
      periods: columns.length,
      values: forecast.members.map((_, i) => {
        const values = columns.map((column) => column[i]);
        return values.length && values.every(finite) ? values.reduce((sum2, value) => sum2 + value, 0) : null;
      })
    };
  };
  const precipitationCoverage = totals("precipitation");
  const snowCoverage = totals("snowfall");
  const precipitation = precipitationCoverage.values;
  const snow = snowCoverage.values;
  const temperatureExtremes = (selectedLevel) => {
    const columns = selected.map(({ start: start2, end: end2 }) => {
      const indices = forecast.times.flatMap(
        (time, i) => time >= start2 && time < end2 ? [i] : []
      );
      const completeTimeline = indices.length === (end2 - start2) / HOUR && forecast.times[indices[0]] === start2 && forecast.times[indices.at(-1)] === end2 - HOUR;
      return forecast.members.map((member) => {
        const series = ensembleTemperatureSeries(member, selectedLevel);
        const values = indices.map((i) => series?.[i]);
        return completeTimeline && values.length && values.every(finite) ? { low: Math.min(...values), high: Math.max(...values) } : null;
      });
    }).filter((column) => column.some((v) => v !== null));
    return {
      periods: columns.length,
      values: forecast.members.map((_, i) => {
        const values = columns.map((column) => column[i]);
        return values.length && values.every((v) => v != null) ? {
          low: Math.min(...values.map((v) => v.low)),
          high: Math.max(...values.map((v) => v.high))
        } : null;
      })
    };
  };
  const temperatures = temperatureExtremes(level);
  const surface = level === "surface" ? temperatures : temperatureExtremes("surface");
  const surfaceComplete = surface.values.filter((v) => v !== null);
  const snowComplete = snow.filter(finite);
  return {
    coverage: {
      selected: selected.length,
      precipitation: precipitationCoverage.periods,
      snow: snowCoverage.periods,
      temperature: temperatures.periods,
      surface: surface.periods
    },
    precipitation: ensembleStatistics(precipitation),
    wet: ensemblePrecipitationAgreement(precipitation),
    temperatureLow: ensembleStatistics(
      temperatures.values.map((v) => v?.low ?? null)
    ),
    temperatureHigh: ensembleStatistics(
      temperatures.values.map((v) => v?.high ?? null)
    ),
    freezing: {
      count: surfaceComplete.length,
      members: surfaceComplete.filter((v) => v.low <= 0).length
    },
    snow: ensembleStatistics(snow),
    snowMembers: snowComplete.filter((v) => v >= 0.1).length
  };
}
function ensembleWinterWindow(forecast, start, end) {
  const month = new Intl.DateTimeFormat("en", {
    timeZone: forecast.timeZone,
    month: "numeric"
  });
  for (let time = start; time < end; time += HOUR) {
    const m = Number(month.format(time));
    if (forecast.latitude < 0 ? m >= 4 && m <= 10 : m >= 10 || m <= 4)
      return true;
  }
  return false;
}
function ensembleSurfaceContext(forecast, index) {
  const values = forecast.members.map(
    (m) => m.temperatureSurface[index] ?? null
  );
  const statistics = ensembleStatistics(values);
  const freezing = values.filter((v) => v !== null && v <= 0).length;
  return {
    ...statistics,
    freezing,
    freezingPercent: statistics.count ? 100 * freezing / statistics.count : null
  };
}

// src/types/common.ts
var WeatherValueType = /* @__PURE__ */ ((WeatherValueType2) => {
  WeatherValueType2["Temperature"] = "temperature";
  WeatherValueType2["ApparentTemperature"] = "apparentTemperature";
  WeatherValueType2["DewPoint"] = "dewPoint";
  WeatherValueType2["Humidity"] = "humidity";
  WeatherValueType2["Pressure"] = "pressure";
  WeatherValueType2["WindSpeed"] = "windSpeed";
  WeatherValueType2["WindDirection"] = "windDirection";
  WeatherValueType2["CloudCover"] = "cloudCover";
  return WeatherValueType2;
})(WeatherValueType || {});

// src/utils/SynopticWnumMapper.ts
function decodeSynopticWnum(wnum) {
  if (!Number.isFinite(wnum) || wnum <= 0) {
    return [];
  }
  const code = Math.trunc(wnum);
  if (code < 80) {
    return [code];
  }
  if (code < 6400) {
    const second2 = Math.floor(code / 80);
    const first2 = code - second2 * 80;
    return [first2, second2].filter((component) => component > 0);
  }
  const first = Math.floor(code / 6400);
  const second = Math.floor((code - first * 6400) / 80);
  const third = code - first * 6400 - second * 80;
  return [first, second, third].filter((component) => component > 0);
}
function convertSynopticWnumToWmo(wnum) {
  const mappedCodes = decodeSynopticWnum(wnum).map(convertSynopticConditionToWmo).filter((code) => code !== INVALID_INT);
  if (mappedCodes.length === 0) {
    return INVALID_INT;
  }
  return mappedCodes.reduce(
    (best, code) => displayPriority(code) > displayPriority(best) ? code : best
  );
}
function convertSynopticConditionToWmo(conditionCode) {
  switch (conditionCode) {
    case 1:
      return 63;
    // R: rain, moderate
    case 2:
      return 53;
    // L: drizzle, moderate
    case 3:
      return 73;
    // S: snow, moderate
    case 4:
      return 90;
    // A: hail, moderate
    case 5:
      return 17;
    // T: thunderstorm without precipitation at observation
    case 6:
      return 5;
    // H: haze
    case 7:
    case 11:
    case 39:
      return 4;
    // K/V/KH: smoke, volcanic ash, smoke/haze
    case 8:
      return 6;
    // D: dust
    case 9:
      return 45;
    // F: fog
    case 10:
      return 18;
    // Q: squalls
    case 13:
      return 61;
    // R-: light rain
    case 14:
      return 65;
    // R+: heavy rain
    case 15:
      return 67;
    // ZR: freezing rain
    case 16:
      return 81;
    // RW: rain shower, moderate
    case 17:
      return 51;
    // L-: light drizzle
    case 18:
      return 55;
    // L+: heavy drizzle
    case 19:
      return 57;
    // ZL: freezing drizzle
    case 20:
      return 71;
    // S-: light snow
    case 21:
      return 75;
    // S+: heavy snow
    case 22:
      return 86;
    // SW: snow shower, moderate
    case 23:
      return 79;
    // IP: ice pellets
    case 24:
      return 77;
    // SG: snow grains
    case 25:
    case 67:
      return 88;
    // SP/SPW: snow pellet shower, moderate/heavy
    case 26:
    case 38:
      return 89;
    // A-/AP: slight hail or small hail
    case 27:
      return 90;
    // A+: moderate/heavy hail
    case 28:
    case 29:
      return 17;
    // T-/T+: thunder without explicit precipitation
    case 30:
      return 48;
    // IF: freezing fog
    case 31:
      return 10;
    // GF: mist
    case 32:
      return 36;
    // BS: blowing snow
    case 33:
    case 35:
      return 31;
    // BD/BN: blowing dust/sand
    case 34:
      return 7;
    // BY: blowing spray
    case 36:
    case 37:
    case 64:
    case 65:
      return 76;
    // IC/IN: diamond dust / ice crystals
    case 40:
      return 8;
    // PO: dust or sand whirl
    case 41:
      return INVALID_INT;
    // UP: unknown precipitation
    case 49:
      return 66;
    // ZR-: light freezing rain
    case 50:
      return 67;
    // ZR+: heavy freezing rain
    case 51:
      return 80;
    // RW-: light rain shower
    case 52:
      return 82;
    // RW+: heavy rain shower
    case 53:
      return 56;
    // ZL-: light freezing drizzle
    case 54:
      return 57;
    // ZL+: heavy freezing drizzle
    case 55:
      return 85;
    // SW-: light snow shower
    case 56:
      return 86;
    // SW+: heavy snow shower
    case 57:
    case 58:
      return 79;
    // IP-/IP+: ice pellets
    case 59:
    case 60:
      return 77;
    // SG-/SG+: snow grains
    case 61:
    case 75:
      return 87;
    // SP-/IPW-: light pellets/small hail shower
    case 62:
    case 63:
    case 76:
      return 88;
    // SP+/IPW/IPW+: heavier pellets/small hail shower
    case 66:
    case 77:
      return 95;
    // TRW/TRW-: thunderstorm with rain shower
    case 68:
    case 69:
      return 35;
    // BD+/BN+: severe dust/sand storm
    case 70:
      return 39;
    // BS+: heavy blowing snow
    case 78:
      return 97;
    // TRW+: heavy thunderstorm with rain shower
    default:
      return INVALID_INT;
  }
}
function displayPriority(wmoCode) {
  if (wmoCode >= 97 && wmoCode <= 99) return 100;
  if (wmoCode >= 95 && wmoCode <= 96 || wmoCode === 17 || wmoCode === 18) return 90;
  if (wmoCode >= 66 && wmoCode <= 67 || wmoCode === 79 || wmoCode >= 87 && wmoCode <= 90) return 85;
  if (wmoCode >= 70 && wmoCode <= 78 || wmoCode >= 85 && wmoCode <= 86 || wmoCode >= 36 && wmoCode <= 39) return 80;
  if (wmoCode >= 60 && wmoCode <= 65 || wmoCode >= 80 && wmoCode <= 82 || wmoCode >= 50 && wmoCode <= 57) return 70;
  if (wmoCode >= 40 && wmoCode <= 49 || wmoCode >= 10 && wmoCode <= 12) return 60;
  if (wmoCode >= 30 && wmoCode <= 35) return 50;
  if (wmoCode >= 4 && wmoCode <= 9) return 40;
  return 0;
}

// src/utils/WeatherCodes.ts
var clearCodes = [0];
var scatteredClouds = [1];
var partlyCloudyCodes = [2];
var cloudyCodes = [3];
var hazeyCodes = [4, 5, 6, 7, 8, 9];
var fogCodes = [10, 11, 12, 44, 45, 46, 47, 48, 49];
var partialFogCodes = [41, 42, 43];
var lightRainCodes = [10, 50, 51, 52, 53, 54, 55, 58, 60, 61, 80, 91];
var rainCodes = [59, 62, 63, 64, 65, 81, 82, 92];
var rainSnowCodes = [68, 69];
var freezingRainCodes = [56, 57, 66, 67];
var lightSnowCodes = [70, 71, 85];
var snowCodes = [72, 73, 74, 75, 86];
var frostCodes = [76, 77, 78];
var icePelletsCodes = [79];
var hailShowersCodes = [87, 88, 89, 90];
var snowShowersCodes = [85, 86];
var rainShowersCodes = [80, 81, 82];
var rainSnowShowersCodes = [83, 84];
var thunderCodes = [17];
var thunderStormChanceCodes = [95, 96];
var thunderStormCodes = [97, 98, 99];
var precipPopThreshold = 20;
function isPrecipitationWeatherCode(code) {
  return lightRainCodes.includes(code) || rainCodes.includes(code) || rainSnowCodes.includes(code) || freezingRainCodes.includes(code) || lightSnowCodes.includes(code) || snowCodes.includes(code) || frostCodes.includes(code) || icePelletsCodes.includes(code) || hailShowersCodes.includes(code) || thunderStormCodes.includes(code) || snowShowersCodes.includes(code) || rainShowersCodes.includes(code) || rainSnowShowersCodes.includes(code) || thunderStormChanceCodes.includes(code);
}
function getWeatherIconName(weatherCode, isDay = true) {
  const iconClearCodes = [0, 1];
  const iconScatteredClouds = [2];
  const iconPartlyCloudyCodes = [3];
  const iconCloudyCodes = [4, 5];
  const iconHazeyCodes = [6, 7, 8, 9];
  const iconFogCodes = [10, 11, 12];
  const iconPartialFogCodes = [40, 41, 42, 43, 44, 45, 46, 47, 48, 49];
  const iconFrostCodes = [22, 23, 24, 76, 77, 78];
  const iconLightRainCodes = [50, 51, 52, 53, 60, 61];
  const iconRainCodes = [54, 55, 56, 57, 62, 63, 64, 65];
  const iconRainSnowCodes = [68, 69, 83, 84];
  const iconFreezingRainCodes = [66, 67, 79];
  const iconLightSnowCodes = [70, 71, 85];
  const iconSnowCodes = [72, 73, 74, 75, 86, 87];
  const iconThunderStormCodes = [95, 96, 97, 98, 99];
  const iconSnowShowersCodes = [77, 78];
  const iconRainShowersCodes = [80, 81, 82];
  const iconRainSnowShowersCodes = [83, 84];
  const iconThunderStormChanceCodes = [17, 29];
  let iconName = isDay ? "DayClear" : "NightClear";
  if (iconClearCodes.includes(weatherCode)) {
    iconName = isDay ? "DayClear" : "NightClear";
  } else if (iconScatteredClouds.includes(weatherCode)) {
    iconName = isDay ? "DayScatteredClouds" : "NightScatteredClouds";
  } else if (iconPartlyCloudyCodes.includes(weatherCode)) {
    iconName = isDay ? "DayPartlyCloudy" : "NightPartlyCloudy";
  } else if (iconCloudyCodes.includes(weatherCode)) {
    iconName = "Cloudy";
  } else if (iconHazeyCodes.includes(weatherCode)) {
    iconName = isDay ? "DayHazey" : "NightHazey";
  } else if (iconFogCodes.includes(weatherCode)) {
    iconName = "CloudyFog";
  } else if (iconPartialFogCodes.includes(weatherCode)) {
    iconName = isDay ? "DayFoggy" : "NightFoggy";
  } else if (iconFrostCodes.includes(weatherCode)) {
    iconName = "CloudyFrost";
  } else if (iconLightRainCodes.includes(weatherCode)) {
    iconName = "CloudyRainDrops";
  } else if (iconRainCodes.includes(weatherCode)) {
    iconName = "CloudyRain";
  } else if (iconRainSnowCodes.includes(weatherCode)) {
    iconName = "CloudyRainSnow";
  } else if (iconFreezingRainCodes.includes(weatherCode)) {
    iconName = "CloudyFreezingRain";
  } else if (iconLightSnowCodes.includes(weatherCode)) {
    iconName = "CloudyLightSnow";
  } else if (iconSnowCodes.includes(weatherCode)) {
    iconName = "CloudySnow";
  } else if (iconThunderStormCodes.includes(weatherCode)) {
    iconName = "CloudyThunderstorm";
  } else if (iconSnowShowersCodes.includes(weatherCode)) {
    iconName = isDay ? "DaySnowShowers" : "NightSnowShowers";
  } else if (iconRainShowersCodes.includes(weatherCode)) {
    iconName = isDay ? "DayShowers" : "NightShowers";
  } else if (iconRainSnowShowersCodes.includes(weatherCode)) {
    iconName = isDay ? "DayRainSnowShowers" : "NightRainSnowShowers";
  } else if (iconThunderStormChanceCodes.includes(weatherCode)) {
    iconName = isDay ? "DayThunderstorms" : "NightThunderstormsShowers";
  }
  return iconName;
}
function isWeatherValueValid(value, type) {
  if (isNaN(value) || value === INVALID_INT) {
    return false;
  }
  switch (type) {
    case "temperature" /* Temperature */:
    case "apparentTemperature" /* ApparentTemperature */:
    case "dewPoint" /* DewPoint */:
      return value >= -80 && value <= 150;
    case "cloudCover" /* CloudCover */:
    case "humidity" /* Humidity */:
      return value >= 0 && value <= 100;
    case "pressure" /* Pressure */:
      return value >= 800 && value <= 1100;
    case "windSpeed" /* WindSpeed */:
      return value >= 0 && value <= 200;
    case "windDirection" /* WindDirection */:
      return value >= 0 && value <= 360;
    default:
      return false;
  }
}
function getWMOCodeFromNWSWeatherGrid(coverage, weather, intensity, visibility) {
  let wmoCode = 0;
  coverage?.toLowerCase() ?? "";
  const weatherStr = weather?.toLowerCase() ?? "";
  const intensityStr = intensity?.toLowerCase() ?? "";
  const visibilityStr = visibility?.toLowerCase() ?? "";
  if (weatherStr.includes("drizzle")) {
    if (intensityStr.includes("very_light") || intensityStr.includes("very light")) {
      wmoCode = 51;
    } else if (intensityStr.includes("heavy")) {
      wmoCode = 55;
    } else {
      wmoCode = 53;
    }
    if (weatherStr.includes("freezing")) {
      wmoCode = intensityStr.includes("heavy") ? 57 : 56;
    }
  } else if (weatherStr.includes("rain")) {
    if (intensityStr.includes("light")) {
      wmoCode = 61;
    } else if (intensityStr.includes("heavy")) {
      wmoCode = 65;
    } else {
      wmoCode = 63;
    }
    if (weatherStr.includes("freezing")) {
      wmoCode = intensityStr.includes("heavy") ? 67 : 66;
    }
  } else if (weatherStr.includes("snow")) {
    if (weatherStr.includes("grains")) {
      wmoCode = 77;
    } else if (intensityStr.includes("light")) {
      wmoCode = 71;
    } else if (intensityStr.includes("heavy")) {
      wmoCode = 75;
    } else {
      wmoCode = 73;
    }
    if (weatherStr.includes("blowing") || weatherStr.includes("drifting")) {
      wmoCode = intensityStr.includes("heavy") ? 39 : 36;
    }
  } else if (weatherStr.includes("sleet") || weatherStr.includes("ice_pellets") || weatherStr.includes("ice pellets")) {
    wmoCode = 79;
  } else if (weatherStr.includes("rain_showers") || weatherStr.includes("rain showers")) {
    if (intensityStr.includes("light")) {
      wmoCode = 80;
    } else if (intensityStr.includes("heavy")) {
      wmoCode = 82;
    } else {
      wmoCode = 81;
    }
  } else if (weatherStr.includes("snow_showers") || weatherStr.includes("snow showers")) {
    wmoCode = intensityStr.includes("heavy") ? 86 : 85;
  } else if (weatherStr.includes("thunderstorms")) {
    if (intensityStr.includes("severe") || intensityStr.includes("heavy")) {
      wmoCode = 99;
    } else {
      wmoCode = 95;
    }
  } else if (weatherStr.includes("fog") || visibilityStr.includes("areas_fog") || visibilityStr.includes("patchy_fog")) {
    if (visibilityStr.includes("patchy")) {
      wmoCode = 41;
    } else if (weatherStr.includes("freezing")) {
      wmoCode = 48;
    } else {
      wmoCode = 45;
    }
  } else if (weatherStr.includes("blowing_dust") || weatherStr.includes("blowing dust")) {
    wmoCode = 31;
  } else if (weatherStr.includes("dust_storm") || weatherStr.includes("sandstorm")) {
    wmoCode = intensityStr.includes("severe") ? 35 : 34;
  } else if (weatherStr.includes("haze") || weatherStr.includes("smoke")) {
    wmoCode = 5;
  } else if (weatherStr.includes("volcanic_ash") || weatherStr.includes("volcanic ash")) {
    wmoCode = 4;
  } else if (weatherStr.includes("rain") && weatherStr.includes("snow")) {
    wmoCode = 68;
  }
  return wmoCode;
}
function getWMOCodeFromNWSWeatherIcon(nwsIcon, description) {
  let wmoCode = 0;
  const icon = nwsIcon.toLowerCase();
  const desc = description.toLowerCase();
  if (icon.includes("skc")) {
    wmoCode = 0;
  } else if (icon.includes("few")) {
    wmoCode = 1;
  } else if (icon.includes("sct") || icon.includes("partly")) {
    wmoCode = 2;
  } else if (icon.includes("bkn") || icon.includes("mostly")) {
    wmoCode = 3;
  } else if (icon.includes("ovc")) {
    wmoCode = 3;
  } else if (icon.includes("rain") && !icon.includes("tsra") && !icon.includes("showers")) {
    if (desc.includes("heavy")) {
      wmoCode = 65;
    } else if (desc.includes("light") || desc.includes("slight")) {
      wmoCode = 61;
    } else if (desc.includes("freezing")) {
      wmoCode = 67;
    } else {
      wmoCode = 63;
    }
  } else if (icon.includes("rain_showers") || icon.includes("showers") && !icon.includes("tsra")) {
    wmoCode = 80;
  } else if (icon.includes("tsra")) {
    wmoCode = 95;
  } else if (icon.includes("fog")) {
    if (desc.includes("then sunny") || desc.includes("then partly sunny") || desc.includes("then clear") || desc.includes("then mostly clear")) {
      wmoCode = 42;
    } else if (desc.includes("patchy")) {
      wmoCode = 41;
    } else {
      wmoCode = 45;
    }
  } else if (icon.includes("snow")) {
    if (desc.includes("heavy")) {
      wmoCode = 75;
    } else if (desc.includes("rain")) {
      if (desc.includes("showers")) {
        wmoCode = 83;
      } else {
        wmoCode = 68;
      }
    } else if (desc.includes("light")) {
      wmoCode = 71;
    } else if (desc.includes("showers") && !desc.includes("rain")) {
      wmoCode = 85;
    } else {
      wmoCode = 73;
    }
  } else if (icon.includes("sleet") || icon.includes("rain_sleet")) {
    wmoCode = 79;
  } else if (icon.includes("snow_fzra")) {
    wmoCode = 68;
  } else if (desc.includes("blizzard")) {
    wmoCode = 39;
  }
  return wmoCode;
}
function weatherDescription(WMOCode) {
  if (WMOCode === INVALID_INT || WMOCode === -999 || isNaN(WMOCode)) {
    return "Weather data unavailable";
  }
  switch (WMOCode) {
    case 0:
      return "Clear skies";
    case 1:
      return "Mainly clear";
    case 2:
      return "Partly cloudy";
    case 3:
      return "Cloudy";
    case 4:
    case 5:
    case 6:
    case 7:
    case 8:
    case 9:
      return "Hazy";
    case 10:
      return "Mist";
    case 17:
      return "Thunderstorms";
    case 28:
      return "Foggy";
    case 31:
      return "Blowing Dust/Sand";
    case 34:
    case 35:
      return "Severe Sandstorm";
    case 36:
      return "Blowing Snow";
    case 44:
    case 45:
    case 46:
    case 47:
    case 49:
      return "Foggy";
    case 48:
      return "Freezing Fog";
    case 51:
    case 53:
    case 55:
      return "Light Rain";
    case 56:
    case 57:
      return "Freezing Rain";
    case 58:
    case 59:
    case 60:
    case 61:
    case 91:
      return "Light Rain";
    case 62:
    case 63:
    case 64:
    case 92:
      return "Rain";
    case 65:
      return "Heavy Rain";
    case 66:
    case 67:
      return "Freezing Rain";
    case 68:
    case 69:
      return "Rain & Snow Mix";
    case 70:
    case 71:
      return "Light Snowfall";
    case 72:
    case 73:
      return "Snow";
    case 75:
      return "Heavy Snowfall";
    case 76:
    case 78:
      return "Ice Crystals";
    case 77:
    case 79:
      return "Ice Pellets";
    case 80:
      return "Light Rain Showers";
    case 81:
      return "Rain Showers";
    case 82:
      return "Heavy Rain Showers";
    case 83:
    case 84:
      return "Rain & Snow Showers";
    case 85:
    case 86:
      return "Snow Showers";
    case 87:
    case 88:
      return "Hail/Snow Pellet Showers";
    case 89:
    case 90:
      return "Hail Showers";
    case 94:
      return "Heavy Snowfall with Thunder";
    case 18:
    case 95:
    case 96:
      return "Thunderstorms";
    case 97:
    case 98:
    case 99:
      return "Severe Thunderstorms";
    case 21:
      return "Rain";
    case 22:
      return "Snow";
    case 23:
      return "Rain & Snow Mix";
    case 24:
      return "Freezing Rain";
    default:
      return `Unknown weather code (${WMOCode})`;
  }
}
function convertSynopticConditionToWmo2(conditionCode) {
  return convertSynopticConditionToWmo(conditionCode);
}
function convertSynopticWnumToWeatherCode(wnum) {
  return convertSynopticWnumToWmo(wnum);
}
function convertNwsToWmo(_nwsCode) {
  return INVALID_INT;
}
function getCloudCoverFromWMO(wmoCode) {
  if (wmoCode === INVALID_INT) {
    return INVALID_INT;
  }
  switch (wmoCode) {
    case 0:
      return 0;
    // Clear sky
    case 1:
      return 25;
    // Scattered clouds
    case 2:
      return 50;
    // Broken clouds
    case 3:
      return 100;
    // Overcast
    default:
      return 100;
  }
}

// src/utils/Common.ts
function roundToNearestFive(value) {
  return 5 * Math.round(value / 5);
}
function getDewPoint(temperatureF, humidity) {
  const temperatureC = (temperatureF - 32) * 5 / 9;
  const a = 17.27 * temperatureC;
  const b = 237.7 + temperatureC;
  const saturationVaporPressure = 6.105 * Math.exp(a / b);
  const actualVaporPressure = humidity / 100 * saturationVaporPressure;
  const dewPointC = 237.7 * Math.log(actualVaporPressure / 6.105) / (17.27 - Math.log(actualVaporPressure / 6.105));
  return Math.round(dewPointC * 9 / 5 + 32);
}
function isLocationInUnitedStates(lat, lng) {
  const continentalUS = {
    latMin: 24.396308,
    latMax: 49.384358,
    lngMin: -125,
    lngMax: -66.93457
  };
  const hawaii = {
    latMin: 18,
    latMax: 23,
    lngMin: -161,
    lngMax: -154
  };
  const alaska = {
    latMin: 51,
    latMax: 71,
    lngMin: -179,
    lngMax: -129
  };
  const inContinentalUS = lat >= continentalUS.latMin && lat <= continentalUS.latMax && lng >= continentalUS.lngMin && lng <= continentalUS.lngMax;
  const inHawaii = lat >= hawaii.latMin && lat <= hawaii.latMax && lng >= hawaii.lngMin && lng <= hawaii.lngMax;
  const inAlaska = lat >= alaska.latMin && lat <= alaska.latMax && lng >= alaska.lngMin && lng <= alaska.lngMax;
  return inContinentalUS || inHawaii || inAlaska;
}
var HOLIDAY_CODES = {
  // 2024
  "2024-12-25": "CHR",
  // Christmas
  "2024-11-11": "VET",
  // Veterans Day
  "2024-10-31": "HAL",
  // Halloween
  "2024-01-01": "NEW",
  // New Year's Day
  "2024-02-14": "VAL",
  // Valentine's Day
  "2024-07-04": "IND",
  // Independence Day
  "2024-11-28": "THA",
  // Thanksgiving
  "2024-01-15": "MLK",
  // Martin Luther King Jr. Day
  "2024-02-19": "PRE",
  // Presidents' Day
  "2024-05-27": "MEM",
  // Memorial Day
  "2024-09-02": "LAB",
  // Labor Day
  "2024-10-14": "IND",
  // Indigenous Peoples' Day
  "2024-03-17": "PAT",
  // St. Patrick's Day
  "2024-05-12": "MOM",
  // Mother's Day
  "2024-06-16": "DAD",
  // Father's Day
  "2024-12-24": "EVE",
  // Christmas Eve
  "2024-12-31": "NYE",
  // New Years Eve
  "2024-02-02": "GRU",
  // Groundhog Day
  "2024-04-01": "APR",
  // April Fool's Day
  "2024-05-05": "CIN",
  // Cinco De Mayo
  // 2025
  "2025-12-25": "CHR",
  "2025-11-11": "VET",
  "2025-10-31": "HAL",
  "2025-01-01": "NEW",
  "2025-02-14": "VAL",
  "2025-07-04": "IND",
  "2025-11-27": "THA",
  "2025-01-20": "MLK",
  "2025-02-17": "PRE",
  "2025-05-26": "MEM",
  "2025-09-01": "LAB",
  "2025-10-13": "IND",
  "2025-03-17": "PAT",
  "2025-05-11": "MOM",
  "2025-06-15": "DAD",
  "2025-12-24": "EVE",
  "2025-12-31": "NYE",
  "2025-02-02": "GRU",
  "2025-04-01": "APR",
  "2025-05-05": "CIN",
  // 2026
  "2026-12-25": "CHR",
  "2026-11-11": "VET",
  "2026-10-31": "HAL",
  "2026-01-01": "NEW",
  "2026-02-14": "VAL",
  "2026-07-04": "IND",
  "2026-11-26": "THA",
  "2026-01-19": "MLK",
  "2026-02-16": "PRE",
  "2026-05-25": "MEM",
  "2026-09-07": "LAB",
  "2026-10-12": "IND",
  "2026-03-17": "PAT",
  "2026-05-10": "MOM",
  "2026-06-21": "DAD",
  "2026-12-24": "EVE",
  "2026-12-31": "NYE",
  "2026-02-02": "GRU",
  "2026-04-01": "APR",
  "2026-05-05": "CIN"
};
function extractShortDayNameFromTimestamp(start, end, timezoneOffsetSeconds) {
  const offsetMs = timezoneOffsetSeconds * 1e3;
  const startLocal = new Date(start.getTime() + offsetMs);
  const endLocal = new Date(end.getTime() + offsetMs);
  const startDay = startLocal.getUTCDate();
  const endDay = endLocal.getUTCDate();
  const dayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  let shortDayName = dayNames[startLocal.getUTCDay()];
  const year = startLocal.getUTCFullYear();
  const month = String(startLocal.getUTCMonth() + 1).padStart(2, "0");
  const day = String(startLocal.getUTCDate()).padStart(2, "0");
  const dateString = `${year}-${month}-${day}`;
  const holidayCode = HOLIDAY_CODES[dateString];
  if (holidayCode) {
    shortDayName = holidayCode;
  }
  if (startDay !== endDay) {
    return shortDayName + " NIGHT";
  } else {
    return shortDayName;
  }
}
function cardinalDirection(degrees, onlyCardinal = false) {
  const deg = degrees === -9999 || degrees === -999 || degrees === -99 ? 0 : degrees;
  let normalizedDegrees = deg % 360;
  if (normalizedDegrees < 0) {
    normalizedDegrees += 360;
  }
  if (onlyCardinal) {
    const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW", "N"];
    const index = Math.floor((normalizedDegrees + 22.5) / 45);
    return directions[index % 8];
  } else {
    const directions = [
      "N",
      "NNE",
      "NE",
      "ENE",
      "E",
      "ESE",
      "SE",
      "SSE",
      "S",
      "SSW",
      "SW",
      "WSW",
      "W",
      "WNW",
      "NW",
      "NNW",
      "N"
    ];
    const index = Math.floor((normalizedDegrees + 11.25) / 22.5);
    return directions[index % 16];
  }
}
function getDegreesFromCardinal(cardinalDirection2) {
  const directions = {
    "N": 0,
    "NNE": 22.5,
    "NE": 45,
    "ENE": 67.5,
    "E": 90,
    "ESE": 112.5,
    "SE": 135,
    "SSE": 157.5,
    "S": 180,
    "SSW": 202.5,
    "SW": 225,
    "WSW": 247.5,
    "W": 270,
    "WNW": 292.5,
    "NW": 315,
    "NNW": 337.5
  };
  return directions[cardinalDirection2] ?? null;
}
function getDistance(lat1, lng1, lat2, lng2) {
  const R = 6371e3;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distanceInMeters = R * c;
  const distanceInMiles = distanceInMeters * 621371e-9;
  return distanceInMiles;
}
function calculateEstimatedUVIndex(lat, lng, cloudCover, date) {
  const rad = Math.PI / 180;
  const utcDate = new Date(date.getTime());
  const startOfYear = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
  const dayOfYear = Math.floor((utcDate.getTime() - startOfYear.getTime()) / (1e3 * 60 * 60 * 24)) + 1;
  const hour = utcDate.getUTCHours();
  const minute = utcDate.getUTCMinutes();
  const timeInHours = hour + minute / 60;
  const declination = 23.45 * Math.sin(rad * (360 / 365) * (dayOfYear - 81));
  const b = rad * (360 / 364) * (dayOfYear - 81);
  const eot = 9.87 * Math.sin(2 * b) - 7.53 * Math.cos(b) - 1.5 * Math.sin(b);
  const solarTimeCorrection = 4 * lng + eot;
  const localSolarTime = timeInHours * 60 + solarTimeCorrection;
  const hourAngle = localSolarTime / 4 - 180;
  const sinElevation = Math.sin(lat * rad) * Math.sin(declination * rad) + Math.cos(lat * rad) * Math.cos(declination * rad) * Math.cos(hourAngle * rad);
  if (sinElevation <= 0) return 0;
  const clearSkyUV = 11.5 * sinElevation;
  const cloudFactor = Math.max(0, 1 - 8e-3 * cloudCover);
  const finalUV = clearSkyUV * cloudFactor;
  return Math.round(finalUV * 10) / 10;
}
function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : r & 3 | 8;
    return v.toString(16);
  });
}

// src/utils/SolarCalculator.ts
var SolarCalculator = class {
  /**
   * Initializes the SolarCalculator with a specific location.
   *
   * @param config - Configuration object with latitude, longitude, and optional elevation
   */
  constructor(config) {
    this.latitude = config.latitude;
    this.longitude = config.longitude;
    this.elevation = config.elevation ?? 0;
  }
  /**
   * Calculates the sunrise and sunset times for a specific date at the configured location.
   *
   * The calculation accounts for:
   * - The date's specific solar declination.
   * - The location's latitude and longitude.
   * - The observer's elevation (correcting for horizon dip).
   * - Atmospheric refraction (assuming standard conditions).
   *
   * @param date - The date for which to calculate solar events (defaults to current date)
   * @param timeZoneId - IANA timezone identifier (e.g., "America/Los_Angeles"). Defaults to system timezone.
   * @returns Object containing optional sunrise and sunset dates. Returns null for either if the sun does not rise or set on that day (e.g., in polar regions).
   */
  calculate(date = /* @__PURE__ */ new Date(), timeZoneId) {
    const tzOffset = this.getTimezoneOffset(date, timeZoneId);
    const dayOfYear = this.getDayOfYear(date, tzOffset);
    const sunriseTimeUTC = this.calculateTime(true, dayOfYear);
    const sunsetTimeUTC = this.calculateTime(false, dayOfYear);
    const localMidnight = this.getStartOfDay(date, tzOffset);
    const utcMidnight = this.getUTCMidnight(date, tzOffset);
    const resolveDate = (utcHours) => {
      if (utcHours === null) return null;
      let calculatedDate = new Date(utcMidnight.getTime() + utcHours * 36e5);
      const localNoon = new Date(localMidnight.getTime() + 432e5);
      const diff = calculatedDate.getTime() - localNoon.getTime();
      if (diff > 432e5) {
        calculatedDate = new Date(calculatedDate.getTime() - 864e5);
      } else if (diff < -432e5) {
        calculatedDate = new Date(calculatedDate.getTime() + 864e5);
      }
      return calculatedDate;
    };
    return {
      sunrise: resolveDate(sunriseTimeUTC),
      sunset: resolveDate(sunsetTimeUTC)
    };
  }
  /**
   * Internal helper to perform the core solar time calculation.
   *
   * @param isSunrise - Boolean indicating if we are calculating sunrise (true) or sunset (false)
   * @param dayOfYear - The day of the year (1-366)
   * @returns The time of the event in UTC hours (0-24), or null if the event does not occur
   */
  calculateTime(isSunrise, dayOfYear) {
    const lngHour = this.longitude / 15;
    const t = isSunrise ? dayOfYear + (6 - lngHour) / 24 : dayOfYear + (18 - lngHour) / 24;
    const M = 0.9856 * t - 3.289;
    let L = M + 1.916 * Math.sin(this.deg2rad(M)) + 0.02 * Math.sin(2 * this.deg2rad(M)) + 282.634;
    L = this.normalize(L, 360);
    let RA = this.rad2deg(Math.atan(0.91764 * Math.tan(this.deg2rad(L))));
    RA = this.normalize(RA, 360);
    const Lquadrant = Math.floor(L / 90) * 90;
    const RAquadrant = Math.floor(RA / 90) * 90;
    RA = RA + (Lquadrant - RAquadrant);
    RA = RA / 15;
    const sinDec = 0.39782 * Math.sin(this.deg2rad(L));
    const cosDec = Math.cos(Math.asin(sinDec));
    const elevationMeters = this.elevation * 0.3048;
    const elevationCorrection = 0.02933 * Math.sqrt(elevationMeters);
    const zenith = 90.8333 + elevationCorrection;
    const cosH = (Math.cos(this.deg2rad(zenith)) - sinDec * Math.sin(this.deg2rad(this.latitude))) / (cosDec * Math.cos(this.deg2rad(this.latitude)));
    if (cosH > 1 || cosH < -1) return null;
    let H;
    if (isSunrise) {
      H = 360 - this.rad2deg(Math.acos(cosH));
    } else {
      H = this.rad2deg(Math.acos(cosH));
    }
    H = H / 15;
    const T = H + RA - 0.06571 * t - 6.622;
    let UT = T - lngHour;
    UT = this.normalize(UT, 24);
    return UT;
  }
  /**
   * Normalizes a value to be within the range [0, max).
   *
   * @param value - The value to normalize
   * @param max - The maximum bound of the range
   * @returns The normalized value
   */
  normalize(value, max) {
    let v = value;
    while (v < 0) v += max;
    while (v >= max) v -= max;
    return v;
  }
  /**
   * Converts degrees to radians
   */
  deg2rad(degrees) {
    return degrees * Math.PI / 180;
  }
  /**
   * Converts radians to degrees
   */
  rad2deg(radians) {
    return radians * 180 / Math.PI;
  }
  /**
   * Gets the day of year for a date in a specific timezone
   */
  getDayOfYear(date, tzOffsetMs) {
    const localDate = new Date(date.getTime() + tzOffsetMs);
    const startOfYear = new Date(Date.UTC(localDate.getUTCFullYear(), 0, 1));
    const diff = localDate.getTime() - startOfYear.getTime();
    return Math.floor(diff / 864e5) + 1;
  }
  /**
   * Gets the start of day (midnight) in the specified timezone
   */
  getStartOfDay(date, tzOffsetMs) {
    const localDate = new Date(date.getTime() + tzOffsetMs);
    const year = localDate.getUTCFullYear();
    const month = localDate.getUTCMonth();
    const day = localDate.getUTCDate();
    const localMidnight = Date.UTC(year, month, day);
    return new Date(localMidnight - tzOffsetMs);
  }
  /**
   * Gets UTC midnight for the same calendar day as the given date in the specified timezone
   */
  getUTCMidnight(date, tzOffsetMs) {
    const localDate = new Date(date.getTime() + tzOffsetMs);
    const year = localDate.getUTCFullYear();
    const month = localDate.getUTCMonth();
    const day = localDate.getUTCDate();
    return new Date(Date.UTC(year, month, day));
  }
  /**
   * Gets the timezone offset in milliseconds for a given timezone ID
   * If no timezone ID is provided, uses the system's local timezone
   */
  getTimezoneOffset(date, timeZoneId) {
    if (!timeZoneId) {
      return -date.getTimezoneOffset() * 6e4;
    }
    try {
      const offsetMinutes = getTimeZoneOffset(date, timeZoneId);
      return offsetMinutes * 6e4;
    } catch (error) {
      console.warn(`Invalid timezone ID: ${timeZoneId}, using local timezone`);
      return -date.getTimezoneOffset() * 6e4;
    }
  }
};

// src/utils/MetricImperial.ts
function roundToPlaces(value, places) {
  const divisor = Math.pow(10, places);
  return Math.round(value * divisor) / divisor;
}
function inchesToMillimeters(value) {
  return value * INCHES_TO_MM;
}
function millimetersToInches(value) {
  return value / INCHES_TO_MM;
}
function fahrenheitToCelsius(value) {
  return (value - 32) * 5 / 9;
}
function celsiusToFahrenheit(value) {
  return value * 9 / 5 + 32;
}
function milesToKilometers(value) {
  return value * MILES_TO_KM;
}
function kilometersToMiles(value) {
  return value / MILES_TO_KM;
}
function feetToMeters(value) {
  return value * FEET_TO_METERS;
}
function metersToFeet(value) {
  return value / FEET_TO_METERS;
}
function milesPerHourToKilometersPerHour(value) {
  return Math.trunc(value * MILES_TO_KM);
}
function kilometersPerHourToMilesPerHour(value) {
  return Math.trunc(value / MILES_TO_KM);
}
function milesToKilometersAsInt(value) {
  return Math.round(value * MILES_TO_KM);
}
function kilometersToMilesAsInt(value) {
  return Math.round(value / MILES_TO_KM);
}
function stationPressureToSeaLevelPressure(pressure, elevationInFeet) {
  const elevationInMeters = feetToMeters(elevationInFeet);
  const tempLapseRate = 65e-4;
  const seaLevelTemp = 288.15;
  const base = 1 - tempLapseRate * elevationInMeters / seaLevelTemp;
  if (base <= 0) return INVALID_DOUBLE;
  const seaLevelPressure = pressure * Math.pow(base, -5.255932362359814);
  return roundToPlaces(seaLevelPressure, 2);
}
function apparentTemperatureBOM(temperature, humidity, windSpeed, units) {
  let temperatureC;
  let windSpeedMS;
  if (units === "Imperial") {
    temperatureC = fahrenheitToCelsius(temperature);
    windSpeedMS = windSpeed * 0.44704;
  } else {
    temperatureC = temperature;
    windSpeedMS = windSpeed / 3.6;
  }
  const e = humidity / 100 * 6.105 * Math.exp(17.27 * temperatureC / (237.7 + temperatureC));
  const apparentTempC = temperatureC + 0.33 * e - 0.7 * windSpeedMS - 4;
  if (units === "Imperial") {
    const apparentTempF = celsiusToFahrenheit(apparentTempC);
    return Math.round(apparentTempF);
  } else {
    return Math.round(apparentTempC);
  }
}
function roundArrayToPlaces(values, places) {
  return values.map((v) => roundToPlaces(v, places));
}
function arrayInchesToMillimeters(values) {
  return values.map(inchesToMillimeters);
}
function arrayMillimetersToInches(values) {
  return values.map(millimetersToInches);
}
function arrayFahrenheitToCelsius(values) {
  return values.map(fahrenheitToCelsius);
}
function arrayCelsiusToFahrenheit(values) {
  return values.map(celsiusToFahrenheit);
}
function arrayMilesToKilometers(values) {
  return values.map(milesToKilometers);
}
function arrayKilometersToMiles(values) {
  return values.map(kilometersToMiles);
}
function arrayMilesToKilometersAsInt(values) {
  return values.map(milesToKilometersAsInt);
}
function arrayKilometersToMilesAsInt(values) {
  return values.map(kilometersToMilesAsInt);
}
function arrayFeetToMeters(values) {
  return values.map(feetToMeters);
}
function arrayMetersToFeet(values) {
  return values.map(metersToFeet);
}
function arrayMilesPerHourToKilometersPerHour(values) {
  return values.map(milesPerHourToKilometersPerHour);
}
function arrayKilometersPerHourToMilesPerHour(values) {
  return values.map(kilometersPerHourToMilesPerHour);
}

// src/utils/SnowWaterRatio.ts
var WET_HEAVY_TEMP_F = 32;
var NEAR_FREEZING_TEMP_F = 28;
var MODERATE_COLD_TEMP_F = 20;
var WET_HEAVY_SWR = 7;
var NEAR_FREEZING_SWR = 9;
var MODERATE_COLD_SWR = 11;
var COLD_SWR = 12;
var DGZ_BONUS_SWR = 1;
var DGZ_LOWER_F = -0.4;
var DGZ_UPPER_F = 10.4;
var MIN_SWR = 7;
var MAX_SWR = 13;
function calculateSnowWaterRatio(surfaceTempF, wetBulbTempF, temp700hPaF) {
  const effectiveTempF = wetBulbTempF ?? surfaceTempF;
  let baseSWR;
  if (effectiveTempF >= WET_HEAVY_TEMP_F) {
    baseSWR = WET_HEAVY_SWR;
  } else if (effectiveTempF >= NEAR_FREEZING_TEMP_F) {
    baseSWR = NEAR_FREEZING_SWR;
  } else if (effectiveTempF >= MODERATE_COLD_TEMP_F) {
    baseSWR = MODERATE_COLD_SWR;
  } else {
    baseSWR = COLD_SWR;
  }
  let swr = baseSWR;
  if (temp700hPaF !== null && temp700hPaF >= DGZ_LOWER_F && temp700hPaF <= DGZ_UPPER_F) {
    swr += DGZ_BONUS_SWR;
  }
  return Math.min(Math.max(swr, MIN_SWR), MAX_SWR);
}

// src/utils/WinterPrecipAnalyzer.ts
var WinterPrecipType = /* @__PURE__ */ ((WinterPrecipType2) => {
  WinterPrecipType2["NONE"] = "NONE";
  WinterPrecipType2["RAIN"] = "RAIN";
  WinterPrecipType2["SNOW"] = "SNOW";
  WinterPrecipType2["FREEZING_RAIN"] = "FREEZING_RAIN";
  WinterPrecipType2["ICE_PELLETS"] = "ICE_PELLETS";
  WinterPrecipType2["MIXED"] = "MIXED";
  return WinterPrecipType2;
})(WinterPrecipType || {});
var FREEZING_F = 32;
var WARM_NOSE_THRESHOLD_F = 34;
var COLD_SURFACE_THRESHOLD_F = 28;
var WET_BULB_SNOW_SURVIVES_F = 32;
var WET_BULB_MELTING_F = 34;
var HEIGHT_925HPA_FT = 2500;
var HEIGHT_850HPA_FT = 5e3;
var HEIGHT_700HPA_FT = 1e4;
var HIGH_ELEVATION_THRESHOLD_FT = 4500;
var MAX_SNOW_LEVEL_FT = 15e3;
function calculateWetBulbTemperature(tempF, humidityPct) {
  const T = (tempF - 32) * 5 / 9;
  const RH = humidityPct;
  const Tw = T * Math.atan(0.151977 * Math.sqrt(RH + 8.313659)) + Math.atan(T + RH) - Math.atan(RH - 1.676331) + 391838e-8 * Math.pow(RH, 1.5) * Math.atan(0.023101 * RH) - 4.686035;
  return Tw * 9 / 5 + 32;
}
var STANDARD_LAPSE_RATE_PER_1000FT = 3.5;
function calculateLapseRate(surfaceTempF, surfaceElevationFt, temp925hPaF, temp850hPaF, temp700hPaF) {
  if (temp925hPaF !== null && temp850hPaF !== null) {
    const tempDiff = temp925hPaF - temp850hPaF;
    const heightDiff = HEIGHT_850HPA_FT - HEIGHT_925HPA_FT;
    const rate = tempDiff / heightDiff * 1e3;
    if (rate > 0 && rate <= 10) {
      return rate;
    }
  }
  if (temp850hPaF !== null && temp700hPaF !== null) {
    const tempDiff = temp850hPaF - temp700hPaF;
    const heightDiff = HEIGHT_700HPA_FT - HEIGHT_850HPA_FT;
    const rate = tempDiff / heightDiff * 1e3;
    if (rate > 0 && rate <= 10) {
      return rate;
    }
  }
  if (temp925hPaF !== null && surfaceElevationFt < HEIGHT_925HPA_FT - 500) {
    const tempDiff = surfaceTempF - temp925hPaF;
    const heightDiff = HEIGHT_925HPA_FT - surfaceElevationFt;
    if (heightDiff > 500) {
      const rate = tempDiff / heightDiff * 1e3;
      if (rate > 0 && rate <= 10) {
        return rate;
      }
    }
  }
  return STANDARD_LAPSE_RATE_PER_1000FT;
}
function calculateSnowLevel(surfaceTempF, temp925hPaF, temp850hPaF, surfaceElevationFt, temp700hPaF = null) {
  if (surfaceTempF === null || !isFinite(surfaceTempF)) {
    return null;
  }
  const lapseRatePer1000ft = calculateLapseRate(
    surfaceTempF,
    surfaceElevationFt,
    temp925hPaF,
    temp850hPaF,
    temp700hPaF
  );
  if (surfaceTempF <= FREEZING_F) {
    const isHighElevation = surfaceElevationFt >= HIGH_ELEVATION_THRESHOLD_FT;
    if (isHighElevation && temp700hPaF !== null && temp700hPaF > WARM_NOSE_THRESHOLD_F) {
      if (temp850hPaF !== null && temp850hPaF <= WARM_NOSE_THRESHOLD_F) {
        return interpolateMeltingLevel(HEIGHT_850HPA_FT, temp850hPaF, HEIGHT_700HPA_FT, temp700hPaF);
      }
      return HEIGHT_700HPA_FT;
    }
    if (!isHighElevation && temp700hPaF !== null && temp700hPaF > WARM_NOSE_THRESHOLD_F) {
      const tempExcess2 = temp700hPaF - WARM_NOSE_THRESHOLD_F;
      const additionalHeight = tempExcess2 / lapseRatePer1000ft * 1e3;
      return Math.min(Math.round(HEIGHT_700HPA_FT + additionalHeight), MAX_SNOW_LEVEL_FT);
    }
    if (temp850hPaF !== null && temp850hPaF > WARM_NOSE_THRESHOLD_F) {
      if (temp700hPaF !== null && temp700hPaF <= WARM_NOSE_THRESHOLD_F) {
        return interpolateMeltingLevel(HEIGHT_850HPA_FT, temp850hPaF, HEIGHT_700HPA_FT, temp700hPaF);
      }
      const baseTemp = temp700hPaF !== null ? temp700hPaF : temp850hPaF;
      const baseHeight = temp700hPaF !== null ? HEIGHT_700HPA_FT : HEIGHT_850HPA_FT;
      const tempExcess2 = baseTemp - WARM_NOSE_THRESHOLD_F;
      const additionalHeight = tempExcess2 / lapseRatePer1000ft * 1e3;
      return Math.min(Math.round(baseHeight + additionalHeight), MAX_SNOW_LEVEL_FT);
    }
    if (temp925hPaF !== null && temp925hPaF > WARM_NOSE_THRESHOLD_F) {
      return interpolateMeltingLevel(surfaceElevationFt, surfaceTempF, HEIGHT_925HPA_FT, temp925hPaF);
    }
    if (temp925hPaF !== null && temp925hPaF <= FREEZING_F && (temp850hPaF === null || temp850hPaF <= FREEZING_F)) {
      const tempDeficit = FREEZING_F - surfaceTempF;
      const estimatedDepth = tempDeficit / lapseRatePer1000ft * 1e3;
      return Math.max(0, Math.round(surfaceElevationFt - estimatedDepth));
    }
    if (temp925hPaF === null && temp850hPaF === null) {
      const tempDeficit = FREEZING_F - surfaceTempF;
      const estimatedDepth = tempDeficit / lapseRatePer1000ft * 1e3;
      return Math.max(0, Math.round(surfaceElevationFt - estimatedDepth));
    }
    return Math.round(surfaceElevationFt);
  }
  if (temp925hPaF !== null) {
    if (temp925hPaF <= FREEZING_F) {
      return interpolateLevel(
        surfaceElevationFt,
        surfaceTempF,
        HEIGHT_925HPA_FT,
        temp925hPaF
      );
    }
    if (temp850hPaF !== null) {
      if (temp850hPaF <= FREEZING_F) {
        return interpolateLevel(
          HEIGHT_925HPA_FT,
          temp925hPaF,
          HEIGHT_850HPA_FT,
          temp850hPaF
        );
      }
      const lapseRatePerFt = lapseRatePer1000ft / 1e3;
      if (lapseRatePerFt > 1e-3) {
        const additionalHeight = (temp850hPaF - FREEZING_F) / lapseRatePerFt;
        const calculatedLevel = HEIGHT_850HPA_FT + additionalHeight;
        return Math.min(Math.round(calculatedLevel), MAX_SNOW_LEVEL_FT);
      }
      return MAX_SNOW_LEVEL_FT;
    }
  }
  const tempExcess = surfaceTempF - FREEZING_F;
  const estimatedHeight = tempExcess / lapseRatePer1000ft * 1e3;
  return Math.min(Math.round(surfaceElevationFt + estimatedHeight), MAX_SNOW_LEVEL_FT);
}
function interpolateLevel(lowerAlt, lowerTemp, upperAlt, upperTemp) {
  if (Math.abs(upperTemp - lowerTemp) < 0.1) {
    return Math.round((lowerAlt + upperAlt) / 2);
  }
  const fraction = (FREEZING_F - lowerTemp) / (upperTemp - lowerTemp);
  const freezingAlt = lowerAlt + fraction * (upperAlt - lowerAlt);
  return Math.round(freezingAlt);
}
function interpolateMeltingLevel(lowerAlt, lowerTemp, upperAlt, upperTemp) {
  if (Math.abs(upperTemp - lowerTemp) < 0.1) {
    return Math.round((lowerAlt + upperAlt) / 2);
  }
  const fraction = (WARM_NOSE_THRESHOLD_F - lowerTemp) / (upperTemp - lowerTemp);
  const meltingAlt = lowerAlt + fraction * (upperAlt - lowerAlt);
  return Math.round(meltingAlt);
}
function analyzeWinterPrecipType(profile) {
  const snowLevelFt = calculateSnowLevel(
    profile.surfaceTempF,
    profile.temp925hPaF,
    profile.temp850hPaF,
    profile.surfaceElevationFt,
    profile.temp700hPaF
  );
  const wetBulbTempF = profile.humidityPct !== null ? calculateWetBulbTemperature(profile.surfaceTempF, profile.humidityPct) : null;
  if (profile.totalPrecipRate <= 0) {
    return {
      type: "NONE" /* NONE */,
      confidence: 100,
      adjustedRainRate: 0,
      adjustedSnowRate: 0,
      iceRate: 0,
      wmoCode: null,
      snowLevelFt,
      wetBulbTempF,
      diagnostic: "No precipitation"
    };
  }
  const surfaceBelowFreezing = profile.surfaceTempF < FREEZING_F;
  const has925Data = profile.temp925hPaF !== null;
  const has850Data = profile.temp850hPaF !== null;
  const has700Data = profile.temp700hPaF !== null;
  const temp925BelowFreezing = has925Data && profile.temp925hPaF < FREEZING_F;
  const temp850BelowFreezing = has850Data && profile.temp850hPaF < FREEZING_F;
  const temp700BelowFreezing = has700Data && profile.temp700hPaF < FREEZING_F;
  const surfaceIsDeepCold = profile.surfaceTempF < COLD_SURFACE_THRESHOLD_F;
  const isHighElevation = profile.surfaceElevationFt >= HIGH_ELEVATION_THRESHOLD_FT;
  let hasWarmNose;
  let warmNoseLevel;
  let warmNoseTemp;
  if (isHighElevation && has700Data) {
    hasWarmNose = profile.temp700hPaF > WARM_NOSE_THRESHOLD_F;
    warmNoseLevel = "700hPa";
    warmNoseTemp = profile.temp700hPaF;
  } else if (has850Data) {
    hasWarmNose = profile.temp850hPaF > WARM_NOSE_THRESHOLD_F;
    warmNoseLevel = "850hPa";
    warmNoseTemp = profile.temp850hPaF;
  } else {
    hasWarmNose = false;
    warmNoseLevel = "N/A";
    warmNoseTemp = null;
  }
  if (surfaceBelowFreezing) {
    if (hasWarmNose) {
      if (surfaceIsDeepCold) {
        return {
          type: "ICE_PELLETS" /* ICE_PELLETS */,
          confidence: 80,
          adjustedRainRate: 0,
          adjustedSnowRate: 0,
          iceRate: profile.totalPrecipRate,
          wmoCode: 79,
          snowLevelFt,
          wetBulbTempF,
          diagnostic: `Warm nose at ${warmNoseLevel} (${warmNoseTemp?.toFixed(0)}\xB0F) with deep cold surface (${profile.surfaceTempF.toFixed(0)}\xB0F)`
        };
      }
      return {
        type: "FREEZING_RAIN" /* FREEZING_RAIN */,
        confidence: 85,
        adjustedRainRate: 0,
        adjustedSnowRate: 0,
        iceRate: profile.totalPrecipRate,
        wmoCode: getFreezingRainWMOCode(profile.totalPrecipRate),
        snowLevelFt,
        wetBulbTempF,
        diagnostic: `Warm nose at ${warmNoseLevel} (${warmNoseTemp?.toFixed(0)}\xB0F) with freezing surface (${profile.surfaceTempF.toFixed(0)}\xB0F)`
      };
    }
    if (has700Data && !isHighElevation && profile.temp700hPaF > WARM_NOSE_THRESHOLD_F) {
      const temp850NotWarmNose = has850Data && profile.temp850hPaF <= WARM_NOSE_THRESHOLD_F;
      if (temp850NotWarmNose && temp925BelowFreezing) {
        return {
          type: "ICE_PELLETS" /* ICE_PELLETS */,
          confidence: 75,
          adjustedRainRate: 0,
          adjustedSnowRate: 0,
          iceRate: profile.totalPrecipRate,
          wmoCode: 79,
          snowLevelFt,
          wetBulbTempF,
          diagnostic: `Warm origin at 700hPa (${profile.temp700hPaF?.toFixed(0)}\xB0F) - rain refreezes in cold layer (850=${profile.temp850hPaF?.toFixed(0)}\xB0F, 925=${profile.temp925hPaF?.toFixed(0)}\xB0F)`
        };
      }
    }
    const upperAirBelowFreezing = has925Data && temp925BelowFreezing || has850Data && temp850BelowFreezing || isHighElevation && has700Data && temp700BelowFreezing;
    if (upperAirBelowFreezing) {
      const diagParts = [`sfc=${profile.surfaceTempF.toFixed(0)}\xB0F`];
      if (has925Data) diagParts.push(`925=${profile.temp925hPaF?.toFixed(0)}\xB0F`);
      if (has850Data) diagParts.push(`850=${profile.temp850hPaF?.toFixed(0)}\xB0F`);
      if (isHighElevation && has700Data) diagParts.push(`700=${profile.temp700hPaF?.toFixed(0)}\xB0F`);
      return {
        type: "SNOW" /* SNOW */,
        confidence: 90,
        adjustedRainRate: 0,
        adjustedSnowRate: profile.totalPrecipRate,
        iceRate: 0,
        wmoCode: getSnowWMOCode(profile.totalPrecipRate),
        snowLevelFt,
        wetBulbTempF,
        diagnostic: `Column frozen: ${diagParts.join(", ")}`
      };
    }
    if (!has925Data && !has850Data && !(isHighElevation && has700Data)) {
      return {
        type: "SNOW" /* SNOW */,
        confidence: 70,
        adjustedRainRate: 0,
        adjustedSnowRate: profile.totalPrecipRate,
        iceRate: 0,
        wmoCode: getSnowWMOCode(profile.totalPrecipRate),
        snowLevelFt,
        wetBulbTempF,
        diagnostic: `Surface below freezing (${profile.surfaceTempF.toFixed(0)}\xB0F), no upper air data`
      };
    }
    if (has925Data && !temp925BelowFreezing) {
      if (hasWarmNose) {
        if (surfaceIsDeepCold) {
          return {
            type: "ICE_PELLETS" /* ICE_PELLETS */,
            confidence: 75,
            adjustedRainRate: 0,
            adjustedSnowRate: 0,
            iceRate: profile.totalPrecipRate,
            wmoCode: 79,
            snowLevelFt,
            wetBulbTempF,
            diagnostic: `Inversion with warm nose, deep cold surface (${profile.surfaceTempF.toFixed(0)}\xB0F)`
          };
        }
        return {
          type: "FREEZING_RAIN" /* FREEZING_RAIN */,
          confidence: 80,
          adjustedRainRate: 0,
          adjustedSnowRate: 0,
          iceRate: profile.totalPrecipRate,
          wmoCode: getFreezingRainWMOCode(profile.totalPrecipRate),
          snowLevelFt,
          wetBulbTempF,
          diagnostic: `Inversion: warm 925hPa (${profile.temp925hPaF?.toFixed(0)}\xB0F), freezing surface (${profile.surfaceTempF.toFixed(0)}\xB0F)`
        };
      }
      return {
        type: "MIXED" /* MIXED */,
        confidence: 60,
        adjustedRainRate: profile.totalPrecipRate * 0.3,
        adjustedSnowRate: profile.totalPrecipRate * 0.7,
        iceRate: 0,
        wmoCode: 68,
        snowLevelFt,
        wetBulbTempF,
        diagnostic: `Marginal: sfc ${profile.surfaceTempF.toFixed(0)}\xB0F, 925 ${profile.temp925hPaF?.toFixed(0)}\xB0F`
      };
    }
  }
  if (profile.surfaceTempF >= FREEZING_F) {
    if (wetBulbTempF !== null) {
      if (wetBulbTempF < WET_BULB_SNOW_SURVIVES_F) {
        const upperAirBelowFreezing = has925Data && temp925BelowFreezing || has850Data && temp850BelowFreezing || isHighElevation && has700Data && temp700BelowFreezing;
        if (upperAirBelowFreezing || !hasWarmNose) {
          return {
            type: "SNOW" /* SNOW */,
            confidence: 85,
            adjustedRainRate: 0,
            adjustedSnowRate: profile.totalPrecipRate,
            iceRate: 0,
            wmoCode: getSnowWMOCode(profile.totalPrecipRate),
            snowLevelFt,
            wetBulbTempF,
            diagnostic: `Wet-bulb ${wetBulbTempF.toFixed(0)}\xB0F < 32\xB0F allows snow survival (air temp ${profile.surfaceTempF.toFixed(0)}\xB0F, RH ${profile.humidityPct?.toFixed(0)}%)`
          };
        }
      }
      if (wetBulbTempF >= WET_BULB_SNOW_SURVIVES_F && wetBulbTempF < WET_BULB_MELTING_F) {
        const upperAirBelowFreezing = has925Data && temp925BelowFreezing || has850Data && temp850BelowFreezing || isHighElevation && has700Data && temp700BelowFreezing;
        if (!upperAirBelowFreezing) {
          return {
            type: "RAIN" /* RAIN */,
            confidence: 85,
            adjustedRainRate: profile.totalPrecipRate,
            adjustedSnowRate: 0,
            iceRate: 0,
            wmoCode: getRainWMOCode(profile.totalPrecipRate),
            snowLevelFt,
            wetBulbTempF,
            diagnostic: `Wet-bulb ${wetBulbTempF.toFixed(0)}\xB0F in transition zone but entire column warm - pure rain`
          };
        }
        const snowRatio = 1 - (wetBulbTempF - WET_BULB_SNOW_SURVIVES_F) / (WET_BULB_MELTING_F - WET_BULB_SNOW_SURVIVES_F);
        return {
          type: "MIXED" /* MIXED */,
          confidence: 75,
          adjustedRainRate: profile.totalPrecipRate * (1 - snowRatio),
          adjustedSnowRate: profile.totalPrecipRate * snowRatio,
          iceRate: 0,
          wmoCode: 68,
          snowLevelFt,
          wetBulbTempF,
          diagnostic: `Wet-bulb ${wetBulbTempF.toFixed(0)}\xB0F in transition zone with cold aloft, air temp ${profile.surfaceTempF.toFixed(0)}\xB0F`
        };
      }
      if (profile.surfaceTempF <= 40 && has925Data && temp925BelowFreezing) {
        const warmth = wetBulbTempF - WET_BULB_MELTING_F;
        const rainRatio = Math.min(1, 0.7 + warmth / 10);
        return {
          type: "MIXED" /* MIXED */,
          confidence: 60,
          adjustedRainRate: profile.totalPrecipRate * rainRatio,
          adjustedSnowRate: profile.totalPrecipRate * (1 - rainRatio),
          iceRate: 0,
          wmoCode: 68,
          snowLevelFt,
          wetBulbTempF,
          diagnostic: `Marginal: wet-bulb ${wetBulbTempF.toFixed(0)}\xB0F, air temp ${profile.surfaceTempF.toFixed(0)}\xB0F, cold at 925hPa`
        };
      }
    } else {
      if (profile.surfaceTempF < 36 && has925Data && temp925BelowFreezing) {
        const warmth = profile.surfaceTempF - FREEZING_F;
        const rainRatio = Math.min(1, warmth / 4);
        return {
          type: "MIXED" /* MIXED */,
          confidence: 65,
          adjustedRainRate: profile.totalPrecipRate * rainRatio,
          adjustedSnowRate: profile.totalPrecipRate * (1 - rainRatio),
          iceRate: 0,
          wmoCode: 68,
          snowLevelFt,
          wetBulbTempF,
          diagnostic: `Near-freezing surface (${profile.surfaceTempF.toFixed(0)}\xB0F) with cold aloft (no humidity data)`
        };
      }
    }
    return {
      type: "RAIN" /* RAIN */,
      confidence: 90,
      adjustedRainRate: profile.totalPrecipRate,
      adjustedSnowRate: 0,
      iceRate: 0,
      wmoCode: getRainWMOCode(profile.totalPrecipRate),
      snowLevelFt,
      wetBulbTempF,
      diagnostic: wetBulbTempF !== null ? `Wet-bulb ${wetBulbTempF.toFixed(0)}\xB0F >= 34\xB0F - snow melts (air temp ${profile.surfaceTempF.toFixed(0)}\xB0F)` : `Surface above freezing (${profile.surfaceTempF.toFixed(0)}\xB0F)`
    };
  }
  return createFallbackResult(profile, snowLevelFt, wetBulbTempF, "Unable to determine from thermal profile");
}
function createFallbackResult(profile, snowLevelFt, wetBulbTempF, reason) {
  const hasRain = profile.originalRainRate > 0;
  const hasSnow = profile.originalSnowRate > 0;
  if (hasRain && hasSnow) {
    return {
      type: "MIXED" /* MIXED */,
      confidence: 50,
      adjustedRainRate: profile.originalRainRate,
      adjustedSnowRate: profile.originalSnowRate,
      iceRate: 0,
      wmoCode: 68,
      snowLevelFt,
      wetBulbTempF,
      diagnostic: `Fallback to model: ${reason}`
    };
  }
  if (hasSnow) {
    return {
      type: "SNOW" /* SNOW */,
      confidence: 50,
      adjustedRainRate: 0,
      adjustedSnowRate: profile.originalSnowRate,
      iceRate: 0,
      wmoCode: getSnowWMOCode(profile.originalSnowRate),
      snowLevelFt,
      wetBulbTempF,
      diagnostic: `Fallback to model: ${reason}`
    };
  }
  return {
    type: "RAIN" /* RAIN */,
    confidence: 50,
    adjustedRainRate: profile.originalRainRate,
    adjustedSnowRate: 0,
    iceRate: 0,
    wmoCode: getRainWMOCode(profile.originalRainRate),
    snowLevelFt,
    wetBulbTempF,
    diagnostic: `Fallback to model: ${reason}`
  };
}
function getRainWMOCode(rateInches) {
  if (rateInches < 0.1) return 61;
  if (rateInches < 0.3) return 63;
  return 65;
}
function getSnowWMOCode(rateInches) {
  if (rateInches < 0.15) return 71;
  if (rateInches < 0.3) return 73;
  return 75;
}
function getFreezingRainWMOCode(rateInches) {
  if (rateInches < 0.1) return 66;
  return 67;
}
function getHypotheticalPrecipType(profile) {
  const snowLevelFt = calculateSnowLevel(
    profile.surfaceTempF,
    profile.temp925hPaF,
    profile.temp850hPaF,
    profile.surfaceElevationFt,
    profile.temp700hPaF
  );
  const wetBulbTempF = profile.humidityPct !== null ? calculateWetBulbTemperature(profile.surfaceTempF, profile.humidityPct) : null;
  const isShowers = profile.cloudCoverPct < 75;
  const surfaceBelowFreezing = profile.surfaceTempF < FREEZING_F;
  const has925Data = profile.temp925hPaF !== null;
  const has850Data = profile.temp850hPaF !== null;
  const has700Data = profile.temp700hPaF !== null;
  const temp925BelowFreezing = has925Data && profile.temp925hPaF < FREEZING_F;
  const temp850BelowFreezing = has850Data && profile.temp850hPaF < FREEZING_F;
  const temp700BelowFreezing = has700Data && profile.temp700hPaF < FREEZING_F;
  const surfaceIsDeepCold = profile.surfaceTempF < COLD_SURFACE_THRESHOLD_F;
  const isHighElevation = profile.surfaceElevationFt >= HIGH_ELEVATION_THRESHOLD_FT;
  let hasWarmNose = false;
  let warmNoseTemp = null;
  if (isHighElevation && has700Data) {
    hasWarmNose = profile.temp700hPaF > WARM_NOSE_THRESHOLD_F;
    warmNoseTemp = profile.temp700hPaF;
  } else if (has850Data) {
    hasWarmNose = profile.temp850hPaF > WARM_NOSE_THRESHOLD_F;
    warmNoseTemp = profile.temp850hPaF;
  }
  if (surfaceBelowFreezing) {
    if (hasWarmNose) {
      if (surfaceIsDeepCold) {
        return {
          type: "ICE_PELLETS" /* ICE_PELLETS */,
          confidence: 80,
          wmoCode: 79,
          // Ice pellets
          snowLevelFt,
          wetBulbTempF,
          diagnostic: `Warm nose (${warmNoseTemp?.toFixed(0)}\xB0F) with deep cold surface (${profile.surfaceTempF.toFixed(0)}\xB0F)`
        };
      }
      return {
        type: "FREEZING_RAIN" /* FREEZING_RAIN */,
        confidence: 85,
        wmoCode: 66,
        // Light freezing rain
        snowLevelFt,
        wetBulbTempF,
        diagnostic: `Warm nose (${warmNoseTemp?.toFixed(0)}\xB0F) with freezing surface (${profile.surfaceTempF.toFixed(0)}\xB0F)`
      };
    }
    if (has700Data && !isHighElevation && profile.temp700hPaF > WARM_NOSE_THRESHOLD_F) {
      const temp850NotWarmNose = has850Data && profile.temp850hPaF <= WARM_NOSE_THRESHOLD_F;
      if (temp850NotWarmNose && temp925BelowFreezing) {
        return {
          type: "ICE_PELLETS" /* ICE_PELLETS */,
          confidence: 75,
          wmoCode: 79,
          snowLevelFt,
          wetBulbTempF,
          diagnostic: `Warm origin at 700hPa - rain refreezes below`
        };
      }
    }
    const upperAirBelowFreezing = has925Data && temp925BelowFreezing || has850Data && temp850BelowFreezing || isHighElevation && has700Data && temp700BelowFreezing;
    if (upperAirBelowFreezing || !has925Data && !has850Data) {
      return {
        type: "SNOW" /* SNOW */,
        confidence: 85,
        wmoCode: isShowers ? 85 : 71,
        // Snow showers or light snow
        snowLevelFt,
        wetBulbTempF,
        diagnostic: `Column frozen - snow expected`
      };
    }
    return {
      type: "MIXED" /* MIXED */,
      confidence: 60,
      wmoCode: 68,
      // Rain and snow mixed
      snowLevelFt,
      wetBulbTempF,
      diagnostic: `Marginal conditions - mixed precip likely`
    };
  }
  if (profile.surfaceTempF >= FREEZING_F) {
    const upperAirBelowFreezing = has925Data && temp925BelowFreezing || has850Data && temp850BelowFreezing || isHighElevation && has700Data && temp700BelowFreezing;
    if (wetBulbTempF !== null) {
      if (wetBulbTempF < WET_BULB_SNOW_SURVIVES_F && upperAirBelowFreezing) {
        return {
          type: "SNOW" /* SNOW */,
          confidence: 80,
          wmoCode: isShowers ? 85 : 71,
          snowLevelFt,
          wetBulbTempF,
          diagnostic: `Wet-bulb ${wetBulbTempF.toFixed(0)}\xB0F allows snow survival`
        };
      }
      if (wetBulbTempF >= WET_BULB_SNOW_SURVIVES_F && wetBulbTempF < WET_BULB_MELTING_F) {
        if (upperAirBelowFreezing) {
          return {
            type: "MIXED" /* MIXED */,
            confidence: 70,
            wmoCode: 68,
            snowLevelFt,
            wetBulbTempF,
            diagnostic: `Wet-bulb ${wetBulbTempF.toFixed(0)}\xB0F in transition zone`
          };
        }
      }
      if (wetBulbTempF >= WET_BULB_MELTING_F && profile.surfaceTempF <= 40 && upperAirBelowFreezing) {
        return {
          type: "MIXED" /* MIXED */,
          confidence: 55,
          wmoCode: 68,
          snowLevelFt,
          wetBulbTempF,
          diagnostic: `Marginal wet-bulb ${wetBulbTempF.toFixed(0)}\xB0F with cold aloft`
        };
      }
    } else {
      if (profile.surfaceTempF < 36 && upperAirBelowFreezing) {
        return {
          type: "MIXED" /* MIXED */,
          confidence: 60,
          wmoCode: 68,
          snowLevelFt,
          wetBulbTempF,
          diagnostic: `Near-freezing surface with cold aloft`
        };
      }
    }
    return {
      type: "RAIN" /* RAIN */,
      confidence: 85,
      wmoCode: isShowers ? 80 : 51,
      // Rain showers or drizzle
      snowLevelFt,
      wetBulbTempF,
      diagnostic: wetBulbTempF !== null ? `Wet-bulb ${wetBulbTempF.toFixed(0)}\xB0F - rain expected` : `Surface above freezing (${profile.surfaceTempF.toFixed(0)}\xB0F) - rain expected`
    };
  }
  return {
    type: "RAIN" /* RAIN */,
    confidence: 50,
    wmoCode: isShowers ? 80 : 51,
    snowLevelFt,
    wetBulbTempF,
    diagnostic: `Fallback - unable to determine`
  };
}

// src/services/ForecastGenerator.ts
var ForecastGenerator = class {
  constructor(selectedUnits = "Imperial") {
    this.invalidDoubleValue = INVALID_DOUBLE;
    this.invalidIntValue = INVALID_INT;
    this.drizzleCodes = [51, 53, 55];
    this.selectedUnits = selectedUnits;
  }
  /**
   * Blends hourly forecasts from multiple weather models (ECMWF, ICON, NBM).
   * Uses weighted averaging with specific weights for each model.
   * Analyzes winter precipitation types using atmospheric profile data.
   *
   * @param iconHourlyData - ICON model hourly data
   * @param nbmHourlyData - NBM model hourly data (US only)
   * @param ecmwfHourlyData - ECMWF model hourly data
   * @param bestHourlyData - Best Match model hourly data
   * @param location - Location for elevation data (optional, enables winter precip analysis)
   * @param ecmwfPressureData - ECMWF pressure level data from dedicated endpoint (optional)
   * @returns Blended hourly forecast array
   */
  blendHourlyForecasts(iconHourlyData, nbmHourlyData, ecmwfHourlyData, bestHourlyData, location, ecmwfPressureData) {
    const blendedForecast = [];
    const isNBMAvailable = nbmHourlyData.length > 0 && nbmHourlyData[0].temperature !== this.invalidDoubleValue;
    const imperialUnits = this.selectedUnits === "Imperial";
    const maxCount = bestHourlyData.length;
    const precipPopThreshold2 = 20;
    const exclusionWMOCodes = [
      51,
      63,
      65,
      // Rain
      71,
      73,
      75,
      // Snow
      68,
      // Mixed precip
      80,
      81,
      82,
      // Rain showers
      85,
      86,
      // Snow showers
      95,
      96,
      99
      // Thunderstorms
    ];
    const blendedTemperatures = this.blendMultipleValues([
      { values: ecmwfHourlyData.map((h) => h.temperature), weight: 0.5 },
      { values: (isNBMAvailable ? nbmHourlyData : iconHourlyData).map((h) => h.temperature), weight: 0.5 }
    ]);
    const blendedApparentTemperatures = this.blendMultipleValues([
      { values: ecmwfHourlyData.map((h) => h.temperatureApparent), weight: 0.5 },
      { values: (isNBMAvailable ? nbmHourlyData : iconHourlyData).map((h) => h.temperatureApparent), weight: 0.5 }
    ]);
    const blendedCloudCovers = this.blendMultipleValues([
      { values: isNBMAvailable ? nbmHourlyData.map((h) => h.cloudCover) : bestHourlyData.map((h) => h.cloudCover), weight: 0.6 },
      { values: ecmwfHourlyData.map((h) => h.cloudCover), weight: 0.4 }
    ]);
    const blendedPoP = this.blendMultipleValues(
      isNBMAvailable ? [
        { values: nbmHourlyData.map((h) => h.pop), weight: 0.3 },
        { values: bestHourlyData.map((h) => h.pop), weight: 0.1 },
        { values: ecmwfHourlyData.map((h) => h.pop), weight: 0.6 }
      ] : [
        { values: bestHourlyData.map((h) => h.pop), weight: 0.65 },
        { values: ecmwfHourlyData.map((h) => h.pop), weight: 0.35 }
      ]
    );
    const blendedRainRates = this.blendMultipleValues(
      isNBMAvailable ? [
        { values: nbmHourlyData.map((h) => h.rainRate), weight: 0.3 },
        { values: bestHourlyData.map((h) => h.rainRate), weight: 0.1 },
        { values: ecmwfHourlyData.map((h) => h.rainRate), weight: 0.6 }
      ] : [
        { values: bestHourlyData.map((h) => h.rainRate), weight: 0.65 },
        { values: ecmwfHourlyData.map((h) => h.rainRate), weight: 0.35 }
      ]
    );
    const blendedSnowRates = this.blendMultipleValues(
      isNBMAvailable ? [
        { values: nbmHourlyData.map((h) => h.snowRate), weight: 0.3 },
        { values: bestHourlyData.map((h) => h.snowRate), weight: 0.1 },
        { values: ecmwfHourlyData.map((h) => h.snowRate), weight: 0.6 }
      ] : [
        { values: bestHourlyData.map((h) => h.snowRate), weight: 0.65 },
        { values: ecmwfHourlyData.map((h) => h.snowRate), weight: 0.35 }
      ]
    );
    const blendedPrecipitation = isNBMAvailable ? this.blendMultipleValues([
      { values: nbmHourlyData.map((h) => h.precipitationRate), weight: 0.3 },
      { values: bestHourlyData.map((h) => h.precipitationRate), weight: 0.1 },
      { values: ecmwfHourlyData.map((h) => h.precipitationRate), weight: 0.6 }
    ]) : this.blendMultipleValues([
      { values: bestHourlyData.map((h) => h.precipitationRate), weight: 0.65 },
      { values: ecmwfHourlyData.map((h) => h.precipitationRate), weight: 0.35 }
    ]);
    const blendedHumidity = this.blendMultipleValues([
      { values: bestHourlyData.map((h) => h.humidity), weight: 0.3 },
      { values: iconHourlyData.map((h) => h.humidity), weight: 0.3 },
      { values: ecmwfHourlyData.map((h) => h.humidity), weight: 0.4 }
    ]);
    const blendedDewPoint = this.blendMultipleValues([
      { values: bestHourlyData.map((h) => h.dewPoint), weight: 0.3 },
      { values: iconHourlyData.map((h) => h.dewPoint), weight: 0.3 },
      { values: ecmwfHourlyData.map((h) => h.dewPoint), weight: 0.4 }
    ]);
    const hasEcmwfPressureData = ecmwfPressureData && ecmwfPressureData.temp850hPa.length > 0 && ecmwfPressureData.temp850hPa.some((v) => v !== null);
    const blendedTemp850hPa = hasEcmwfPressureData ? this.blendMultipleValues([
      { values: ecmwfPressureData.temp850hPa.map((v) => v ?? this.invalidDoubleValue), weight: 0.6 },
      { values: bestHourlyData.map((h) => h.temp850hPa ?? this.invalidDoubleValue), weight: 0.2 },
      { values: iconHourlyData.map((h) => h.temp850hPa ?? this.invalidDoubleValue), weight: 0.2 }
    ]) : this.blendMultipleValues([
      { values: bestHourlyData.map((h) => h.temp850hPa ?? this.invalidDoubleValue), weight: 0.5 },
      { values: iconHourlyData.map((h) => h.temp850hPa ?? this.invalidDoubleValue), weight: 0.5 }
    ]);
    const blendedTemp925hPa = hasEcmwfPressureData ? this.blendMultipleValues([
      { values: ecmwfPressureData.temp925hPa.map((v) => v ?? this.invalidDoubleValue), weight: 0.6 },
      { values: bestHourlyData.map((h) => h.temp925hPa ?? this.invalidDoubleValue), weight: 0.2 },
      { values: iconHourlyData.map((h) => h.temp925hPa ?? this.invalidDoubleValue), weight: 0.2 }
    ]) : this.blendMultipleValues([
      { values: bestHourlyData.map((h) => h.temp925hPa ?? this.invalidDoubleValue), weight: 0.5 },
      { values: iconHourlyData.map((h) => h.temp925hPa ?? this.invalidDoubleValue), weight: 0.5 }
    ]);
    const blendedTemp700hPa = hasEcmwfPressureData ? this.blendMultipleValues([
      { values: ecmwfPressureData.temp700hPa.map((v) => v ?? this.invalidDoubleValue), weight: 0.6 },
      { values: bestHourlyData.map((h) => h.temp700hPa ?? this.invalidDoubleValue), weight: 0.2 },
      { values: iconHourlyData.map((h) => h.temp700hPa ?? this.invalidDoubleValue), weight: 0.2 }
    ]) : this.blendMultipleValues([
      { values: bestHourlyData.map((h) => h.temp700hPa ?? this.invalidDoubleValue), weight: 0.5 },
      { values: iconHourlyData.map((h) => h.temp700hPa ?? this.invalidDoubleValue), weight: 0.5 }
    ]);
    const elevationFt = location?.elevation ?? null;
    if (maxCount === 0) {
      return [];
    }
    for (let i = 0; i < maxCount; i++) {
      const isDay = bestHourlyData[i].isDay;
      const temperature = blendedTemperatures[i];
      const temperatureApparent = blendedApparentTemperatures[i];
      const dewPoint = bestHourlyData[i].dewPoint;
      const humidity = bestHourlyData[i].humidity;
      const windSpeed = bestHourlyData[i].windSpeed;
      const windGust = bestHourlyData[i].windGust;
      const windBearing = bestHourlyData[i].windBearing;
      const cloudCover = blendedCloudCovers[i];
      let iceRate = 0;
      let rainRate = blendedRainRates[i];
      let snowRate = blendedSnowRates[i];
      const pop = blendedPoP[i];
      const startTime = bestHourlyData[i].startTime;
      const endTime = bestHourlyData[i].endTime;
      const precipThresholds = this.getPrecipThresholds(imperialUnits);
      const temp850hPa = blendedTemp850hPa[i];
      const temp925hPa = blendedTemp925hPa[i];
      const temp700hPa = blendedTemp700hPa[i];
      const blendedHumidityPct = blendedHumidity[i];
      const blendedDewPointF = blendedDewPoint[i];
      const isThunderstorm = i < bestHourlyData.length && bestHourlyData[i].weatherCode >= 95 && bestHourlyData[i].weatherCode <= 99;
      const isFrozenPrecip = this.isFrozenWmoCode(
        bestHourlyData[i].weatherCode
      );
      let winterPrecipWmoOverride = null;
      let snowLevelHeight = void 0;
      const precipitationRate = blendedPrecipitation[i];
      const totalPrecipRate = precipitationRate > 0 ? precipitationRate : rainRate + snowRate;
      if (elevationFt !== null && !isThunderstorm) {
        const profile = {
          surfaceElevationFt: elevationFt,
          temp850hPaF: this.isValidValue(temp850hPa) && temp850hPa !== this.invalidDoubleValue ? temp850hPa : null,
          temp925hPaF: this.isValidValue(temp925hPa) && temp925hPa !== this.invalidDoubleValue ? temp925hPa : null,
          temp700hPaF: this.isValidValue(temp700hPa) && temp700hPa !== this.invalidDoubleValue ? temp700hPa : null,
          surfaceTempF: imperialUnits ? temperature : celsiusToFahrenheit(temperature),
          dewPointF: this.isValidValue(blendedDewPointF) && blendedDewPointF !== this.invalidDoubleValue ? imperialUnits ? blendedDewPointF : celsiusToFahrenheit(blendedDewPointF) : null,
          humidityPct: this.isValidValue(blendedHumidityPct) && blendedHumidityPct !== this.invalidIntValue ? blendedHumidityPct : null,
          totalPrecipRate,
          originalRainRate: rainRate,
          originalSnowRate: snowRate
        };
        const precipResult = analyzeWinterPrecipType(profile);
        snowLevelHeight = precipResult.snowLevelFt ?? void 0;
        if (totalPrecipRate > 0 && precipResult.confidence >= 60) {
          const wetBulbF = precipResult.wetBulbTempF ?? null;
          const surfaceF = imperialUnits ? temperature : celsiusToFahrenheit(temperature);
          switch (precipResult.type) {
            case "RAIN" /* RAIN */:
            case "MIXED" /* MIXED */:
              rainRate = totalPrecipRate;
              snowRate = 0;
              iceRate = 0;
              break;
            case "SNOW" /* SNOW */: {
              const swr = calculateSnowWaterRatio(
                surfaceF,
                wetBulbF,
                profile.temp700hPaF ?? null
              );
              rainRate = 0;
              snowRate = totalPrecipRate * swr;
              iceRate = 0;
              break;
            }
            case "FREEZING_RAIN" /* FREEZING_RAIN */:
            case "ICE_PELLETS" /* ICE_PELLETS */:
              rainRate = 0;
              snowRate = 0;
              iceRate = totalPrecipRate;
              break;
            case "NONE" /* NONE */:
            default:
              rainRate = 0;
              snowRate = 0;
              iceRate = 0;
          }
          if (precipResult.type === "FREEZING_RAIN" /* FREEZING_RAIN */ || precipResult.type === "ICE_PELLETS" /* ICE_PELLETS */ || precipResult.type === "MIXED" /* MIXED */) {
            winterPrecipWmoOverride = precipResult.wmoCode;
          } else if (precipResult.type === "SNOW" /* SNOW */ && precipResult.confidence >= 80) {
            winterPrecipWmoOverride = precipResult.wmoCode;
          } else if (precipResult.type === "RAIN" /* RAIN */ && precipResult.confidence >= 80) {
            winterPrecipWmoOverride = precipResult.wmoCode;
          }
        }
        const originalModelWmo = bestHourlyData[i].weatherCode;
        const isRainOrShowerCode = this.isRainWmoCode(originalModelWmo) || this.isShowerWmoCode(originalModelWmo);
        if (isRainOrShowerCode) {
          if (precipResult.type === "SNOW" /* SNOW */ && precipResult.confidence >= 70) {
            winterPrecipWmoOverride = this.convertRainToSnowWmoCode(originalModelWmo);
          } else if (precipResult.type === "NONE" /* NONE */) {
            const surfaceTemp = imperialUnits ? temperature : celsiusToFahrenheit(temperature);
            const snowLevelUnderground = snowLevelHeight !== void 0 && snowLevelHeight < 0;
            const allTempsBelowFreezing = surfaceTemp <= 32 && (temp850hPa === void 0 || temp850hPa <= 32) && (temp925hPa === void 0 || temp925hPa <= 32) && (temp700hPa === void 0 || temp700hPa <= 32);
            if (snowLevelUnderground || allTempsBelowFreezing) {
              winterPrecipWmoOverride = this.convertRainToSnowWmoCode(originalModelWmo);
            }
          }
        }
        const isSnowCode = this.isFrozenWmoCode(originalModelWmo);
        if (isSnowCode) {
          if (precipResult.type === "FREEZING_RAIN" /* FREEZING_RAIN */ && precipResult.confidence >= 70) {
            winterPrecipWmoOverride = precipResult.wmoCode;
          } else if (precipResult.type === "ICE_PELLETS" /* ICE_PELLETS */ && precipResult.confidence >= 70) {
            winterPrecipWmoOverride = precipResult.wmoCode;
          } else if (precipResult.type === "MIXED" /* MIXED */ && precipResult.confidence >= 70) {
            winterPrecipWmoOverride = precipResult.wmoCode;
          } else if (precipResult.type === "RAIN" /* RAIN */ && precipResult.confidence >= 70) {
            winterPrecipWmoOverride = this.convertSnowToRainWmoCode(originalModelWmo);
          }
          if (winterPrecipWmoOverride === null) {
            const surfaceTemp = imperialUnits ? temperature : celsiusToFahrenheit(temperature);
            const hasWarmNose = this.isValidValue(temp850hPa) && temp850hPa !== this.invalidDoubleValue && temp850hPa > 34;
            const surfaceBelowFreezing = surfaceTemp <= 32;
            const surfaceDeepCold = surfaceTemp <= 25;
            if (hasWarmNose && surfaceBelowFreezing) {
              if (surfaceDeepCold) {
                winterPrecipWmoOverride = 79;
              } else {
                winterPrecipWmoOverride = 66;
              }
            }
          }
        }
      }
      const hasDisplayablePrecipSignal = this.hasDisplayablePrecipSignal(
        pop,
        rainRate,
        snowRate,
        iceRate,
        precipThresholds,
        precipPopThreshold2
      );
      const weatherCodeDecision = this.resolveWeatherCode({
        pop,
        rainRate,
        snowRate,
        cloudCover,
        isThunderstorm,
        bestWeatherCode: bestHourlyData[i].weatherCode,
        nbmWeatherCode: i < nbmHourlyData.length ? nbmHourlyData[i].weatherCode : this.invalidIntValue,
        isFrozenPrecip,
        elevationFt,
        temp850hPa,
        temp925hPa,
        temp700hPa,
        temperature,
        blendedHumidityPct,
        imperialUnits,
        precipThresholds,
        precipPopThreshold: precipPopThreshold2,
        hasDisplayablePrecipSignal,
        exclusionWMOCodes
      });
      let wmoCode = weatherCodeDecision.wmoCode;
      rainRate = weatherCodeDecision.rainRate;
      snowRate = weatherCodeDecision.snowRate;
      if (winterPrecipWmoOverride !== null && hasDisplayablePrecipSignal) {
        wmoCode = winterPrecipWmoOverride;
      }
      const isFreezingPrecipCode = wmoCode === 56 || wmoCode === 57 || wmoCode === 66 || wmoCode === 67;
      if (winterPrecipWmoOverride === null && elevationFt !== null && !isFreezingPrecipCode && (this.isRainWmoCode(wmoCode) || this.isShowerWmoCode(wmoCode))) {
        const surfaceTemp = imperialUnits ? temperature : celsiusToFahrenheit(temperature);
        const surfaceElevationFt = elevationFt;
        const hasWarmNose = this.isValidValue(temp850hPa) && temp850hPa !== this.invalidDoubleValue && temp850hPa > 34 && surfaceTemp <= 32;
        if (hasWarmNose) {
          if (surfaceTemp <= 25) {
            wmoCode = 79;
          } else {
            wmoCode = 66;
          }
        } else {
          const snowLevelBelowSurface = snowLevelHeight !== void 0 && snowLevelHeight <= surfaceElevationFt;
          const HIGH_ELEV_925_THRESHOLD_FT = 2e3;
          const HIGH_ELEV_850_THRESHOLD_FT = 4500;
          const shouldCheck925 = surfaceElevationFt < HIGH_ELEV_925_THRESHOLD_FT;
          const shouldCheck850 = surfaceElevationFt < HIGH_ELEV_850_THRESHOLD_FT;
          const relevantTempsBelowFreezing = surfaceTemp <= 32 && (!shouldCheck925 || temp925hPa === void 0 || temp925hPa <= 32) && (!shouldCheck850 || temp850hPa === void 0 || temp850hPa <= 32) && (temp700hPa === void 0 || temp700hPa <= 32);
          if (snowLevelBelowSurface || relevantTempsBelowFreezing) {
            wmoCode = this.convertRainToSnowWmoCode(wmoCode);
          }
        }
      }
      if (this.isDrizzleWmoCode(wmoCode) && elevationFt !== null) {
        const thermalProfile = {
          surfaceElevationFt: elevationFt,
          temp850hPaF: this.isValidValue(temp850hPa) && temp850hPa !== this.invalidDoubleValue ? temp850hPa : null,
          temp925hPaF: this.isValidValue(temp925hPa) && temp925hPa !== this.invalidDoubleValue ? temp925hPa : null,
          temp700hPaF: this.isValidValue(temp700hPa) && temp700hPa !== this.invalidDoubleValue ? temp700hPa : null,
          surfaceTempF: imperialUnits ? temperature : celsiusToFahrenheit(temperature),
          humidityPct: this.isValidValue(blendedHumidityPct) && blendedHumidityPct !== this.invalidIntValue ? blendedHumidityPct : null,
          cloudCoverPct: cloudCover
        };
        const hypotheticalResult = getHypotheticalPrecipType(thermalProfile);
        if (hypotheticalResult.type === "SNOW" /* SNOW */) {
          wmoCode = wmoCode === 51 ? 77 : 71;
        } else if (hypotheticalResult.type === "MIXED" /* MIXED */) {
          wmoCode = 68;
        }
      }
      const blendedHour = {
        id: `hour-${i}-${startTime.getTime()}`,
        index: i,
        startTime,
        endTime,
        isDay,
        temperature,
        temperatureApparent,
        dewPoint,
        humidity,
        weatherCode: wmoCode,
        windSpeed,
        windGust,
        windBearing,
        cloudCover,
        precipitationRate,
        rainRate,
        convectivePrecipitation: 0,
        // TODO: Add convective precip to blending
        snowRate,
        iceRate,
        pop,
        // Include atmospheric profile data for debugging/future use
        // snowLevelHeight is calculated from temps in WinterPrecipAnalyzer
        snowLevelHeight,
        temp850hPa: this.isValidValue(temp850hPa) && temp850hPa !== this.invalidDoubleValue ? temp850hPa : void 0,
        temp925hPa: this.isValidValue(temp925hPa) && temp925hPa !== this.invalidDoubleValue ? temp925hPa : void 0,
        temp700hPa: this.isValidValue(temp700hPa) && temp700hPa !== this.invalidDoubleValue ? temp700hPa : void 0
      };
      blendedForecast.push(blendedHour);
    }
    return blendedForecast;
  }
  getPrecipThresholds(imperialUnits) {
    return {
      rainLight: imperialUnits ? 0.01 : 0.254,
      rainModerate: imperialUnits ? 0.1 : 2.54,
      rainHeavy: imperialUnits ? 0.3 : 7.62,
      snowLight: imperialUnits ? 0.02 : 0.5,
      snowModerate: imperialUnits ? 0.15 : 4,
      snowHeavy: imperialUnits ? 0.3 : 8
    };
  }
  hasDisplayablePrecipSignal(pop, rainRate, snowRate, iceRate, thresholds, precipPopThreshold2) {
    return pop >= precipPopThreshold2 || rainRate > thresholds.rainLight || snowRate > thresholds.snowLight || iceRate > thresholds.rainLight;
  }
  resolveWeatherCode(params) {
    const {
      pop,
      cloudCover,
      isThunderstorm,
      bestWeatherCode,
      nbmWeatherCode,
      isFrozenPrecip,
      elevationFt,
      temp850hPa,
      temp925hPa,
      temp700hPa,
      temperature,
      blendedHumidityPct,
      imperialUnits,
      precipThresholds,
      precipPopThreshold: precipPopThreshold2,
      hasDisplayablePrecipSignal,
      exclusionWMOCodes
    } = params;
    let { rainRate, snowRate } = params;
    let wmoCode = 0;
    if (hasDisplayablePrecipSignal && !isThunderstorm) {
      if (rainRate > 0 && snowRate < precipThresholds.snowLight) {
        if (rainRate < precipThresholds.rainModerate) {
          wmoCode = 51;
        } else if (rainRate < precipThresholds.rainHeavy) {
          wmoCode = 63;
        } else {
          wmoCode = 65;
        }
      } else if (snowRate > 0 && rainRate < precipThresholds.rainLight) {
        if (snowRate < precipThresholds.snowModerate) {
          wmoCode = 71;
        } else if (snowRate < precipThresholds.snowHeavy) {
          wmoCode = 73;
        } else {
          wmoCode = 75;
        }
      } else if (rainRate >= precipThresholds.rainLight && snowRate >= precipThresholds.snowLight) {
        wmoCode = 68;
      } else if (pop >= precipPopThreshold2) {
        wmoCode = this.resolvePossiblePrecipWeatherCode({
          elevationFt,
          temp850hPa,
          temp925hPa,
          temp700hPa,
          temperature,
          blendedHumidityPct,
          imperialUnits,
          cloudCover,
          isFrozenPrecip
        });
      }
      wmoCode = this.applyShowersByCloudCover(wmoCode, cloudCover);
    } else if (isThunderstorm && pop >= precipPopThreshold2 || isThunderstorm && rainRate > 0) {
      wmoCode = bestWeatherCode;
    } else {
      const bestMatchCode = nbmWeatherCode !== this.invalidIntValue ? nbmWeatherCode : bestWeatherCode;
      wmoCode = this.resolveNonPrecipFallbackWeatherCode(
        bestMatchCode,
        cloudCover,
        exclusionWMOCodes
      );
      if (!isPrecipitationWeatherCode(wmoCode)) {
        rainRate = 0;
        snowRate = 0;
      }
    }
    return { wmoCode, rainRate, snowRate };
  }
  resolvePossiblePrecipWeatherCode(params) {
    const {
      elevationFt,
      temp850hPa,
      temp925hPa,
      temp700hPa,
      temperature,
      blendedHumidityPct,
      imperialUnits,
      cloudCover,
      isFrozenPrecip
    } = params;
    if (elevationFt !== null) {
      const thermalProfile = {
        surfaceElevationFt: elevationFt,
        temp850hPaF: this.isValidValue(temp850hPa) && temp850hPa !== this.invalidDoubleValue ? temp850hPa : null,
        temp925hPaF: this.isValidValue(temp925hPa) && temp925hPa !== this.invalidDoubleValue ? temp925hPa : null,
        temp700hPaF: this.isValidValue(temp700hPa) && temp700hPa !== this.invalidDoubleValue ? temp700hPa : null,
        surfaceTempF: imperialUnits ? temperature : celsiusToFahrenheit(temperature),
        humidityPct: this.isValidValue(blendedHumidityPct) && blendedHumidityPct !== this.invalidIntValue ? blendedHumidityPct : null,
        cloudCoverPct: cloudCover
      };
      return getHypotheticalPrecipType(thermalProfile).wmoCode;
    }
    return isFrozenPrecip ? cloudCover < 75 ? 85 : 71 : cloudCover < 75 ? 80 : 51;
  }
  resolveNonPrecipFallbackWeatherCode(bestMatchCode, cloudCover, exclusionWMOCodes) {
    if (bestMatchCode > 3 && !exclusionWMOCodes.includes(bestMatchCode) && !isPrecipitationWeatherCode(bestMatchCode)) {
      return bestMatchCode;
    }
    if (cloudCover < 10) {
      return 0;
    }
    if (cloudCover < 25) {
      return 1;
    }
    if (cloudCover < 75) {
      return 2;
    }
    return 3;
  }
  applyShowersByCloudCover(wmoCode, cloudCover) {
    if (cloudCover >= 75) {
      return wmoCode;
    }
    switch (wmoCode) {
      case 51:
        return 80;
      case 63:
        return 81;
      case 65:
        return 82;
      case 71:
        return 85;
      case 73:
      case 75:
        return 86;
      default:
        return wmoCode;
    }
  }
  /**
   * Blends multiple value sets with different weights.
   *
   * @param valueSets - Array of value sets with their weights
   * @returns Blended values array
   */
  blendMultipleValues(valueSets) {
    if (valueSets.length === 0) {
      return [];
    }
    const maxLength = Math.max(...valueSets.map((set) => set.values.length));
    const blendedValues = [];
    for (let i = 0; i < maxLength; i++) {
      let weightedSum = 0;
      let totalWeight = 0;
      let hasValidValue = false;
      for (const { values, weight } of valueSets) {
        if (i < values.length && this.isValidValue(values[i])) {
          weightedSum += values[i] * weight;
          totalWeight += weight;
          hasValidValue = true;
        }
      }
      if (hasValidValue && totalWeight > 0) {
        blendedValues.push(weightedSum / totalWeight);
      } else {
        const fallbackValue = valueSets.map((set) => i < set.values.length ? set.values[i] : null).find((v) => v !== null) ?? this.invalidDoubleValue;
        blendedValues.push(fallbackValue);
      }
    }
    return blendedValues;
  }
  /**
   * Validates whether a numeric value is considered valid.
   *
   * @param value - The numeric value to validate
   * @returns true if the value is valid, false otherwise
   */
  isValidValue(value) {
    return value !== this.invalidDoubleValue && value !== this.invalidIntValue && !isNaN(value) && isFinite(value);
  }
  /**
   * Formats a precipitation amount for human-readable display.
   * Returns trace description for very small amounts, or the formatted value for significant amounts.
   *
   * @param amount - The precipitation amount
   * @param type - The type of precipitation ("rain", "snow", or "ice")
   * @returns Object with isTrace flag and formatted string, or null if amount is effectively zero
   */
  formatPrecipAmount(amount, type) {
    const traceThreshold = type === "snow" ? 0.1 : 0.01;
    const decimals = type === "snow" ? 1 : 2;
    const minDisplayable = type === "snow" ? 0.05 : 5e-3;
    if (amount < minDisplayable) {
      return null;
    }
    if (amount < traceThreshold) {
      return { isTrace: true, formatted: `Little or no ${type} accumulation` };
    }
    return { isTrace: false, formatted: `${amount.toFixed(decimals)}"` };
  }
  /**
   * Checks if a WMO code represents frozen precipitation.
   *
   * @param code - WMO weather code
   * @returns true if the code represents frozen precipitation
   */
  isFrozenWmoCode(code) {
    const frozenCodes = [
      71,
      73,
      75,
      // Snow
      77,
      79,
      // Snow grains, ice pellets
      85,
      86,
      // Snow showers
      87,
      88
      // Snow pellet showers
    ];
    return frozenCodes.includes(code);
  }
  /**
   * Checks if a WMO code represents rain (non-shower).
   *
   * @param code - WMO weather code
   * @returns true if the code represents rain
   */
  isRainWmoCode(code) {
    const rainCodes2 = [
      51,
      53,
      55,
      // Drizzle
      56,
      57,
      // Freezing drizzle
      61,
      63,
      65,
      // Rain
      66,
      67
      // Freezing rain
    ];
    return rainCodes2.includes(code);
  }
  /**
   * Checks if a WMO code represents showers.
   *
   * @param code - WMO weather code
   * @returns true if the code represents showers
   */
  isShowerWmoCode(code) {
    const showerCodes = [
      80,
      81,
      82
      // Rain showers
    ];
    return showerCodes.includes(code);
  }
  /**
   * Checks if a WMO code represents drizzle.
   *
   * @param code - WMO weather code
   * @returns true if the code represents drizzle
   */
  isDrizzleWmoCode(code) {
    const drizzleCodes = [
      51,
      53,
      55
      // Drizzle (light, moderate, dense)
    ];
    return drizzleCodes.includes(code);
  }
  /**
   * Converts a rain/drizzle/shower WMO code to its snow equivalent.
   * Rain showers → Snow showers, Drizzle/Rain → Snow
   *
   * @param code - WMO weather code for rain/drizzle/showers
   * @returns Equivalent snow WMO code
   */
  convertRainToSnowWmoCode(code) {
    switch (code) {
      // Rain showers → Snow showers
      case 80:
        return 85;
      // Slight rain showers → Slight snow showers
      case 81:
        return 86;
      // Moderate rain showers → Heavy snow showers
      case 82:
        return 86;
      // Violent rain showers → Heavy snow showers
      // Drizzle → Light snow
      case 51:
        return 71;
      // Light drizzle → Light snow
      case 53:
        return 73;
      // Moderate drizzle → Moderate snow
      case 55:
        return 75;
      // Dense drizzle → Heavy snow
      // Freezing drizzle → Light snow (already frozen precip type)
      case 56:
        return 71;
      // Light freezing drizzle → Light snow
      case 57:
        return 73;
      // Dense freezing drizzle → Moderate snow
      // Rain → Snow
      case 61:
        return 71;
      // Slight rain → Light snow
      case 63:
        return 73;
      // Moderate rain → Moderate snow
      case 65:
        return 75;
      // Heavy rain → Heavy snow
      // Freezing rain → Snow (already frozen precip type)
      case 66:
        return 71;
      // Light freezing rain → Light snow
      case 67:
        return 73;
      // Moderate freezing rain → Moderate snow
      // Default fallback
      default:
        return 71;
    }
  }
  /**
   * Converts snow WMO codes to equivalent rain WMO codes.
   * Used when thermal profile indicates snow would melt to rain.
   *
   * @param code - WMO weather code for snow/snow showers
   * @returns Equivalent rain WMO code
   */
  convertSnowToRainWmoCode(code) {
    switch (code) {
      // Snow showers → Rain showers
      case 85:
        return 80;
      // Slight snow showers → Slight rain showers
      case 86:
        return 81;
      // Heavy snow showers → Moderate rain showers
      // Snow → Rain
      case 71:
        return 61;
      // Light snow → Slight rain
      case 73:
        return 63;
      // Moderate snow → Moderate rain
      case 75:
        return 65;
      // Heavy snow → Heavy rain
      // Snow grains/pellets → Light rain
      case 77:
        return 61;
      // Snow grains → Slight rain
      case 79:
        return 61;
      // Ice pellets → Slight rain
      // Snow pellet showers → Rain showers
      case 87:
        return 80;
      // Slight snow pellet showers → Slight rain showers
      case 88:
        return 81;
      // Heavy snow pellet showers → Moderate rain showers
      // Default fallback
      default:
        return 61;
    }
  }
  /**
   * Gets the dominant cloud cover and trend from hourly data.
   *
   * @param hourlyData - Array of hourly weather data
   * @returns Object containing dominant cloud cover value and trend
   */
  getDominantCloudCover(hourlyData) {
    if (hourlyData.length === 0) {
      return { dominantValue: null, trend: "stable" /* Stable */ };
    }
    const cloudCovers = hourlyData.map((h) => h.cloudCover).filter((cc) => cc !== this.invalidIntValue);
    if (cloudCovers.length === 0) {
      return { dominantValue: null, trend: "stable" /* Stable */ };
    }
    const avgCloudCover = cloudCovers.reduce((sum2, cc) => sum2 + cc, 0) / cloudCovers.length;
    const midpoint = Math.floor(cloudCovers.length / 2);
    const firstHalfAvg = cloudCovers.slice(0, midpoint).reduce((sum2, cc) => sum2 + cc, 0) / midpoint;
    const secondHalfAvg = cloudCovers.slice(midpoint).reduce((sum2, cc) => sum2 + cc, 0) / (cloudCovers.length - midpoint);
    let trend;
    const diff = secondHalfAvg - firstHalfAvg;
    if (diff > 15) {
      trend = "increasing" /* Increasing */;
    } else if (diff < -15) {
      trend = "decreasing" /* Decreasing */;
    } else {
      trend = "stable" /* Stable */;
    }
    return { dominantValue: Math.round(avgCloudCover), trend };
  }
  /**
   * Gets the dominant wind bearing from an array of bearings.
   *
   * @param windBearings - Array of wind bearings in degrees
   * @returns Dominant wind bearing or null
   */
  getDominantWindBearing(windBearings) {
    const validBearings = windBearings.filter(
      (b) => b !== this.invalidIntValue && b >= 0 && b <= 360
    );
    if (validBearings.length === 0) {
      return null;
    }
    let sinSum = 0;
    let cosSum = 0;
    for (const bearing of validBearings) {
      const rad = bearing * Math.PI / 180;
      sinSum += Math.sin(rad);
      cosSum += Math.cos(rad);
    }
    const meanRad = Math.atan2(sinSum, cosSum);
    let meanDegrees = meanRad * 180 / Math.PI;
    if (meanDegrees < 0) {
      meanDegrees += 360;
    }
    return Math.round(meanDegrees);
  }
  /**
   * Gets the most common WMO code from an array of codes.
   *
   * @param wmoCodes - Array of WMO weather codes
   * @returns Most common WMO code or null
   */
  getMostCommonWmoCode(wmoCodes) {
    const validCodes = wmoCodes.filter((code) => code !== this.invalidIntValue);
    if (validCodes.length === 0) {
      return null;
    }
    const counts = /* @__PURE__ */ new Map();
    for (const code of validCodes) {
      counts.set(code, (counts.get(code) ?? 0) + 1);
    }
    let maxCount = 0;
    let mostCommon = null;
    for (const [code, count] of counts.entries()) {
      if (count > maxCount) {
        maxCount = count;
        mostCommon = code;
      }
    }
    return mostCommon;
  }
  /**
   * Gets the dominant weather code from an array, considering priority and cloud cover.
   *
   * @param wmoCodes - Array of WMO weather codes
   * @param cloudCover - Average cloud cover percentage (optional)
   * @returns Dominant WMO code or null
   */
  getDominantWeatherCode(wmoCodes, cloudCover) {
    if (wmoCodes.length === 0) {
      return null;
    }
    let highestPriority = -1;
    let dominantCode = -1;
    const commonWeatherCode = this.getMostCommonWmoCode(wmoCodes);
    for (const code of wmoCodes) {
      const currentPriority = this.getPriority(code);
      if (currentPriority > highestPriority) {
        highestPriority = currentPriority;
        dominantCode = code;
      }
    }
    if (highestPriority === -1) {
      return 0;
    }
    let finalDominantCode = dominantCode;
    const validCodes = wmoCodes.filter((code) => this.getPriority(code) >= 0);
    const fogLikeCodeCount = validCodes.filter((code) => this.isFogLikeWmoCode(code)).length;
    const fogThreshold = this.minimumFogDominanceCount(validCodes.length);
    if (this.isFogLikeWmoCode(finalDominantCode) && fogLikeCodeCount < fogThreshold) {
      return this.patchyFogWmoCodeForCloudCover(cloudCover, commonWeatherCode) ?? this.skyWmoCodeForCloudCover(cloudCover) ?? commonWeatherCode;
    }
    if (cloudCover != null && cloudCover < 75) {
      const originalCodeForIntensity = finalDominantCode;
      if (originalCodeForIntensity === 61) {
        finalDominantCode = 80;
      } else if (originalCodeForIntensity === 63) {
        finalDominantCode = 81;
      } else if (originalCodeForIntensity === 65) {
        finalDominantCode = 82;
      } else if (originalCodeForIntensity === 71) {
        finalDominantCode = 85;
      } else if (originalCodeForIntensity === 73 || originalCodeForIntensity === 75) {
        finalDominantCode = 86;
      } else if (originalCodeForIntensity === 68) {
        finalDominantCode = 83;
      } else if (originalCodeForIntensity === 69) {
        finalDominantCode = 84;
      }
    }
    if (finalDominantCode >= 1 && finalDominantCode <= 4) {
      if (commonWeatherCode != null && commonWeatherCode !== finalDominantCode) {
        return commonWeatherCode;
      }
    }
    return finalDominantCode;
  }
  isFogLikeWmoCode(code) {
    return code >= 10 && code <= 12 || code >= 40 && code <= 49;
  }
  minimumFogDominanceCount(totalHours) {
    if (totalHours < 4) {
      return 1;
    }
    return Math.max(2, Math.ceil(totalHours * 0.25));
  }
  patchyFogWmoCodeForCloudCover(cloudCover, commonWeatherCode) {
    if (cloudCover != null) {
      return cloudCover < 75 ? 41 : null;
    }
    if (commonWeatherCode != null && commonWeatherCode >= 0 && commonWeatherCode <= 2) {
      return 41;
    }
    return null;
  }
  skyWmoCodeForCloudCover(cloudCover) {
    if (cloudCover == null) {
      return null;
    }
    if (cloudCover <= 10) return 0;
    if (cloudCover <= 45) return 1;
    if (cloudCover <= 85) return 2;
    return 3;
  }
  /**
   * Assigns a priority level to a WMO weather code.
   * Higher numbers mean higher priority (more significant weather).
   *
   * @param wmoCode - The WMO weather code (0-99)
   * @returns The priority level, or -1 for invalid/unknown codes
   */
  getPriority(wmoCode) {
    if (wmoCode < 0 || wmoCode > 99) {
      return -1;
    }
    if (wmoCode >= 90 && wmoCode <= 99 || // Thunderstorms with precipitation/hail
    wmoCode >= 67 && wmoCode <= 69 || // Freezing rain/drizzle
    wmoCode >= 77 && wmoCode <= 79 || // Significant snow grains/ice crystals
    wmoCode >= 87 && wmoCode <= 89 || // Heavy snow/ice showers
    wmoCode === 27) {
      return 11;
    }
    if (wmoCode >= 17 && wmoCode <= 19 || // Squall, TS without precipitation
    wmoCode === 29 || // Thunderstorm (used if type isn't specified)
    wmoCode >= 60 && wmoCode <= 66 || // Rain (moderate to heavy)
    wmoCode >= 70 && wmoCode <= 76 || // Snow, Ice Pellets (moderate to heavy)
    wmoCode >= 22 && wmoCode <= 24) {
      return 10;
    }
    if (wmoCode >= 80 && wmoCode <= 86) {
      return 9;
    }
    if (wmoCode >= 13 && wmoCode <= 16 || // Precipitation nearby, reaching surface
    wmoCode >= 50 && wmoCode <= 59 || // Drizzle
    wmoCode >= 20 && wmoCode <= 21) {
      return 8;
    }
    if (wmoCode >= 40 && wmoCode <= 49) {
      return 7;
    }
    if (wmoCode >= 30 && wmoCode <= 39) {
      return 6;
    }
    if (wmoCode >= 5 && wmoCode <= 9 || // Haze, Smoke, Dust/Sand (not rising/blowing)
    wmoCode >= 10 && wmoCode <= 12) {
      return 5;
    }
    if (wmoCode >= 3 && wmoCode <= 4) {
      return 4;
    }
    if (wmoCode >= 1 && wmoCode <= 2) {
      return 3;
    }
    if (wmoCode === 0) {
      return 2;
    }
    return -1;
  }
  /**
   * Generates a textual forecast description based on hourly weather data.
   *
   * @param hourlyData - Array of hourly weather data to analyze
   * @param location - Location information
   * @param timezone - IANA timezone identifier (optional)
   * @returns Human-readable forecast description
   */
  generateForecastDescription(hourlyData, location, timezone) {
    if (hourlyData.length === 0) {
      return "No forecast data available.";
    }
    const periodIsDay = hourlyData[0]?.isDay ?? false;
    const temperatures = hourlyData.map((h) => h.temperature).filter((t) => t !== this.invalidDoubleValue);
    const maxTemp = temperatures.length > 0 ? Math.max(...temperatures) : 0;
    const minTemp = temperatures.length > 0 ? Math.min(...temperatures) : 0;
    const tempRange = maxTemp - minTemp;
    const precipChances = hourlyData.map((h) => h.pop).filter((p) => p !== this.invalidIntValue);
    const maxPrecipChance = precipChances.length > 0 ? Math.max(...precipChances) : 0;
    const rainAccumulation = hourlyData.map((h) => h.rainRate).filter((r) => r !== 0).reduce((sum2, r) => sum2 + r, 0);
    const convectiveAccumulation = hourlyData.map((h) => h.convectivePrecipitation).filter((c) => c !== 0).reduce((sum2, c) => sum2 + c, 0);
    const snowAccumulation = hourlyData.map((h) => h.snowRate).filter((s) => s !== 0).reduce((sum2, s) => sum2 + s, 0);
    const windSpeeds = hourlyData.map((h) => h.windSpeed).filter((w) => w !== this.invalidIntValue);
    const maxWindSpeed = windSpeeds.length > 0 ? Math.max(...windSpeeds) : 0;
    const windGusts = hourlyData.map((h) => h.windGust).filter((g) => g !== this.invalidIntValue);
    const maxWindGust = windGusts.length > 0 ? Math.max(...windGusts) : 0;
    const cloudCoverInfo = this.getDominantCloudCover(hourlyData);
    const dominantCloudCover = cloudCoverInfo.dominantValue ?? 0;
    const cloudTrend = cloudCoverInfo.trend;
    const weatherCodes = hourlyData.map((h) => h.weatherCode).filter((c) => c !== this.invalidIntValue);
    const dominantWeatherCode = this.getDominantWeatherCode(weatherCodes, dominantCloudCover) ?? 0;
    let forecast = "";
    forecast += this.getWeatherCodeDescription(
      dominantWeatherCode,
      maxPrecipChance,
      rainAccumulation,
      convectiveAccumulation
    );
    if (tempRange < 5) {
      forecast += ` Temperatures steady around ${Math.round(maxTemp)}\xB0F. `;
    } else if (periodIsDay) {
      forecast += ` High temperature around ${Math.round(maxTemp)}\xB0F. `;
    } else if (!periodIsDay) {
      forecast += ` Low temperature around ${Math.round(minTemp)}\xB0F. `;
    }
    if (maxPrecipChance > 0) {
      const rainInfo = this.formatPrecipAmount(rainAccumulation, "rain");
      const snowInfo = this.formatPrecipAmount(snowAccumulation, "snow");
      const hasSignificantRain = rainInfo && !rainInfo.isTrace;
      const hasSignificantSnow = snowInfo && !snowInfo.isTrace;
      const hasTraceRain = rainInfo?.isTrace;
      const hasTraceSnow = snowInfo?.isTrace;
      if (hasSignificantRain && hasSignificantSnow) {
        const precipHours = this.findPrecipitationTimeframe(
          hourlyData,
          null,
          timezone
        );
        forecast += `Rain and snow mix, expecting ${rainInfo.formatted} of rain and ${snowInfo.formatted} of snow`;
        if (precipHours) {
          forecast += precipHours;
        }
        forecast += ". ";
      } else if (hasSignificantRain && hasTraceSnow) {
        const rainHours = this.findPrecipitationTimeframe(
          hourlyData,
          "rain",
          timezone
        );
        forecast += `Expect around ${rainInfo.formatted} of rain`;
        if (rainHours) {
          forecast += rainHours;
        }
        forecast += ". ";
      } else if (hasSignificantSnow && hasTraceRain) {
        const snowHours = this.findPrecipitationTimeframe(
          hourlyData,
          "snow",
          timezone
        );
        forecast += `Expect around ${snowInfo.formatted} of snow`;
        if (snowHours) {
          forecast += snowHours;
        }
        forecast += ". ";
      } else if (hasSignificantRain) {
        const rainHours = this.findPrecipitationTimeframe(
          hourlyData,
          "rain",
          timezone
        );
        forecast += `Expect around ${rainInfo.formatted} of rain`;
        if (rainHours) {
          forecast += rainHours;
        }
        forecast += ". ";
      } else if (hasSignificantSnow) {
        const snowHours = this.findPrecipitationTimeframe(
          hourlyData,
          "snow",
          timezone
        );
        forecast += `Expect around ${snowInfo.formatted} of snow`;
        if (snowHours) {
          forecast += snowHours;
        }
        forecast += ". ";
      } else if (hasTraceRain && !hasTraceSnow) {
        forecast += `${rainInfo.formatted}. `;
      } else if (hasTraceSnow && !hasTraceRain) {
        forecast += `${snowInfo.formatted}. `;
      }
    }
    if (maxWindSpeed > 0) {
      const windDirection = cardinalDirection(
        this.getDominantWindBearing(hourlyData.map((h) => h.windBearing)) ?? 0
      );
      if (maxWindSpeed < 5) {
        forecast += `Light ${windDirection} winds. `;
      } else if (maxWindSpeed <= 10) {
        forecast += `Light ${windDirection} winds around ${maxWindSpeed} mph. `;
      } else if (maxWindSpeed < 15) {
        forecast += `Moderate ${windDirection} winds around ${maxWindSpeed} mph. `;
      } else {
        forecast += `Strong ${windDirection} winds around ${maxWindSpeed} mph`;
        if (maxWindGust > maxWindSpeed + 10) {
          forecast += ` with gusts up to ${maxWindGust} mph. `;
        } else {
          forecast += ". ";
        }
      }
    }
    switch (cloudTrend) {
      case "increasing" /* Increasing */:
        forecast += periodIsDay ? "Clouds increasing during the day. " : "Clouds increasing throughout the night. ";
        break;
      case "decreasing" /* Decreasing */:
        forecast += "Clearing skies expected. ";
        break;
    }
    return forecast.trim();
  }
  /**
   * Generates a short, human-readable forecast description suitable for a watch face.
   * Focuses on the condition, likelihood, and significant wind.
   *
   * @param hourlyData - Array of hourly weather data
   * @returns Short forecast description
   */
  generateShortForecastDescription(hourlyData) {
    if (hourlyData.length === 0) {
      return "No Data";
    }
    const wmoCodes = hourlyData.map((h) => h.weatherCode);
    const pops = hourlyData.map((h) => h.pop);
    const windSpeeds = hourlyData.map((h) => h.windSpeed);
    const cloudCoverAgg = this.getDominantCloudCover(hourlyData);
    const cloudCover = cloudCoverAgg.dominantValue;
    const dominantCode = this.getDominantWeatherCode(wmoCodes, cloudCover) ?? 0;
    const maxPop = pops.length > 0 ? Math.max(...pops) : 0;
    let description = this.getShortWeatherCodeDescription(dominantCode, maxPop);
    const maxWind = windSpeeds.length > 0 ? Math.max(...windSpeeds) : 0;
    const isMetric = this.selectedUnits !== "Imperial";
    const windyThreshold = isMetric ? 32 : 20;
    const veryWindyThreshold = isMetric ? 48 : 30;
    if (maxWind >= veryWindyThreshold) {
      description += ", very windy";
    } else if (maxWind >= windyThreshold) {
      description += ", windy";
    }
    return description;
  }
  /**
   * Gets a time period description based on the date and location.
   *
   * @param date - The date to describe
   * @param isBasic - Whether to use basic descriptions (true) or detailed (false)
   * @param location - Location information for solar calculations
   * @param timezoneOffsetSeconds - Timezone offset in seconds (optional)
   * @returns Time period description string
   */
  getTimePeriodDescription(date, isBasic = true, location, timezoneOffsetSeconds) {
    const targetTZ = timezoneOffsetSeconds != null ? this.getTimezoneFromOffset(timezoneOffsetSeconds) : null;
    const solarCalculator = new SolarCalculator({
      latitude: location.lat,
      longitude: location.lng,
      elevation: location.elevation ?? 0
    });
    const timeZoneId = targetTZ ?? getSystemTimeZone();
    const solarTimes = solarCalculator.calculate(date, timeZoneId);
    if (!solarTimes.sunrise || !solarTimes.sunset) {
      console.warn("[GTPD][WARN] Solar calc failed.");
      return "Now";
    }
    const sunriseHour = this.getHourInTimezone(solarTimes.sunrise, targetTZ);
    const sunsetHour = this.getHourInTimezone(solarTimes.sunset, targetTZ);
    const hour = this.getHourInTimezone(date, targetTZ);
    const isToday = this.isDateInToday(date, targetTZ);
    const isTomorrow = this.isDateInTomorrow(date, targetTZ);
    let result;
    if (isBasic) {
      if (hour < sunriseHour) {
        result = "Overnight";
      } else if (hour < sunsetHour) {
        result = hour >= 12 ? isToday ? "This Afternoon" : "Tomorrow Afternoon" : isToday ? "Today" : "Tomorrow";
      } else {
        result = isToday ? "Tonight" : "Tomorrow Night";
      }
    } else {
      if (hour < sunriseHour) {
        result = isToday ? "Overnight" : isTomorrow ? "Early Tomorrow Morning" : "Early Morning";
      } else if (hour < 12) {
        result = isToday ? "This Morning" : isTomorrow ? "Tomorrow Morning" : "Morning";
      } else if (hour < sunsetHour) {
        result = isToday ? "This Afternoon" : isTomorrow ? "Tomorrow Afternoon" : "Afternoon";
      } else {
        result = isToday ? "This Evening" : isTomorrow ? "Tomorrow Evening" : "Evening";
      }
    }
    return result;
  }
  /**
   * Helper to map WMO codes to short but descriptive forecast strings.
   *
   * @param wmoCode - WMO weather code
   * @param pop - Probability of precipitation
   * @returns Short weather description
   */
  getShortWeatherCodeDescription(wmoCode, pop) {
    let condition;
    switch (wmoCode) {
      case 0:
        return "Clear";
      case 1:
        return "Mostly Clear";
      case 2:
        return "Partly Cloudy";
      case 3:
        return "Cloudy";
      case 45:
      case 48:
        return "Foggy";
      case 51:
      case 53:
      case 55:
        condition = "Rain";
        break;
      case 56:
      case 57:
        condition = "Freezing Rain";
        break;
      case 61:
      case 63:
      case 65:
        condition = "Rain";
        break;
      case 66:
      case 67:
        condition = "Freezing Rain";
        break;
      case 71:
      case 73:
      case 75:
        condition = "Snow";
        break;
      case 77:
        condition = "Flurries";
        break;
      case 79:
        condition = "Sleet";
        break;
      case 80:
      case 81:
      case 82:
        condition = "Rain Showers";
        break;
      case 85:
      case 86:
        condition = "Snow Showers";
        break;
      case 95:
      case 96:
      case 99:
        condition = "Thunderstorms";
        break;
      default:
        return "Partly Cloudy";
    }
    if (pop >= 80) {
      return condition;
    } else if (pop >= 60) {
      return `${condition} Likely`;
    } else if (pop >= 40) {
      return `Chance of ${condition}`;
    } else if (pop >= 20) {
      return `Possible ${condition}`;
    }
    return condition;
  }
  /**
   * Gets a detailed weather code description for forecast text.
   *
   * @param wmoCode - WMO weather code
   * @param pop - Probability of precipitation
   * @param rainAccumulation - Total rain accumulation
   * @param convectivePrecipitation - Total convective precipitation
   * @returns Detailed weather description
   */
  getWeatherCodeDescription(wmoCode, pop, rainAccumulation, convectivePrecipitation) {
    const isShowers = convectivePrecipitation > rainAccumulation * 0.3;
    let condition;
    switch (wmoCode) {
      case 0:
        return "Clear skies.";
      case 1:
        return "Mostly clear skies.";
      case 2:
        return "Partly cloudy.";
      case 3:
        return "Cloudy skies.";
      case 45:
      case 48:
        return "Foggy conditions.";
      case 51:
      case 53:
      case 55:
        condition = "light rain";
        break;
      case 56:
      case 57:
        condition = "freezing rain";
        break;
      case 61:
      case 80:
        condition = isShowers ? "rain showers" : "light rain";
        break;
      case 63:
      case 81:
        condition = isShowers ? "rain showers" : "rain";
        break;
      case 65:
      case 82:
        condition = isShowers ? "heavy rain showers" : "heavy rain";
        break;
      case 66:
      case 67:
        condition = "freezing rain";
        break;
      case 68:
      case 69:
        condition = "rain and snow mix";
        break;
      case 71:
      case 85:
        condition = isShowers ? "snow showers" : "light snow";
        break;
      case 73:
      case 86:
        condition = isShowers ? "snow showers" : "snow";
        break;
      case 75:
        condition = "heavy snow";
        break;
      case 77:
        condition = "flurries";
        break;
      case 79:
        condition = "sleet";
        break;
      case 95:
      case 96:
      case 99:
        condition = "thunderstorms";
        break;
      default:
        return "Partly cloudy.";
    }
    if (pop >= 80) {
      return `Expect ${condition}.`;
    } else if (pop >= 60) {
      const capitalized = condition.charAt(0).toUpperCase() + condition.slice(1);
      return `${capitalized} likely.`;
    } else if (pop >= 40) {
      return `A chance of ${condition}.`;
    } else if (pop >= 20) {
      return `A slight chance of ${condition}.`;
    } else {
      const capitalized = condition.charAt(0).toUpperCase() + condition.slice(1);
      return `${capitalized} possible.`;
    }
  }
  /**
   * Finds the timeframe when significant precipitation is expected to occur.
   *
   * @param hourlyData - Array of hourly weather data
   * @param precipType - Optional type of precipitation ("rain" or "snow")
   * @param timeZone - IANA timezone identifier (optional)
   * @returns String describing when precipitation will occur, or null
   */
  findPrecipitationTimeframe(hourlyData, precipType, timeZone) {
    if (hourlyData.length === 0) {
      return null;
    }
    const significantPrecipHours = [];
    for (const hour of hourlyData) {
      if (precipType === "rain") {
        if (hour.rainRate >= 0.05) {
          significantPrecipHours.push(hour);
        }
      } else if (precipType === "snow") {
        if (hour.snowRate >= 0.1) {
          significantPrecipHours.push(hour);
        }
      } else {
        if (hour.rainRate >= 0.05 || hour.snowRate >= 0.1) {
          significantPrecipHours.push(hour);
        }
      }
    }
    if (significantPrecipHours.length === 0) {
      return null;
    }
    const formatTime = (date) => {
      const hour = date.getHours();
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? "AM" : "PM";
      return `${hour12} ${ampm}`;
    };
    if (significantPrecipHours.length === 1) {
      const hour = significantPrecipHours[0];
      return ` around ${formatTime(hour.startTime)}`;
    } else {
      let hasSignificantGap = false;
      for (let i = 1; i < significantPrecipHours.length; i++) {
        const timeDifference = significantPrecipHours[i].startTime.getTime() - significantPrecipHours[i - 1].endTime.getTime();
        const hoursDifference = timeDifference / 36e5;
        if (hoursDifference >= 3) {
          hasSignificantGap = true;
          break;
        }
      }
      if (hasSignificantGap) {
        const timeRanges = [];
        let currentStart = significantPrecipHours[0].startTime;
        let currentEnd = significantPrecipHours[0].endTime;
        for (let i = 1; i < significantPrecipHours.length; i++) {
          const hour = significantPrecipHours[i];
          const timeDifference = hour.startTime.getTime() - currentEnd.getTime();
          const hoursDifference = timeDifference / 36e5;
          if (hoursDifference < 3) {
            currentEnd = hour.endTime;
          } else {
            timeRanges.push({ start: currentStart, end: currentEnd });
            currentStart = hour.startTime;
            currentEnd = hour.endTime;
          }
        }
        timeRanges.push({ start: currentStart, end: currentEnd });
        if (timeRanges.length === 1) {
          const start = formatTime(timeRanges[0].start);
          const end = formatTime(timeRanges[0].end);
          return ` between ${start} and ${end}`;
        } else {
          let timeDescription = " during ";
          for (let index = 0; index < timeRanges.length; index++) {
            const range = timeRanges[index];
            const start = formatTime(range.start);
            const end = formatTime(range.end);
            if (index > 0) {
              timeDescription += index === timeRanges.length - 1 ? " and " : ", ";
            }
            timeDescription += `${start} to ${end}`;
          }
          return timeDescription;
        }
      } else {
        const startTime = significantPrecipHours[0].startTime;
        const endTime = significantPrecipHours[significantPrecipHours.length - 1].endTime;
        const start = formatTime(startTime);
        const end = formatTime(endTime);
        return ` between ${start} and ${end}`;
      }
    }
  }
  /**
   * Helper to get timezone string from offset in seconds.
   *
   * @param offsetSeconds - Timezone offset in seconds
   * @returns Timezone identifier string
   */
  getTimezoneFromOffset(offsetSeconds) {
    const offsetHours = offsetSeconds / 3600;
    const sign = offsetHours >= 0 ? "+" : "-";
    const absHours = Math.abs(Math.floor(offsetHours));
    const minutes = Math.abs(offsetSeconds % 3600 / 60);
    return `Etc/GMT${sign}${absHours}${minutes > 0 ? `:${minutes}` : ""}`;
  }
  /**
   * Gets the hour of a date in a specific timezone.
   *
   * @param date - Date to get hour from
   * @param timezone - Timezone identifier (optional)
   * @returns Hour (0-23) in the target timezone
   */
  getHourInTimezone(date, timezone) {
    const tz = timezone ?? getSystemTimeZone();
    const hourStr = date.toLocaleTimeString("en-US", {
      timeZone: tz,
      hour: "numeric",
      hour12: false
    });
    return parseInt(hourStr, 10);
  }
  /**
   * Checks if a date is today in the given timezone.
   *
   * @param date - Date to check
   * @param timezone - Timezone identifier (optional)
   * @returns True if date is today
   */
  isDateInToday(date, timezone) {
    const now = /* @__PURE__ */ new Date();
    const tz = timezone ?? getSystemTimeZone();
    const dateStr = date.toLocaleDateString("en-CA", { timeZone: tz });
    const nowStr = now.toLocaleDateString("en-CA", { timeZone: tz });
    return dateStr === nowStr;
  }
  /**
   * Checks if a date is tomorrow in the given timezone.
   *
   * @param date - Date to check
   * @param timezone - Timezone identifier (optional)
   * @returns True if date is tomorrow
   */
  isDateInTomorrow(date, timezone) {
    const now = /* @__PURE__ */ new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tz = timezone ?? getSystemTimeZone();
    const dateStr = date.toLocaleDateString("en-CA", { timeZone: tz });
    const tomorrowStr = tomorrow.toLocaleDateString("en-CA", { timeZone: tz });
    return dateStr === tomorrowStr;
  }
};

// src/services/WeatherPeriodAggregator.ts
var WeatherPeriodAggregator = class {
  /**
   * Aggregates hourly data into day/night periods and builds daily weather summary.
   *
   * @param hourlyData - Array of hourly weather data
   * @param location - Location information
   * @param timezone - IANA timezone string
   * @param timezoneOffsetSeconds - Timezone offset in seconds
   * @param deps - Dependencies object
   * @returns RODailyWeather object with daily summary and periods
   */
  static aggregateDayNightPeriods(hourlyData, location, timezone, timezoneOffsetSeconds, deps) {
    if (hourlyData.length === 0) {
      return {
        maxTemperatureWeek: INVALID_DOUBLE,
        minTemperatureWeek: INVALID_DOUBLE,
        todayMaxTemperature: INVALID_DOUBLE,
        todayMinTemperature: INVALID_DOUBLE,
        currentIndex: 0,
        currentDayName: "Now",
        currentDayForecast: "",
        currentDayForecastShort: "",
        tomorrowDayName: "Tomorrow",
        tomorrowDayForecast: "",
        tomorrowDayForecastShort: "",
        todayRain: 0,
        todaySnow: 0,
        todayIce: 0,
        todayWindSpeed: 0,
        todayWindDirection: "",
        todayHumidity: 0,
        todayHumidityLow: 0,
        todayUVIndex: 0,
        days: []
      };
    }
    const periods = [];
    let bucket = [];
    let currentFlag = null;
    for (const h of hourlyData) {
      if (currentFlag === null) {
        currentFlag = h.isDay;
      }
      if (h.isDay === currentFlag) {
        bucket.push(h);
      } else {
        if (bucket.length > 0) {
          periods.push(bucket);
        }
        bucket = [h];
        currentFlag = h.isDay;
      }
    }
    if (bucket.length > 0) {
      periods.push(bucket);
    }
    const days = periods.map(
      (hours, index) => this.buildPeriodDay(hours, index, location, timezone, timezoneOffsetSeconds, deps)
    );
    const now = /* @__PURE__ */ new Date();
    const currentIndex = days.findIndex(
      (d) => d.startTime <= now && now < d.endTime
    );
    if (currentIndex === -1) {
      return {
        maxTemperatureWeek: INVALID_DOUBLE,
        minTemperatureWeek: INVALID_DOUBLE,
        todayMaxTemperature: INVALID_DOUBLE,
        todayMinTemperature: INVALID_DOUBLE,
        currentIndex: 0,
        currentDayName: "Now",
        currentDayForecast: "",
        currentDayForecastShort: "",
        tomorrowDayName: "Tomorrow",
        tomorrowDayForecast: "",
        tomorrowDayForecastShort: "",
        todayRain: 0,
        todaySnow: 0,
        todayIce: 0,
        todayWindSpeed: 0,
        todayWindDirection: "",
        todayHumidity: 0,
        todayHumidityLow: 0,
        todayUVIndex: 0,
        days: []
      };
    }
    const currentPeriod = days[currentIndex];
    const previousPeriod = currentIndex > 0 ? days[currentIndex - 1] : null;
    const nextIndex = Math.min(currentIndex + 1, days.length - 1);
    const nextPeriod = days[nextIndex];
    let todayMax;
    let todayMin;
    if (currentPeriod.isDay) {
      todayMax = currentPeriod.maxTemperature;
      todayMin = nextPeriod.minTemperature;
    } else {
      todayMax = previousPeriod?.maxTemperature ?? nextPeriod.maxTemperature;
      todayMin = currentPeriod.minTemperature;
    }
    const localHour = this.getLocalHour(now, timezone);
    const currentDayName = deps.forecastGenerator.getTimePeriodDescription(
      now,
      true,
      location,
      timezoneOffsetSeconds
    );
    const tomorrowDayName = deps.forecastGenerator.getTimePeriodDescription(
      nextPeriod.startTime,
      true,
      location,
      timezoneOffsetSeconds
    );
    const currentForecast = localHour >= 12 && currentPeriod.detailedForecastAfternoon.length > 0 ? currentPeriod.detailedForecastAfternoon : currentPeriod.detailedForecast;
    const currentForecastShort = localHour >= 12 && currentPeriod.detailedForecastAfternoonShort.length > 0 ? currentPeriod.detailedForecastAfternoonShort : currentPeriod.shortForecast;
    const temps = hourlyData.map((h) => h.temperature).filter((t) => t !== INVALID_DOUBLE);
    const weekMax = temps.length > 0 ? Math.max(...temps) : INVALID_DOUBLE;
    const weekMin = temps.length > 0 ? Math.min(...temps) : INVALID_DOUBLE;
    const { startOfLocalDay, startOfNextLocalDay } = this.getLocalDayBounds(
      now,
      timezone
    );
    const todayHourly = hourlyData.filter(
      (hour) => hour.startTime >= startOfLocalDay && hour.startTime < startOfNextLocalDay
    );
    const todayRainTotal = Number(
      todayHourly.map((h) => h.rainRate).reduce((sum2, r) => sum2 + r, 0).toFixed(2)
    );
    const todaySnowTotal = Number(
      todayHourly.map((h) => h.snowRate).reduce((sum2, s) => sum2 + s, 0).toFixed(2)
    );
    const todayIceTotal = Number(
      todayHourly.map((h) => h.iceRate).reduce((sum2, ice) => sum2 + ice, 0).toFixed(2)
    );
    const todayHumidityMax = todayHourly.length > 0 ? Math.max(...todayHourly.map((h) => h.humidity)) : INVALID_INT;
    const todayHumidityMin = todayHourly.length > 0 ? Math.min(...todayHourly.map((h) => h.humidity)) : INVALID_INT;
    const todayWindSpeed = todayHourly.length > 0 ? Math.max(...todayHourly.map((h) => h.windSpeed)) : INVALID_INT;
    const todayWindDirectionCardinal = (() => {
      if (todayWindSpeed === INVALID_INT) return "N";
      const maxWindHour = todayHourly.find((h) => h.windSpeed === todayWindSpeed);
      if (!maxWindHour || maxWindHour.windBearing === INVALID_INT) return "N";
      return deps.cardinalDirection(maxWindHour.windBearing);
    })();
    return {
      maxTemperatureWeek: weekMax,
      minTemperatureWeek: weekMin,
      todayMaxTemperature: todayMax,
      todayMinTemperature: todayMin,
      currentIndex,
      currentDayName,
      currentDayForecast: currentForecast,
      currentDayForecastShort: currentForecastShort,
      tomorrowDayName,
      tomorrowDayForecast: nextPeriod.detailedForecast,
      tomorrowDayForecastShort: nextPeriod.shortForecast,
      todayRain: todayRainTotal,
      todaySnow: todaySnowTotal,
      todayIce: todayIceTotal,
      todayWindSpeed,
      todayWindDirection: todayWindDirectionCardinal,
      todayHumidity: todayHumidityMax,
      todayHumidityLow: todayHumidityMin,
      todayUVIndex: 0,
      // TODO: Calculate UV index
      days
    };
  }
  /**
   * Builds a single day/night period from hourly data.
   *
   * @param hours - Array of hourly data for this period
   * @param index - Index of this period in the periods array
   * @param location - Location information
   * @param timezone - IANA timezone string
   * @param tzOffset - Timezone offset in seconds
   * @param deps - Dependencies object
   * @returns RODailyWeatherDay object
   */
  static buildPeriodDay(hours, index, location, timezone, tzOffset, deps) {
    const fg = deps.forecastGenerator;
    const isDay = hours[0]?.isDay ?? false;
    const start = hours[0]?.startTime ?? /* @__PURE__ */ new Date();
    const end = hours[hours.length - 1]?.endTime ?? start;
    const temps = hours.map((h) => h.temperature);
    const appTemps = hours.map((h) => h.temperatureApparent);
    const windSpeeds = hours.map((h) => h.windSpeed);
    const windGusts = hours.map((h) => h.windGust);
    const wmoCodes = hours.map((h) => h.weatherCode);
    const pops = hours.map((h) => h.pop);
    const cloudCoverAgg = fg.getDominantCloudCover(hours);
    const cloudCoverForCode = cloudCoverAgg.dominantValue;
    const dominantCode = fg.getDominantWeatherCode(wmoCodes, cloudCoverForCode) ?? 0;
    const forecastDescription = fg.generateForecastDescription(
      hours,
      location,
      timezone
    );
    const forecastDescriptionShort = fg.generateShortForecastDescription(hours);
    let forecastDescriptionAfternoon = "";
    let forecastDescriptionAfternoonShort = "";
    if (isDay) {
      const noonIdx = hours.findIndex((h) => {
        const hour = this.getLocalHour(h.startTime, timezone);
        return hour >= 12;
      });
      if (noonIdx !== -1) {
        const afternoonHours = hours.slice(noonIdx);
        forecastDescriptionAfternoon = fg.generateForecastDescription(
          afternoonHours,
          location,
          timezone
        );
        forecastDescriptionAfternoonShort = fg.generateShortForecastDescription(afternoonHours);
      }
    }
    const shortName = deps.extractShortDayName(start, end, tzOffset);
    const dominantBearing = fg.getDominantWindBearing(hours.map((h) => h.windBearing)) ?? 0;
    const cardinal = deps.cardinalDirection(dominantBearing);
    const rainTotal = Number(
      hours.map((h) => h.rainRate).reduce((sum2, r) => sum2 + r, 0).toFixed(3)
    );
    const snowTotal = Number(
      hours.map((h) => h.snowRate).reduce((sum2, s) => sum2 + s, 0).toFixed(3)
    );
    const iceTotal = Number(
      hours.map((h) => h.iceRate).reduce((sum2, ice) => sum2 + ice, 0).toFixed(3)
    );
    const precipitationTotal = Number(
      hours.map((h) => h.precipitationRate).reduce((sum2, p) => sum2 + p, 0).toFixed(3)
    );
    const precipChance = pops.length > 0 ? Math.max(...pops) : 0;
    return {
      id: `day-${index}-${start.getTime()}`,
      index,
      startTime: start,
      endTime: end,
      name: shortName,
      nameShort: shortName,
      isDay,
      maxTemperature: temps.length > 0 ? Math.max(...temps) : 0,
      minTemperature: temps.length > 0 ? Math.min(...temps) : 0,
      maxTemperatureApparent: appTemps.length > 0 ? Math.max(...appTemps) : 0,
      minTemperatureApparent: appTemps.length > 0 ? Math.min(...appTemps) : 0,
      windSpeed: windSpeeds.length > 0 ? Math.max(...windSpeeds) : 0,
      windGust: windGusts.length > 0 ? Math.max(...windGusts) : 0,
      windDirection: cardinal,
      weatherCode: dominantCode,
      shortForecast: forecastDescriptionShort,
      detailedForecast: forecastDescription,
      detailedForecastAfternoon: forecastDescriptionAfternoon,
      detailedForecastAfternoonShort: forecastDescriptionAfternoonShort,
      cloudCover: cloudCoverAgg.dominantValue ?? 0,
      cloudCoverTrend: cloudCoverAgg.trend,
      precipitationAccumulation: precipitationTotal,
      rainAccumulation: rainTotal,
      snowAccumulation: snowTotal,
      iceAccumulation: iceTotal,
      precipChance
    };
  }
  /**
   * Gets the local hour from a date in a given timezone.
   *
   * @param date - The date to extract hour from
   * @param timezone - IANA timezone string
   * @returns Local hour (0-23)
   */
  static getLocalHour(date, timezone) {
    if (!timezone) {
      return date.getHours();
    }
    return new Date(
      date.toLocaleString("en-US", { timeZone: timezone })
    ).getHours();
  }
  /**
   * Gets the start and end of the local day in a given timezone.
   *
   * @param date - The date to get day bounds for
   * @param timezone - IANA timezone string
   * @returns Object with startOfLocalDay and startOfNextLocalDay
   */
  static getLocalDayBounds(date, timezone) {
    const tz = timezone ?? getSystemTimeZone();
    const localDateStr = date.toLocaleDateString("en-CA", {
      timeZone: tz
    });
    const startOfLocalDay = /* @__PURE__ */ new Date(`${localDateStr}T00:00:00`);
    const nextDay = new Date(startOfLocalDay);
    nextDay.setDate(nextDay.getDate() + 1);
    const startOfNextLocalDay = nextDay;
    return { startOfLocalDay, startOfNextLocalDay };
  }
};

// src/utils/TemperatureColors.ts
function rgbToHex(r, g, b) {
  const toHex = (value) => {
    const hex = Math.round(value * 255).toString(16).padStart(2, "0");
    return hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
function toRGBColor(r, g, b) {
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
}
var cNeg20 = { r: 0.671, g: 0.067, b: 0.69 };
var cNeg15 = { r: 0.612, g: 0.153, b: 0.612 };
var cNeg10 = { r: 0.612, g: 0.153, b: 0.69 };
var cNeg5 = { r: 0.545, g: 0.325, b: 0.612 };
var c0 = { r: 0.545, g: 0.325, b: 0.69 };
var c5 = { r: 0.404, g: 0.227, b: 0.639 };
var c10 = { r: 0.404, g: 0.227, b: 0.718 };
var c15 = { r: 0.188, g: 0.247, b: 0.545 };
var c20 = { r: 0.188, g: 0.247, b: 0.624 };
var c25 = { r: 0.118, g: 0.533, b: 0.8 };
var c30 = { r: 0.118, g: 0.533, b: 0.898 };
var c35 = { r: 0.153, g: 0.38, b: 0.51 };
var c40 = { r: 0.188, g: 0.38, b: 0.51 };
var c45 = { r: 0.129, g: 0.518, b: 0.42 };
var c50 = { r: 0.129, g: 0.518, b: 0.353 };
var c55 = { r: 0.184, g: 0.553, b: 0.337 };
var c60 = { r: 0.251, g: 0.592, b: 0.337 };
var c65 = { r: 0.337, g: 0.635, b: 0.337 };
var c70 = { r: 0.424, g: 0.678, b: 0.337 };
var c75 = { r: 0.847, g: 0.616, b: 0.063 };
var c80 = { r: 0.918, g: 0.557, b: 0.063 };
var c85 = { r: 0.867, g: 0.435, b: 0.063 };
var c90 = { r: 0.898, g: 0.31, b: 0.09 };
var c95 = { r: 0.769, g: 0.129, b: 0.11 };
var c100 = { r: 0.639, g: 0.051, b: 0.024 };
var c105 = { r: 0.431, g: 0.106, b: 0.137 };
var c110 = { r: 0.373, g: 0.055, b: 0.102 };
var c115 = { r: 0.302, g: 0.059, b: 0.082 };
function colorForTemperature(temperature, units = "Imperial") {
  const tempF = units === "Imperial" ? temperature : temperature * 9 / 5 + 32;
  if (!Number.isFinite(tempF)) {
    return rgbToHex(c70.r, c70.g, c70.b);
  }
  const step = 5;
  const roundedValue = step * Math.round(tempF / step);
  const clamped = Math.min(Math.max(roundedValue, -20), 115);
  let color;
  switch (clamped) {
    case -20:
      color = cNeg20;
      break;
    case -15:
      color = cNeg15;
      break;
    case -10:
      color = cNeg10;
      break;
    case -5:
      color = cNeg5;
      break;
    case 0:
      color = c0;
      break;
    case 5:
      color = c5;
      break;
    case 10:
      color = c10;
      break;
    case 15:
      color = c15;
      break;
    case 20:
      color = c20;
      break;
    case 25:
      color = c25;
      break;
    case 30:
      color = c30;
      break;
    case 35:
      color = c35;
      break;
    case 40:
      color = c40;
      break;
    case 45:
      color = c45;
      break;
    case 50:
      color = c50;
      break;
    case 55:
      color = c55;
      break;
    case 60:
      color = c60;
      break;
    case 65:
      color = c65;
      break;
    case 70:
      color = c70;
      break;
    case 75:
      color = c75;
      break;
    case 80:
      color = c80;
      break;
    case 85:
      color = c85;
      break;
    case 90:
      color = c90;
      break;
    case 95:
      color = c95;
      break;
    case 100:
      color = c100;
      break;
    case 105:
      color = c105;
      break;
    case 110:
      color = c110;
      break;
    case 115:
      color = c115;
      break;
    default:
      color = c70;
  }
  return rgbToHex(color.r, color.g, color.b);
}
function rgbColorForTemperature(temperature, units = "Imperial") {
  const tempF = units === "Imperial" ? temperature : temperature * 9 / 5 + 32;
  if (!Number.isFinite(tempF)) {
    return toRGBColor(c70.r, c70.g, c70.b);
  }
  const step = 5;
  const roundedValue = step * Math.round(tempF / step);
  const clamped = Math.min(Math.max(roundedValue, -20), 115);
  let color;
  switch (clamped) {
    case -20:
      color = cNeg20;
      break;
    case -15:
      color = cNeg15;
      break;
    case -10:
      color = cNeg10;
      break;
    case -5:
      color = cNeg5;
      break;
    case 0:
      color = c0;
      break;
    case 5:
      color = c5;
      break;
    case 10:
      color = c10;
      break;
    case 15:
      color = c15;
      break;
    case 20:
      color = c20;
      break;
    case 25:
      color = c25;
      break;
    case 30:
      color = c30;
      break;
    case 35:
      color = c35;
      break;
    case 40:
      color = c40;
      break;
    case 45:
      color = c45;
      break;
    case 50:
      color = c50;
      break;
    case 55:
      color = c55;
      break;
    case 60:
      color = c60;
      break;
    case 65:
      color = c65;
      break;
    case 70:
      color = c70;
      break;
    case 75:
      color = c75;
      break;
    case 80:
      color = c80;
      break;
    case 85:
      color = c85;
      break;
    case 90:
      color = c90;
      break;
    case 95:
      color = c95;
      break;
    case 100:
      color = c100;
      break;
    case 105:
      color = c105;
      break;
    case 110:
      color = c110;
      break;
    case 115:
      color = c115;
      break;
    default:
      color = c70;
  }
  return toRGBColor(color.r, color.g, color.b);
}
function colorsForTemperatureValues(temperatures, units = "Imperial") {
  return temperatures.map((temp) => colorForTemperature(temp, units));
}
function rgbColorsForTemperatureValues(temperatures, units = "Imperial") {
  return temperatures.map((temp) => rgbColorForTemperature(temp, units));
}
function gradientColorsForTemperatures(minTemp, maxTemp, units = "Imperial") {
  const start = Math.min(minTemp, maxTemp);
  const end = Math.max(minTemp, maxTemp);
  const startF = units === "Imperial" ? start : start * 9 / 5 + 32;
  const endF = units === "Imperial" ? end : end * 9 / 5 + 32;
  const colors = [];
  colors.push(colorForTemperature(start, units));
  const firstStep = (Math.floor(startF / 5) + 1) * 5;
  let currentStep = firstStep;
  while (currentStep < endF) {
    colors.push(colorForTemperature(currentStep, "Imperial"));
    currentStep += 5;
  }
  colors.push(colorForTemperature(end, units));
  return colors;
}
function rgbGradientColorsForTemperatures(minTemp, maxTemp, units = "Imperial") {
  const start = Math.min(minTemp, maxTemp);
  const end = Math.max(minTemp, maxTemp);
  const startF = units === "Imperial" ? start : start * 9 / 5 + 32;
  const endF = units === "Imperial" ? end : end * 9 / 5 + 32;
  const colors = [];
  colors.push(rgbColorForTemperature(start, units));
  const firstStep = (Math.floor(startF / 5) + 1) * 5;
  let currentStep = firstStep;
  while (currentStep < endF) {
    colors.push(rgbColorForTemperature(currentStep, "Imperial"));
    currentStep += 5;
  }
  colors.push(rgbColorForTemperature(end, units));
  return colors;
}

// src/services/WeatherOMClass.ts
var WeatherOMClass = class {
  constructor(params = {}) {
    this.m_location = defaultLocation;
    this.m_timezone = null;
    this.m_timezoneOffsetSeconds = 0;
    this.invalidDoubleValue = INVALID_DOUBLE;
    this.invalidIntValue = INVALID_INT;
    const {
      selectedUnits = "Imperial",
      loading = false,
      timestamp = null,
      formattedTime = "",
      temperature = INVALID_DOUBLE,
      temperatureApparent = INVALID_DOUBLE,
      dewPoint = INVALID_DOUBLE,
      humidity = INVALID_DOUBLE,
      windBearing = INVALID_DOUBLE,
      windSpeed = INVALID_DOUBLE,
      windGust = INVALID_DOUBLE,
      pressure = INVALID_DOUBLE,
      elevation = 0,
      rainRate = INVALID_DOUBLE,
      snowRate = INVALID_DOUBLE,
      weatherCode = INVALID_INT,
      isDay = false,
      cloudCover = INVALID_DOUBLE,
      timezoneOffsetSeconds = 0
    } = params;
    this.selectedUnits = selectedUnits;
    this.loading = loading;
    this.timestamp = timestamp;
    this.formattedTime = formattedTime;
    this.temperature = temperature;
    this.temperatureApparent = temperatureApparent;
    this.dewPoint = dewPoint;
    this.humidity = humidity;
    this.windBearing = windBearing;
    this.windSpeed = windSpeed;
    this.windGust = windGust;
    this.pressure = pressure;
    this.elevation = elevation;
    this.rainRate = rainRate;
    this.snowRate = snowRate;
    this.weatherCode = weatherCode;
    this.isDay = isDay;
    this.cloudCover = cloudCover;
    this.m_timezoneOffsetSeconds = timezoneOffsetSeconds;
    this.dailyWeather = {
      maxTemperatureWeek: INVALID_DOUBLE,
      minTemperatureWeek: INVALID_DOUBLE,
      todayMaxTemperature: INVALID_DOUBLE,
      todayMinTemperature: INVALID_DOUBLE,
      currentIndex: 0,
      currentDayName: "Now",
      currentDayForecast: "",
      currentDayForecastShort: "",
      tomorrowDayName: "Tomorrow",
      tomorrowDayForecast: "",
      tomorrowDayForecastShort: "",
      todayRain: 0,
      todaySnow: 0,
      todayIce: 0,
      todayWindSpeed: 0,
      todayWindDirection: "",
      todayHumidity: 0,
      todayHumidityLow: 0,
      todayUVIndex: 0,
      days: []
    };
    this.hourlyWeather = {
      currentIndex: -1,
      maxTemperature: INVALID_DOUBLE,
      minTemperature: INVALID_DOUBLE,
      temperatureColors: ["transparent"],
      hours: []
    };
    this.forecastGenerator = new ForecastGenerator(selectedUnits);
  }
  setLocation(location) {
    this.m_location = location;
  }
  setTimeZone(timeZone, offsetSeconds = 0) {
    this.m_timezone = timeZone;
    this.m_timezoneOffsetSeconds = offsetSeconds;
  }
  /**
   * Sets current weather values from OpenMeteo current weather data.
   * Handles unit conversion based on selectedUnits.
   */
  setValues(omData) {
    if (!omData || !omData.current) {
      console.error("Error: OpenMeteoData or current is nil");
      return;
    }
    const current = omData.current;
    this.timestamp = new Date(current.time * 1e3);
    const hours = this.timestamp.getHours();
    const minutes = this.timestamp.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;
    const minuteStr = minutes.toString().padStart(2, "0");
    this.formattedTime = `${hour12}:${minuteStr} ${ampm}`;
    const isImperial = this.selectedUnits === "Imperial";
    const windSpeed = current.windSpeed != null && current.windSpeed >= 0 ? current.windSpeed : 0;
    const windBearing = current.windBearing != null && current.windBearing >= 0 && current.windBearing <= 360 ? current.windBearing : 0;
    if (isImperial) {
      this.temperature = current.temperature;
      this.temperatureApparent = current.temperatureApparent;
      this.dewPoint = roundToPlaces(
        getDewPoint(current.temperature, current.humidity),
        2
      );
      this.windSpeed = windSpeed;
      this.elevation = Math.round(metersToFeet(omData.elevation));
      this.rainRate = current.rainRate;
      this.snowRate = current.snowRate;
    } else {
      this.temperature = roundToPlaces(fahrenheitToCelsius(current.temperature), 2);
      this.temperatureApparent = roundToPlaces(
        fahrenheitToCelsius(current.temperatureApparent),
        2
      );
      const dpF = getDewPoint(current.temperature, current.humidity);
      this.dewPoint = roundToPlaces(fahrenheitToCelsius(dpF), 2);
      this.windSpeed = roundToPlaces(milesToKilometers(windSpeed), 1);
      this.elevation = omData.elevation;
      this.rainRate = roundToPlaces(inchesToMillimeters(current.rainRate), 2);
      this.snowRate = roundToPlaces(inchesToMillimeters(current.snowRate), 2);
    }
    this.pressure = current.pressure;
    this.humidity = Math.round(current.humidity);
    this.windBearing = windBearing;
    this.weatherCode = current.weatherCode;
    this.isDay = current.isDay !== 0;
    this.cloudCover = current.cloudCover;
  }
  /**
   * Sets hourly weather values from OpenMeteo hourly forecast data.
   * Blends multiple forecast models and aggregates into daily periods.
   *
   * @param omHourly - OpenMeteo hourly data from generic forecast endpoint
   * @param ecmwfPressureData - Optional ECMWF pressure level data from dedicated endpoint
   */
  setHourlyValues(omHourly, ecmwfPressureData) {
    this.forecastGenerator.selectedUnits = this.selectedUnits;
    if (!omHourly) {
      this.hourlyWeather = {
        currentIndex: this.invalidIntValue,
        maxTemperature: this.invalidDoubleValue,
        minTemperature: this.invalidDoubleValue,
        temperatureColors: ["transparent"],
        hours: []
      };
      return;
    }
    const now = /* @__PURE__ */ new Date();
    const modelSpecificData = this.extractModelSpecificHourlyData(omHourly);
    const blendedHourlyData = this.forecastGenerator.blendHourlyForecasts(
      modelSpecificData.icon,
      modelSpecificData.nbm,
      modelSpecificData.ecmwf,
      modelSpecificData.best,
      this.m_location,
      ecmwfPressureData
    );
    let currentIndex = -1;
    const temperatures = blendedHourlyData.map((h) => h.temperature);
    const maxTemperature = temperatures.length > 0 ? Math.max(...temperatures) : 0;
    const minTemperature = temperatures.length > 0 ? Math.min(...temperatures) : 0;
    const temperatureColors = colorsForTemperatureValues(temperatures);
    for (let i = 0; i < omHourly.time.length; i++) {
      const time = omHourly.time[i];
      const startTime = new Date((time - 3600) * 1e3);
      const endTime = new Date(time * 1e3);
      if (now >= startTime && now <= endTime) {
        currentIndex = i;
        break;
      }
    }
    this.hourlyWeather = {
      currentIndex,
      maxTemperature,
      minTemperature,
      temperatureColors,
      hours: blendedHourlyData
    };
    const deps = {
      forecastGenerator: this.forecastGenerator,
      extractShortDayName: (start, end, tzOffset) => extractShortDayNameFromTimestamp(start, end, tzOffset),
      cardinalDirection: (bearing) => cardinalDirection(bearing)
    };
    this.dailyWeather = WeatherPeriodAggregator.aggregateDayNightPeriods(
      blendedHourlyData,
      this.m_location,
      this.m_timezone,
      this.m_timezoneOffsetSeconds,
      deps
    );
  }
  /**
   * Extracts model-specific hourly data from OpenMeteo response.
   * Returns separate arrays for BEST, ICON, NBM, and ECMWF models.
   */
  extractModelSpecificHourlyData(omHourly) {
    const bestHourlyWeatherItems = [];
    const iconHourlyWeatherItems = [];
    const nbmHourlyWeatherItems = [];
    const ecmwfHourlyWeatherItems = [];
    for (let idx = 0; idx < omHourly.time.length; idx++) {
      const timeValue = omHourly.time[idx];
      const startTime = new Date((timeValue - 3600) * 1e3);
      const endTime = new Date(timeValue * 1e3);
      const isDay = idx < omHourly.isDaysBEST.length ? omHourly.isDaysBEST[idx] === 1 : false;
      bestHourlyWeatherItems.push(
        this.createHourForModel(
          idx,
          startTime,
          endTime,
          isDay,
          omHourly.temperaturesBEST,
          omHourly.humiditiesBEST,
          omHourly.windSpeedsBEST,
          omHourly.windGustsBEST,
          omHourly.windDirectionsBEST,
          omHourly.cloudsBEST,
          omHourly.weatherCodesBEST,
          omHourly.rainAccumulationsBEST,
          omHourly.showerAccumulationsBEST,
          omHourly.snowAccumulationsBEST,
          omHourly.precipitationsBEST,
          omHourly.popsBEST,
          omHourly.temp850hPaBEST,
          omHourly.temp925hPaBEST,
          omHourly.temp700hPaBEST,
          omHourly.dewPointsBEST
        )
      );
      iconHourlyWeatherItems.push(
        this.createHourForModel(
          idx,
          startTime,
          endTime,
          isDay,
          omHourly.temperaturesICON,
          omHourly.humiditiesICON,
          omHourly.windSpeedsICON,
          omHourly.windGustsICON,
          omHourly.windDirectionsICON,
          omHourly.cloudsICON,
          omHourly.weatherCodesICON,
          omHourly.rainAccumulationsICON,
          omHourly.showerAccumulationsICON,
          omHourly.snowAccumulationsICON,
          omHourly.precipitationsICON,
          omHourly.popsICON,
          omHourly.temp850hPaICON,
          omHourly.temp925hPaICON,
          omHourly.temp700hPaICON,
          omHourly.dewPointsICON
        )
      );
      nbmHourlyWeatherItems.push(
        this.createHourForModel(
          idx,
          startTime,
          endTime,
          isDay,
          omHourly.temperaturesNBM ?? [],
          omHourly.humiditiesNBM ?? [],
          omHourly.windSpeedsNBM ?? [],
          omHourly.windGustsNBM ?? [],
          omHourly.windDirectionsNBM ?? [],
          omHourly.cloudsNBM ?? [],
          omHourly.weatherCodesNBM ?? [],
          omHourly.rainAccumulationsNBM ?? [],
          omHourly.showerAccumulationsNBM ?? [],
          omHourly.snowAccumulationsNBM ?? [],
          omHourly.precipitationsNBM ?? [],
          omHourly.popsNBM ?? [],
          [],
          // NBM doesn't provide pressure level temperatures
          [],
          [],
          omHourly.dewPointsNBM ?? []
        )
      );
      ecmwfHourlyWeatherItems.push(
        this.createHourForModel(
          idx,
          startTime,
          endTime,
          isDay,
          omHourly.temperaturesECMWF,
          omHourly.humiditiesECMWF,
          omHourly.windSpeedsECMWF,
          omHourly.windGustsECMWF,
          omHourly.windDirectionsECMWF,
          omHourly.cloudsECMWF,
          omHourly.weatherCodesECMWF,
          omHourly.rainAccumulationsECMWF,
          omHourly.showerAccumulationsECMWF,
          omHourly.snowAccumulationsECMWF,
          omHourly.precipitationsECMWF,
          omHourly.popsECMWF,
          omHourly.temp850hPaECMWF,
          omHourly.temp925hPaECMWF,
          omHourly.temp700hPaECMWF,
          omHourly.dewPointsECMWF
        )
      );
    }
    return {
      best: bestHourlyWeatherItems,
      icon: iconHourlyWeatherItems,
      nbm: nbmHourlyWeatherItems,
      ecmwf: ecmwfHourlyWeatherItems
    };
  }
  /**
   * Creates a ROHourlyWeatherHour object from model-specific data arrays.
   * Handles unit conversion and calculates derived values.
   */
  createHourForModel(idx, startTime, endTime, isDay, temperatures, humidities, windSpeeds, windGusts, windBearings, cloudCovers, weatherCodes, rainAccumulations, showerAccumulations, snowAccumulations, precipitations, pops, temp850hPas = [], temp925hPas = [], temp700hPas = [], dewPoints = []) {
    const rawTemp = idx < temperatures.length && temperatures[idx] !== null ? temperatures[idx] : this.invalidDoubleValue;
    const humidity = idx < humidities.length && humidities[idx] !== null ? humidities[idx] : this.invalidIntValue;
    const rawWindSpeed = idx < windSpeeds.length && windSpeeds[idx] !== null && windSpeeds[idx] >= 0 ? Math.round(windSpeeds[idx]) : 0;
    const rawWindGust = idx < windGusts.length && windGusts[idx] !== null && windGusts[idx] >= 0 ? Math.round(windGusts[idx]) : 0;
    const windBearing = idx < windBearings.length && windBearings[idx] !== null && windBearings[idx] >= 0 && windBearings[idx] <= 360 ? windBearings[idx] : 0;
    const cloudCover = idx < cloudCovers.length && cloudCovers[idx] !== null ? cloudCovers[idx] : this.invalidIntValue;
    const wmoCode = idx < weatherCodes.length && weatherCodes[idx] !== null ? weatherCodes[idx] : this.invalidIntValue;
    const rawRainRate = idx < rainAccumulations.length && rainAccumulations[idx] !== null ? Math.abs(roundToPlaces(rainAccumulations[idx], 3)) : 0;
    const rawConvectivePrecip = idx < showerAccumulations.length && showerAccumulations[idx] !== null ? Math.abs(roundToPlaces(showerAccumulations[idx], 3)) : 0;
    const rawSnowRate = idx < snowAccumulations.length && snowAccumulations[idx] !== null ? Math.abs(roundToPlaces(snowAccumulations[idx], 3)) : 0;
    const rawPrecipitation = idx < precipitations.length && precipitations[idx] !== null ? Math.abs(roundToPlaces(precipitations[idx], 3)) : 0;
    const pop = idx < pops.length && pops[idx] !== null ? pops[idx] : 0;
    const temp850hPa = idx < temp850hPas.length && temp850hPas[idx] !== null ? temp850hPas[idx] : void 0;
    const temp925hPa = idx < temp925hPas.length && temp925hPas[idx] !== null ? temp925hPas[idx] : void 0;
    const temp700hPa = idx < temp700hPas.length && temp700hPas[idx] !== null ? temp700hPas[idx] : void 0;
    const apiDewPoint = idx < dewPoints.length && dewPoints[idx] !== null ? dewPoints[idx] : null;
    const rawDewPoint = apiDewPoint !== null ? apiDewPoint : rawTemp !== this.invalidDoubleValue && humidity !== this.invalidIntValue ? getDewPoint(rawTemp, humidity) : this.invalidDoubleValue;
    const apparentTemp = rawTemp !== this.invalidDoubleValue && humidity !== this.invalidIntValue ? apparentTemperatureBOM(
      rawTemp,
      humidity,
      rawWindSpeed,
      this.selectedUnits
    ) : this.invalidDoubleValue;
    const isImperial = this.selectedUnits === "Imperial";
    let finalTemp;
    let finalDewPoint;
    let finalWindSpeed;
    let finalWindGust;
    let finalRainRate;
    let finalConvectivePrecip;
    let finalSnowRate;
    let finalPrecipitation;
    if (isImperial) {
      finalTemp = rawTemp;
      finalDewPoint = rawDewPoint;
      finalWindSpeed = rawWindSpeed;
      finalWindGust = rawWindGust;
      finalRainRate = rawRainRate;
      finalConvectivePrecip = rawConvectivePrecip;
      finalSnowRate = rawSnowRate;
      finalPrecipitation = rawPrecipitation;
    } else {
      finalTemp = rawTemp !== this.invalidDoubleValue ? roundToPlaces(fahrenheitToCelsius(rawTemp), 2) : this.invalidDoubleValue;
      finalDewPoint = rawDewPoint !== this.invalidDoubleValue ? roundToPlaces(fahrenheitToCelsius(rawDewPoint), 2) : this.invalidDoubleValue;
      finalWindSpeed = Math.round(milesToKilometers(rawWindSpeed));
      finalWindGust = Math.round(milesToKilometers(rawWindGust));
      finalRainRate = roundToPlaces(inchesToMillimeters(rawRainRate), 3);
      finalConvectivePrecip = roundToPlaces(
        inchesToMillimeters(rawConvectivePrecip),
        3
      );
      finalSnowRate = roundToPlaces(inchesToMillimeters(rawSnowRate), 3);
      finalPrecipitation = roundToPlaces(inchesToMillimeters(rawPrecipitation), 3);
    }
    return {
      id: `hour-${idx}-${startTime.getTime()}`,
      index: idx,
      startTime,
      endTime,
      isDay,
      temperature: finalTemp,
      temperatureApparent: apparentTemp,
      dewPoint: finalDewPoint,
      humidity,
      weatherCode: wmoCode,
      windSpeed: finalWindSpeed,
      windGust: finalWindGust,
      windBearing,
      cloudCover,
      precipitationRate: finalPrecipitation,
      rainRate: finalRainRate + finalConvectivePrecip,
      convectivePrecipitation: finalConvectivePrecip,
      snowRate: finalSnowRate,
      iceRate: 0,
      // Will be populated by WinterPrecipAnalyzer in ForecastGenerator
      pop,
      // Winter precipitation analysis fields (snowLevelHeight calculated in ForecastGenerator)
      snowLevelHeight: void 0,
      // Calculated from blended temps in ForecastGenerator
      temp850hPa,
      temp925hPa,
      temp700hPa
    };
  }
  /**
   * Logs current observation values to console.
   * If properties array is provided, logs only those properties.
   * Otherwise logs all current observation values.
   */
  logValuesCurrent(properties) {
    const prefix = `[WeatherOM: Current]`;
    if (properties && properties.length > 0) {
      console.log(`${prefix} Logging ${properties.length} properties:`);
      properties.forEach((prop) => {
        if (prop in this) {
          const value = this[prop];
          console.log(`  ${prop}: ${value}`);
        } else {
          console.log(`  ${prop}: [property not found]`);
        }
      });
    } else {
      console.log(`${prefix} Observations:`);
      console.log(`  Time: ${this.formattedTime}`);
      console.log(`  Temperature: ${this.temperature}\xB0${this.selectedUnits === "Imperial" ? "F" : "C"}`);
      console.log(`  Feels Like: ${this.temperatureApparent}\xB0${this.selectedUnits === "Imperial" ? "F" : "C"}`);
      console.log(`  Dew Point: ${this.dewPoint}\xB0${this.selectedUnits === "Imperial" ? "F" : "C"}`);
      console.log(`  Humidity: ${this.humidity}%`);
      console.log(`  Wind: ${this.windSpeed} ${this.selectedUnits === "Imperial" ? "mph" : "km/h"} @ ${this.windBearing}\xB0`);
      console.log(`  Wind Gust: ${this.windGust} ${this.selectedUnits === "Imperial" ? "mph" : "km/h"}`);
      console.log(`  Pressure: ${this.pressure} mb`);
      console.log(`  Elevation: ${this.elevation} ${this.selectedUnits === "Imperial" ? "ft" : "m"}`);
      console.log(`  Rain Rate: ${this.rainRate} ${this.selectedUnits === "Imperial" ? "in/hr" : "mm/hr"}`);
      console.log(`  Snow Rate: ${this.snowRate} ${this.selectedUnits === "Imperial" ? "in/hr" : "mm/hr"}`);
      console.log(`  Weather Code: ${this.weatherCode} (WMO)`);
      console.log(`  Cloud Cover: ${this.cloudCover}%`);
      console.log(`  Is Day: ${this.isDay}`);
    }
  }
  /**
   * Logs hourly forecast values to console.
   * If properties array is provided, logs only those properties from each hour.
   * Otherwise logs all hourly forecast values.
   *
   * @param hoursToLog - Number of hours to log (default: 12)
   * @param properties - Optional array of property names to log
   */
  logValuesHourly(hoursToLog = 12, properties) {
    const prefix = `[WeatherOM: Hourly]`;
    const hours = this.hourlyWeather.hours.slice(0, hoursToLog);
    console.log(`${prefix} Forecast (${hours.length} hours):`);
    console.log(`  Current Index: ${this.hourlyWeather.currentIndex}`);
    console.log(`  Max Temperature: ${this.hourlyWeather.maxTemperature}\xB0${this.selectedUnits === "Imperial" ? "F" : "C"}`);
    console.log(`  Min Temperature: ${this.hourlyWeather.minTemperature}\xB0${this.selectedUnits === "Imperial" ? "F" : "C"}`);
    console.log("");
    hours.forEach((hour, i) => {
      if (properties && properties.length > 0) {
        console.log(`  Hour ${i} (${hour.startTime.toLocaleTimeString()}):`);
        properties.forEach((prop) => {
          if (prop in hour) {
            const value = hour[prop];
            console.log(`    ${prop}: ${value}`);
          } else {
            console.log(`    ${prop}: [property not found]`);
          }
        });
      } else {
        const tempF = this.selectedUnits === "Imperial" ? hour.temperature : hour.temperature * 9 / 5 + 32;
        const wetBulb = hour.humidity !== this.invalidIntValue ? calculateWetBulbTemperature(tempF, hour.humidity) : null;
        const wetBulbDisplay = wetBulb !== null ? this.selectedUnits === "Imperial" ? wetBulb.toFixed(0) : ((wetBulb - 32) * 5 / 9).toFixed(0) : "N/A";
        console.log(`  Hour ${i} (${hour.startTime.toLocaleTimeString()}):`);
        console.log(`    Temp: ${hour.temperature}\xB0${this.selectedUnits === "Imperial" ? "F" : "C"} (feels ${hour.temperatureApparent}\xB0)`);
        console.log(`    RH: ${hour.humidity}%, WetBulb: ${wetBulbDisplay}\xB0${this.selectedUnits === "Imperial" ? "F" : "C"}`);
        console.log(`    Weather: Code ${hour.weatherCode}, Cloud ${hour.cloudCover}%`);
        console.log(`    Wind: ${hour.windSpeed} @ ${hour.windBearing}\xB0, Gust ${hour.windGust}`);
        console.log(`    Precip: Rain ${hour.rainRate}, Snow ${hour.snowRate}, Ice ${hour.iceRate}, POP ${hour.pop}%`);
        console.log(`    Atmos: Snow Level ${hour.snowLevelHeight != null ? Math.round(hour.snowLevelHeight) + "ft" : "N/A"}, 850hPa ${hour.temp850hPa != null ? hour.temp850hPa.toFixed(1) + "\xB0F" : "N/A"}, 925hPa ${hour.temp925hPa != null ? hour.temp925hPa.toFixed(1) + "\xB0F" : "N/A"}`);
      }
    });
  }
  /**
   * Logs daily forecast values to console.
   * If properties array is provided, logs only those properties from each day.
   * Otherwise logs all daily forecast values.
   *
   * @param daysToLog - Number of days to log (default: 7)
   * @param properties - Optional array of property names to log
   */
  logValuesDaily(daysToLog = 7, properties) {
    const prefix = `[WeatherOM: Daily]`;
    const days = this.dailyWeather.days.slice(0, daysToLog);
    console.log(`${prefix} Forecast (${days.length} days):`);
    console.log(`  Current Index: ${this.dailyWeather.currentIndex}`);
    console.log(`  Week Max: ${this.dailyWeather.maxTemperatureWeek}\xB0${this.selectedUnits === "Imperial" ? "F" : "C"}`);
    console.log(`  Week Min: ${this.dailyWeather.minTemperatureWeek}\xB0${this.selectedUnits === "Imperial" ? "F" : "C"}`);
    console.log(`  Today: ${this.dailyWeather.currentDayName}`);
    console.log(`  Tomorrow: ${this.dailyWeather.tomorrowDayName}`);
    console.log("");
    days.forEach((day, i) => {
      if (properties && properties.length > 0) {
        console.log(`  Day ${i} (${day.name}):`);
        properties.forEach((prop) => {
          if (prop in day) {
            const value = day[prop];
            console.log(`    ${prop}: ${value}`);
          } else {
            console.log(`    ${prop}: [property not found]`);
          }
        });
      } else {
        console.log(`  Day ${i} (${day.name}):`);
        console.log(`    Temp: ${day.maxTemperature}\xB0/${day.minTemperature}\xB0 (feels ${day.maxTemperatureApparent}\xB0/${day.minTemperatureApparent}\xB0)`);
        console.log(`    Weather: Code ${day.weatherCode}, Cloud ${day.cloudCover}%`);
        console.log(`    Wind: ${day.windSpeed} ${day.windDirection}, Gust ${day.windGust}`);
        console.log(`    Precip: Rain ${day.rainAccumulation}, Snow ${day.snowAccumulation}, Ice ${day.iceAccumulation}`);
        console.log(`    Forecast: ${day.shortForecast}`);
      }
    });
  }
};

// src/services/WeatherSYN.ts
var WeatherSYNClass = class {
  constructor(params = {}) {
    this.invalidDoubleValue = INVALID_DOUBLE;
    this.invalidIntValue = INVALID_INT;
    const {
      selectedUnits = "Imperial",
      loading = false,
      stationId = "",
      stationName = "",
      timestamp = /* @__PURE__ */ new Date(),
      formattedTime = "",
      temperature = INVALID_DOUBLE,
      temperatureApparent = INVALID_DOUBLE,
      dewPoint = INVALID_DOUBLE,
      humidity = 0,
      windBearing = 0,
      windSpeed = INVALID_INT,
      windGust = INVALID_INT,
      pressure = 0,
      elevation = INVALID_INT,
      rain1hr = INVALID_DOUBLE,
      rainDay = INVALID_DOUBLE,
      rain24hr = INVALID_DOUBLE,
      rainStorm = INVALID_DOUBLE,
      rainRate = INVALID_DOUBLE,
      snowRate = INVALID_DOUBLE,
      snowDepth = INVALID_DOUBLE,
      weatherCode = 0,
      isDay = true,
      cloudCover = 0
    } = params;
    this.selectedUnits = selectedUnits;
    this.loading = loading;
    this.stationId = stationId;
    this.stationName = stationName;
    this.timestamp = timestamp;
    this.formattedTime = formattedTime;
    this.temperature = temperature;
    this.temperatureApparent = temperatureApparent;
    this.dewPoint = dewPoint;
    this.humidity = humidity;
    this.windBearing = windBearing;
    this.windSpeed = windSpeed;
    this.windGust = windGust;
    this.pressure = pressure;
    this.elevation = elevation;
    this.rain1hr = rain1hr;
    this.rainDay = rainDay;
    this.rain24hr = rain24hr;
    this.rainStorm = rainStorm;
    this.rainRate = rainRate;
    this.snowRate = snowRate;
    this.snowDepth = snowDepth;
    this.weatherCode = weatherCode;
    this.isDay = isDay;
    this.cloudCover = cloudCover;
    this.dailyMinimums = this.createEmptyExtremes();
    this.dailyMaximums = this.createEmptyExtremes();
    this.yesterdayMinimums = this.createEmptyExtremes();
    this.yesterdayMaximums = this.createEmptyExtremes();
  }
  /**
   * Creates an empty RODailyWeatherExtremes object
   */
  createEmptyExtremes() {
    return {
      temperature: INVALID_DOUBLE,
      temperatureTimestamp: /* @__PURE__ */ new Date(),
      dewPoint: INVALID_DOUBLE,
      dewPointTimestamp: /* @__PURE__ */ new Date(),
      humidity: INVALID_INT,
      humidityTimestamp: /* @__PURE__ */ new Date(),
      windSpeed: INVALID_INT,
      windSpeedTimestamp: /* @__PURE__ */ new Date(),
      windGust: INVALID_INT,
      windGustTimestamp: /* @__PURE__ */ new Date(),
      pressure: INVALID_INT,
      pressureTimestamp: /* @__PURE__ */ new Date()
    };
  }
  sanitizeWindSpeed(value) {
    if (!Number.isFinite(value) || value < 0 || value > 200) {
      return 0;
    }
    return value;
  }
  sanitizeWindDirection(value) {
    if (!Number.isFinite(value) || value < 0 || value > 360) {
      return 0;
    }
    return value;
  }
  /**
   * Sets values from Synoptic station data.
   * Handles unit conversion, NWS to WMO code conversion, and daily extremes.
   */
  setValues(synData) {
    if (!synData.stations || synData.stations.length === 0) {
      console.error("No stations in Synoptic data");
      return;
    }
    const station = synData.stations[0];
    const lng = parseFloat(station.longitude ?? "-122.676483") || -122.676483;
    const lat = parseFloat(station.latitude ?? "45.523064") || 45.523064;
    const elevation = parseFloat(station.elevation ?? "0.0") || 0;
    const timezone = station.timeZone ?? null;
    const temperature = this.getStandardizedObservationData(
      synData.stations,
      "temperature" /* Temperature */,
      this.selectedUnits
    );
    const timestampStr = this.getObservationData(
      synData.stations,
      "dateTime" /* DateTime */
    );
    const timestamp = timestampStr ? new Date(timestampStr) : /* @__PURE__ */ new Date();
    const hours = timestamp.getHours();
    const minutes = timestamp.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;
    const minuteStr = minutes.toString().padStart(2, "0");
    const formattedTime = `${hour12}:${minuteStr} ${ampm}`;
    const dewPoint = this.getStandardizedObservationData(
      synData.stations,
      "dewPoint" /* DewPoint */,
      this.selectedUnits
    );
    const windDirection = this.getStandardizedObservationData(
      synData.stations,
      "windDirection" /* WindDirection */,
      this.selectedUnits
    );
    const windSpeed = this.getStandardizedObservationData(
      synData.stations,
      "windSpeed" /* WindSpeed */,
      this.selectedUnits
    );
    const windGust = this.getStandardizedObservationData(
      synData.stations,
      "windGust" /* WindGust */,
      this.selectedUnits
    );
    const pressure = this.getStandardizedObservationData(
      synData.stations,
      "pressure" /* Pressure */,
      this.selectedUnits
    );
    const adjustedPressure = stationPressureToSeaLevelPressure(
      pressure,
      elevation
    );
    const humidity = this.getStandardizedObservationData(
      synData.stations,
      "humidity" /* Humidity */,
      this.selectedUnits
    );
    const snowDepth = this.getStandardizedObservationData(
      synData.stations,
      "snowDepth" /* SnowDepth */,
      this.selectedUnits,
      true
    );
    const precip1hr = this.getStandardizedObservationData(
      synData.stations,
      "precip1hr" /* Precip1hr */,
      this.selectedUnits,
      true
    );
    const precip24hr = this.getStandardizedObservationData(
      synData.stations,
      "precip24hr" /* Precip24hr */,
      this.selectedUnits,
      true
    );
    const weatherSummary = this.getObservationData(
      synData.stations,
      "weatherSummary" /* WeatherSummary */
    );
    const weatherConditionCode = this.getObservationData(
      synData.stations,
      "weatherConditionCode" /* WeatherConditionCode */
    );
    let wmoCode = this.invalidIntValue;
    if (weatherConditionCode !== null) {
      wmoCode = convertSynopticWnumToWeatherCode(weatherConditionCode);
      if (wmoCode === this.invalidIntValue) {
        wmoCode = this.convertSkyConditionToWmo(weatherSummary);
      }
    } else {
      wmoCode = this.convertSkyConditionToWmo(weatherSummary);
    }
    const sanitizedWindSpeed = this.sanitizeWindSpeed(windSpeed);
    const sanitizedWindGust = this.sanitizeWindSpeed(windGust);
    const sanitizedWindDirection = this.sanitizeWindDirection(windDirection);
    const apparentTemp = apparentTemperatureBOM(
      temperature,
      humidity,
      sanitizedWindSpeed,
      this.selectedUnits
    );
    this.stationId = station.stid;
    this.stationName = station.name;
    this.isDay = this.isDaytime(/* @__PURE__ */ new Date(), lat, lng);
    this.timestamp = timestamp;
    this.formattedTime = formattedTime;
    if (this.selectedUnits === "Imperial") {
      this.elevation = Math.round(elevation);
      this.temperature = temperature;
      this.temperatureApparent = apparentTemp;
      this.dewPoint = dewPoint;
      this.windSpeed = Math.round(sanitizedWindSpeed);
      this.windGust = Math.round(sanitizedWindGust);
      this.rain1hr = precip1hr;
      this.rain24hr = precip24hr;
      this.snowDepth = snowDepth;
    } else {
      this.elevation = Math.round(feetToMeters(elevation));
      this.temperature = fahrenheitToCelsius(temperature);
      this.temperatureApparent = fahrenheitToCelsius(apparentTemp);
      this.dewPoint = fahrenheitToCelsius(dewPoint);
      this.windSpeed = Math.round(milesToKilometers(sanitizedWindSpeed));
      this.windGust = Math.round(milesToKilometers(sanitizedWindGust));
      this.rain1hr = inchesToMillimeters(precip1hr);
      this.rain24hr = inchesToMillimeters(precip24hr);
      this.snowDepth = inchesToMillimeters(snowDepth);
    }
    this.pressure = Math.round(adjustedPressure);
    this.windBearing = Math.round(sanitizedWindDirection);
    this.humidity = Math.round(humidity);
    this.weatherCode = wmoCode;
    this.cloudCover = getCloudCoverFromWMO(wmoCode);
    this.rainRate = INVALID_DOUBLE;
    this.snowRate = INVALID_DOUBLE;
    this.clearPrecipitationValues();
    const temperatureMinMaxValues = this.getMinMaxData(
      synData.stations,
      "temperature" /* Temperature */,
      timezone
    );
    const dewPointMinMaxValues = this.getMinMaxData(
      synData.stations,
      "dewPoint" /* DewPoint */,
      timezone
    );
    const humidityMinMaxValues = this.getMinMaxData(
      synData.stations,
      "humidity" /* Humidity */,
      timezone
    );
    const windSpeedMinMaxValues = this.getMinMaxData(
      synData.stations,
      "windSpeed" /* WindSpeed */,
      timezone
    );
    const windGustMinMaxValues = this.getMinMaxData(
      synData.stations,
      "windGust" /* WindGust */,
      timezone
    );
    const pressureMinMaxValues = this.getMinMaxData(
      synData.stations,
      "pressure" /* Pressure */,
      timezone
    );
    this.dailyMinimums = this.createEmptyExtremes();
    this.dailyMaximums = this.createEmptyExtremes();
    this.yesterdayMinimums = this.createEmptyExtremes();
    this.yesterdayMaximums = this.createEmptyExtremes();
    this.setExtremesFromMinMax(
      this.dailyMinimums,
      this.dailyMaximums,
      1,
      temperatureMinMaxValues,
      dewPointMinMaxValues,
      humidityMinMaxValues,
      windSpeedMinMaxValues,
      windGustMinMaxValues,
      pressureMinMaxValues,
      elevation
    );
    this.setExtremesFromMinMax(
      this.yesterdayMinimums,
      this.yesterdayMaximums,
      0,
      temperatureMinMaxValues,
      dewPointMinMaxValues,
      humidityMinMaxValues,
      windSpeedMinMaxValues,
      windGustMinMaxValues,
      pressureMinMaxValues,
      elevation
    );
  }
  /**
   * Updates precipitation-specific values from Synoptic's precipitation endpoint.
   */
  setPrecipitationValues(precipData) {
    this.clearPrecipitationValues();
    const analysis = analyzeSynopticPrecipitation(
      precipData,
      this.stationId || void 0,
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
    );
    if (!analysis) return;
    const isMetric = this.selectedUnits === "Metric";
    if (analysis.dailyTotal !== void 0) {
      this.rainDay = isMetric ? inchesToMillimeters(analysis.dailyTotal) : analysis.dailyTotal;
    }
    if (analysis.latestHourly !== void 0) {
      this.rain1hr = isMetric ? inchesToMillimeters(analysis.latestHourly) : analysis.latestHourly;
    }
    if (analysis.stormTotal !== void 0) {
      this.rainStorm = isMetric ? inchesToMillimeters(analysis.stormTotal) : analysis.stormTotal;
    }
    this.rainRate = INVALID_DOUBLE;
    if (analysis.todayMaxHourly) {
      this.dailyMaximums.rainHour = isMetric ? inchesToMillimeters(analysis.todayMaxHourly.value) : analysis.todayMaxHourly.value;
      this.dailyMaximums.rainHourTimestamp = analysis.todayMaxHourly.timestamp;
    }
    if (analysis.yesterdayMaxHourly) {
      this.yesterdayMaximums.rainHour = isMetric ? inchesToMillimeters(analysis.yesterdayMaxHourly.value) : analysis.yesterdayMaxHourly.value;
      this.yesterdayMaximums.rainHourTimestamp = analysis.yesterdayMaxHourly.timestamp;
    }
  }
  clearPrecipitationValues() {
    this.rain1hr = INVALID_DOUBLE;
    this.rainDay = INVALID_DOUBLE;
    this.rainStorm = INVALID_DOUBLE;
    this.rainRate = INVALID_DOUBLE;
    this.dailyMaximums.rainHour = void 0;
    this.dailyMaximums.rainHourTimestamp = void 0;
    this.yesterdayMaximums.rainHour = void 0;
    this.yesterdayMaximums.rainHourTimestamp = void 0;
  }
  /**
   * Sets extreme values from min/max arrays
   */
  setExtremesFromMinMax(minExtremes, maxExtremes, index, tempData, dewPointData, humidityData, windSpeedData, windGustData, pressureData, elevation) {
    if (index < tempData.length) {
      const data = tempData[index];
      maxExtremes.temperature = data.maxValue;
      maxExtremes.temperatureTimestamp = data.maxTimestamp ?? /* @__PURE__ */ new Date();
      minExtremes.temperature = data.minValue;
      minExtremes.temperatureTimestamp = data.minTimestamp ?? /* @__PURE__ */ new Date();
    }
    if (index < dewPointData.length) {
      const data = dewPointData[index];
      maxExtremes.dewPoint = data.maxValue;
      maxExtremes.dewPointTimestamp = data.maxTimestamp ?? /* @__PURE__ */ new Date();
      minExtremes.dewPoint = data.minValue;
      minExtremes.dewPointTimestamp = data.minTimestamp ?? /* @__PURE__ */ new Date();
    }
    if (index < humidityData.length) {
      const data = humidityData[index];
      maxExtremes.humidity = Math.round(data.maxValue);
      maxExtremes.humidityTimestamp = data.maxTimestamp ?? /* @__PURE__ */ new Date();
      minExtremes.humidity = Math.round(data.minValue);
      minExtremes.humidityTimestamp = data.minTimestamp ?? /* @__PURE__ */ new Date();
    }
    if (index < windSpeedData.length) {
      const data = windSpeedData[index];
      maxExtremes.windSpeed = Math.round(data.maxValue);
      maxExtremes.windSpeedTimestamp = data.maxTimestamp ?? /* @__PURE__ */ new Date();
      minExtremes.windSpeed = Math.round(data.minValue);
      minExtremes.windSpeedTimestamp = data.minTimestamp ?? /* @__PURE__ */ new Date();
    }
    if (index < windGustData.length) {
      const data = windGustData[index];
      maxExtremes.windGust = Math.round(data.maxValue);
      maxExtremes.windGustTimestamp = data.maxTimestamp ?? /* @__PURE__ */ new Date();
      minExtremes.windGust = Math.round(data.minValue);
      minExtremes.windGustTimestamp = data.minTimestamp ?? /* @__PURE__ */ new Date();
    }
    if (index < pressureData.length) {
      const data = pressureData[index];
      maxExtremes.pressure = Math.round(
        stationPressureToSeaLevelPressure(data.maxValue, elevation)
      );
      maxExtremes.pressureTimestamp = data.maxTimestamp ?? /* @__PURE__ */ new Date();
      minExtremes.pressure = Math.round(
        stationPressureToSeaLevelPressure(data.minValue, elevation)
      );
      minExtremes.pressureTimestamp = data.minTimestamp ?? /* @__PURE__ */ new Date();
    }
  }
  /**
   * Converts sky condition string to WMO weather code
   */
  convertSkyConditionToWmo(skyCondition) {
    if (!skyCondition) {
      return this.invalidIntValue;
    }
    const lowercased = skyCondition.toLowerCase();
    switch (lowercased) {
      case "clear":
        return 0;
      // Clear sky
      case "scattered":
      case "thin scattered":
        return 1;
      // Scattered clouds
      case "broken":
      case "thin broken":
        return 2;
      // Broken clouds
      case "overcast":
      case "obscured":
      case "thin overcast":
      case "thin obscured":
        return 3;
      // Overcast
      default:
        return 0;
    }
  }
  /**
   * Determines if the date is during daytime using solar calculator
   */
  isDaytime(date, lat, lng) {
    const solar = new SolarCalculator({ latitude: lat, longitude: lng, elevation: 0 });
    const result = solar.calculate(date);
    if (result.sunrise && result.sunset) {
      return date >= result.sunrise && date < result.sunset;
    }
    return true;
  }
  /**
   * Gets raw observation data for non-numeric or timestamp fields
   */
  getObservationData(stations, key) {
    for (const station of stations) {
      const obs = station.observations;
      switch (key) {
        case "temperature" /* Temperature */:
          if (obs.temperature) return obs.temperature.value;
          break;
        case "dateTime" /* DateTime */:
          if (obs.temperature) return obs.temperature.dateTime;
          break;
        case "windSpeed" /* WindSpeed */:
          if (obs.windSpeed) return obs.windSpeed.value;
          break;
        case "windDirection" /* WindDirection */:
          if (obs.windDirection) return obs.windDirection.value;
          break;
        case "dewPoint" /* DewPoint */:
          if (obs.dewPoint) return obs.dewPoint.value;
          break;
        case "windGust" /* WindGust */:
          if (obs.windGust) return obs.windGust.value;
          break;
        case "pressure" /* Pressure */:
          if (obs.pressure) return obs.pressure.value;
          break;
        case "humidity" /* Humidity */:
          if (obs.humidity) return obs.humidity.value;
          break;
        case "precip24hr" /* Precip24hr */:
          if (obs.precip24hr) return obs.precip24hr.value;
          break;
        case "precip1hr" /* Precip1hr */:
          if (obs.precip1hr) return obs.precip1hr.value;
          break;
        case "snowDepth" /* SnowDepth */:
          if (obs.snowDepth) return obs.snowDepth.value;
          break;
        case "weatherSummary" /* WeatherSummary */:
          if (obs.weatherSummary) return obs.weatherSummary.value;
          break;
        case "weatherConditionCode" /* WeatherConditionCode */:
          if (obs.weatherConditionCode) {
            if (obs.weatherSummary) {
              if (obs.weatherSummary.dateTime === obs.weatherConditionCode.dateTime) {
                if (obs.weatherConditionCode.value === 41) {
                  continue;
                }
                return obs.weatherConditionCode.value;
              }
            }
          }
          break;
      }
    }
    return null;
  }
  /**
   * Gets standardized observation data with unit conversion heuristics.
   * Detects and converts mixed metric/imperial values.
   */
  getStandardizedObservationData(stations, key, toUnitSystem, isPrecip = false) {
    const values = [];
    for (const station of stations) {
      const obs = station.observations;
      let value = null;
      switch (key) {
        case "temperature" /* Temperature */:
          value = obs.temperature?.value ?? null;
          break;
        case "dewPoint" /* DewPoint */:
          value = obs.dewPoint?.value ?? null;
          break;
        case "humidity" /* Humidity */:
          value = obs.humidity?.value ?? null;
          break;
        case "windSpeed" /* WindSpeed */:
          value = obs.windSpeed?.value ?? null;
          break;
        case "windGust" /* WindGust */:
          value = obs.windGust?.value ?? null;
          break;
        case "windDirection" /* WindDirection */:
          value = obs.windDirection?.value ?? null;
          break;
        case "pressure" /* Pressure */:
          value = obs.pressure?.value ?? null;
          break;
        case "precip1hr" /* Precip1hr */:
          value = obs.precip1hr?.value ?? null;
          break;
        case "precip24hr" /* Precip24hr */:
          value = obs.precip24hr?.value ?? null;
          break;
        case "snowDepth" /* SnowDepth */:
          value = obs.snowDepth?.value ?? null;
          break;
      }
      if (value !== null) {
        values.push(value);
      }
    }
    if (values.length === 0) {
      return this.invalidDoubleValue;
    }
    const sortedValues = values.slice().sort((a, b) => a - b);
    const median = sortedValues.length % 2 === 0 ? (sortedValues[sortedValues.length / 2 - 1] + sortedValues[sortedValues.length / 2]) / 2 : sortedValues[Math.floor(sortedValues.length / 2)];
    const standardizedValues = [];
    for (const value of values) {
      let standardizedValue = value;
      if (toUnitSystem === "Imperial") {
        if (isPrecip) {
          if (median > 0 && value / median > 25) {
            standardizedValue = millimetersToInches(value);
          }
        } else if (key === "temperature" /* Temperature */ || key === "dewPoint" /* DewPoint */) {
          if (median > 50 && value < 40) {
            standardizedValue = celsiusToFahrenheit(value);
          }
        }
      } else {
        if (isPrecip) {
          if (median > 0 && value / median < 0.04) {
            standardizedValue = inchesToMillimeters(value);
          }
        } else if (key === "temperature" /* Temperature */ || key === "dewPoint" /* DewPoint */) {
          if (median < 30 && value > 40) {
            standardizedValue = fahrenheitToCelsius(value);
          }
        }
      }
      standardizedValues.push(standardizedValue);
    }
    if (standardizedValues.length === 0) {
      return this.invalidDoubleValue;
    }
    const sortedStandardized = standardizedValues.slice().sort((a, b) => a - b);
    const finalMedian = sortedStandardized.length % 2 === 0 ? (sortedStandardized[sortedStandardized.length / 2 - 1] + sortedStandardized[sortedStandardized.length / 2]) / 2 : sortedStandardized[Math.floor(sortedStandardized.length / 2)];
    return roundToPlaces(finalMedian, 2);
  }
  /**
   * Gets min/max values and timestamps for a given metric
   */
  getMinMaxData(stations, key, timezone) {
    const results = [];
    for (const station of stations) {
      if (!station.minmax) continue;
      let minMaxValues = null;
      switch (key) {
        case "temperature" /* Temperature */:
          minMaxValues = station.minmax.temperatureMinMax ?? null;
          break;
        case "dewPoint" /* DewPoint */:
          minMaxValues = station.minmax.dewPointMinMax ?? null;
          break;
        case "humidity" /* Humidity */:
          minMaxValues = station.minmax.humidityMinMax ?? null;
          break;
        case "windSpeed" /* WindSpeed */:
          minMaxValues = station.minmax.windSpeedMinMax ?? null;
          break;
        case "windGust" /* WindGust */:
          minMaxValues = station.minmax.windGustMinMax ?? null;
          break;
        case "pressure" /* Pressure */:
          minMaxValues = station.minmax.pressureMinMax ?? null;
          break;
      }
      if (minMaxValues) {
        const minVals = minMaxValues.minValues.filter(
          (v) => v !== null
        );
        const maxVals = minMaxValues.maxValues.filter(
          (v) => v !== null
        );
        const minTs = minMaxValues.minTimestamp.filter(
          (t) => t !== null
        );
        const maxTs = minMaxValues.maxTimestamp.filter(
          (t) => t !== null
        );
        for (let i = 0; i < minVals.length; i++) {
          results.push({
            minValue: minVals[i],
            maxValue: maxVals[i] ?? minVals[i],
            minTimestamp: minTs[i] ? new Date(minTs[i]) : null,
            maxTimestamp: maxTs[i] ? new Date(maxTs[i]) : null
          });
        }
        if (results.length > 0) break;
      }
    }
    return results;
  }
  /**
   * Logs weather values to console.
   * If properties array is provided, logs only those properties.
   * Otherwise logs all current observation values.
   */
  logValues(properties) {
    const prefix = `[WeatherSYN: ${this.stationName}]`;
    if (properties && properties.length > 0) {
      console.log(`${prefix} Logging ${properties.length} properties:`);
      properties.forEach((prop) => {
        if (prop in this) {
          const value = this[prop];
          console.log(`  ${prop}: ${value}`);
        } else {
          console.log(`  ${prop}: [property not found]`);
        }
      });
    } else {
      console.log(`${prefix} Current Observations:`);
      console.log(`  Station: ${this.stationId} - ${this.stationName}`);
      console.log(`  Time: ${this.formattedTime}`);
      console.log(`  Temperature: ${this.temperature}\xB0${this.selectedUnits === "Imperial" ? "F" : "C"}`);
      console.log(`  Feels Like: ${this.temperatureApparent}\xB0${this.selectedUnits === "Imperial" ? "F" : "C"}`);
      console.log(`  Dew Point: ${this.dewPoint}\xB0${this.selectedUnits === "Imperial" ? "F" : "C"}`);
      console.log(`  Humidity: ${this.humidity}%`);
      console.log(`  Wind: ${this.windSpeed} ${this.selectedUnits === "Imperial" ? "mph" : "km/h"} @ ${this.windBearing}\xB0`);
      console.log(`  Wind Gust: ${this.windGust} ${this.selectedUnits === "Imperial" ? "mph" : "km/h"}`);
      console.log(`  Pressure: ${this.pressure} mb`);
      console.log(`  Elevation: ${this.elevation} ${this.selectedUnits === "Imperial" ? "ft" : "m"}`);
      console.log(`  Rain 1hr: ${this.rain1hr} ${this.selectedUnits === "Imperial" ? "in" : "mm"}`);
      console.log(`  Rain daily: ${this.rainDay} ${this.selectedUnits === "Imperial" ? "in" : "mm"}`);
      console.log(`  Rain Storm: ${this.rainStorm} ${this.selectedUnits === "Imperial" ? "in" : "mm"}`);
      console.log(`  Snow Depth: ${this.snowDepth} ${this.selectedUnits === "Imperial" ? "in" : "mm"}`);
      console.log(`  Weather Code: ${this.weatherCode} (WMO)`);
      console.log(`  Cloud Cover: ${this.cloudCover}%`);
      console.log(`  Is Day: ${this.isDay}`);
    }
  }
};

// src/services/WeatherNWSClass.ts
var WeatherNWSClass = class {
  constructor(params = {}) {
    // Location and timezone
    this.m_location = null;
    this.m_timezone = null;
    this.m_timezoneOffsetSeconds = 0;
    // Raw API data
    this.endpoints = null;
    this.hourlyGrid = null;
    // Parsed hourly data (using existing NWSHourlyGridWeatherData interface)
    this.parsedHourlyData = null;
    this.dailyWeather = null;
    // Current conditions (interpolated from hourly data)
    this.timestamp = null;
    this.formattedTime = "";
    this.temperature = INVALID_DOUBLE;
    this.temperatureApparent = INVALID_DOUBLE;
    this.dewPoint = INVALID_DOUBLE;
    this.humidity = INVALID_INT;
    this.windSpeed = INVALID_INT;
    this.windGust = INVALID_INT;
    this.windDirection = INVALID_INT;
    this.weatherCode = INVALID_INT;
    this.cloudCover = INVALID_INT;
    this.isDay = true;
    this.pop = 0;
    this.invalidDoubleValue = INVALID_DOUBLE;
    this.invalidIntValue = INVALID_INT;
    this.selectedUnits = params.selectedUnits ?? "Imperial";
    this.loading = false;
    this.forecastGenerator = new ForecastGenerator(this.selectedUnits);
    this.hourlyWeather = {
      currentIndex: -1,
      maxTemperature: INVALID_DOUBLE,
      minTemperature: INVALID_DOUBLE,
      temperatureColors: ["transparent"],
      hours: []
    };
  }
  /**
   * Set location for timezone-aware daily weather generation
   */
  setLocation(location) {
    this.m_location = location;
  }
  /**
   * Set timezone information
   */
  setTimeZone(timezone, offsetSeconds) {
    this.m_timezone = timezone;
    this.m_timezoneOffsetSeconds = offsetSeconds;
  }
  /**
   * Set NWS endpoint URLs for a location
   */
  setEndpoints(data) {
    this.endpoints = data;
  }
  /**
   * Set and parse NWS hourly grid forecast data
   */
  setHourlyGridValues(data) {
    this.hourlyGrid = data;
    if (!data?.properties) {
      console.error("[WeatherNWSClass] Invalid NWS grid data");
      return;
    }
    this.parsedHourlyData = this.parseGridToHourlyData(data);
    this.hourlyWeather = this.convertToROHourlyWeather();
    this.setCurrentConditions();
  }
  /**
   * Parse NWS ISO 8601 duration time string to Date
   * Format: "2024-01-15T06:00:00+00:00/PT1H"
   * Returns the start time of the period
   */
  parseNWSTimeString(validTime) {
    if (!validTime) return null;
    const parts = validTime.split("/");
    if (parts.length === 0) return null;
    const startTimeStr = parts[0];
    try {
      return new Date(startTimeStr);
    } catch {
      console.error(`[WeatherNWSClass] Failed to parse time: ${validTime}`);
      return null;
    }
  }
  /**
   * Parse ISO 8601 duration string to hours
   * Handles formats like: "PT1H", "PT8H", "P1DT8H", "P7DT11H"
   * From Swift: WeatherNWS.swift:675-704
   */
  parseDurationHours(duration) {
    if (!duration) return 1;
    let totalHours = 0;
    if (duration.includes("D") || duration.includes("H")) {
      let timeString = duration;
      if (timeString.startsWith("P")) {
        timeString = timeString.slice(1);
      }
      const parts = timeString.split("T");
      if (parts[0] && parts[0].includes("D")) {
        const days = parseInt(parts[0].replace("D", ""), 10) || 0;
        totalHours += days * 24;
      }
      if (parts.length > 1 && parts[1]) {
        const hoursPart = parts[1].replace("H", "");
        const hours = parseInt(hoursPart, 10) || 0;
        totalHours += hours;
      } else if (parts[0] && parts[0].includes("H") && !parts[0].includes("D")) {
        const hours = parseInt(parts[0].replace("H", ""), 10) || 0;
        totalHours += hours;
      }
    } else {
      totalHours = parseInt(duration, 10) || 1;
    }
    return Math.max(totalHours, 1);
  }
  /**
   * Get duration from validTime string
   */
  getDurationFromValidTime(validTime) {
    const parts = validTime.split("/");
    if (parts.length < 2) return 1;
    return this.parseDurationHours(parts[1]);
  }
  /**
   * Convert NWS weather condition to WMO weather code.
   * Complete implementation from Swift: Common.swift:571-811
   *
   * @param weatherObject - NWS weather value with coverage, weather, and intensity
   * @param cloudCover - Cloud cover percentage (0-100)
   * @param windSpeed - Wind speed in km/h (not currently used but matches Swift signature)
   * @returns WMO weather code (0-99)
   */
  getWMOCodeFromNWSWeatherGrid(weatherObject, cloudCover, windSpeed) {
    let wmoCode = -1;
    if (cloudCover <= 12) {
      wmoCode = 0;
    } else if (cloudCover > 12 && cloudCover <= 35) {
      wmoCode = 1;
    } else if (cloudCover > 35 && cloudCover <= 65) {
      wmoCode = 2;
    } else if (cloudCover > 65) {
      wmoCode = 3;
    }
    if (!weatherObject || !weatherObject.weather) {
      return wmoCode >= 0 ? wmoCode : 0;
    }
    const weather = weatherObject.weather.toLowerCase();
    const intensity = (weatherObject.intensity || "").toLowerCase();
    if (weather === "fog" || weather === "freezing_fog" || weather === "ice_fog") {
      wmoCode = 45;
    } else if (weather === "frost") {
      wmoCode = 76;
    } else if (weather === "drizzle") {
      if (intensity === "very_light") {
        wmoCode = 51;
      } else if (intensity === "heavy") {
        wmoCode = 55;
      } else {
        wmoCode = 53;
      }
    } else if (weather === "freezing_drizzle") {
      if (intensity === "very_light" || intensity === "light") {
        wmoCode = 56;
      } else {
        wmoCode = 57;
      }
    } else if (weather === "rain") {
      if (intensity === "very_light" || intensity === "light") {
        wmoCode = 61;
      } else if (intensity === "heavy") {
        wmoCode = 65;
      } else {
        wmoCode = 63;
      }
    } else if (weather === "freezing_rain") {
      if (intensity === "very_light" || intensity === "light") {
        wmoCode = 66;
      } else {
        wmoCode = 67;
      }
    } else if (weather === "snow") {
      if (intensity === "very_light" || intensity === "light") {
        wmoCode = 71;
      } else if (intensity === "heavy") {
        wmoCode = 75;
      } else {
        wmoCode = 73;
      }
    } else if (weather === "sleet" || weather === "ice_crystals") {
      wmoCode = 77;
    } else if (weather === "rain_showers") {
      if (intensity === "very_light" || intensity === "light") {
        wmoCode = cloudCover < 60 ? 80 : 61;
      } else if (intensity === "heavy") {
        wmoCode = cloudCover < 60 ? 82 : 65;
      } else {
        wmoCode = cloudCover < 60 ? 81 : 63;
      }
    } else if (weather === "snow_showers") {
      if (intensity === "very_light" || intensity === "light") {
        wmoCode = 85;
      } else {
        wmoCode = 86;
      }
    } else if (weather === "thunderstorms" || weather === "hail") {
      if (intensity === "very_light" || intensity === "light") {
        wmoCode = 95;
      } else if (intensity === "heavy") {
        wmoCode = 99;
      } else {
        wmoCode = weather === "hail" ? 96 : 95;
      }
    } else if (weather === "blowing_snow") {
      wmoCode = 38;
    } else if (weather === "haze") {
      wmoCode = 5;
    } else if (weather === "smoke") {
      wmoCode = 4;
    } else if (weather === "blowing_dust" || weather === "blowing_sand") {
      wmoCode = 6;
    } else if (weather === "volcanic_ash") {
      wmoCode = 4;
    } else if (weather === "water_spouts") {
      wmoCode = 19;
    } else if (weather === "freezing_spray") {
      wmoCode = 48;
    } else if (weather === "cloudy") {
      wmoCode = 3;
    } else if (weather === "partly_cloudy") {
      wmoCode = 2;
    } else if (weather === "clear") {
      wmoCode = 0;
    }
    return wmoCode >= 0 ? wmoCode : 0;
  }
  /**
   * Simple version of WMO code mapping (kept for backwards compatibility)
   */
  nwsWeatherToWMOCode(weather) {
    return this.getWMOCodeFromNWSWeatherGrid(weather, 0, 0);
  }
  /**
   * Override empty weather values by deriving conditions from precipitation and cloud cover.
   * This is critical because NWS often returns empty weather fields.
   * From Swift: WeatherNWS.swift:706-800
   *
   * @param expandedWeather - Array of weather values (may have empty weather fields)
   * @param rainAccumulationsMm - Rain in mm per hour
   * @param snowAccumulationsMm - Snow in mm per hour
   * @param iceAccumulationsMm - Ice in mm per hour
   * @param pop - Probability of precipitation (%)
   * @param cloudCover - Cloud cover (%)
   * @returns Array of weather values with derived conditions where empty
   */
  overrideWeatherValues(expandedWeather, rainAccumulationsMm, snowAccumulationsMm, iceAccumulationsMm, pop, cloudCover) {
    const RAIN_DRIZZLE_MAX = 0.2;
    const RAIN_LIGHT_MAX = 2.5;
    const RAIN_MODERATE_MAX = 7.6;
    const SNOW_LIGHT_MAX = 2;
    const SNOW_MODERATE_MAX = 5;
    const ICE_LIGHT_MAX = 1;
    const ICE_MODERATE_MAX = 3;
    const adjusted = [];
    for (let i = 0; i < expandedWeather.length; i++) {
      const original = expandedWeather[i];
      if (original.weather && original.weather.trim() !== "") {
        adjusted.push(original);
        continue;
      }
      const rain = i < rainAccumulationsMm.length ? rainAccumulationsMm[i] : 0;
      const snow = i < snowAccumulationsMm.length ? snowAccumulationsMm[i] : 0;
      const ice = i < iceAccumulationsMm.length ? iceAccumulationsMm[i] : 0;
      const clouds = i < cloudCover.length ? cloudCover[i] : 0;
      const coverage = original.coverage;
      let wxType = "";
      let intensity = "";
      if (rain > 0) {
        wxType = rain <= RAIN_DRIZZLE_MAX ? "drizzle" : "rain";
        if (rain <= RAIN_DRIZZLE_MAX) {
          intensity = "light";
        } else if (rain <= RAIN_LIGHT_MAX) {
          intensity = "light";
        } else if (rain <= RAIN_MODERATE_MAX) {
          intensity = "moderate";
        } else {
          intensity = "heavy";
        }
      } else if (snow > 0) {
        wxType = "snow";
        if (snow <= SNOW_LIGHT_MAX) {
          intensity = "light";
        } else if (snow <= SNOW_MODERATE_MAX) {
          intensity = "moderate";
        } else {
          intensity = "heavy";
        }
      } else if (ice > 0) {
        wxType = "freezing_rain";
        if (ice <= ICE_LIGHT_MAX) {
          intensity = "light";
        } else if (ice <= ICE_MODERATE_MAX) {
          intensity = "moderate";
        } else {
          intensity = "heavy";
        }
      } else {
        if (clouds >= 70) {
          wxType = "cloudy";
        } else if (clouds >= 40) {
          wxType = "partly_cloudy";
        } else {
          wxType = "clear";
        }
        intensity = "none";
      }
      adjusted.push({
        coverage,
        weather: wxType,
        intensity
      });
    }
    return adjusted;
  }
  /**
   * Determine if a given time is during daylight hours
   * Simple approximation: 6 AM to 8 PM local time
   */
  isDayTime(date) {
    const hour = date.getHours();
    return hour >= 6 && hour < 20;
  }
  /**
   * Parse raw NWS grid forecast to NWSHourlyGridWeatherData
   */
  parseGridToHourlyData(grid) {
    const props = grid.properties;
    const timestamps = [];
    const tempValues = props.temperature?.values || [];
    for (const val of tempValues) {
      const startTime = this.parseNWSTimeString(val.validTime);
      if (!startTime) continue;
      const durationHours = this.getDurationFromValidTime(val.validTime);
      for (let h = 0; h < durationHours; h++) {
        const hourTime = new Date(startTime.getTime() + h * 36e5);
        timestamps.push(hourTime);
      }
    }
    const now = /* @__PURE__ */ new Date();
    let currentIndex = 0;
    for (let i = 0; i < timestamps.length; i++) {
      const hourStart = timestamps[i];
      const hourEnd = new Date(hourStart.getTime() + 36e5);
      if (now >= hourStart && now < hourEnd) {
        currentIndex = i;
        break;
      }
      if (hourStart > now) {
        currentIndex = Math.max(0, i - 1);
        break;
      }
    }
    const expandGridValues = (gridValues, defaultValue) => {
      if (!gridValues) return timestamps.map(() => defaultValue);
      const result = [];
      for (const val of gridValues) {
        const durationHours = this.getDurationFromValidTime(val.validTime);
        const value = val.value ?? defaultValue;
        for (let h = 0; h < durationHours; h++) {
          result.push(value);
        }
      }
      while (result.length < timestamps.length) {
        result.push(defaultValue);
      }
      return result.slice(0, timestamps.length);
    };
    const expandPrecipitationValues = (gridValues) => {
      if (!gridValues) return timestamps.map(() => 0);
      const result = [];
      for (const val of gridValues) {
        const durationHours = this.getDurationFromValidTime(val.validTime);
        const totalAccumulation = val.value ?? 0;
        const perHour = totalAccumulation / Math.max(durationHours, 1);
        for (let h = 0; h < durationHours; h++) {
          result.push(perHour);
        }
      }
      while (result.length < timestamps.length) {
        result.push(0);
      }
      return result.slice(0, timestamps.length);
    };
    const expandWeatherValues = () => {
      if (!props.weather?.values) {
        return timestamps.map(() => ({ coverage: "", weather: "", intensity: "" }));
      }
      const result = [];
      for (const val of props.weather.values) {
        const durationHours = this.getDurationFromValidTime(val.validTime);
        const condition = val.value?.[0] || { coverage: "", weather: "", intensity: "" };
        const simplifiedCondition = {
          coverage: condition.coverage || "",
          weather: condition.weather || "",
          intensity: condition.intensity || ""
        };
        for (let h = 0; h < durationHours; h++) {
          result.push(simplifiedCondition);
        }
      }
      while (result.length < timestamps.length) {
        result.push({ coverage: "", weather: "", intensity: "" });
      }
      return result.slice(0, timestamps.length);
    };
    const rawTempsC = expandGridValues(props.temperature?.values, null);
    const rawApparentC = expandGridValues(props.apparentTemperature?.values, null);
    const rawDewPointC = expandGridValues(props.dewPoint?.values, null);
    const rawHumidities = expandGridValues(props.relativeHumidity?.values, null);
    const rawWindChillC = expandGridValues(props.windChill?.values, null);
    const rawWindSpeedsKmh = expandGridValues(props.windSpeed?.values, null);
    const rawWindGustsKmh = expandGridValues(props.windGust?.values, null);
    const rawWindDirections = expandGridValues(props.windDirection?.values, null);
    const rawSkyCover = expandGridValues(props.skyCover?.values, null);
    const rawPop = expandGridValues(props.pop?.values, null);
    const rawQpfMm = expandPrecipitationValues(props.qpf?.values);
    const rawSnowMm = expandPrecipitationValues(props.snow?.values);
    const rawIceMm = expandPrecipitationValues(props.ice?.values);
    const rawWeather = expandWeatherValues();
    const temperatures = rawTempsC.map((c) => {
      if (c === null) return { fahrenheit: INVALID_DOUBLE, celsius: INVALID_DOUBLE };
      const fahrenheit = celsiusToFahrenheit(c);
      return { fahrenheit: roundToPlaces(fahrenheit, 1), celsius: roundToPlaces(c, 1) };
    });
    const apparentTemperatures = rawApparentC.map((c) => {
      if (c === null) return { fahrenheit: INVALID_DOUBLE, celsius: INVALID_DOUBLE };
      const fahrenheit = celsiusToFahrenheit(c);
      return { fahrenheit: roundToPlaces(fahrenheit, 1), celsius: roundToPlaces(c, 1) };
    });
    const dewPoints = rawDewPointC.map((c) => {
      if (c === null) return { fahrenheit: INVALID_DOUBLE, celsius: INVALID_DOUBLE };
      const fahrenheit = celsiusToFahrenheit(c);
      return { fahrenheit: roundToPlaces(fahrenheit, 1), celsius: roundToPlaces(c, 1) };
    });
    const windChills = rawWindChillC.map((c) => {
      if (c === null) return { fahrenheit: INVALID_DOUBLE, celsius: INVALID_DOUBLE };
      const fahrenheit = celsiusToFahrenheit(c);
      return { fahrenheit: roundToPlaces(fahrenheit, 1), celsius: roundToPlaces(c, 1) };
    });
    const humidities = rawHumidities.map(
      (h) => h !== null ? Math.round(h) : INVALID_INT
    );
    const windSpeeds = rawWindSpeedsKmh.map((kmh) => {
      if (kmh === null)
        return { milesPerHour: INVALID_DOUBLE, kilometersPerHour: INVALID_DOUBLE };
      const mph = kilometersToMiles(kmh);
      return {
        milesPerHour: roundToPlaces(mph, 1),
        kilometersPerHour: roundToPlaces(kmh, 1)
      };
    });
    const windGusts = rawWindGustsKmh.map((kmh) => {
      if (kmh === null)
        return { milesPerHour: INVALID_DOUBLE, kilometersPerHour: INVALID_DOUBLE };
      const mph = kilometersToMiles(kmh);
      return {
        milesPerHour: roundToPlaces(mph, 1),
        kilometersPerHour: roundToPlaces(kmh, 1)
      };
    });
    const windDirections = rawWindDirections.map(
      (d) => d !== null ? Math.round(d) : INVALID_INT
    );
    const skyCover = rawSkyCover.map(
      (s) => s !== null ? Math.round(s) : INVALID_INT
    );
    const pop = rawPop.map((p) => p !== null ? Math.round(p) : 0);
    const rainAccumulations = rawQpfMm.map((mm) => {
      const inches = millimetersToInches(mm);
      return { inches: roundToPlaces(inches, 3), millimeters: roundToPlaces(mm, 2) };
    });
    const snowAccumulations = rawSnowMm.map((mm) => {
      const inches = millimetersToInches(mm);
      return { inches: roundToPlaces(inches, 3), millimeters: roundToPlaces(mm, 2) };
    });
    const iceAccumulations = rawIceMm.map((mm) => {
      const inches = millimetersToInches(mm);
      return { inches: roundToPlaces(inches, 3), millimeters: roundToPlaces(mm, 2) };
    });
    const isDay = timestamps.map((t) => this.isDayTime(t));
    const skyCoverInts = skyCover.map((s) => s !== INVALID_INT ? s : 0);
    const popInts = pop.map((p) => p);
    const adjustedWeather = this.overrideWeatherValues(
      rawWeather,
      rawQpfMm,
      rawSnowMm,
      rawIceMm,
      popInts,
      skyCoverInts
    );
    const wmoCodes = adjustedWeather.map((w, idx) => {
      const cloudCoverValue = skyCoverInts[idx] ?? 0;
      const windSpeedValue = Math.round(windSpeeds[idx]?.kilometersPerHour ?? 0);
      return this.getWMOCodeFromNWSWeatherGrid(w, cloudCoverValue, windSpeedValue);
    });
    return {
      currentIndex,
      timestamps,
      isDay,
      temperatures,
      apparentTemperatures,
      dewPoints,
      humidities,
      weather: adjustedWeather,
      // Use adjusted weather with derived conditions
      wmoCodes,
      windChills,
      windSpeeds,
      windGusts,
      windDirections,
      skyCover,
      pop,
      rainAccumulations,
      snowAccumulations,
      iceAccumulations
    };
  }
  /**
   * Convert parsed NWS data to ROHourlyWeather format
   */
  convertToROHourlyWeather() {
    if (!this.parsedHourlyData) {
      return {
        currentIndex: -1,
        maxTemperature: INVALID_DOUBLE,
        minTemperature: INVALID_DOUBLE,
        temperatureColors: ["transparent"],
        hours: []
      };
    }
    const data = this.parsedHourlyData;
    const isImperial = this.selectedUnits === "Imperial";
    const hours = data.timestamps.map((timestamp, idx) => {
      const temp = isImperial ? data.temperatures[idx]?.fahrenheit ?? INVALID_DOUBLE : data.temperatures[idx]?.celsius ?? INVALID_DOUBLE;
      const apparentTemp = isImperial ? data.apparentTemperatures[idx]?.fahrenheit ?? INVALID_DOUBLE : data.apparentTemperatures[idx]?.celsius ?? INVALID_DOUBLE;
      const dewPoint = isImperial ? data.dewPoints[idx]?.fahrenheit ?? INVALID_DOUBLE : data.dewPoints[idx]?.celsius ?? INVALID_DOUBLE;
      const windSpeed = isImperial ? data.windSpeeds[idx]?.milesPerHour ?? INVALID_INT : data.windSpeeds[idx]?.kilometersPerHour ?? INVALID_INT;
      const windGust = isImperial ? data.windGusts[idx]?.milesPerHour ?? INVALID_INT : data.windGusts[idx]?.kilometersPerHour ?? INVALID_INT;
      const rainRate = isImperial ? data.rainAccumulations[idx]?.inches ?? 0 : data.rainAccumulations[idx]?.millimeters ?? 0;
      const snowRate = isImperial ? data.snowAccumulations[idx]?.inches ?? 0 : data.snowAccumulations[idx]?.millimeters ?? 0;
      const iceRate = isImperial ? data.iceAccumulations[idx]?.inches ?? 0 : data.iceAccumulations[idx]?.millimeters ?? 0;
      const startTime = timestamp;
      const endTime = new Date(timestamp.getTime() + 36e5);
      return {
        id: `nws-hour-${idx}-${timestamp.getTime()}`,
        index: idx,
        startTime,
        endTime,
        isDay: data.isDay[idx] ?? true,
        temperature: temp,
        temperatureApparent: apparentTemp,
        dewPoint,
        humidity: data.humidities[idx] ?? INVALID_INT,
        weatherCode: data.wmoCodes[idx] ?? 0,
        windSpeed: Math.round(windSpeed),
        windGust: Math.round(windGust),
        windBearing: data.windDirections[idx] ?? INVALID_INT,
        cloudCover: data.skyCover[idx] ?? INVALID_INT,
        precipitationRate: rainRate + snowRate + iceRate,
        rainRate,
        convectivePrecipitation: 0,
        // NWS doesn't separate convective
        snowRate,
        iceRate,
        pop: data.pop[idx] ?? 0
      };
    });
    const validTemps = hours.map((h) => h.temperature).filter((t) => t !== INVALID_DOUBLE);
    const maxTemperature = validTemps.length > 0 ? Math.max(...validTemps) : INVALID_DOUBLE;
    const minTemperature = validTemps.length > 0 ? Math.min(...validTemps) : INVALID_DOUBLE;
    const temperatureColors = colorsForTemperatureValues(hours.map((h) => h.temperature));
    return {
      currentIndex: data.currentIndex,
      maxTemperature,
      minTemperature,
      temperatureColors,
      hours
    };
  }
  /**
   * Set current conditions from the current hour's data
   */
  setCurrentConditions() {
    if (!this.parsedHourlyData || this.parsedHourlyData.timestamps.length === 0) {
      return;
    }
    const idx = this.parsedHourlyData.currentIndex;
    const data = this.parsedHourlyData;
    const isImperial = this.selectedUnits === "Imperial";
    this.timestamp = data.timestamps[idx] || /* @__PURE__ */ new Date();
    const hours = this.timestamp.getHours();
    const minutes = this.timestamp.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;
    const minuteStr = minutes.toString().padStart(2, "0");
    this.formattedTime = `${hour12}:${minuteStr} ${ampm}`;
    this.temperature = isImperial ? data.temperatures[idx]?.fahrenheit ?? INVALID_DOUBLE : data.temperatures[idx]?.celsius ?? INVALID_DOUBLE;
    this.temperatureApparent = isImperial ? data.apparentTemperatures[idx]?.fahrenheit ?? INVALID_DOUBLE : data.apparentTemperatures[idx]?.celsius ?? INVALID_DOUBLE;
    this.dewPoint = isImperial ? data.dewPoints[idx]?.fahrenheit ?? INVALID_DOUBLE : data.dewPoints[idx]?.celsius ?? INVALID_DOUBLE;
    this.humidity = data.humidities[idx] ?? INVALID_INT;
    this.windSpeed = isImperial ? Math.round(data.windSpeeds[idx]?.milesPerHour ?? INVALID_INT) : Math.round(data.windSpeeds[idx]?.kilometersPerHour ?? INVALID_INT);
    this.windGust = isImperial ? Math.round(data.windGusts[idx]?.milesPerHour ?? INVALID_INT) : Math.round(data.windGusts[idx]?.kilometersPerHour ?? INVALID_INT);
    this.windDirection = data.windDirections[idx] ?? INVALID_INT;
    this.weatherCode = data.wmoCodes[idx] ?? 0;
    this.cloudCover = data.skyCover[idx] ?? INVALID_INT;
    this.isDay = data.isDay[idx] ?? true;
    this.pop = data.pop[idx] ?? 0;
  }
  /**
   * Check if NWS data is valid and usable
   */
  hasValidData() {
    return this.parsedHourlyData !== null && this.parsedHourlyData.timestamps.length > 0 && this.hourlyWeather.hours.length > 0;
  }
  /**
   * Log current NWS weather values for debugging
   */
  logValues() {
    const prefix = "[WeatherNWS]";
    console.log(`${prefix} Current Conditions:`);
    console.log(`  Time: ${this.formattedTime}`);
    console.log(
      `  Temperature: ${this.temperature}\xB0${this.selectedUnits === "Imperial" ? "F" : "C"}`
    );
    console.log(
      `  Feels Like: ${this.temperatureApparent}\xB0${this.selectedUnits === "Imperial" ? "F" : "C"}`
    );
    console.log(`  Humidity: ${this.humidity}%`);
    console.log(
      `  Wind: ${this.windSpeed} ${this.selectedUnits === "Imperial" ? "mph" : "km/h"} @ ${this.windDirection}\xB0`
    );
    console.log(`  Weather Code: ${this.weatherCode} (WMO)`);
    console.log(`  Cloud Cover: ${this.cloudCover}%`);
    console.log(`  POP: ${this.pop}%`);
    console.log(`  Is Day: ${this.isDay}`);
    console.log(`  Hourly Forecast: ${this.hourlyWeather.hours.length} hours`);
  }
  /**
   * Merge NWS (base) hours with supplement (OpenMeteo) hours while CLAMPING to the supplement range.
   * Fully respects the supplementHours time domain (typically starts exactly at local midnight).
   * Any baseHours that start before the first supplement hour or extend past the last supplement hour are ignored.
   * For every supplement hour:
   *   - If a base hour with the SAME startTime exists, use the base hour (preferred NWS data).
   *   - Otherwise fall back to the supplement hour (to keep continuity).
   *
   * From Swift: WeatherNWS.swift:940-1027
   *
   * @param baseHours - NWS hourly data (preferred)
   * @param supplementHours - OpenMeteo hourly data (fallback)
   */
  fillMissingHours(baseHours, supplementHours) {
    if (supplementHours.length === 0) {
      const temps2 = baseHours.map((h) => h.temperature).filter((t) => t !== this.invalidDoubleValue);
      const maxTemp2 = temps2.length > 0 ? Math.max(...temps2) : this.invalidDoubleValue;
      const minTemp2 = temps2.length > 0 ? Math.min(...temps2) : this.invalidDoubleValue;
      const foundIndex2 = baseHours.findIndex((h) => {
        const now = /* @__PURE__ */ new Date();
        return now >= h.startTime && now < h.endTime;
      });
      const currentIndex2 = foundIndex2 >= 0 ? foundIndex2 : 0;
      this.hourlyWeather = {
        currentIndex: currentIndex2,
        maxTemperature: maxTemp2,
        minTemperature: minTemp2,
        temperatureColors: colorsForTemperatureValues(temps2),
        hours: baseHours
      };
      return;
    }
    const baseMap = /* @__PURE__ */ new Map();
    for (const hour of baseHours) {
      baseMap.set(hour.startTime.getTime(), hour);
    }
    const sortedSupplement = [...supplementHours].sort(
      (a, b) => a.startTime.getTime() - b.startTime.getTime()
    );
    const authoritativeStart = sortedSupplement[0].startTime;
    const authoritativeEnd = sortedSupplement[sortedSupplement.length - 1].endTime;
    const merged = [];
    for (const sup of sortedSupplement) {
      const replacement = baseMap.get(sup.startTime.getTime());
      if (replacement) {
        merged.push(replacement);
      } else {
        merged.push(sup);
      }
    }
    for (let i = 0; i < merged.length; i++) {
      const h = merged[i];
      if (h.index !== i) {
        merged[i] = {
          ...h,
          id: `merged-hour-${i}-${h.startTime.getTime()}`,
          index: i
        };
      }
    }
    const clamped = merged.filter(
      (h) => h.startTime >= authoritativeStart && h.endTime <= authoritativeEnd
    );
    const temps = clamped.map((h) => h.temperature).filter((t) => t !== this.invalidDoubleValue);
    const maxTemp = temps.length > 0 ? Math.max(...temps) : this.invalidDoubleValue;
    const minTemp = temps.length > 0 ? Math.min(...temps) : this.invalidDoubleValue;
    const foundIndex = clamped.findIndex((h) => {
      const now = /* @__PURE__ */ new Date();
      return now >= h.startTime && now < h.endTime;
    });
    const currentIndex = foundIndex >= 0 ? foundIndex : 0;
    this.hourlyWeather = {
      currentIndex,
      maxTemperature: maxTemp,
      minTemperature: minTemp,
      temperatureColors: colorsForTemperatureValues(temps),
      hours: clamped
    };
  }
  /**
   * Build daily weather from hourly grid data using WeatherPeriodAggregator.
   * This should be called after setHourlyGridValues() to generate daily forecasts.
   *
   * From Swift: WeatherNWS.swift:432-455
   */
  buildDailyWeather() {
    if (!this.hourlyWeather.hours || this.hourlyWeather.hours.length === 0) {
      console.warn("[WeatherNWSClass] buildDailyWeather() - No hourly data available");
      return;
    }
    if (!this.m_location) {
      console.warn("[WeatherNWSClass] buildDailyWeather() - Location not set");
      return;
    }
    const deps = {
      forecastGenerator: this.forecastGenerator,
      extractShortDayName: (start, end, tzOffset) => extractShortDayNameFromTimestamp(start, end, tzOffset),
      cardinalDirection: (bearing) => cardinalDirection(bearing)
    };
    this.dailyWeather = WeatherPeriodAggregator.aggregateDayNightPeriods(
      this.hourlyWeather.hours,
      this.m_location,
      this.m_timezone,
      this.m_timezoneOffsetSeconds,
      deps
    );
    console.log(
      `[WeatherNWSClass] buildDailyWeather() - Generated ${this.dailyWeather.days?.length || 0} day/night periods`
    );
  }
  /**
   * Set daily weather from NWS daily forecast API response.
   * This provides pre-aggregated day/night periods with high/low temps directly from NWS.
   * Use this instead of buildDailyWeather() when you have the NWS daily forecast.
   *
   * @param data - NWS daily forecast response
   */
  setDailyForecastValues(data) {
    if (!data?.properties?.periods || data.properties.periods.length === 0) {
      console.warn("[WeatherNWSClass] setDailyForecastValues() - No periods in forecast data");
      return;
    }
    const periods = data.properties.periods;
    const days = [];
    let weekMin = Infinity;
    let weekMax = -Infinity;
    for (let i = 0; i < periods.length; i++) {
      const period = periods[i];
      const startTime = new Date(period.startTime);
      const endTime = new Date(period.endTime);
      const windSpeedMatch = period.windSpeed.match(/(\d+)/);
      const windSpeed = windSpeedMatch ? parseInt(windSpeedMatch[1], 10) : 0;
      const wmoCode = this.getWMOCodeFromShortForecast(period.shortForecast, period.isDaytime);
      if (period.temperature < weekMin) weekMin = period.temperature;
      if (period.temperature > weekMax) weekMax = period.temperature;
      const shortName = this.extractDayName(period.name);
      const day = {
        id: `nws-daily-${i}-${startTime.getTime()}`,
        index: i,
        startTime,
        endTime,
        name: period.name,
        nameShort: shortName,
        isDay: period.isDaytime,
        // For NWS daily, daytime periods have high temp, night periods have low temp
        maxTemperature: period.isDaytime ? period.temperature : 0,
        minTemperature: period.isDaytime ? 0 : period.temperature,
        maxTemperatureApparent: period.isDaytime ? period.temperature : 0,
        minTemperatureApparent: period.isDaytime ? 0 : period.temperature,
        windSpeed,
        windGust: 0,
        // NWS daily doesn't provide gusts
        windDirection: period.windDirection,
        weatherCode: wmoCode,
        shortForecast: period.shortForecast,
        detailedForecast: period.detailedForecast,
        detailedForecastAfternoon: "",
        detailedForecastAfternoonShort: "",
        cloudCover: 0,
        // Not provided by daily API
        cloudCoverTrend: "stable" /* Stable */,
        precipitationAccumulation: 0,
        // Not provided by daily API
        rainAccumulation: 0,
        // Not provided by daily API
        snowAccumulation: 0,
        iceAccumulation: 0,
        precipChance: 0
      };
      days.push(day);
    }
    const todayDay = days.find((d) => d.isDay);
    const todayNight = days.find((d) => !d.isDay);
    const todayMax = todayDay?.maxTemperature ?? weekMax;
    const todayMin = todayNight?.minTemperature ?? weekMin;
    const now = /* @__PURE__ */ new Date();
    const currentIndex = days.findIndex((d) => now >= d.startTime && now < d.endTime);
    const currentPeriod = days[currentIndex] || days[0];
    const nextPeriod = days[currentIndex + 1] || days[1];
    this.dailyWeather = {
      maxTemperatureWeek: weekMax === -Infinity ? 0 : weekMax,
      minTemperatureWeek: weekMin === Infinity ? 0 : weekMin,
      todayMaxTemperature: todayMax,
      todayMinTemperature: todayMin,
      currentIndex: currentIndex >= 0 ? currentIndex : 0,
      currentDayName: currentPeriod?.name || "Today",
      currentDayForecast: currentPeriod?.detailedForecast || "",
      currentDayForecastShort: currentPeriod?.shortForecast || "",
      tomorrowDayName: nextPeriod?.name || "Tomorrow",
      tomorrowDayForecast: nextPeriod?.detailedForecast || "",
      tomorrowDayForecastShort: nextPeriod?.shortForecast || "",
      todayRain: 0,
      todaySnow: 0,
      todayIce: 0,
      todayWindSpeed: todayDay?.windSpeed ?? 0,
      todayWindDirection: todayDay?.windDirection ?? "",
      todayHumidity: 0,
      todayHumidityLow: 0,
      todayUVIndex: 0,
      days
    };
    console.log(
      `[WeatherNWSClass] setDailyForecastValues() - Processed ${days.length} periods from NWS daily forecast`
    );
  }
  /**
   * Extract short day name from NWS period name.
   * Examples: "Monday" -> "Mon", "Monday Night" -> "Mon", "This Afternoon" -> "Today"
   */
  extractDayName(name) {
    const dayMap = {
      "Sunday": "Sun",
      "Monday": "Mon",
      "Tuesday": "Tue",
      "Wednesday": "Wed",
      "Thursday": "Thu",
      "Friday": "Fri",
      "Saturday": "Sat"
    };
    for (const [full, short] of Object.entries(dayMap)) {
      if (name.includes(full)) return short;
    }
    if (name.includes("Today") || name.includes("This")) return "Today";
    if (name.includes("Tonight")) return "Tonight";
    if (name.includes("Tomorrow")) return "Tomorrow";
    return name.substring(0, 3);
  }
  /**
   * Get WMO weather code from NWS short forecast text.
   * Parses phrases like "Sunny", "Partly Cloudy", "Chance Rain Showers", etc.
   */
  getWMOCodeFromShortForecast(shortForecast, isDay) {
    const forecast = shortForecast.toLowerCase();
    if (forecast.includes("thunderstorm") || forecast.includes("thunder")) {
      if (forecast.includes("severe")) return 99;
      return 95;
    }
    if (forecast.includes("blizzard")) return 75;
    if (forecast.includes("snow")) {
      if (forecast.includes("heavy")) return 75;
      if (forecast.includes("light") || forecast.includes("flurr")) return 71;
      return 73;
    }
    if (forecast.includes("freezing rain") || forecast.includes("ice")) {
      if (forecast.includes("heavy")) return 67;
      return 66;
    }
    if (forecast.includes("rain") || forecast.includes("shower")) {
      if (forecast.includes("heavy")) return 65;
      if (forecast.includes("light") || forecast.includes("drizzle")) return 61;
      if (forecast.includes("shower")) return 80;
      return 63;
    }
    if (forecast.includes("drizzle")) {
      if (forecast.includes("freezing")) return 56;
      return 51;
    }
    if (forecast.includes("fog")) return 45;
    if (forecast.includes("overcast") || forecast.includes("cloudy")) {
      if (forecast.includes("mostly cloudy")) return 3;
      if (forecast.includes("partly")) return 2;
      return 3;
    }
    if (forecast.includes("sunny") || forecast.includes("clear")) {
      if (forecast.includes("mostly sunny") || forecast.includes("mostly clear")) return 1;
      return 0;
    }
    return isDay ? 0 : 0;
  }
};

// src/services/AlertsNWSClass.ts
var AlertsNWSClass = class {
  constructor() {
    /** Processed and sorted alerts */
    this.alerts = [];
  }
  /**
   * Beautifies a National Weather Service alert description for mobile display.
   *
   * Simplifies raw NWS alert text by:
   * - Extracting WHAT content as the lead paragraph (what's happening)
   * - For combined alerts, extracts only the relevant portion matching the alert type
   * - Skipping WHERE (redundant - user is viewing location-specific alerts)
   * - Skipping WHEN (redundant - shown separately in UI with startTime/endTime)
   * - Skipping county/location lists (redundant for location-based alerts)
   * - Formatting remaining sections like IMPACTS with plain text headers
   * - Merging broken lines into readable paragraphs
   *
   * @param description - The raw weather alert string from NWS API
   * @param alertType - The alert type/event name (e.g., "Dense Fog Advisory") to extract specific content
   * @returns A simplified, mobile-friendly alert description
   */
  beautifyNWSAlert(description, alertType) {
    let sections = description.split("\n\n");
    const firstSection = sections[0];
    if (firstSection && sections.length > 1 && // Only remove if there are other sections
    firstSection.length < 500 && // Counties lists are typically short
    (firstSection.toLowerCase().startsWith("in ") || firstSection.toLowerCase().startsWith("portions of ") || firstSection.toLowerCase().includes(" counties in "))) {
      sections.shift();
    }
    let whatContent;
    const remainingSections = [];
    for (const section of sections) {
      const trimmedSection = section.trim();
      const lines = trimmedSection.split("\n");
      const firstLine = lines[0];
      if (!firstLine) continue;
      const normalizedFirstLine = firstLine.startsWith("* ") ? firstLine.slice(2) : firstLine;
      if (normalizedFirstLine.startsWith("WHAT...")) {
        const fullWhatContent = this.extractContentAfterEllipsis(trimmedSection);
        if (alertType) {
          whatContent = this.extractAlertSpecificContent(fullWhatContent, alertType);
        } else {
          whatContent = fullWhatContent;
        }
      } else if (normalizedFirstLine.startsWith("WHERE...")) {
        continue;
      } else if (normalizedFirstLine.startsWith("WHEN...")) {
        continue;
      } else {
        remainingSections.push(trimmedSection);
      }
    }
    let beautifiedAlert = "";
    if (whatContent) {
      beautifiedAlert += whatContent.trim() + "\n\n";
    }
    for (const section of remainingSections) {
      if (section.length === 0) continue;
      let title;
      let content;
      const lines = section.split("\n");
      const firstLine = lines[0] ?? "";
      if (firstLine.startsWith("* ") && firstLine.includes("...")) {
        const ellipsisIndex = firstLine.indexOf("...");
        if (ellipsisIndex !== -1) {
          const titleText = firstLine.slice(2, ellipsisIndex).trim();
          title = `${this.capitalize(titleText)}:`;
          const firstLineContent = firstLine.slice(ellipsisIndex + 3);
          content = [firstLineContent, ...lines.slice(1)].join(" ").trim();
          if (alertType && (titleText.toLowerCase() === "impacts" || titleText.toLowerCase() === "additional details")) {
            content = this.extractAlertSpecificContent(content, alertType);
          }
        } else {
          title = void 0;
          content = lines.join(" ");
        }
      } else if (firstLine.endsWith(":")) {
        title = firstLine;
        content = lines.slice(1).join(" ");
        if (alertType && (firstLine.toLowerCase().includes("impacts") || firstLine.toLowerCase().includes("additional details"))) {
          content = this.extractAlertSpecificContent(content, alertType);
        }
      } else {
        title = void 0;
        content = lines.join(" ");
      }
      if (title && content.length > 0) {
        beautifiedAlert += `${title} ${content}

`;
      } else if (content.length > 0) {
        beautifiedAlert += `${content}

`;
      }
    }
    const result = beautifiedAlert.trim();
    if (result.length === 0) {
      return description.split("\n").map((line) => line.trim()).filter((line) => line.length > 0).join(" ").trim();
    }
    return result;
  }
  /**
   * Helper function to extract the content part of a section that starts with a "KEYWORD..." pattern.
   * It correctly handles multi-line content.
   *
   * @param section - The section text starting with a keyword pattern
   * @returns The extracted content
   */
  extractContentAfterEllipsis(section) {
    const lines = section.split("\n");
    const firstLine = lines[0];
    if (!firstLine) {
      return lines.join(" ");
    }
    const ellipsisIndex = firstLine.indexOf("...");
    if (ellipsisIndex === -1) {
      return lines.join(" ");
    }
    const firstLineContent = firstLine.slice(ellipsisIndex + 3);
    const allContent = [firstLineContent, ...lines.slice(1)].join(" ");
    return allContent.trim();
  }
  /**
   * Extracts alert-specific content from combined NWS alerts.
   *
   * NWS sometimes issues combined alerts where a single description covers multiple
   * alert types (e.g., "For the Dense Fog Advisory... For the Air Stagnation Advisory...").
   * This method extracts only the portion relevant to the specific alert type.
   *
   * Handles two patterns:
   * 1. Explicit: "For the [Alert Type], [description]."
   * 2. Implicit: Multiple sentences combined - filters by keywords related to alert type
   *
   * @param content - The full content that may contain multiple alerts
   * @param alertType - The alert type to extract (e.g., "Dense Fog Advisory")
   * @returns The extracted content for this specific alert type, or full content if no match
   */
  extractAlertSpecificContent(content, alertType) {
    const forThePattern = `For the ${alertType},`;
    const forTheIndex = content.indexOf(forThePattern);
    if (forTheIndex !== -1) {
      const startIndex = forTheIndex + forThePattern.length;
      const nextForTheIndex = content.indexOf(" For the ", startIndex);
      let alertSpecificText;
      if (nextForTheIndex !== -1) {
        alertSpecificText = content.substring(startIndex, nextForTheIndex).trim();
      } else {
        alertSpecificText = content.substring(startIndex).trim();
      }
      alertSpecificText = alertSpecificText.replace(/\.\s*$/, "").trim();
      return alertSpecificText;
    }
    const sentences = content.split(/(?<=[.!?])\s+/).filter((s) => s.trim().length > 0);
    if (sentences.length <= 1) {
      return content;
    }
    const alertKeywords = this.extractAlertKeywords(alertType);
    const scoredSentences = sentences.map((sentence) => {
      const lowerSentence = sentence.toLowerCase();
      let score = 0;
      for (const keyword of alertKeywords) {
        if (lowerSentence.includes(keyword)) {
          score += 1;
        }
      }
      return { sentence, score };
    });
    const matchingSentences = scoredSentences.filter((s) => s.score > 0).sort((a, b) => b.score - a.score).map((s) => s.sentence);
    if (matchingSentences.length > 0) {
      return matchingSentences.join(" ").trim();
    }
    return content;
  }
  /**
   * Extracts relevant keywords from an alert type for sentence matching.
   *
   * @param alertType - The alert type (e.g., "Dense Fog Advisory", "Air Stagnation Advisory")
   * @returns Array of lowercase keywords
   */
  extractAlertKeywords(alertType) {
    const keywords = [];
    const type = alertType.toLowerCase();
    const keywordMap = {
      "fog": ["fog", "visibility", "driving", "visibilities"],
      "air stagnation": ["air quality", "air", "stagnant", "pollution", "respiratory", "breathing"],
      "wind": ["wind", "winds", "gust", "gusts", "blow"],
      "heat": ["heat", "hot", "temperature", "warm", "overheat"],
      "cold": ["cold", "freeze", "freezing", "frost", "frostbite", "hypothermia"],
      "winter": ["snow", "ice", "icy", "slippery", "winter", "frozen", "accumulation"],
      "snow": ["snow", "accumulation", "snowfall", "slippery"],
      "ice": ["ice", "icy", "freezing", "sleet", "glaze"],
      "rain": ["rain", "rainfall", "flooding", "flood", "heavy rain"],
      "flood": ["flood", "flooding", "water", "inundation", "rise", "overflow"],
      "storm": ["storm", "severe", "damaging", "destructive"],
      "thunderstorm": ["thunderstorm", "lightning", "thunder", "hail"],
      "tornado": ["tornado", "tornadic", "rotation"],
      "fire": ["fire", "wildfire", "smoke", "burn", "flame"],
      "smoke": ["smoke", "air quality", "visibility", "respiratory"]
    };
    for (const [key, words] of Object.entries(keywordMap)) {
      if (type.includes(key)) {
        keywords.push(...words);
      }
    }
    const mainWords = alertType.toLowerCase().split(/\s+/).filter((word) => word.length > 3 && !["advisory", "warning", "watch", "statement"].includes(word));
    keywords.push(...mainWords);
    return [...new Set(keywords)];
  }
  /**
   * Capitalizes the first letter of each word in a string.
   *
   * @param str - The string to capitalize
   * @returns The capitalized string
   */
  capitalize(str) {
    return str.split(" ").map((word) => {
      if (word.length === 0) return word;
      return word[0].toUpperCase() + word.slice(1).toLowerCase();
    }).join(" ");
  }
  /**
   * Processes an array of raw NWS alerts into formatted AlertsNWS objects.
   * Beautifies descriptions, parses severity, deduplicates, and sorts by severity and time.
   *
   * @param nwsAlerts - Array of raw NWS alert features
   */
  setAlertValues(nwsAlerts) {
    const processedAlerts = [];
    const severityOrder = {
      extreme: 4,
      severe: 3,
      moderate: 2,
      minor: 1,
      unknown: 0
    };
    for (const alert of nwsAlerts) {
      const startDate = alert.properties.onset ? new Date(alert.properties.onset) : void 0;
      const endDate = alert.properties.ends ? new Date(alert.properties.ends) : void 0;
      const alertType = alert.properties.event;
      const beautifiedDescription = this.beautifyNWSAlert(
        alert.properties.description,
        alertType
      );
      const rawSeverity = alert.properties.severity?.toLowerCase() || "unknown";
      const severity = rawSeverity.charAt(0).toUpperCase() + rawSeverity.slice(1);
      const headline = alert.properties.headline;
      const issuedIndex = headline.indexOf(" issued");
      const headlineShort = issuedIndex !== -1 ? headline.slice(0, issuedIndex) : headline;
      const newAlert = {
        id: alert.properties.id,
        startTime: startDate,
        endTime: endDate,
        severity,
        headlineShort,
        headline: alert.properties.headline,
        shortDescription: beautifiedDescription,
        description: alert.properties.description
        // Keep original for reference
      };
      processedAlerts.push(newAlert);
    }
    const sortedAlerts = processedAlerts.sort((a, b) => {
      const severity1 = severityOrder[a.severity.toLowerCase()] ?? 0;
      const severity2 = severityOrder[b.severity.toLowerCase()] ?? 0;
      if (severity1 !== severity2) {
        return severity2 - severity1;
      }
      const time1 = a.startTime?.getTime() ?? Number.MIN_SAFE_INTEGER;
      const time2 = b.startTime?.getTime() ?? Number.MIN_SAFE_INTEGER;
      return time2 - time1;
    });
    const uniqueAlerts = [];
    for (const alert of sortedAlerts) {
      const existingAlertIndex = uniqueAlerts.findIndex(
        (a) => a.headlineShort === alert.headlineShort
      );
      if (existingAlertIndex !== -1) {
        const existingAlert = uniqueAlerts[existingAlertIndex];
        if (existingAlert.startTime && alert.startTime) {
          if (alert.startTime > existingAlert.startTime) {
            uniqueAlerts[existingAlertIndex] = alert;
          }
        } else if (alert.startTime && !existingAlert.startTime) {
          uniqueAlerts[existingAlertIndex] = alert;
        }
      } else {
        uniqueAlerts.push(alert);
      }
    }
    this.alerts = uniqueAlerts;
  }
  /**
   * Logs processed alerts for debugging.
   */
  logAlerts() {
    console.log("=== NWS ALERTS LOG ===");
    console.log(`Total alerts: ${this.alerts.length}`);
    if (this.alerts.length === 0) {
      console.log("No alerts available");
      console.log("======================");
      return;
    }
    for (let i = 0; i < this.alerts.length; i++) {
      const alert = this.alerts[i];
      console.log(`
--- Alert ${i + 1} ---`);
      console.log(`ID: ${alert.id}`);
      console.log(`Severity: ${alert.severity}`);
      console.log(`Headline Short: ${alert.headlineShort}`);
      console.log(`Headline: ${alert.headline}`);
      console.log(`Short Description: ${alert.shortDescription}`);
      console.log(`Description: ${alert.description}`);
      if (alert.startTime) {
        console.log(`Start Time: ${alert.startTime.toISOString()}`);
      } else {
        console.log("Start Time: Not available");
      }
      if (alert.endTime) {
        console.log(`End Time: ${alert.endTime.toISOString()}`);
      } else {
        console.log("End Time: Not available");
      }
    }
    console.log("\n======================");
  }
  /**
   * Logs raw NWS alert data for debugging.
   *
   * @param nwsAlerts - Array of raw NWS alert features
   */
  logRawNWSData(nwsAlerts) {
    console.log("=== RAW NWS ALERTS DATA ===");
    console.log(`Total raw alerts: ${nwsAlerts.length}`);
    if (nwsAlerts.length === 0) {
      console.log("No raw alerts available");
      console.log("============================");
      return;
    }
    for (let i = 0; i < nwsAlerts.length; i++) {
      const alert = nwsAlerts[i];
      console.log(`
--- Raw Alert ${i + 1} ---`);
      console.log(`Type: ${alert.type}`);
      console.log("Properties:");
      console.log(`  ID: ${alert.properties.id}`);
      console.log(`  Status: ${alert.properties.status}`);
      console.log(`  Message Type: ${alert.properties.messageType}`);
      console.log(`  Category: ${alert.properties.category}`);
      console.log(`  Severity: ${alert.properties.severity}`);
      console.log(`  Urgency: ${alert.properties.urgency}`);
      console.log(`  Event: ${alert.properties.event}`);
      console.log(`  Sender Name: ${alert.properties.senderName}`);
      console.log(`  Headline: ${alert.properties.headline}`);
      console.log(`  Description: ${alert.properties.description}`);
      console.log(
        `  Onset: ${alert.properties.onset ?? "nil"}`
      );
      console.log(
        `  Expires: ${alert.properties.expires ?? "nil"}`
      );
      console.log(`  Ends: ${alert.properties.ends ?? "nil"}`);
    }
    console.log("\n============================");
  }
};

// src/services/HttpAdapter.nativescript.ts
var NativeScriptHttpAdapter = class {
  constructor() {
    this.httpModule = null;
  }
  async request(options) {
    if (!this.httpModule) {
      const ns = await import('@nativescript/core');
      this.httpModule = ns.Http;
    }
    const response = await this.httpModule.request({
      url: options.url,
      method: options.method,
      headers: options.headers,
      timeout: options.timeout
    });
    if (!response.content) {
      throw new Error("Empty response content");
    }
    const rawContent = response.content.toString();
    try {
      const jsonContent = response.content.toJSON();
      return {
        statusCode: response.statusCode || 200,
        content: jsonContent
      };
    } catch (parseError) {
      const preview = rawContent.length > 200 ? rawContent.substring(0, 200) + "...(truncated)" : rawContent;
      const statusCode = response.statusCode || "unknown";
      throw new Error(
        `JSON parse failed (status ${statusCode}): ${parseError instanceof Error ? parseError.message : parseError}
Response preview: ${preview}`
      );
    }
  }
};

// src/services/RONetworkManager.ts
var MOBILE_SAFARI_USER_AGENT = "Mozilla/5.0 (iPhone; CPU iPhone OS 18_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/26.0 Mobile/15E148 Safari/604.1";
var DEFAULT_OPEN_METEO_API_KEY = "f0145f2f30cb426c37fe7c6871de1dd59500a1b682e570692387b6a8d2c16a3d";
var _RONetworkManager = class _RONetworkManager {
  /**
   * Private constructor to enforce singleton pattern
   */
  constructor(config) {
    // API base URLs
    this.nwsEndPointsURL = "https://api.weather.gov/points/";
    this.nwsAlertsURL = "https://api.weather.gov/alerts/active/zone/";
    this.rainViewerFramesURL = "https://api.rainviewer.com/public/weather-maps.json";
    this.rainOwlsURLBase = "https://api-jzntfk4vra-uc.a.run.app/v1/";
    this.openMeteoStatusUrl = "https://app.statuscake.com/Workfloor/PublicReportHandler.php?PublicID=2dcTJhVI7P&callback=";
    this.openMeteoBaseUrl = "https://api.open-meteo.com";
    this.openMeteoApiKey = "";
    this.openMeteoFallbackBaseUrl = "https://om.rainowls.com";
    this.openMeteoWindBaseUrl = "";
    this.openMeteoStatusCache = null;
    this.openMeteoStatusCheck = null;
    this.synopticBaseUrl = "https://api.synopticdata.com";
    this.openMeteoStatusCheckEnabled = true;
    this.openMeteoStatusCacheMs = 6e4;
    this.openMeteoStatusTimeout = 3e3;
    // Synoptic Data variable configuration
    this.synopticModelVars = [
      "metar",
      "air_temp",
      "dew_point_temperature",
      "relative_humidity",
      "wind_speed",
      "wind_direction",
      "wind_gust",
      "pressure",
      "snow_depth",
      "snow_accum",
      "weather_cond_code",
      "snow_interval",
      "snow_accum_24_hour",
      "weather_summary",
      "weather_condition"
    ];
    // Open-Meteo model configuration
    this.openMeteoModelVars = [
      "best_match",
      "ecmwf_ifs",
      "icon_seamless",
      "ncep_nbm_conus"
    ];
    // Open-Meteo format configuration
    this.openMeteoFormatVars = [
      "temperature_unit=fahrenheit",
      "wind_speed_unit=mph",
      "precipitation_unit=inch",
      "length_unit=imperial",
      // elevation in feet
      "timeformat=unixtime",
      "timezone=auto"
    ];
    // Open-Meteo current weather variables
    this.openMeteoCurrentVars = [
      "temperature_2m",
      "relative_humidity_2m",
      "apparent_temperature",
      "is_day",
      "precipitation",
      "rain",
      "showers",
      "snowfall",
      "weather_code",
      "cloud_cover",
      "pressure_msl",
      "surface_pressure",
      "wind_speed_10m",
      "wind_direction_10m",
      "wind_gusts_10m",
      "dew_point_2m"
    ];
    // Open-Meteo hourly variables
    this.openMeteoHourlyVars = [
      "temperature_2m",
      "precipitation_probability",
      "relative_humidity_2m",
      "dew_point_2m",
      // Needed for wet-bulb temperature calculation
      "precipitation",
      // Blended liquid water equivalent — source of truth for totals
      "rain",
      "showers",
      "snowfall",
      "weather_code",
      "cloud_cover",
      "wind_speed_10m",
      "wind_direction_10m",
      "wind_gusts_10m",
      "is_day",
      // Winter precipitation type analysis variables
      "temperature_850hPa",
      "temperature_925hPa",
      "temperature_700hPa"
      // For high-elevation locations (above 850hPa level)
    ];
    // Open-Meteo daily variables
    this.openMeteoDailyVars = [
      "weather_code",
      "temperature_2m_max",
      "temperature_2m_min",
      "apparent_temperature_max",
      "apparent_temperature_min",
      "wind_speed_10m_max",
      "wind_gusts_10m_max",
      "wind_direction_10m_dominant",
      "precipitation_probability_max",
      "precipitation_hours",
      "precipitation_sum",
      "rain_sum",
      "showers_sum",
      "snowfall_sum"
    ];
    // Open-Meteo daily range configuration
    this.openMeteoDailyRangeVars = ["past_days=1"];
    this.synopticAPIKey = config?.synopticAPIKey ?? "";
    this.synopticBaseUrl = config?.synopticBaseUrl || "https://api.synopticdata.com";
    this.firebaseAPIKey = config?.firebaseAPIKey ?? "";
    this.timeout = config?.timeout ?? 3e4;
    this.openMeteoStatusCheckEnabled = config?.openMeteoStatusCheckEnabled ?? true;
    this.httpAdapter = config?.httpAdapter ?? new NativeScriptHttpAdapter();
    this.openMeteoBaseUrl = config?.openMeteoBaseUrl || "https://api.open-meteo.com";
    this.openMeteoApiKey = config?.openMeteoApiKey || DEFAULT_OPEN_METEO_API_KEY;
    this.openMeteoFallbackBaseUrl = config?.openMeteoFallbackBaseUrl ?? "https://om.rainowls.com";
    this.openMeteoWindBaseUrl = config?.openMeteoWindBaseUrl || "";
  }
  /**
   * Gets the singleton instance of RONetworkManager
   *
   * @param config - Optional configuration (only used on first call)
   * @returns The singleton instance
   */
  static getInstance(config) {
    if (!_RONetworkManager.instance) {
      _RONetworkManager.instance = new _RONetworkManager(config);
    }
    return _RONetworkManager.instance;
  }
  /**
   * Configures the singleton instance with new API keys
   * Use this to update API keys after the singleton has been created
   *
   * @param config - Configuration with API keys to update
   */
  static configure(config) {
    if (_RONetworkManager.instance) {
      if (config.synopticAPIKey !== void 0) {
        _RONetworkManager.instance.synopticAPIKey = config.synopticAPIKey;
      }
      if (config.synopticBaseUrl) {
        _RONetworkManager.instance.synopticBaseUrl = config.synopticBaseUrl;
      }
      if (config.firebaseAPIKey !== void 0) {
        _RONetworkManager.instance.firebaseAPIKey = config.firebaseAPIKey;
      }
      if (config.timeout !== void 0) {
        _RONetworkManager.instance.timeout = config.timeout;
      }
      if (config.openMeteoStatusCheckEnabled !== void 0) {
        _RONetworkManager.instance.openMeteoStatusCheckEnabled = config.openMeteoStatusCheckEnabled;
      }
      if (config.httpAdapter !== void 0) {
        _RONetworkManager.instance.httpAdapter = config.httpAdapter;
      }
      if (config.openMeteoBaseUrl) {
        _RONetworkManager.instance.openMeteoBaseUrl = config.openMeteoBaseUrl;
      }
      if (config.openMeteoApiKey !== void 0) {
        _RONetworkManager.instance.openMeteoApiKey = config.openMeteoApiKey;
      }
      if (config.openMeteoFallbackBaseUrl !== void 0) {
        _RONetworkManager.instance.openMeteoFallbackBaseUrl = config.openMeteoFallbackBaseUrl;
      }
      if (config.openMeteoWindBaseUrl !== void 0) {
        _RONetworkManager.instance.openMeteoWindBaseUrl = config.openMeteoWindBaseUrl;
      }
    }
  }
  /**
   * Resets the singleton instance (for testing or re-initialization)
   */
  static resetInstance() {
    _RONetworkManager.instance = null;
  }
  /**
   * Checks if a response from Open-Meteo is an error response.
   * Open-Meteo returns { error: true, reason: "..." } when rate limited or other errors occur.
   */
  isOpenMeteoError(data) {
    return data && data.error === true;
  }
  /** Returns the Open-Meteo forecast endpoint URL */
  get openMeteoForecastUrl() {
    return `${this.openMeteoBaseUrl}/v1/forecast`;
  }
  /** Returns the Open-Meteo ECMWF endpoint URL */
  get openMeteoEcmwfUrl() {
    return `${this.openMeteoBaseUrl}/v1/ecmwf`;
  }
  /** Returns the RainOwls Open-Meteo mirror forecast endpoint URL */
  get openMeteoFallbackForecastUrl() {
    if (!this.openMeteoFallbackBaseUrl) return null;
    if (this.openMeteoFallbackBaseUrl === this.openMeteoBaseUrl) return null;
    return `${this.openMeteoFallbackBaseUrl}/v1/forecast`;
  }
  headersForOpenMeteoUrl(url) {
    return new URL(url).hostname === "api.open-meteo.com" || !this.openMeteoApiKey ? void 0 : { "X-API-KEY": this.openMeteoApiKey };
  }
  /** Returns the dedicated wind grid forecast URL (falls back to default Open-Meteo if not configured) */
  get openMeteoWindForecastUrl() {
    if (this.openMeteoWindBaseUrl) {
      return `${this.openMeteoWindBaseUrl}/v1/forecast`;
    }
    return this.openMeteoForecastUrl;
  }
  isOpenMeteoFallbackStatus(statusCode) {
    return statusCode === 408 || statusCode === 429 || statusCode !== void 0 && statusCode >= 500;
  }
  /**
   * Detects Open-Meteo JSON error payloads even when the HTTP status is 200.
   */
  shouldFallbackForOpenMeteoPayload(content) {
    let rawData = content;
    if (typeof rawData === "string") {
      try {
        rawData = JSON.parse(rawData);
      } catch {
        return false;
      }
    }
    return this.isOpenMeteoError(rawData);
  }
  /**
   * Parses the Open-Meteo public status payload and extracts the free forecast API state.
   */
  static isOpenMeteoFreeForecastApiUp(statusContent) {
    let rawData = statusContent;
    if (typeof rawData === "string") {
      try {
        rawData = JSON.parse(rawData);
      } catch {
        return null;
      }
    }
    const tests = rawData?.TestData;
    if (!Array.isArray(tests)) return null;
    const forecastApi = tests.find(
      (test) => String(test?.Name || "").toLowerCase().includes("forecast api (free)")
    );
    if (!forecastApi) return null;
    return String(forecastApi.Status || "").toLowerCase() === "up";
  }
  /**
   * Returns the cached/coalesced status-page decision, failing open when status is unknown.
   */
  async isOpenMeteoFreeForecastApiUp() {
    if (!this.openMeteoStatusCheckEnabled) return true;
    const now = Date.now();
    if (this.openMeteoStatusCache && now - this.openMeteoStatusCache.checkedAtMs < this.openMeteoStatusCacheMs) {
      return this.openMeteoStatusCache.isFreeForecastApiUp;
    }
    if (this.openMeteoStatusCheck) {
      return this.openMeteoStatusCheck;
    }
    this.openMeteoStatusCheck = this.fetchOpenMeteoFreeForecastApiStatus();
    const isUp = await this.openMeteoStatusCheck;
    this.openMeteoStatusCache = {
      checkedAtMs: Date.now(),
      isFreeForecastApiUp: isUp
    };
    this.openMeteoStatusCheck = null;
    return isUp;
  }
  /**
   * Fetches the Open-Meteo status page data with a short timeout so it cannot dominate refresh time.
   */
  async fetchOpenMeteoFreeForecastApiStatus() {
    try {
      const response = await this.httpAdapter.request({
        url: this.openMeteoStatusUrl,
        method: "GET",
        timeout: this.openMeteoStatusTimeout
      });
      if (response.statusCode !== 200) return true;
      return _RONetworkManager.isOpenMeteoFreeForecastApiUp(response.content) ?? true;
    } catch {
      return true;
    }
  }
  /**
   * Runs Open-Meteo requests through the configured primary and fallback endpoints.
   */
  async requestOpenMeteoWithFallback(url, fallbackUrl, operation) {
    const request = {
      url,
      method: "GET",
      headers: this.headersForOpenMeteoUrl(url),
      timeout: this.timeout
    };
    let triedFallback = false;
    if (fallbackUrl && new URL(url).hostname === "api.open-meteo.com" && !await this.isOpenMeteoFreeForecastApiUp()) {
      console.warn(
        `[${operation}] Open-Meteo status is down; using RainOwls mirror`
      );
      triedFallback = true;
      try {
        const fallbackResponse = await this.requestOpenMeteoFallback(fallbackUrl);
        if (!this.isOpenMeteoFallbackStatus(fallbackResponse.statusCode) && !this.shouldFallbackForOpenMeteoPayload(fallbackResponse.content)) {
          return fallbackResponse;
        }
        console.warn(
          `[${operation}] RainOwls mirror response was unavailable while Open-Meteo status was down; trying public Open-Meteo`
        );
      } catch (error) {
        console.warn(
          `[${operation}] RainOwls mirror failed while Open-Meteo status was down; trying public Open-Meteo`,
          error
        );
      }
    }
    let response;
    try {
      response = await this.httpAdapter.request(request);
    } catch (error) {
      if (!fallbackUrl || triedFallback) throw error;
      console.warn(
        `[${operation}] Primary Open-Meteo request failed; retrying fallback endpoint`,
        error
      );
      return this.requestOpenMeteoFallback(fallbackUrl);
    }
    if (fallbackUrl && !triedFallback && (this.isOpenMeteoFallbackStatus(response.statusCode) || this.shouldFallbackForOpenMeteoPayload(response.content))) {
      console.warn(
        `[${operation}] Primary Open-Meteo response was unavailable; retrying fallback endpoint`
      );
      return this.requestOpenMeteoFallback(fallbackUrl);
    }
    return response;
  }
  /**
   * Sends a fallback request, attaching the client token only to private endpoints.
   */
  requestOpenMeteoFallback(fallbackUrl) {
    return this.httpAdapter.request({
      url: fallbackUrl,
      method: "GET",
      headers: this.headersForOpenMeteoUrl(fallbackUrl),
      timeout: this.timeout
    });
  }
  /**
   * Transforms raw Open-Meteo API hourly response to OpenMeteoHourly model.
   * Maps snake_case model-specific fields to camelCase field names.
   */
  transformHourlyData(rawHourly) {
    if (!rawHourly || !rawHourly.time) {
      return void 0;
    }
    const getArray = (arr) => {
      return Array.isArray(arr) ? arr : [];
    };
    return {
      time: getArray(rawHourly.time),
      // Temperature
      temperaturesBEST: getArray(rawHourly.temperature_2m_best_match),
      temperaturesICON: getArray(rawHourly.temperature_2m_icon_seamless),
      temperaturesNBM: rawHourly.temperature_2m_ncep_nbm_conus ? getArray(rawHourly.temperature_2m_ncep_nbm_conus) : void 0,
      temperaturesECMWF: getArray(rawHourly.temperature_2m_ecmwf_ifs),
      // Humidity
      humiditiesBEST: getArray(rawHourly.relative_humidity_2m_best_match),
      humiditiesICON: getArray(rawHourly.relative_humidity_2m_icon_seamless),
      humiditiesNBM: rawHourly.relative_humidity_2m_ncep_nbm_conus ? getArray(rawHourly.relative_humidity_2m_ncep_nbm_conus) : void 0,
      humiditiesECMWF: getArray(rawHourly.relative_humidity_2m_ecmwf_ifs),
      // Dew Point (Fahrenheit) - for wet-bulb temperature calculation
      dewPointsBEST: getArray(rawHourly.dew_point_2m_best_match),
      dewPointsICON: getArray(rawHourly.dew_point_2m_icon_seamless),
      dewPointsNBM: rawHourly.dew_point_2m_ncep_nbm_conus ? getArray(rawHourly.dew_point_2m_ncep_nbm_conus) : void 0,
      dewPointsECMWF: getArray(rawHourly.dew_point_2m_ecmwf_ifs),
      // Precipitation probability (POP)
      popsBEST: getArray(rawHourly.precipitation_probability_best_match),
      popsICON: getArray(rawHourly.precipitation_probability_icon_seamless),
      popsNBM: rawHourly.precipitation_probability_ncep_nbm_conus ? getArray(rawHourly.precipitation_probability_ncep_nbm_conus) : void 0,
      popsECMWF: getArray(rawHourly.precipitation_probability_ecmwf_ifs),
      // Rain accumulation
      rainAccumulationsBEST: getArray(rawHourly.rain_best_match),
      rainAccumulationsICON: getArray(rawHourly.rain_icon_seamless),
      rainAccumulationsNBM: rawHourly.rain_ncep_nbm_conus ? getArray(rawHourly.rain_ncep_nbm_conus) : void 0,
      rainAccumulationsECMWF: getArray(rawHourly.rain_ecmwf_ifs),
      // Total precipitation (liquid water equivalent)
      precipitationsBEST: getArray(rawHourly.precipitation_best_match),
      precipitationsICON: getArray(rawHourly.precipitation_icon_seamless),
      precipitationsNBM: rawHourly.precipitation_ncep_nbm_conus ? getArray(rawHourly.precipitation_ncep_nbm_conus) : void 0,
      precipitationsECMWF: getArray(rawHourly.precipitation_ecmwf_ifs),
      // Shower/convective precipitation
      showerAccumulationsBEST: getArray(rawHourly.showers_best_match),
      showerAccumulationsICON: getArray(rawHourly.showers_icon_seamless),
      showerAccumulationsNBM: rawHourly.showers_ncep_nbm_conus ? getArray(rawHourly.showers_ncep_nbm_conus) : void 0,
      showerAccumulationsECMWF: getArray(rawHourly.showers_ecmwf_ifs),
      // Snow accumulation
      snowAccumulationsBEST: getArray(rawHourly.snowfall_best_match),
      snowAccumulationsICON: getArray(rawHourly.snowfall_icon_seamless),
      snowAccumulationsNBM: rawHourly.snowfall_ncep_nbm_conus ? getArray(rawHourly.snowfall_ncep_nbm_conus) : void 0,
      snowAccumulationsECMWF: getArray(rawHourly.snowfall_ecmwf_ifs),
      // Weather codes
      weatherCodesBEST: getArray(rawHourly.weather_code_best_match),
      weatherCodesICON: getArray(rawHourly.weather_code_icon_seamless),
      weatherCodesNBM: rawHourly.weather_code_ncep_nbm_conus ? getArray(rawHourly.weather_code_ncep_nbm_conus) : void 0,
      weatherCodesECMWF: getArray(rawHourly.weather_code_ecmwf_ifs),
      // Cloud cover
      cloudsBEST: getArray(rawHourly.cloud_cover_best_match),
      cloudsICON: getArray(rawHourly.cloud_cover_icon_seamless),
      cloudsNBM: rawHourly.cloud_cover_ncep_nbm_conus ? getArray(rawHourly.cloud_cover_ncep_nbm_conus) : void 0,
      cloudsECMWF: getArray(rawHourly.cloud_cover_ecmwf_ifs),
      // Wind speed
      windSpeedsBEST: getArray(rawHourly.wind_speed_10m_best_match),
      windSpeedsICON: getArray(rawHourly.wind_speed_10m_icon_seamless),
      windSpeedsNBM: rawHourly.wind_speed_10m_ncep_nbm_conus ? getArray(rawHourly.wind_speed_10m_ncep_nbm_conus) : void 0,
      windSpeedsECMWF: getArray(rawHourly.wind_speed_10m_ecmwf_ifs),
      // Wind gusts
      windGustsBEST: getArray(rawHourly.wind_gusts_10m_best_match),
      windGustsICON: getArray(rawHourly.wind_gusts_10m_icon_seamless),
      windGustsNBM: rawHourly.wind_gusts_10m_ncep_nbm_conus ? getArray(rawHourly.wind_gusts_10m_ncep_nbm_conus) : void 0,
      windGustsECMWF: getArray(rawHourly.wind_gusts_10m_ecmwf_ifs),
      // Wind direction
      windDirectionsBEST: getArray(rawHourly.wind_direction_10m_best_match),
      windDirectionsICON: getArray(rawHourly.wind_direction_10m_icon_seamless),
      windDirectionsNBM: rawHourly.wind_direction_10m_ncep_nbm_conus ? getArray(rawHourly.wind_direction_10m_ncep_nbm_conus) : void 0,
      windDirectionsECMWF: getArray(rawHourly.wind_direction_10m_ecmwf_ifs),
      // Day/night indicator (only from BEST_MATCH)
      isDaysBEST: getArray(rawHourly.is_day_best_match),
      // Temperature at 850hPa (~5,000ft / 1,500m)
      // Note: NBM doesn't provide pressure level temperatures
      temp850hPaBEST: getArray(rawHourly.temperature_850hPa_best_match),
      temp850hPaICON: getArray(rawHourly.temperature_850hPa_icon_seamless),
      temp850hPaECMWF: getArray(rawHourly.temperature_850hPa_ecmwf_ifs),
      // Temperature at 925hPa (~2,500ft / 750m)
      temp925hPaBEST: getArray(rawHourly.temperature_925hPa_best_match),
      temp925hPaICON: getArray(rawHourly.temperature_925hPa_icon_seamless),
      temp925hPaECMWF: getArray(rawHourly.temperature_925hPa_ecmwf_ifs),
      // Temperature at 700hPa (~10,000ft / 3,000m) - for high-elevation locations
      temp700hPaBEST: getArray(rawHourly.temperature_700hPa_best_match),
      temp700hPaICON: getArray(rawHourly.temperature_700hPa_icon_seamless),
      temp700hPaECMWF: getArray(rawHourly.temperature_700hPa_ecmwf_ifs)
    };
  }
  /**
   * Fetches the current weather data from Firebase for a specific station.
   *
   * @param stationId - The ID of the station to fetch weather for
   * @returns FirebaseCurrentWeather object containing the current weather data
   */
  async fetchFirebaseCurrentWeather(stationId) {
    const sid = stationId ?? "spippin";
    const path = `weather/${sid}/current`;
    const urlString = this.rainOwlsURLBase + path;
    try {
      const response = await this.httpAdapter.request({
        url: urlString,
        method: "GET",
        headers: {
          "x-api-key": this.firebaseAPIKey
        },
        timeout: this.timeout
      });
      return response.content;
    } catch (error) {
      this.logError("fetchFirebaseCurrentWeather", error, urlString);
      return createInvalidFirebaseCurrentWeather();
    }
  }
  /**
   * Fetches the daily weather extremes from Firebase for a specific station and date range.
   *
   * @param stationId - The ID of the station to fetch extremes for
   * @param startDate - The start date string in "YYYY-MM-DD" format
   * @param endDate - The end date string in "YYYY-MM-DD" format
   * @returns Array of FirebaseExtremes objects
   */
  async fetchFirebaseExtremes(stationId, startDate, endDate) {
    const sid = stationId ?? "spippin";
    const path = `daily/${sid}/extremes`;
    const urlString = `${this.rainOwlsURLBase}${path}?startDate=${startDate}&endDate=${endDate}`;
    try {
      const response = await this.httpAdapter.request({
        url: urlString,
        method: "GET",
        headers: {
          "x-api-key": this.firebaseAPIKey
        },
        timeout: this.timeout
      });
      return response.content;
    } catch (error) {
      this.logError("fetchFirebaseExtremes", error, urlString);
      return [];
    }
  }
  /**
   * Fetches weather station data from Synoptic API within a specified radius of a location.
   *
   * @param location - The Location object containing latitude and longitude
   * @param radiusMiles - The radius in miles to search for stations (default: 6)
   * @returns SynopticStationModel containing a list of weather stations and their data
   */
  async fetchWeatherSynoptic(location, radiusMiles = 6) {
    const locationString = `radius=${location.lat},${location.lng},${radiusMiles}`;
    const stationLimit = "limit=20";
    const statusString = "status=active&complete=1&obtimezone=local";
    const variables = this.synopticModelVars.join(",");
    const minMax = "minmax=2";
    const recent = "within=72";
    const formats = "units=english";
    const urlString = `${this.synopticBaseUrl}/v2/stations/latest?token=${this.synopticAPIKey}&${locationString}&${stationLimit}&${statusString}&vars=${variables}&${formats}&${minMax}&${recent}`;
    try {
      const isDirectApi = this.synopticBaseUrl.includes("api.synopticdata.com");
      const headers = {};
      if (isDirectApi) {
        headers["Origin"] = "https://www.weather.gov";
        headers["User-Agent"] = MOBILE_SAFARI_USER_AGENT;
      }
      const response = await this.httpAdapter.request({
        url: urlString,
        method: "GET",
        timeout: this.timeout,
        headers
      });
      const rawData = response.content;
      const summaryResponse = transformSynopticSummary(rawData);
      const hasValidSummary = summaryResponse.summary.numberOfObjects > 0 && summaryResponse.summary.responseCode >= 0;
      const hasStationsArray = rawData.STATION && Array.isArray(rawData.STATION) && rawData.STATION.length > 0;
      if (hasValidSummary || hasStationsArray) {
        if (!hasValidSummary && hasStationsArray) {
          console.log(
            `\u26A0\uFE0F Synoptic API: Summary reports no stations but STATION array has data (using stations anyway)`
          );
        }
        return transformSynopticResponse(rawData);
      } else {
        console.log(
          `fetchWeatherSynoptic: Summary indicates no stations (objects=${summaryResponse.summary.numberOfObjects}, code=${summaryResponse.summary.responseCode})`
        );
        this.logBodyPreview(
          JSON.stringify(rawData),
          "Synoptic payload (no stations)"
        );
      }
    } catch (error) {
      this.logError("fetchWeatherSynoptic", error, urlString);
    }
    console.log("fetchWeatherSynoptic: Returning invalid station");
    return { stations: [createInvalidSynopticStation()] };
  }
  /**
   * Fetches hourly precipitation intervals from Synoptic for the recent local station period.
   */
  async fetchSynopticPrecipitation(location, radiusMiles = 6, recentMinutes = 2880) {
    const locationString = `radius=${location.lat},${location.lng},${radiusMiles}`;
    const stationLimit = "limit=20";
    const statusString = "status=active";
    const recent = `recent=${recentMinutes}`;
    const precipitationMode = "pmode=intervals&interval=hour&obtimezone=local";
    const formats = "units=english";
    const urlString = `${this.synopticBaseUrl}/v2/stations/precipitation?token=${this.synopticAPIKey}&${locationString}&${stationLimit}&${statusString}&${recent}&${precipitationMode}&${formats}`;
    try {
      const isDirectApi = this.synopticBaseUrl.includes("api.synopticdata.com");
      const headers = {};
      if (isDirectApi) {
        headers["Origin"] = "https://www.weather.gov";
        headers["User-Agent"] = MOBILE_SAFARI_USER_AGENT;
      }
      const response = await this.httpAdapter.request({
        url: urlString,
        method: "GET",
        timeout: this.timeout,
        headers
      });
      const rawData = response.content;
      const summaryResponse = transformSynopticSummary(rawData);
      const hasValidSummary = summaryResponse.summary.numberOfObjects > 0 && summaryResponse.summary.responseCode >= 0;
      const hasStationsArray = rawData.STATION && Array.isArray(rawData.STATION) && rawData.STATION.length > 0;
      if (hasValidSummary || hasStationsArray) {
        return transformSynopticPrecipitationResponse(rawData);
      }
      console.log(
        `fetchSynopticPrecipitation: Summary indicates no stations (objects=${summaryResponse.summary.numberOfObjects}, code=${summaryResponse.summary.responseCode})`
      );
      this.logBodyPreview(
        JSON.stringify(rawData),
        "Synoptic precipitation payload (no stations)"
      );
    } catch (error) {
      this.logError("fetchSynopticPrecipitation", error, urlString);
    }
    console.log("fetchSynopticPrecipitation: Returning invalid precipitation model");
    return createInvalidSynopticPrecipitationModel();
  }
  /**
   * Fetches hourly weather data from the Open-Meteo API for a specified location.
   *
   * @param location - The Location object containing latitude and longitude
   * @returns OpenMeteoFetchResult with data and error information for fallback handling
   */
  async fetchOpenMeteoHourlyWeather(location) {
    const locationString = `latitude=${location.lat}&longitude=${location.lng}`;
    const hourlyWeatherStringVars = "hourly=" + this.openMeteoHourlyVars.join(",");
    const modelString = "models=" + this.openMeteoModelVars.join(",");
    const daysString = "forecast_days=9";
    const formats = this.openMeteoFormatVars.join("&");
    const urlString = `${this.openMeteoForecastUrl}?${locationString}&${hourlyWeatherStringVars}&${modelString}&${daysString}&${formats}`;
    const fallbackUrlString = this.openMeteoFallbackForecastUrl ? `${this.openMeteoFallbackForecastUrl}?${locationString}&${hourlyWeatherStringVars}&${modelString}&${daysString}&${formats}` : null;
    try {
      const response = await this.requestOpenMeteoWithFallback(
        urlString,
        fallbackUrlString,
        "fetchOpenMeteoHourlyWeather"
      );
      const statusCode = response && typeof response === "object" && "statusCode" in response ? response.statusCode : void 0;
      if (statusCode === 429) {
        console.error(
          "[fetchOpenMeteoHourlyWeather] Rate limit exceeded (429)"
        );
        return {
          data: null,
          error: "rate_limit",
          errorMessage: "Open-Meteo rate limit exceeded",
          statusCode: 429
        };
      }
      if (statusCode && statusCode >= 500) {
        console.error(
          `[fetchOpenMeteoHourlyWeather] Server error (${statusCode})`
        );
        return {
          data: null,
          error: "server",
          errorMessage: `Open-Meteo server error: ${statusCode}`,
          statusCode
        };
      }
      let rawData = response.content;
      if (typeof rawData === "string") {
        try {
          rawData = JSON.parse(rawData);
        } catch (parseError) {
          console.error(
            "[fetchOpenMeteoHourlyWeather] Failed to parse JSON response"
          );
          return {
            data: null,
            error: "parse",
            errorMessage: "Failed to parse Open-Meteo JSON response"
          };
        }
      }
      if (this.isOpenMeteoError(rawData)) {
        const reason = rawData.reason || "Unknown error";
        console.error(
          "[fetchOpenMeteoHourlyWeather] Open-Meteo error:",
          reason
        );
        const errorType = reason.toLowerCase().includes("rate") ? "rate_limit" : "server";
        return {
          data: null,
          error: errorType,
          errorMessage: reason
        };
      }
      const transformedData = {
        lat: rawData.latitude,
        lng: rawData.longitude,
        current: void 0,
        hourly: this.transformHourlyData(rawData.hourly),
        unitsCurrent: void 0,
        elevation: rawData.elevation,
        utcOffset: rawData.utc_offset_seconds,
        timezone: rawData.timezone,
        timezoneAbbr: rawData.timezone_abbreviation
      };
      return {
        data: transformedData,
        error: null
      };
    } catch (error) {
      this.logError("fetchOpenMeteoHourlyWeather", error, urlString);
      const errorMessage = error instanceof Error ? error.message : "Network request failed";
      return {
        data: null,
        error: "network",
        errorMessage
      };
    }
  }
  /**
   * Fetches ECMWF pressure level data for winter precipitation analysis.
   * Uses the dedicated ECMWF endpoint which provides pressure level variables
   * that aren't available through the generic forecast endpoint.
   *
   * @param location - The Location object containing latitude and longitude
   * @returns Object with pressure level arrays (850hPa, 925hPa, 700hPa temps)
   */
  async fetchECMWFPressureLevelData(location) {
    const locationString = `latitude=${location.lat}&longitude=${location.lng}`;
    const hourlyVars = "hourly=temperature_850hPa,temperature_925hPa,temperature_700hPa";
    const daysString = "forecast_days=9";
    const formats = this.openMeteoFormatVars.join("&");
    const urlString = `${this.openMeteoEcmwfUrl}?${locationString}&${hourlyVars}&${daysString}&${formats}`;
    const fallbackUrlString = this.openMeteoFallbackForecastUrl ? `${this.openMeteoFallbackForecastUrl}?${locationString}&${hourlyVars}&models=ecmwf_ifs025&${daysString}&${formats}` : null;
    try {
      const response = await this.requestOpenMeteoWithFallback(
        urlString,
        fallbackUrlString,
        "fetchECMWFPressureLevelData"
      );
      let rawData = response.content;
      if (typeof rawData === "string") {
        try {
          rawData = JSON.parse(rawData);
        } catch (parseError) {
          console.error(
            "[fetchECMWFPressureLevelData] Failed to parse JSON response"
          );
          return null;
        }
      }
      if (this.isOpenMeteoError(rawData)) {
        console.error(
          "[fetchECMWFPressureLevelData] ECMWF error:",
          rawData.reason
        );
        return null;
      }
      if (!rawData.hourly) {
        console.error(
          "[fetchECMWFPressureLevelData] No hourly data in response"
        );
        return null;
      }
      const hourly = rawData.hourly;
      return {
        time: Array.isArray(hourly.time) ? hourly.time : [],
        temp850hPa: Array.isArray(hourly.temperature_850hPa) ? hourly.temperature_850hPa : [],
        temp925hPa: Array.isArray(hourly.temperature_925hPa) ? hourly.temperature_925hPa : [],
        temp700hPa: Array.isArray(hourly.temperature_700hPa) ? hourly.temperature_700hPa : []
      };
    } catch (error) {
      this.logError("fetchECMWFPressureLevelData", error, urlString);
      return null;
    }
  }
  /**
   * Fetches current weather data for a specified location from the Open-Meteo API.
   *
   * @param locations - Array of Location objects containing latitude and longitude
   * @returns OpenMeteoFetchResultMultiple with data array and error information for fallback handling
   */
  async fetchWeather(locations) {
    if (locations.length === 0) {
      return { data: [], error: null };
    }
    const latitudes = locations.map((loc) => loc.lat).join(",");
    const longitudes = locations.map((loc) => loc.lng).join(",");
    const locationString = `latitude=${latitudes}&longitude=${longitudes}`;
    const modelString = "models=" + this.openMeteoModelVars.join(",");
    const currentWeatherStringVars = "current=" + this.openMeteoCurrentVars.join(",");
    const dayRangeStringVars = this.openMeteoDailyRangeVars.join("&");
    const dataVariables = currentWeatherStringVars;
    const formats = this.openMeteoFormatVars.join("&");
    const urlString = `${this.openMeteoForecastUrl}?${locationString}&${dataVariables}&${dayRangeStringVars}&${formats}&${modelString}`;
    const fallbackUrlString = this.openMeteoFallbackForecastUrl ? `${this.openMeteoFallbackForecastUrl}?${locationString}&${dataVariables}&${dayRangeStringVars}&${formats}&${modelString}` : null;
    try {
      const response = await this.requestOpenMeteoWithFallback(
        urlString,
        fallbackUrlString,
        "fetchWeather"
      );
      const statusCode = response && typeof response === "object" && "statusCode" in response ? response.statusCode : void 0;
      if (statusCode === 429) {
        console.error("[fetchWeather] Rate limit exceeded (429)");
        return {
          data: [],
          error: "rate_limit",
          errorMessage: "Open-Meteo rate limit exceeded",
          statusCode: 429
        };
      }
      if (statusCode && statusCode >= 500) {
        console.error(`[fetchWeather] Server error (${statusCode})`);
        return {
          data: [],
          error: "server",
          errorMessage: `Open-Meteo server error: ${statusCode}`,
          statusCode
        };
      }
      let rawData = response.content;
      if (typeof rawData === "string") {
        try {
          rawData = JSON.parse(rawData);
        } catch (parseError) {
          console.error("[fetchWeather] Failed to parse JSON response");
          return {
            data: [],
            error: "parse",
            errorMessage: "Failed to parse Open-Meteo JSON response"
          };
        }
      }
      if (this.isOpenMeteoError(rawData)) {
        const reason = rawData.reason || "Unknown error";
        console.error("[fetchWeather] Open-Meteo error:", reason);
        const errorType = reason.toLowerCase().includes("rate") ? "rate_limit" : "server";
        return {
          data: [],
          error: errorType,
          errorMessage: reason
        };
      }
      const rawDataArray = Array.isArray(rawData) ? rawData : [rawData];
      const data = rawDataArray.map((item) => {
        const transformedCurrent = {};
        if (item.current) {
          const c = item.current;
          transformedCurrent.time = c.time;
          transformedCurrent.temperature = c.temperature_2m;
          transformedCurrent.temperatureApparent = c.apparent_temperature;
          transformedCurrent.humidity = c.relative_humidity_2m;
          transformedCurrent.windBearing = c.wind_direction_10m;
          transformedCurrent.windSpeed = c.wind_speed_10m;
          transformedCurrent.pressure = c.pressure_msl || c.surface_pressure;
          transformedCurrent.rainRate = c.rain || 0;
          transformedCurrent.snowRate = c.snowfall || 0;
          transformedCurrent.weatherCode = c.weather_code;
          transformedCurrent.isDay = c.is_day;
          transformedCurrent.cloudCover = c.cloud_cover;
        }
        return {
          lat: item.latitude,
          lng: item.longitude,
          current: transformedCurrent,
          hourly: this.transformHourlyData(item.hourly),
          unitsCurrent: item.current_units,
          elevation: item.elevation,
          utcOffset: item.utc_offset_seconds,
          timezone: item.timezone,
          timezoneAbbr: item.timezone_abbreviation
        };
      });
      return { data, error: null };
    } catch (error) {
      this.logError("fetchWeather", error, urlString);
      const errorMessage = error instanceof Error ? error.message : "Network request failed";
      return {
        data: [],
        error: "network",
        errorMessage
      };
    }
  }
  /**
   * Fetches minimal current conditions for multiple locations (saved location cards).
   * Uses only the default model (best_match) and requests only temperature, weather code,
   * and is_day — costing 1 API call per location instead of 4.
   *
   * @param locations - Array of Location objects containing latitude and longitude
   * @returns OpenMeteoFetchResultMultiple with minimal current data
   */
  async fetchCurrentSummaries(locations) {
    if (locations.length === 0) {
      return { data: [], error: null };
    }
    const latitudes = locations.map((loc) => loc.lat).join(",");
    const longitudes = locations.map((loc) => loc.lng).join(",");
    const locationString = `latitude=${latitudes}&longitude=${longitudes}`;
    const currentVars = "current=temperature_2m,weather_code,is_day";
    const formats = this.openMeteoFormatVars.join("&");
    const urlString = `${this.openMeteoForecastUrl}?${locationString}&${currentVars}&${formats}`;
    const fallbackUrlString = this.openMeteoFallbackForecastUrl ? `${this.openMeteoFallbackForecastUrl}?${locationString}&${currentVars}&${formats}` : null;
    try {
      const response = await this.requestOpenMeteoWithFallback(
        urlString,
        fallbackUrlString,
        "fetchCurrentSummaries"
      );
      const statusCode = response && typeof response === "object" && "statusCode" in response ? response.statusCode : void 0;
      if (statusCode === 429) {
        return {
          data: [],
          error: "rate_limit",
          errorMessage: "Open-Meteo rate limit exceeded",
          statusCode: 429
        };
      }
      if (statusCode && statusCode >= 500) {
        return {
          data: [],
          error: "server",
          errorMessage: `Open-Meteo server error: ${statusCode}`,
          statusCode
        };
      }
      let rawData = response.content;
      if (typeof rawData === "string") {
        try {
          rawData = JSON.parse(rawData);
        } catch {
          return {
            data: [],
            error: "parse",
            errorMessage: "Failed to parse Open-Meteo JSON response"
          };
        }
      }
      if (this.isOpenMeteoError(rawData)) {
        const reason = rawData.reason || "Unknown error";
        const errorType = reason.toLowerCase().includes("rate") ? "rate_limit" : "server";
        return { data: [], error: errorType, errorMessage: reason };
      }
      const rawDataArray = Array.isArray(rawData) ? rawData : [rawData];
      const data = rawDataArray.map((item) => {
        const transformedCurrent = {};
        if (item.current) {
          transformedCurrent.temperature = item.current.temperature_2m;
          transformedCurrent.weatherCode = item.current.weather_code;
          transformedCurrent.isDay = item.current.is_day;
        }
        return {
          lat: item.latitude,
          lng: item.longitude,
          current: transformedCurrent,
          hourly: void 0,
          unitsCurrent: item.current_units,
          elevation: item.elevation,
          utcOffset: item.utc_offset_seconds,
          timezone: item.timezone,
          timezoneAbbr: item.timezone_abbreviation
        };
      });
      return { data, error: null };
    } catch (error) {
      this.logError("fetchCurrentSummaries", error, urlString);
      const errorMessage = error instanceof Error ? error.message : "Network request failed";
      return { data: [], error: "network", errorMessage };
    }
  }
  /**
   * Fetches current wind speed and direction for a grid of coordinates.
   * Used by the wind particle overlay to animate wind flow on maps.
   * Only requests current conditions (no forecast data) for minimal payload.
   *
   * @param latitudes - Array of latitude values for grid points
   * @param longitudes - Array of longitude values for grid points
   * @returns Array of { wind_speed_10m, wind_direction_10m } objects, or null on error
   */
  async fetchWindGrid(latitudes, longitudes) {
    if (latitudes.length === 0 || longitudes.length === 0) return null;
    const locationString = `latitude=${latitudes.join(",")}&longitude=${longitudes.join(",")}`;
    const currentVars = "current=wind_speed_10m,wind_direction_10m";
    const formats = "wind_speed_unit=mph&timeformat=unixtime&forecast_days=0";
    const urlString = `${this.openMeteoWindForecastUrl}?${locationString}&${currentVars}&${formats}`;
    try {
      const fallbackUrl = this.openMeteoFallbackForecastUrl ? `${this.openMeteoFallbackForecastUrl}?${locationString}&${currentVars}&${formats}` : null;
      const response = await this.requestOpenMeteoWithFallback(urlString, fallbackUrl, "fetchWindGrid");
      if (response.statusCode !== 200) return null;
      let rawData = response.content;
      if (typeof rawData === "string") {
        try {
          rawData = JSON.parse(rawData);
        } catch {
          console.error("[fetchWindGrid] Failed to parse JSON response");
          return null;
        }
      }
      if (this.isOpenMeteoError(rawData)) {
        console.warn("[fetchWindGrid] API error:", rawData.reason);
        return null;
      }
      const results = Array.isArray(rawData) ? rawData : [rawData];
      return results.map((item) => ({
        wind_speed_10m: item?.current?.wind_speed_10m ?? 0,
        wind_direction_10m: item?.current?.wind_direction_10m ?? 0
      }));
    } catch (error) {
      this.logError("fetchWindGrid", error, urlString);
      return null;
    }
  }
  /** Shared NWS transport: validate HTTP status before decoding a forecast or alert. */
  async requestNWS(url, timeout = this.timeout) {
    const response = await this.httpAdapter.request({
      url,
      method: "GET",
      headers: {
        "User-Agent": "RainOwls-Weather-App/1.0 (contact@rainowls.com)",
        Accept: "application/geo+json"
      },
      timeout
    });
    if (response.statusCode !== 200) {
      throw new Error(`NWS request failed (${response.statusCode})`);
    }
    const content = typeof response.content === "string" ? JSON.parse(response.content) : response.content;
    if (!content || typeof content !== "object" || Array.isArray(content)) {
      throw new Error("Invalid NWS response");
    }
    return content;
  }
  /**
   * Fetches National Weather Service alerts for a specified zone.
   *
   * @param zoneUrl - The zone URL or zone ID
   * @returns NWSAlerts object containing active alerts
   */
  async fetchNWSAlerts(zoneUrl) {
    const zone = zoneUrl.split("/").pop() ?? "";
    const fullURL = this.nwsAlertsURL + zone;
    try {
      return await this.requestNWS(fullURL);
    } catch (error) {
      this.logError("fetchNWSAlerts", error, fullURL);
      return {
        features: [],
        updated: ""
      };
    }
  }
  /**
   * Fetches the National Weather Service API URLs for a given location.
   *
   * @param location - The Location object containing latitude and longitude
   * @returns NWSWeatherProperties object containing API endpoints
   */
  async fetchNWSURLS(location) {
    const locationString = `${location.lat.toFixed(4)},${location.lng.toFixed(4)}`;
    const urlString = this.nwsEndPointsURL + locationString;
    console.log(`fetchNWSURLS::URLS ${urlString}`);
    try {
      return await this.requestNWS(urlString);
    } catch (error) {
      this.logError("fetchNWSURLS", error, urlString);
      return {
        id: "",
        type: "",
        properties: {
          forecastOffice: "",
          gridId: "",
          gridX: -1,
          gridY: -1,
          forecast: "",
          forecastHourly: "",
          forecastGridData: "",
          observationStations: "",
          forecastZone: "",
          county: "",
          fireWeatherZone: "",
          timeZone: ""
        }
      };
    }
  }
  /**
   * Fetches the hourly grid weather forecast from the National Weather Service (NWS) using the provided URL.
   *
   * @param url - The forecast grid data URL from NWS
   * @returns NWSForecastHourlyGrid object containing hourly grid forecast
   */
  async fetchNWSHourlyGridWeather(url) {
    try {
      return await this.requestNWS(url, 1e4);
    } catch (error) {
      this.logError("fetchNWSHourlyGridWeather", error, url);
      return createInvalidNWSForecastHourlyGrid();
    }
  }
  /**
   * Fetches NWS daily forecast from the forecast endpoint.
   * This provides day/night periods with pre-aggregated high/low temps.
   *
   * @param url - The forecast URL from NWSWeatherPoints.forecast
   * @returns NWSForecastDaily object containing daily forecast periods
   */
  async fetchNWSDailyForecast(url) {
    try {
      return await this.requestNWS(url);
    } catch (error) {
      this.logError("fetchNWSDailyForecast", error, url);
      return null;
    }
  }
  /** Fetch the current RainViewer frames with their provider-supplied tile paths. */
  async fetchRainViewerFrames() {
    try {
      const response = await this.httpAdapter.request({
        url: this.rainViewerFramesURL,
        method: "GET",
        timeout: this.timeout
      });
      const data = response.content;
      if (typeof data?.host !== "string" || !Array.isArray(data.radar?.past)) {
        return [];
      }
      const host = new URL(data.host);
      if (host.protocol !== "https:") return [];
      return data.radar.past.flatMap((frame) => {
        if (!Number.isSafeInteger(frame?.time) || frame.time <= 0 || typeof frame.path !== "string" || !/^\/v2\/radar\/[a-zA-Z0-9_-]+$/.test(frame.path)) {
          return [];
        }
        return [{ epoch: frame.time, tileUrlBase: host.origin + frame.path }];
      });
    } catch (error) {
      this.logError(
        "fetchRainViewerFrames",
        error,
        this.rainViewerFramesURL
      );
      return [];
    }
  }
  // MARK: - Error logging helpers
  /**
   * Logs error details for debugging
   */
  logError(functionName, error, url) {
    if (error instanceof Error) {
      console.error(
        `${functionName}: Network request failed -> ${error.message}`
      );
      if (url) {
        console.error(`  URL: ${url}`);
      }
      const httpError = error;
      if (httpError.response) {
        console.error(
          `  Status: ${httpError.response.statusCode || httpError.response.status}`
        );
        if (httpError.response.content) {
          this.logBodyPreview(
            httpError.response.content.toString(),
            `${functionName} response body`
          );
        }
      }
    } else {
      console.error(`${functionName}: Unexpected error -> ${error}`);
      if (url) {
        console.error(`  URL: ${url}`);
      }
    }
  }
  /**
   * Logs a preview of response body for debugging
   */
  logBodyPreview(body, label) {
    const preview = body.length > 2e3 ? body.substring(0, 2e3) + "\u2026(truncated)" : body;
    console.log(`${label}:
${preview}`);
  }
};
_RONetworkManager.instance = null;
var RONetworkManager = _RONetworkManager;
var networkManager = RONetworkManager.getInstance();
var STATION_MAX_AGE_MS = 6 * 60 * 1e3;
function parseStationTimestamp(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  const raw = value.trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(raw)) {
    const formatter = new Intl.DateTimeFormat("sv-SE", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23"
    });
    const candidates = ["-07:00", "-08:00"].map((offset) => parseISO(raw + offset)).filter((date2) => {
      if (!Number.isFinite(date2.getTime())) return false;
      const parts = Object.fromEntries(formatter.formatToParts(date2).map((part) => [part.type, part.value]));
      return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}` === raw;
    });
    return candidates.length === 1 ? candidates[0] : null;
  }
  const numeric = /^\d+(?:\.\d+)?$/.test(raw) ? Number(raw) : null;
  const date = numeric !== null ? new Date(numeric < 1e12 ? numeric * 1e3 : numeric) : /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(
    raw
  ) ? parseISO(raw) : null;
  return date && Number.isFinite(date.getTime()) ? date : null;
}
function freshStationTimestamp(value, now = Date.now()) {
  const date = parseStationTimestamp(value);
  if (!date) return null;
  const age = now - date.getTime();
  return age >= -6e4 && age <= STATION_MAX_AGE_MS ? date : null;
}

// src/utils/weatherValidation.ts
var INVALID_DOUBLE_VALUE = -999;
var INVALID_INT_VALUE = -999;
function validateCurrentWeatherObservation(observation, modeledHours) {
  const result = {
    temperatureScore: 0,
    weatherScore: null,
    isValid: false
  };
  if (modeledHours.length === 0) {
    return result;
  }
  const obsTime = observation.timestamp.getTime();
  let closestHour = null;
  let minTimeDiff = Infinity;
  for (const hour of modeledHours) {
    const timeDiff = Math.abs(hour.startTime.getTime() - obsTime);
    if (timeDiff < minTimeDiff) {
      minTimeDiff = timeDiff;
      closestHour = hour;
    }
  }
  if (!closestHour) {
    return result;
  }
  const threeHoursMs = 3 * 60 * 60 * 1e3;
  if (minTimeDiff > threeHoursMs) {
    return result;
  }
  const tempDiff = Math.abs(observation.temperature - closestHour.temperature);
  if (tempDiff <= 2) {
    result.temperatureScore = 100;
  } else if (tempDiff <= 5) {
    result.temperatureScore = 80;
  } else if (tempDiff <= 10) {
    result.temperatureScore = 50;
  } else if (tempDiff <= 15) {
    result.temperatureScore = 30;
  } else {
    result.temperatureScore = 10;
  }
  if (observation.weatherCode !== INVALID_INT_VALUE && observation.weatherCode >= 0) {
    const weatherCodeMatch = observation.weatherCode === closestHour.weatherCode;
    const getWeatherCategory = (code) => {
      if (code === 0) return "clear";
      if (code >= 1 && code <= 3) return "cloudy";
      if (code >= 45 && code <= 48) return "fog";
      if (code >= 51 && code <= 67) return "rain";
      if (code >= 71 && code <= 77) return "snow";
      if (code >= 80 && code <= 82) return "rain_showers";
      if (code >= 85 && code <= 86) return "snow_showers";
      if (code >= 95 && code <= 99) return "thunderstorm";
      return "unknown";
    };
    const obsCategory = getWeatherCategory(observation.weatherCode);
    const modelCategory = getWeatherCategory(closestHour.weatherCode);
    if (weatherCodeMatch) {
      result.weatherScore = 100;
    } else if (obsCategory === modelCategory) {
      result.weatherScore = 60;
    } else {
      result.weatherScore = 20;
    }
  }
  result.isValid = result.temperatureScore >= 30;
  return result;
}
function getDistanceMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLng = (lng2 - lng1) * (Math.PI / 180);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance2 = R * c;
  return distance2;
}

// src/services/RainOwlsMain.ts
var RAINOWLS_STATIONS = {
  spippin: { lat: SPIPPIN_LOCATION.latitude, lng: SPIPPIN_LOCATION.longitude },
  // Forest Grove, OR
  dpippin: { lat: 45.423173, lng: -122.83921 },
  // Tigard, OR
  jpippin: { lat: 45.557064, lng: -122.849246 }
  // Portland, OR
};
var RainOwlsMain = class _RainOwlsMain {
  constructor() {
    this.synopticPrecipitationTimeoutMs = 4e3;
    // Weather data
    this._homeWeather = new MainWeatherInfo();
    this._searchedHomeWeather = new MainWeatherInfo();
    // Location tracking
    this._isTrackedLocation = true;
    this._locationTracked = null;
    this._locationSelected = null;
    // Data source mode (for testing NWS fallback)
    this._dataSourceMode = "auto";
    // Optional observation-source opt-out for backend callers
    this._skipSynoptic = false;
    this.nwsAlertsEnabled = true;
    // Weather class instances
    this.omWeather = new WeatherOMClass();
    this.synWeather = new WeatherSYNClass();
    this.nwsWeather = new WeatherNWSClass();
    // Throttling
    this.lastFetchAt = null;
    this.lastFetchLocationKey = null;
    // Subscribers
    this.subscribers = /* @__PURE__ */ new Set();
    this.searchedSubscribers = /* @__PURE__ */ new Set();
    // Separate alerts tracking (to avoid race conditions with weather updates)
    this._alerts = [];
    this.alertSubscribers = /* @__PURE__ */ new Set();
    this.networkManager = RONetworkManager.getInstance();
  }
  /**
   * Get singleton instance
   */
  static getInstance() {
    if (!_RainOwlsMain.instance) {
      _RainOwlsMain.instance = new _RainOwlsMain();
    }
    return _RainOwlsMain.instance;
  }
  /**
   * Get current home weather data
   */
  get homeWeather() {
    return this._homeWeather;
  }
  /**
   * Get searched location weather data
   */
  get searchedHomeWeather() {
    return this._searchedHomeWeather;
  }
  /**
   * Get/set tracked location
   */
  get locationTracked() {
    return this._locationTracked;
  }
  set locationTracked(location) {
    const previousLocation = this._locationTracked;
    this._locationTracked = location;
    if (location && location.name && location.name !== "Locating...") {
      const shouldUpdate = (
        // Weather location matches coordinates
        this._homeWeather.location && this._homeWeather.location.lat === location.lat && this._homeWeather.location.lng === location.lng || // Or weather location name is still "Locating..."
        this._homeWeather.locationName === "Locating..." || // Or previous location matches (same location being updated)
        previousLocation && previousLocation.lat === location.lat && previousLocation.lng === location.lng
      );
      if (shouldUpdate) {
        this._homeWeather.location = location;
        this._homeWeather.locationName = location.name;
        this.notifySubscribers();
      }
    }
  }
  /**
   * Get/set selected location
   */
  get locationSelected() {
    return this._locationSelected;
  }
  set locationSelected(location) {
    this._locationSelected = location;
  }
  /**
   * Get/set location tracking mode
   */
  get isTrackedLocation() {
    return this._isTrackedLocation;
  }
  set isTrackedLocation(value) {
    this._isTrackedLocation = value;
  }
  /**
   * Get/set data source mode
   * - "auto": Use Open-Meteo as primary, NWS as fallback (default)
   * - "openmeteo": Open-Meteo forecasts with station observations
   * - "nws": Force NWS only (for testing)
   */
  get dataSourceMode() {
    return this._dataSourceMode;
  }
  set dataSourceMode(mode) {
    this._dataSourceMode = mode;
    console.log(`[RainOwlsMain] Data source mode set to: ${mode}`);
  }
  /**
   * Get/set skip Synoptic flag
   * When true, skips Synoptic API calls (for web app where endpoint is not available)
   */
  get skipSynoptic() {
    return this._skipSynoptic;
  }
  set skipSynoptic(value) {
    this._skipSynoptic = value;
    console.log(`[RainOwlsMain] Skip Synoptic set to: ${value}`);
  }
  /**
   * Subscribe to home weather updates
   */
  subscribe(callback) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }
  /**
   * Subscribe to searched weather updates
   */
  subscribeToSearched(callback) {
    this.searchedSubscribers.add(callback);
    return () => this.searchedSubscribers.delete(callback);
  }
  /**
   * Get current alerts
   */
  get alerts() {
    return this._alerts;
  }
  /**
   * Subscribe to alerts updates (separate from weather to avoid race conditions)
   */
  subscribeToAlerts(callback) {
    this.alertSubscribers.add(callback);
    return () => this.alertSubscribers.delete(callback);
  }
  /**
   * Notify alert subscribers
   */
  notifyAlertSubscribers() {
    this.alertSubscribers.forEach((callback) => callback(this._alerts));
  }
  /**
   * Notify all subscribers
   */
  notifySubscribers(searched = false) {
    const weather = searched ? this._searchedHomeWeather : this._homeWeather;
    const subs = searched ? this.searchedSubscribers : this.subscribers;
    subs.forEach((callback) => callback(weather));
  }
  /**
   * Generate location key for throttling
   */
  locationKey(loc, precision = 2) {
    const round2 = (value, p) => {
      const multiplier = Math.pow(10, p);
      return Math.round(value * multiplier) / multiplier;
    };
    return `${round2(loc.lat, precision)}|${round2(loc.lng, precision)}`;
  }
  /**
   * Check if we should start a new fetch (throttling)
   */
  shouldStartFetch(loc, throttleSeconds = 3) {
    const key = this.locationKey(loc);
    const now = /* @__PURE__ */ new Date();
    if (this.lastFetchAt && this.lastFetchLocationKey) {
      const timeSinceLastFetch = (now.getTime() - this.lastFetchAt.getTime()) / 1e3;
      if (timeSinceLastFetch < throttleSeconds && this.lastFetchLocationKey === key) {
        console.log(
          `Fetch Weather: Skipping duplicate (<=${throttleSeconds}s) for ~same location ${key}`
        );
        return false;
      }
    }
    this.lastFetchAt = now;
    this.lastFetchLocationKey = key;
    return true;
  }
  /**
   * Set loading states
   */
  setLoading(searchedLocation = false, loading = true) {
    const weather = searchedLocation ? this._searchedHomeWeather : this._homeWeather;
    weather.isFirebaseLoading = loading;
    weather.isSynopticLoading = loading;
    weather.isOMHourlyLoading = loading;
    weather.isOMDailyLoading = loading;
    weather.isNWSAlertsLoading = loading;
  }
  /**
   * Determine nearest RainOwls station
   */
  getNearestRainOwlsStation(location, maxMileageRange = 7) {
    const spippinDistance = getDistanceMiles(
      RAINOWLS_STATIONS.spippin.lat,
      RAINOWLS_STATIONS.spippin.lng,
      location.lat,
      location.lng
    );
    const dpippinDistance = getDistanceMiles(
      RAINOWLS_STATIONS.dpippin.lat,
      RAINOWLS_STATIONS.dpippin.lng,
      location.lat,
      location.lng
    );
    const jpippinDistance = getDistanceMiles(
      RAINOWLS_STATIONS.jpippin.lat,
      RAINOWLS_STATIONS.jpippin.lng,
      location.lat,
      location.lng
    );
    if (spippinDistance < maxMileageRange) {
      return { stationId: "spippin", isRainOwlsStation: true };
    } else if (dpippinDistance < maxMileageRange) {
      return { stationId: "dpippin", isRainOwlsStation: true };
    } else if (jpippinDistance < maxMileageRange) {
      return { stationId: "jpippin", isRainOwlsStation: true };
    }
    return { stationId: "ModelData", isRainOwlsStation: false };
  }
  /**
   * Convert Firebase extremes values to RODailyWeatherExtremes
   */
  convertFirebaseExtremesToROExtremes(values) {
    const extremes = createDefaultExtremes();
    if (!values) return extremes;
    if (values.temperature !== void 0) {
      extremes.temperature = values.temperature;
      extremes.temperatureTimestamp = values.temperatureTimestamp ? new Date(values.temperatureTimestamp) : /* @__PURE__ */ new Date(0);
    }
    if (values.humidity !== void 0) {
      extremes.humidity = values.humidity;
      extremes.humidityTimestamp = values.humidityTimestamp ? new Date(values.humidityTimestamp) : /* @__PURE__ */ new Date(0);
    }
    if (values.pressure !== void 0) {
      extremes.pressure = values.pressure;
      extremes.pressureTimestamp = values.pressureTimestamp ? new Date(values.pressureTimestamp) : /* @__PURE__ */ new Date(0);
    }
    if (values.windSpeed !== void 0) {
      extremes.windSpeed = values.windSpeed;
      extremes.windSpeedTimestamp = values.windSpeedTimestamp ? new Date(values.windSpeedTimestamp) : /* @__PURE__ */ new Date(0);
    }
    if (values.rainRate !== void 0) {
      extremes.rainRate = values.rainRate;
      extremes.rainRateTimestamp = values.rainRateTimestamp ? new Date(values.rainRateTimestamp) : void 0;
    }
    if (values.rainHour !== void 0) {
      extremes.rainHour = values.rainHour;
      extremes.rainHourTimestamp = values.rainHourTimestamp ? new Date(values.rainHourTimestamp) : void 0;
    }
    return extremes;
  }
  /**
   * Fetch weather for a location
   */
  async fetchWeather(location = null, searchedLocation = false) {
    const fetchLocation = location || this.determineFetchLocation(searchedLocation);
    if (!this.shouldStartFetch(fetchLocation)) {
      return;
    }
    const homeWeatherInfo = new MainWeatherInfo();
    this.setLoading(searchedLocation, true);
    homeWeatherInfo.location = fetchLocation;
    homeWeatherInfo.isLocationUSA = homeWeatherInfo.location.country.toLowerCase().includes("united states");
    const { stationId, isRainOwlsStation } = this.getNearestRainOwlsStation(
      homeWeatherInfo.location
    );
    homeWeatherInfo.stationId = stationId;
    homeWeatherInfo.isRainOwlsStation = isRainOwlsStation;
    console.log(
      `Fetching weather for: ${homeWeatherInfo.location.lat}, ${homeWeatherInfo.location.lng}, ${homeWeatherInfo.location.name}`
    );
    console.log(`[RainOwlsMain] Nearby RainOwls station candidate: ${stationId}`);
    this.omWeather = new WeatherOMClass();
    this.synWeather = new WeatherSYNClass();
    this.nwsWeather = new WeatherNWSClass();
    this.omWeather.setLocation(homeWeatherInfo.location);
    homeWeatherInfo.locationName = homeWeatherInfo.location.name;
    await this.fetchAndUpdateAllWeather(homeWeatherInfo);
    console.log(
      `[RainOwlsMain] Current observation source: ${homeWeatherInfo.currentObservationSource}; station: ${homeWeatherInfo.stationId}`
    );
    const hasCurrent = isWeatherValueValid(homeWeatherInfo.temperature, "temperature" /* Temperature */);
    const hasForecast = homeWeatherInfo.hours.hours.length > 0 && homeWeatherInfo.dailyWeather.days.length > 0;
    homeWeatherInfo.availabilityError = !hasCurrent && !hasForecast ? "Weather is unavailable. Please try refreshing." : !hasForecast ? "Forecast unavailable. Showing available current observations." : !hasCurrent ? "Current conditions unavailable. Showing the forecast." : null;
    if (searchedLocation) {
      this._searchedHomeWeather = homeWeatherInfo;
    } else {
      this._homeWeather = homeWeatherInfo;
    }
    this.notifySubscribers(searchedLocation);
  }
  /**
   * Determine which location to fetch weather for
   */
  determineFetchLocation(searchedLocation) {
    if (this._isTrackedLocation && !searchedLocation && this._locationTracked) {
      return this._locationTracked;
    }
    if (this._locationSelected) {
      return this._locationSelected;
    }
    return defaultLocation;
  }
  /**
   * Fetch RainOwls personal weather station data
   */
  async fetchRainOwlsStationData(homeWeatherInfo, stationId) {
    try {
      homeWeatherInfo.isRainOwlsStation = true;
      const today = /* @__PURE__ */ new Date();
      const twoDaysAgo = new Date(today);
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
      const formatDate = (d) => d.toISOString().split("T")[0];
      const startDate = formatDate(twoDaysAgo);
      const endDate = formatDate(today);
      const [rainOwlsData, extremesData] = await Promise.all([
        this.networkManager.fetchFirebaseCurrentWeather(stationId),
        this.networkManager.fetchFirebaseExtremes(
          stationId,
          startDate,
          endDate
        )
      ]);
      const observationTimestamp = freshStationTimestamp(
        rainOwlsData.timestamp
      );
      const parsedTemperature = Number(rainOwlsData.temperature);
      const roTemperature = rainOwlsData.temperature?.trim() && Number.isFinite(parsedTemperature) ? parsedTemperature : INVALID_DOUBLE_VALUE;
      if (!observationTimestamp || !isWeatherValueValid(roTemperature, "temperature" /* Temperature */)) {
        console.warn(
          `[RainOwlsMain] RainOwls station ${stationId} rejected: ${!observationTimestamp ? "timestamp is missing, invalid, stale, or in the future" : "temperature is invalid"}; timestamp=${rainOwlsData.timestamp ?? "missing"}. Trying fallback sources.`
        );
        homeWeatherInfo.isRainOwlsStation = false;
        homeWeatherInfo.stationId = "Invalid";
        homeWeatherInfo.isFirebaseLoading = false;
        return;
      }
      homeWeatherInfo.currentObservationTimestamp = observationTimestamp;
      homeWeatherInfo.currentObservationSource = "firebase";
      const roHumidity = Number(rainOwlsData.humidity) || INVALID_DOUBLE_VALUE;
      const roDewPoint = getDewPoint(roTemperature, roHumidity);
      const roWindSpeed = this.sanitizeWindSpeed(
        Number(rainOwlsData.windSpeed)
      );
      const roWindBearing = this.sanitizeWindDirection(
        Number(rainOwlsData.windDirection)
      );
      const roApparentTemperature = apparentTemperatureBOM(
        roTemperature,
        roHumidity,
        roWindSpeed,
        "Imperial"
      );
      const roPressure = Number(rainOwlsData.pressure) || INVALID_INT_VALUE;
      const roRainDay = Number(rainOwlsData.rain) || 0;
      const stormReading = rainOwlsData.rainStorm?.trim();
      const stormAmount = stormReading ? Number(stormReading) : NaN;
      const roRainStorm = Number.isFinite(stormAmount) && stormAmount >= 0 ? stormAmount : INVALID_DOUBLE_VALUE;
      const roRainRate = Number(rainOwlsData.rainRate) || 0;
      const roRainYesterday = Number(rainOwlsData.rainYesterday) || 0;
      const roRainMonth = Number(rainOwlsData.rainMonth) || 0;
      const roRainYear = Number(rainOwlsData.rainYear) || 0;
      homeWeatherInfo.temperature = roTemperature;
      homeWeatherInfo.humidity = roHumidity;
      homeWeatherInfo.dewPoint = roDewPoint;
      homeWeatherInfo.windSpeed = roWindSpeed;
      homeWeatherInfo.windDirection = roWindBearing;
      homeWeatherInfo.pressure = roPressure;
      homeWeatherInfo.apparentTemperature = roApparentTemperature;
      homeWeatherInfo.rainDay = roRainDay;
      homeWeatherInfo.rainRate = roRainRate;
      homeWeatherInfo.rainStorm = roRainStorm;
      homeWeatherInfo.rainYesterday = roRainYesterday;
      homeWeatherInfo.rainYear = roRainYear;
      homeWeatherInfo.rainMonth = roRainMonth;
      if (roRainRate > 0) {
        if (roRainRate >= 0.3) {
          homeWeatherInfo.weatherCode = 65;
        } else if (roRainRate >= 0.1) {
          homeWeatherInfo.weatherCode = 63;
        } else {
          homeWeatherInfo.weatherCode = 61;
        }
      } else {
        homeWeatherInfo.weatherCode = -1;
      }
      const sortedData = [...extremesData].sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
      });
      if (sortedData.length > 0 && sortedData[0]) {
        homeWeatherInfo.dailyMinimums = this.convertFirebaseExtremesToROExtremes(sortedData[0].minimums);
        homeWeatherInfo.dailyMaximums = this.convertFirebaseExtremesToROExtremes(sortedData[0].maximums);
      }
      if (sortedData.length > 1 && sortedData[1]) {
        homeWeatherInfo.yesterdayMinimums = this.convertFirebaseExtremesToROExtremes(sortedData[1].minimums);
        homeWeatherInfo.yesterdayMaximums = this.convertFirebaseExtremesToROExtremes(sortedData[1].maximums);
      }
      homeWeatherInfo.isFirebaseLoading = false;
    } catch (error) {
      console.error("Error fetching RainOwls station data:", error);
      homeWeatherInfo.isFirebaseLoading = false;
      homeWeatherInfo.isRainOwlsStation = false;
    }
  }
  /**
   * Fetch NWS alerts for a US location
   */
  async fetchNWSAlertsForLocation(location) {
    if (!this.nwsAlertsEnabled) return [];
    const nwsUrls = await this.networkManager.fetchNWSURLS(location);
    if (!nwsUrls.properties?.forecastZone) {
      console.log("[NWS Alerts] No forecast zone available for location");
      return [];
    }
    const zoneUrl = nwsUrls.properties.forecastZone;
    const alertsResponse = await this.networkManager.fetchNWSAlerts(zoneUrl);
    if (!alertsResponse.features || alertsResponse.features.length === 0) {
      console.log("[NWS Alerts] No active alerts for zone");
      return [];
    }
    const alertsProcessor = new AlertsNWSClass();
    alertsProcessor.setAlertValues(alertsResponse.features);
    console.log(
      `[NWS Alerts] Found ${alertsProcessor.alerts.length} active alerts`
    );
    return alertsProcessor.alerts;
  }
  /**
   * Fetch NWS weather data as fallback when Open-Meteo fails.
   * Only works for US locations.
   *
   * @param homeWeatherInfo - The weather info object to populate
   * @param useForHourly - If true, use NWS for hourly forecast
   * @param useForCurrent - If true, use NWS for current conditions
   */
  async fetchNWSFallbackData(homeWeatherInfo, useForHourly = true, useForDaily = true, useForCurrent = true) {
    try {
      console.log("[NWS Fallback] Fetching NWS data as Open-Meteo fallback...");
      const nwsUrls = await this.networkManager.fetchNWSURLS(
        homeWeatherInfo.location
      );
      if (!nwsUrls.properties?.forecastGridData) {
        console.warn("[NWS Fallback] No grid data URL available for location");
        return;
      }
      const fetchPromises = [];
      let gridDataPromise;
      let dailyDataPromise;
      gridDataPromise = this.networkManager.fetchNWSHourlyGridWeather(
        nwsUrls.properties.forecastGridData
      );
      fetchPromises.push(gridDataPromise);
      if (useForDaily && nwsUrls.properties?.forecast) {
        dailyDataPromise = this.networkManager.fetchNWSDailyForecast(
          nwsUrls.properties.forecast
        );
        fetchPromises.push(dailyDataPromise);
      } else {
        fetchPromises.push(Promise.resolve(null));
      }
      const [nwsGridData, nwsDailyData] = await Promise.all(fetchPromises);
      if (!nwsGridData?.properties?.temperature?.values?.length) {
        console.warn("[NWS Fallback] No valid NWS grid data returned");
        return;
      }
      const nwsTimezone = nwsUrls.properties?.timeZone || null;
      if (nwsTimezone) {
        homeWeatherInfo.timeZone = nwsTimezone;
        homeWeatherInfo.timeZoneIdentifier = nwsTimezone;
      }
      this.nwsWeather.setLocation(homeWeatherInfo.location);
      this.nwsWeather.setTimeZone(
        nwsTimezone || "America/Los_Angeles",
        homeWeatherInfo.timeZoneOffsetFromGMT
      );
      this.nwsWeather.setHourlyGridValues(nwsGridData);
      if (!this.nwsWeather.hasValidData()) {
        console.warn("[NWS Fallback] WeatherNWSClass failed to parse NWS data");
        return;
      }
      const hourCount = this.nwsWeather.hourlyWeather.hours.length;
      const daysCovered = Math.floor(hourCount / 24);
      console.log(
        `[NWS Fallback] Successfully parsed ${hourCount} hours of NWS data (~${daysCovered} days)`
      );
      if (useForHourly) {
        if (this.omWeather.hourlyWeather?.hours?.length > 0 && this.nwsWeather.hourlyWeather.hours.length > 0) {
          this.nwsWeather.fillMissingHours(
            this.nwsWeather.hourlyWeather.hours,
            this.omWeather.hourlyWeather.hours
          );
          homeWeatherInfo.hours = this.nwsWeather.hourlyWeather;
          console.log("[NWS Fallback] Merged NWS data with Open-Meteo hourly");
        } else if (this.nwsWeather.hourlyWeather.hours.length > 0) {
          homeWeatherInfo.hours = this.nwsWeather.hourlyWeather;
          console.log("[NWS Fallback] Using NWS hourly data directly");
        }
      }
      if (useForDaily) {
        try {
          const periodsLength = nwsDailyData?.properties?.periods?.length ?? 0;
          console.log(
            `[NWS Fallback] Daily forecast API returned ${periodsLength} periods`
          );
          if (nwsDailyData && periodsLength > 0) {
            this.nwsWeather.setDailyForecastValues(nwsDailyData);
            if (this.nwsWeather.dailyWeather && this.nwsWeather.dailyWeather.days?.length > 0) {
              homeWeatherInfo.dailyWeather = this.nwsWeather.dailyWeather;
              console.log(
                `[NWS Fallback] Using NWS daily forecast (${this.nwsWeather.dailyWeather.days.length} periods)`
              );
            }
          } else {
            console.warn(
              "[NWS Fallback] Daily forecast API returned no data, falling back to hourly aggregation"
            );
            this.nwsWeather.buildDailyWeather();
            if (this.nwsWeather.dailyWeather && this.nwsWeather.dailyWeather.days?.length > 0) {
              homeWeatherInfo.dailyWeather = this.nwsWeather.dailyWeather;
              console.log(
                `[NWS Fallback] Using NWS daily from hourly aggregation (${this.nwsWeather.dailyWeather.days.length} periods)`
              );
            }
          }
        } catch (dailyError) {
          console.error(
            "[NWS Fallback] Error fetching daily forecast, falling back to hourly aggregation:",
            dailyError
          );
          this.nwsWeather.buildDailyWeather();
          if (this.nwsWeather.dailyWeather && this.nwsWeather.dailyWeather.days?.length > 0) {
            homeWeatherInfo.dailyWeather = this.nwsWeather.dailyWeather;
            console.log(
              `[NWS Fallback] Using NWS daily from hourly aggregation (${this.nwsWeather.dailyWeather.days.length} periods)`
            );
          }
        }
      }
      if (useForCurrent) {
        if (!homeWeatherInfo.isRainOwlsStation) {
          const synTemp = this.synWeather.temperature;
          const synTempValid = isWeatherValueValid(
            synTemp,
            "temperature" /* Temperature */
          );
          if (synTempValid) {
            Object.assign(homeWeatherInfo, {
              temperature: this.synWeather.temperature,
              apparentTemperature: this.synWeather.temperatureApparent,
              dewPoint: this.synWeather.dewPoint,
              humidity: this.synWeather.humidity,
              pressure: this.synWeather.pressure,
              windSpeed: this.synWeather.windSpeed,
              windDirection: this.synWeather.windBearing,
              currentObservationTimestamp: this.synWeather.timestamp,
              currentObservationSource: "synoptic"
            });
          } else if (isWeatherValueValid(this.nwsWeather.temperature, "temperature" /* Temperature */)) {
            homeWeatherInfo.currentObservationSource = "nws";
            homeWeatherInfo.currentObservationTimestamp = this.nwsWeather.timestamp;
            homeWeatherInfo.weatherCode = this.nwsWeather.weatherCode;
            homeWeatherInfo.isDay = this.nwsWeather.isDay;
            homeWeatherInfo.temperature = this.nwsWeather.temperature;
            homeWeatherInfo.apparentTemperature = this.nwsWeather.temperatureApparent;
            if (!isNaN(this.nwsWeather.dewPoint) && this.nwsWeather.dewPoint !== INVALID_DOUBLE_VALUE) {
              homeWeatherInfo.dewPoint = this.nwsWeather.dewPoint;
            }
            homeWeatherInfo.humidity = this.nwsWeather.humidity;
            homeWeatherInfo.windSpeed = this.nwsWeather.windSpeed;
            homeWeatherInfo.windDirection = this.nwsWeather.windDirection;
            homeWeatherInfo.cloudCover = this.nwsWeather.cloudCover;
            console.log("[NWS Fallback] Using NWS current conditions");
          }
        } else {
          console.log(
            "[NWS Fallback] Keeping RainOwls station current conditions"
          );
        }
      }
      homeWeatherInfo.dataSource = "nws_fallback";
    } catch (error) {
      console.error("[NWS Fallback] Error fetching NWS fallback data:", error);
    }
  }
  /**
   * Fetch and update all weather data from Synoptic and Open-Meteo
   */
  async fetchAndUpdateAllWeather(homeWeatherInfo) {
    try {
      if (this._dataSourceMode === "nws" && homeWeatherInfo.isLocationUSA) {
        console.log(
          "[RainOwlsMain] Data source mode: NWS (skipping Open-Meteo entirely)"
        );
        const synopticDataPromise = this.networkManager.fetchWeatherSynoptic(
          homeWeatherInfo.location,
          6
        );
        const synopticPrecipitationDataPromise = this.fetchSynopticPrecipitationOptional(homeWeatherInfo.location, 6);
        const [synopticData2, synopticPrecipitationData2] = await Promise.all([
          synopticDataPromise,
          synopticPrecipitationDataPromise
        ]);
        try {
          this.synWeather.setValues(synopticData2);
          this.synWeather.setPrecipitationValues(synopticPrecipitationData2);
        } catch (error) {
          console.error("Error setting Synoptic values:", error);
        }
        await this.fetchNWSFallbackData(homeWeatherInfo, true, true, true);
        homeWeatherInfo.isOMHourlyLoading = false;
        homeWeatherInfo.isOMDailyLoading = false;
        homeWeatherInfo.isSynopticLoading = false;
        console.log(
          `[RainOwlsMain] Hourly forecast source: NWS (${homeWeatherInfo.hours?.hours?.length || 0} hours)`
        );
        console.log(
          `[RainOwlsMain] Daily forecast source: NWS (${homeWeatherInfo.dailyWeather?.days?.length || 0} days)`
        );
        this.fetchNWSAlertsForLocation(homeWeatherInfo.location).then((alerts) => {
          this._alerts = alerts;
          homeWeatherInfo.isNWSAlertsLoading = false;
          this.notifyAlertSubscribers();
        }).catch((alertsError) => {
          console.error("[NWS Alerts] Error fetching alerts:", alertsError);
          this._alerts = [];
          homeWeatherInfo.isNWSAlertsLoading = false;
          this.notifyAlertSubscribers();
        });
        return;
      }
      const stationPromise = homeWeatherInfo.isRainOwlsStation ? this.fetchRainOwlsStationData(
        homeWeatherInfo,
        homeWeatherInfo.stationId
      ) : Promise.resolve();
      if (!homeWeatherInfo.isRainOwlsStation) {
        homeWeatherInfo.isFirebaseLoading = false;
      }
      const [
        synopticData,
        synopticPrecipitationData,
        openMeteoResult,
        openMeteoHourlyResult,
        ecmwfPressureData,
        _stationResult
      ] = await Promise.all([
        // Skip Synoptic fetch if flag is set (for web app)
        this._skipSynoptic ? Promise.resolve({ stations: [] }) : this.networkManager.fetchWeatherSynoptic(
          homeWeatherInfo.location,
          6
        ),
        this._skipSynoptic ? Promise.resolve({ stations: [] }) : this.fetchSynopticPrecipitationOptional(
          homeWeatherInfo.location,
          6
        ),
        this.networkManager.fetchWeather([homeWeatherInfo.location]),
        this.networkManager.fetchOpenMeteoHourlyWeather(
          homeWeatherInfo.location
        ),
        this.networkManager.fetchECMWFPressureLevelData(
          homeWeatherInfo.location
        ),
        stationPromise
      ]);
      const openMeteoData = Array.isArray(openMeteoResult.data) && openMeteoResult.data.length > 0 ? openMeteoResult.data[0] : void 0;
      const openMeteoHourlyData = openMeteoHourlyResult.data;
      let needsHourlyFallback = openMeteoHourlyResult.error !== null;
      let needsCurrentFallback = openMeteoResult.error !== null;
      if (openMeteoResult.error) {
        console.warn(
          `[RainOwlsMain] Open-Meteo current weather error: ${openMeteoResult.error} - ${openMeteoResult.errorMessage}`
        );
      }
      if (openMeteoHourlyResult.error) {
        console.warn(
          `[RainOwlsMain] Open-Meteo hourly weather error: ${openMeteoHourlyResult.error} - ${openMeteoHourlyResult.errorMessage}`
        );
      }
      try {
        this.synWeather.setValues(synopticData);
        this.synWeather.setPrecipitationValues(synopticPrecipitationData);
      } catch (error) {
        console.error("Error setting Synoptic values:", error);
      }
      if (this._dataSourceMode === "openmeteo") {
        console.log(
          "[RainOwlsMain] Data source mode: Open-Meteo only (no NWS fallback)"
        );
      } else {
        if ((needsHourlyFallback || needsCurrentFallback) && homeWeatherInfo.isLocationUSA) {
          console.log(
            "[RainOwlsMain] Triggering NWS fallback for US location..."
          );
          await this.fetchNWSFallbackData(
            homeWeatherInfo,
            needsHourlyFallback,
            // useForHourly
            needsHourlyFallback,
            // useForDaily (use same as hourly - if hourly failed, daily likely did too)
            needsCurrentFallback
            // useForCurrent
          );
        }
      }
      if (openMeteoData) {
        homeWeatherInfo.timeZone = openMeteoData.timezone;
        homeWeatherInfo.timeZoneIdentifier = openMeteoData.timezone;
        homeWeatherInfo.timeZoneOffsetFromGMT = openMeteoData.utcOffset;
        this.omWeather.setTimeZone(
          openMeteoData.timezone,
          openMeteoData.utcOffset
        );
      }
      try {
        if (openMeteoData?.current) {
          this.omWeather.setValues(openMeteoData);
          if (homeWeatherInfo.location && (homeWeatherInfo.location.elevation === void 0 || homeWeatherInfo.location.elevation === 0)) {
            homeWeatherInfo.location.elevation = this.omWeather.elevation;
            console.log(
              `[RainOwlsMain] Backfilled elevation from OpenMeteo: ${this.omWeather.elevation} ft`
            );
          }
        }
      } catch (error) {
        console.error("Error setting Open-Meteo values:", error);
      }
      try {
        if (openMeteoHourlyData?.hourly) {
          this.omWeather.setHourlyValues(
            openMeteoHourlyData.hourly,
            ecmwfPressureData ?? void 0
          );
        }
      } catch (error) {
        console.error("Error setting Open-Meteo hourly values:", error);
      }
      if (homeWeatherInfo.dataSource !== "nws_fallback") {
        if (this.omWeather.hourlyWeather && this.omWeather.hourlyWeather.hours) {
          homeWeatherInfo.hours = this.omWeather.hourlyWeather;
        }
        if (this.omWeather.dailyWeather && this.omWeather.dailyWeather.days) {
          homeWeatherInfo.dailyWeather = this.omWeather.dailyWeather;
        }
      }
      const hourlySource = homeWeatherInfo.dataSource === "nws_fallback" ? "NWS" : "Open-Meteo";
      const dailySource = homeWeatherInfo.dataSource === "nws_fallback" ? "NWS" : "Open-Meteo";
      console.log(
        `[RainOwlsMain] Hourly forecast source: ${hourlySource} (${homeWeatherInfo.hours?.hours?.length || 0} hours)`
      );
      console.log(
        `[RainOwlsMain] Daily forecast source: ${dailySource} (${homeWeatherInfo.dailyWeather?.days?.length || 0} days)`
      );
      homeWeatherInfo.isOMHourlyLoading = false;
      homeWeatherInfo.isOMDailyLoading = false;
      if (homeWeatherInfo.dailyWeather && homeWeatherInfo.dailyWeather.days && homeWeatherInfo.dailyWeather.days.length > 0) {
        homeWeatherInfo.currentDayForecastDetails = homeWeatherInfo.dailyWeather.days[0].detailedForecast || "";
      }
      const observation = {
        timestamp: this.synWeather.timestamp || /* @__PURE__ */ new Date(),
        temperature: this.synWeather.temperature,
        weatherCode: this.synWeather.weatherCode
      };
      const validationResult = validateCurrentWeatherObservation(
        observation,
        homeWeatherInfo.hours?.hours || []
      );
      const weatherSYN = this.synWeather;
      const weatherOM = this.omWeather;
      if (!homeWeatherInfo.isRainOwlsStation && homeWeatherInfo.currentObservationSource !== "nws") {
        const usesStationTemperature = (homeWeatherInfo.hours.hours.length === 0 || validationResult.temperatureScore > 30) && isWeatherValueValid(
          weatherSYN.temperature,
          "temperature" /* Temperature */
        );
        homeWeatherInfo.temperature = usesStationTemperature ? weatherSYN.temperature : weatherOM.temperature;
        homeWeatherInfo.currentObservationTimestamp = usesStationTemperature ? weatherSYN.timestamp : weatherOM.timestamp;
        homeWeatherInfo.currentObservationSource = usesStationTemperature ? "synoptic" : openMeteoData?.current ? "open-meteo" : null;
        homeWeatherInfo.apparentTemperature = weatherSYN.temperatureApparent;
        if (!usesStationTemperature) homeWeatherInfo.stationId = "Open-Meteo";
        homeWeatherInfo.dewPoint = weatherSYN.dewPoint;
        homeWeatherInfo.humidity = weatherSYN.humidity;
        homeWeatherInfo.pressure = weatherSYN.pressure;
        homeWeatherInfo.windSpeed = weatherSYN.windSpeed;
        homeWeatherInfo.windDirection = weatherSYN.windBearing;
        homeWeatherInfo.rainRate = weatherSYN.rainRate;
        homeWeatherInfo.rain1hr = weatherSYN.rain1hr;
        homeWeatherInfo.rainDay = weatherSYN.rainDay;
        homeWeatherInfo.rain24hr = weatherSYN.rain24hr;
        homeWeatherInfo.rainStorm = weatherSYN.rainStorm;
        homeWeatherInfo.snowRate = weatherSYN.snowRate;
        homeWeatherInfo.snowDepth = weatherSYN.snowDepth;
        homeWeatherInfo.cloudCover = weatherSYN.cloudCover;
        homeWeatherInfo.dailyMinimums = weatherSYN.dailyMinimums;
        homeWeatherInfo.dailyMaximums = weatherSYN.dailyMaximums;
        homeWeatherInfo.yesterdayMinimums = weatherSYN.yesterdayMinimums;
        homeWeatherInfo.yesterdayMaximums = weatherSYN.yesterdayMaximums;
        const isApparentTemperatureValid = isWeatherValueValid(
          homeWeatherInfo.apparentTemperature,
          "apparentTemperature" /* ApparentTemperature */
        );
        const isDewPointValid = isWeatherValueValid(
          homeWeatherInfo.dewPoint,
          "dewPoint" /* DewPoint */
        );
        const isHumidityValid = isWeatherValueValid(
          homeWeatherInfo.humidity,
          "humidity" /* Humidity */
        );
        const isPressureValid = isWeatherValueValid(
          homeWeatherInfo.pressure,
          "pressure" /* Pressure */
        );
        const isWindSpeedValid = isWeatherValueValid(
          homeWeatherInfo.windSpeed,
          "windSpeed" /* WindSpeed */
        );
        const isWindDirectionValid = isWeatherValueValid(
          homeWeatherInfo.windDirection,
          "windDirection" /* WindDirection */
        );
        const isCloudCoverValid = isWeatherValueValid(
          homeWeatherInfo.cloudCover,
          "cloudCover" /* CloudCover */
        );
        const isWeatherCodeValid = homeWeatherInfo.weatherCode !== INVALID_INT_VALUE && homeWeatherInfo.weatherCode >= 0;
        if (usesStationTemperature && weatherSYN.stationName && weatherSYN.stationName !== "Invalid") {
          homeWeatherInfo.stationId = weatherSYN.stationName;
        }
        if (!isCloudCoverValid) {
          homeWeatherInfo.cloudCover = weatherOM.cloudCover;
        }
        if (!isWeatherCodeValid) {
          homeWeatherInfo.weatherCode = weatherOM.weatherCode;
        }
        if (!isApparentTemperatureValid) {
          homeWeatherInfo.apparentTemperature = weatherOM.temperatureApparent;
        }
        if (!isDewPointValid) {
          homeWeatherInfo.dewPoint = weatherOM.dewPoint;
        }
        if (!isHumidityValid) {
          homeWeatherInfo.humidity = weatherOM.humidity;
        }
        if (!isPressureValid) {
          homeWeatherInfo.pressure = weatherOM.pressure;
        }
        if (!isWindSpeedValid) {
          homeWeatherInfo.windSpeed = weatherOM.windSpeed;
        }
        if (!isWindDirectionValid) {
          homeWeatherInfo.windDirection = weatherOM.windBearing;
        }
      } else if (homeWeatherInfo.isRainOwlsStation) {
        if (homeWeatherInfo.weatherCode === -1) {
          if (validationResult.weatherScore && validationResult.weatherScore > 40) {
            homeWeatherInfo.weatherCode = weatherSYN.weatherCode;
          } else {
            homeWeatherInfo.weatherCode = weatherOM.weatherCode;
          }
        }
        if (homeWeatherInfo.windSpeed === INVALID_DOUBLE_VALUE || homeWeatherInfo.windSpeed === -999) {
          if (weatherSYN.windSpeed !== INVALID_DOUBLE_VALUE) {
            homeWeatherInfo.windSpeed = weatherSYN.windSpeed;
          } else {
            homeWeatherInfo.windSpeed = weatherOM.windSpeed;
          }
        }
        if (homeWeatherInfo.windDirection === INVALID_DOUBLE_VALUE || homeWeatherInfo.windDirection === -999) {
          if (weatherSYN.windBearing !== INVALID_DOUBLE_VALUE) {
            homeWeatherInfo.windDirection = weatherSYN.windBearing;
          } else {
            homeWeatherInfo.windDirection = weatherOM.windBearing;
          }
        }
      }
      homeWeatherInfo.windSpeed = this.sanitizeWindSpeed(
        homeWeatherInfo.windSpeed
      );
      homeWeatherInfo.windDirection = this.sanitizeWindDirection(
        homeWeatherInfo.windDirection
      );
      if (weatherSYN.cloudCover !== INVALID_DOUBLE_VALUE) {
        homeWeatherInfo.cloudCover = weatherSYN.cloudCover;
      } else if (homeWeatherInfo.currentObservationSource !== "nws") {
        homeWeatherInfo.cloudCover = weatherOM.cloudCover;
      }
      homeWeatherInfo.stationFormattedTime = "";
      if (homeWeatherInfo.currentObservationTimestamp) {
        const local = new Date(
          homeWeatherInfo.currentObservationTimestamp.getTime() + homeWeatherInfo.timeZoneOffsetFromGMT * 1e3
        );
        const hours = local.getUTCHours();
        homeWeatherInfo.stationFormattedTime = `${hours % 12 || 12}:${String(local.getUTCMinutes()).padStart(2, "0")} ${hours >= 12 ? "PM" : "AM"}`;
      }
      if (openMeteoData?.current) homeWeatherInfo.isDay = weatherOM.isDay;
      homeWeatherInfo.isSynopticLoading = false;
      if (homeWeatherInfo.isLocationUSA) {
        this.fetchNWSAlertsForLocation(homeWeatherInfo.location).then((alerts) => {
          this._alerts = alerts;
          homeWeatherInfo.isNWSAlertsLoading = false;
          this.notifyAlertSubscribers();
        }).catch((alertsError) => {
          console.error("[NWS Alerts] Error fetching alerts:", alertsError);
          this._alerts = [];
          homeWeatherInfo.isNWSAlertsLoading = false;
          this.notifyAlertSubscribers();
        });
      } else {
        this._alerts = [];
        homeWeatherInfo.isNWSAlertsLoading = false;
        this.notifyAlertSubscribers();
      }
    } catch (error) {
      console.error("Error fetching weather data:", error);
      homeWeatherInfo.isSynopticLoading = false;
      homeWeatherInfo.isOMHourlyLoading = false;
      homeWeatherInfo.isOMDailyLoading = false;
      homeWeatherInfo.isNWSAlertsLoading = false;
    }
  }
  sanitizeWindSpeed(value) {
    if (!Number.isFinite(value) || value < 0 || value > 200) {
      return 0;
    }
    return value;
  }
  sanitizeWindDirection(value) {
    if (!Number.isFinite(value) || value < 0 || value > 360) {
      return 0;
    }
    return value;
  }
  async fetchSynopticPrecipitationOptional(location, radiusMiles) {
    let timeoutId;
    const timeout = new Promise((resolve) => {
      timeoutId = setTimeout(() => {
        console.warn(
          `[RainOwlsMain] Synoptic precipitation timed out after ${this.synopticPrecipitationTimeoutMs}ms`
        );
        resolve(createInvalidSynopticPrecipitationModel());
      }, this.synopticPrecipitationTimeoutMs);
    });
    try {
      return await Promise.race([
        this.networkManager.fetchSynopticPrecipitation(location, radiusMiles),
        timeout
      ]);
    } catch (error) {
      console.warn("[RainOwlsMain] Synoptic precipitation failed:", error);
      return createInvalidSynopticPrecipitationModel();
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }
};
var LAST_SELECTED_LOCATION_KEY = "lastSelectedLocation";
var LAST_TRACKED_LOCATION_KEY = "lastTrackedLocation";
var ROLocationManager = class {
  constructor() {
    // Public observable properties
    this.location = null;
    this.roLocation = null;
    // Private properties
    this.watchId = null;
  }
  /**
   * Request location permission from the user
   *
   * @returns Promise<boolean> - true if permission granted, false otherwise
   */
  async requestUserAuthorization() {
    try {
      const hasPermission = await Geolocation.isEnabled();
      if (!hasPermission) {
        await Geolocation.enableLocationRequest();
        const nowEnabled = await Geolocation.isEnabled();
        if (!nowEnabled) {
          console.log("Location services not enabled by user");
          return false;
        }
      }
      console.log("Location authorization granted");
      return true;
    } catch (error) {
      console.warn("Location authorization denied:", error);
      return false;
    }
  }
  /**
   * Check if location services are enabled and authorized
   *
   * @returns Promise<boolean>
   */
  async isAuthorized() {
    try {
      return await Geolocation.isEnabled();
    } catch (error) {
      console.error("Error checking location authorization:", error);
      return false;
    }
  }
  /**
   * Start updating the current location asynchronously
   *
   * This method:
   * 1. Checks authorization status and location services availability
   * 2. If disabled/denied, loads a fallback location
   * 3. Otherwise, gets the current location
   * 4. Performs reverse geocoding on the location
   * 5. Falls back to default location on error
   *
   * @param options - Location update options
   * @param onAddressResolved - Callback triggered when reverse geocoding completes
   * @returns Promise<boolean> - true if a new location was successfully fetched, false otherwise
   */
  async startCurrentLocationUpdates(options, onAddressResolved) {
    try {
      const isEnabled2 = await Geolocation.isEnabled();
      if (!isEnabled2) {
        console.log("Location services disabled, loading fallback location");
        this.loadFallbackLocation();
        return false;
      }
      const locationOptions = {
        desiredAccuracy: options?.desiredAccuracy ?? CoreTypes.Accuracy.any,
        updateDistance: options?.updateDistance ?? 10,
        minimumUpdateTime: options?.minimumUpdateTime ?? 1e3,
        timeout: options?.timeout ?? 5e3,
        maximumAge: options?.maximumAge ?? 6e5
        // 10 minutes cache
      };
      const currentLocation = await Geolocation.getCurrentLocation(locationOptions);
      if (currentLocation) {
        this.location = currentLocation;
        console.log(
          `Got location: ${currentLocation.latitude}, ${currentLocation.longitude}`
        );
        await this.reverseGeocode(currentLocation);
        if (this.roLocation && onAddressResolved) {
          onAddressResolved(this.roLocation);
        }
        return true;
      } else {
        console.log("No location fix obtained");
        this.loadFallbackLocation();
        return false;
      }
    } catch (error) {
      console.error("Error getting live location updates:", error);
      if (this.roLocation === null) {
        this.loadFallbackLocation();
      }
      return false;
    }
  }
  /**
   * Start watching location updates continuously
   *
   * @param callback - Callback function called on each location update
   * @param options - Location update options
   */
  async startWatchingLocation(callback, options) {
    if (this.watchId !== null) {
      console.warn("Already watching location updates");
      return;
    }
    const locationOptions = {
      desiredAccuracy: options?.desiredAccuracy ?? CoreTypes.Accuracy.high,
      updateDistance: options?.updateDistance ?? 10,
      minimumUpdateTime: options?.minimumUpdateTime ?? 1e3,
      timeout: options?.timeout ?? 2e4
    };
    this.watchId = await Geolocation.watchLocation(
      async (location) => {
        if (location) {
          this.location = location;
          await this.reverseGeocode(location);
          if (this.roLocation && callback) {
            callback(this.roLocation);
          }
        }
      },
      (error) => {
        console.error("Location watch error:", error);
      },
      locationOptions
    );
    console.log("Started watching location updates");
  }
  /**
   * Stop watching location updates
   */
  stopWatchingLocation() {
    if (this.watchId !== null) {
      Geolocation.clearWatch(this.watchId);
      this.watchId = null;
      console.log("Stopped watching location updates");
    }
  }
  /**
   * Load a fallback ROLocation when live location updates are unavailable
   *
   * Fallback resolution order:
   * 1. Last selected location (ApplicationSettings)
   * 2. Last tracked location (ApplicationSettings)
   * 3. Default location (Portland, OR)
   */
  loadFallbackLocation() {
    console.log("Loading fallback location...");
    const setFallback = (loc) => {
      this.roLocation = loc;
      this.location = {
        latitude: loc.lat,
        longitude: loc.lng,
        altitude: loc.elevation ?? 0,
        horizontalAccuracy: 0,
        verticalAccuracy: 0,
        speed: 0,
        direction: 0,
        timestamp: /* @__PURE__ */ new Date()
      };
      console.log(`Fallback set to: ${loc.name}`);
    };
    const lastSelected = ApplicationSettings.getString(
      LAST_SELECTED_LOCATION_KEY
    );
    if (lastSelected) {
      try {
        const loc = JSON.parse(lastSelected);
        setFallback(loc);
        return;
      } catch (error) {
        console.error("Error parsing last selected location:", error);
      }
    }
    const lastTracked = ApplicationSettings.getString(
      LAST_TRACKED_LOCATION_KEY
    );
    if (lastTracked) {
      try {
        const loc = JSON.parse(lastTracked);
        setFallback(loc);
        return;
      } catch (error) {
        console.error("Error parsing last tracked location:", error);
      }
    }
    setFallback(defaultLocation);
  }
  /**
   * Save a location as the last selected location (e.g., user search)
   *
   * @param location - The location to save
   */
  saveLastSelectedLocation(location) {
    try {
      const json = JSON.stringify(location);
      ApplicationSettings.setString(LAST_SELECTED_LOCATION_KEY, json);
      console.log("Saved last selected location:", location.name);
    } catch (error) {
      console.error("Error saving last selected location:", error);
    }
  }
  /**
   * Clear all saved locations from storage
   */
  clearSavedLocations() {
    ApplicationSettings.remove(LAST_SELECTED_LOCATION_KEY);
    ApplicationSettings.remove(LAST_TRACKED_LOCATION_KEY);
    console.log("Cleared all saved locations");
  }
  /**
   * Geocode a human-readable address and return an ROLocation
   * Uses native iOS CLGeocoder for improved privacy, speed, and offline capability
   *
   * @param address - The address string to geocode (e.g., "Portland, OR")
   * @returns ROLocation or null if geocoding fails
   */
  async getCoordinates(address) {
    const trimmedAddress = address.trim();
    if (!trimmedAddress) {
      console.error("getCoordinates: Empty address string");
      return null;
    }
    const result = await this.forwardGeocodeNative(trimmedAddress);
    if (!result) {
      console.error(
        `getCoordinates: No results found for address '${trimmedAddress}'`
      );
      return null;
    }
    const { latitude, longitude, city, state, country, countryCode, timeZone } = result;
    let locationName;
    if (countryCode === "US" && state) {
      locationName = `${city}, ${state}`;
    } else if (city !== "Unknown" && country !== "Unknown") {
      locationName = `${city}, ${country}`;
    } else {
      locationName = trimmedAddress;
    }
    const roLocation = {
      id: generateUUID(),
      lat: latitude,
      lng: longitude,
      timeZone,
      name: locationName,
      country,
      city,
      elevation: result.elevation
    };
    this.roLocation = roLocation;
    this.location = {
      latitude,
      longitude,
      altitude: 0,
      horizontalAccuracy: 0,
      verticalAccuracy: 0,
      speed: 0,
      direction: 0,
      timestamp: /* @__PURE__ */ new Date()
    };
    console.log(
      `getCoordinates: Geocoded '${trimmedAddress}' -> ${locationName} [${latitude}, ${longitude}]`
    );
    return roLocation;
  }
  /**
   * Reverse geocode a location to get address information
   * Uses native iOS CLGeocoder for improved privacy, speed, and offline capability
   *
   * @param location - The location to reverse geocode
   */
  async reverseGeocode(location) {
    const { latitude, longitude } = location;
    const elevation = location.altitude || 0;
    const result = await this.reverseGeocodeNative(latitude, longitude);
    if (result) {
      const { city, state, country, countryCode, timeZone } = result;
      let locationName;
      if (countryCode === "US" && state) {
        locationName = `${city}, ${state}`;
      } else if (country) {
        locationName = `${city}, ${country}`;
      } else {
        locationName = city;
      }
      if (this.roLocation) {
        this.roLocation.lat = latitude;
        this.roLocation.lng = longitude;
        this.roLocation.timeZone = timeZone;
        this.roLocation.name = locationName;
        this.roLocation.country = country;
        this.roLocation.city = city;
        this.roLocation.elevation = elevation;
      } else {
        this.roLocation = {
          id: generateUUID(),
          lat: latitude,
          lng: longitude,
          timeZone,
          name: locationName,
          country,
          city,
          elevation
        };
      }
      console.log(`Reverse Geocode Success: ${locationName}`);
    } else {
      console.error("Reverse geocoding failed");
      if (!this.roLocation) {
        this.roLocation = {
          id: generateUUID(),
          lat: latitude,
          lng: longitude,
          timeZone: getSystemTimeZone(),
          name: "Current Location",
          country: "",
          city: "",
          elevation
        };
      } else {
        this.roLocation.lat = latitude;
        this.roLocation.lng = longitude;
        if (this.roLocation.name === "Locating...") {
          this.roLocation.name = "Current Location";
        }
      }
      console.log("Using fallback location name: Current Location");
    }
  }
  /**
   * Reverse geocode an ROLocation to get a proper address name
   * Uses native iOS CLGeocoder for improved privacy, speed, and offline capability
   *
   * @param location - The ROLocation to reverse geocode
   * @returns The updated ROLocation with proper name, or the original if geocoding fails
   */
  async reverseGeocodeROLocation(location) {
    const { lat, lng } = location;
    const result = await this.reverseGeocodeNative(lat, lng);
    if (result) {
      const { city, state, country, countryCode, timeZone } = result;
      let locationName;
      if (countryCode === "US" && state) {
        locationName = `${city}, ${state}`;
      } else if (country) {
        locationName = `${city}, ${country}`;
      } else {
        locationName = city;
      }
      console.log(`Reverse Geocode ROLocation Success: ${locationName}`);
      return {
        ...location,
        name: locationName,
        city,
        country,
        timeZone
      };
    } else {
      console.error("Reverse geocoding ROLocation failed");
      if (location.name === "Locating...") {
        return {
          ...location,
          name: "Current Location"
        };
      }
      return location;
    }
  }
  /**
   * Reverse geocode using native iOS CLGeocoder
   *
   * @param latitude - Latitude
   * @param longitude - Longitude
   * @returns Promise with geocoded data or null if failed
   */
  async reverseGeocodeNative(latitude, longitude) {
    return new Promise((resolve) => {
      try {
        const clLocation = CLLocation.alloc().initWithLatitudeLongitude(
          latitude,
          longitude
        );
        const geocoder = CLGeocoder.alloc().init();
        geocoder.reverseGeocodeLocationCompletionHandler(
          clLocation,
          (placemarks, error) => {
            if (error || !placemarks || placemarks.count === 0) {
              console.log("CLGeocoder failed:", error?.localizedDescription);
              resolve(null);
              return;
            }
            const placemark = placemarks.objectAtIndex(0);
            resolve({
              city: placemark.locality || "Unknown Location",
              state: placemark.administrativeArea || "",
              country: placemark.country || "",
              countryCode: (placemark.ISOcountryCode || "").toUpperCase(),
              timeZone: placemark.timeZone?.name || getSystemTimeZone()
            });
          }
        );
      } catch (error) {
        console.error("CLGeocoder exception:", error);
        resolve(null);
      }
    });
  }
  /**
   * Forward geocode using native iOS CLGeocoder
   *
   * @param address - Address string to geocode
   * @returns Promise with geocoded data or null if failed
   */
  async forwardGeocodeNative(address) {
    return new Promise((resolve) => {
      try {
        const geocoder = CLGeocoder.alloc().init();
        geocoder.geocodeAddressStringCompletionHandler(
          address,
          (placemarks, error) => {
            if (error || !placemarks || placemarks.count === 0) {
              console.log(
                "CLGeocoder forward failed:",
                error?.localizedDescription
              );
              resolve(null);
              return;
            }
            const placemark = placemarks.objectAtIndex(0);
            const location = placemark.location;
            resolve({
              latitude: location.coordinate.latitude,
              longitude: location.coordinate.longitude,
              city: placemark.locality || "Unknown",
              state: placemark.administrativeArea || "",
              country: placemark.country || "Unknown",
              countryCode: (placemark.ISOcountryCode || "").toUpperCase(),
              timeZone: placemark.timeZone?.name || getSystemTimeZone(),
              elevation: location.altitude || 0
            });
          }
        );
      } catch (error) {
        console.error("CLGeocoder forward exception:", error);
        resolve(null);
      }
    });
  }
  /**
   * Get the distance between two locations in meters
   *
   * @param from - Starting location
   * @param to - Ending location
   * @returns Distance in meters
   */
  static distance(from, to) {
    return Geolocation.distance(
      {
        latitude: from.lat,
        longitude: from.lng,
        altitude: from.elevation || 0,
        horizontalAccuracy: 0,
        verticalAccuracy: 0,
        speed: 0,
        direction: 0,
        timestamp: /* @__PURE__ */ new Date()
      },
      {
        latitude: to.lat,
        longitude: to.lng,
        altitude: to.elevation || 0,
        horizontalAccuracy: 0,
        verticalAccuracy: 0,
        speed: 0,
        direction: 0,
        timestamp: /* @__PURE__ */ new Date()
      }
    );
  }
  /**
   * Cleanup resources
   */
  dispose() {
    this.stopWatchingLocation();
    this.location = null;
    this.roLocation = null;
  }
};

// src/services/WeathercomRadarService.ts
var INTERVAL_MINUTES = 5;
var DELAY_INTERVALS = 2;
var WEATHERCOM_TILE_BASE_URL = "https://api.weather.com/v3/TileServer/tile";
var DEFAULT_PRODUCT = "twcRadarMosaic";
var WeathercomRadarService = class {
  /**
   * Compute the latest valid radar epoch timestamp.
   * Rounds down to the nearest 5-minute mark with a 10-minute delay offset
   * to account for the lag between mosaic time and tile availability.
   */
  static getLatestRadarTimestamp() {
    const now = /* @__PURE__ */ new Date();
    now.setSeconds(0, 0);
    const minutes = now.getMinutes();
    now.setMinutes(
      minutes - minutes % INTERVAL_MINUTES - INTERVAL_MINUTES * DELAY_INTERVALS
    );
    return Math.floor(now.getTime() / 1e3);
  }
  /**
   * Build a tile URL template for the given proxy base URL and epoch.
   * Returns a template with {z}, {x}, {y} placeholders compatible with MKTileOverlay.
   */
  static buildProxyTileUrlTemplate(proxyBaseUrl, epoch) {
    return `${proxyBaseUrl}/tile/{z}/{x}/{y}?product=twcRadarMosaic&ts=${epoch}`;
  }
  /**
   * Build a direct Weather.com tile URL template for native clients.
   * Returns a template with {z}, {x}, {y} placeholders compatible with MKTileOverlay.
   */
  static buildDirectTileUrlTemplate(apiKey, epoch) {
    return `${WEATHERCOM_TILE_BASE_URL}?${new URLSearchParams({
      product: DEFAULT_PRODUCT,
      ts: String(epoch),
      xyz: "{x}:{y}:{z}",
      apiKey
    }).toString()}`;
  }
  /**
   * Backward-compatible alias for the proxy tile builder.
   */
  static buildTileUrlTemplate(proxyBaseUrl, epoch) {
    return this.buildProxyTileUrlTemplate(proxyBaseUrl, epoch);
  }
};

// src/utils/WeatherColors.generated.ts
var WeatherColors = {
  "humidity": "#2E8A99",
  "wind": "#C63C51",
  "pressure": "#9E8CFF",
  "rain": "#247D32",
  "snow": "#03A9F4",
  "ice": "#F06292",
  "pop": "#80BDFF"
};

// src/utils/AppColors.ts
var BackgroundGradient = {
  /** Clear sky gradient - deep blue tones */
  clear: {
    top: "#0C1F41",
    bottom: "#00385F"
  },
  /** Cloudy/overcast gradient - muted gray-blue */
  cloudy: {
    top: "#1A1F30",
    bottom: "#232840"
  },
  /** Partly cloudy gradient - slightly warmer */
  partlyCloudy: {
    top: "#19223D",
    bottom: "#293052"
  },
  /** Rain gradient - moody deep blue */
  rain: {
    top: "#0f1729",
    bottom: "#1e3a5f"
  },
  /** Snow gradient - soft arctic silver-blue */
  snow: {
    top: "#1a1f2e",
    bottom: "#3d4f6a"
  },
  /** Thunderstorm gradient - dark purple */
  thunderstorm: {
    top: "#0F0B26",
    bottom: "#2B214E"
  },
  /** Night gradient - dark warm tones */
  night: {
    top: "#222831",
    bottom: "#151515"
  }
};
var AlertColors = {
  /** Default/informational alerts - blue */
  info: "#03A9F4",
  /** Extreme alerts (tornado, hurricane) - magenta/pink */
  extreme: "#EB3678",
  /** Severe alerts (severe thunderstorm) - red */
  severe: "#ED2B2A",
  /** Moderate alerts (winter storm, flood) - yellow/orange */
  moderate: "#FFCC00"
};
var ThemeColors = {
  /** Primary background color - dark gray */
  background: "#151515",
  /** Secondary background color */
  background2: "#1E1E1E",
  /** Tertiary background color */
  background3: "#2A2A2A",
  /** Night-specific background */
  backgroundNight: "#222831",
  /** Primary foreground/text color */
  foreground: "#FFFFFF",
  /** Border color */
  border: "#333333",
  /** Secondary border color */
  border2: "#444444",
  /** Map marker color (coral) */
  mapMarker: "#E84C3C"
};
var AccentThemes = {
  default: {
    accent: "#4A90D9",
    accent2: "#3A7BC8"
  },
  purple: {
    accent: "#9B59B6",
    accent2: "#8E44AD"
  },
  teal: {
    accent: "#1ABC9C",
    accent2: "#16A085"
  },
  green: {
    accent: "#2ECC71",
    accent2: "#27AE60"
  }
};
var BorderGradients = [
  {
    id: "ocean",
    accent: "#3B82F6",
    name: "Ocean",
    colors: ["#3B82F6", "#8B5CF6"],
    direction: "horizontal"
  },
  {
    id: "sunset",
    accent: "#F97316",
    name: "Sunset",
    colors: ["#F97316", "#EC4899"],
    direction: "horizontal"
  },
  {
    id: "aurora",
    accent: "#10B981",
    name: "Aurora",
    colors: ["#10B981", "#3B82F6"],
    direction: "horizontal"
  },
  {
    id: "ember",
    accent: "#EF4444",
    name: "Ember",
    colors: ["#EF4444", "#F59E0B"],
    direction: "horizontal"
  },
  {
    id: "lavender",
    accent: "#A855F7",
    name: "Lavender",
    colors: ["#A855F7", "#EC4899"],
    direction: "horizontal"
  },
  {
    id: "mint",
    accent: "#14B8A6",
    name: "Mint",
    colors: ["#14B8A6", "#22D3EE"],
    direction: "horizontal"
  },
  {
    id: "neon",
    accent: "#22D3EE",
    name: "Neon",
    colors: ["#22D3EE", "#A855F7"],
    direction: "horizontal"
  },
  {
    id: "rose",
    accent: "#F43F5E",
    name: "Rose",
    colors: ["#F43F5E", "#FB7185"],
    direction: "horizontal"
  },
  {
    id: "rainbow",
    accent: "#3B82F6",
    name: "Rainbow",
    colors: ["#EF4444", "#F97316", "#EAB308", "#22C55E", "#3B82F6", "#8B5CF6"],
    direction: "horizontal"
  }
];
function getBorderGradient(id) {
  return BorderGradients.find((g) => g.id === id) || BorderGradients[0];
}
function getBackgroundGradient(weatherCode, isDay) {
  if (!isDay) {
    return BackgroundGradient.night;
  }
  if (weatherCode >= 51 && weatherCode <= 67 || weatherCode >= 80 && weatherCode <= 82) {
    return BackgroundGradient.rain;
  }
  if (weatherCode >= 71 && weatherCode <= 77 || weatherCode >= 85 && weatherCode <= 86) {
    return BackgroundGradient.snow;
  }
  if (weatherCode >= 95 && weatherCode <= 99) {
    return BackgroundGradient.thunderstorm;
  }
  if (weatherCode >= 45 && weatherCode <= 48) {
    return BackgroundGradient.cloudy;
  }
  if (weatherCode === 3) {
    return BackgroundGradient.cloudy;
  }
  if (weatherCode === 1 || weatherCode === 2) {
    return BackgroundGradient.partlyCloudy;
  }
  return BackgroundGradient.clear;
}
function getAlertColor(severity) {
  switch (severity) {
    case "extreme":
      return AlertColors.extreme;
    case "severe":
      return AlertColors.severe;
    case "moderate":
      return AlertColors.moderate;
    default:
      return AlertColors.info;
  }
}
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return { r: 0, g: 0, b: 0 };
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  };
}
function rgbToHex2(r, g, b) {
  const toHex = (value) => Math.round(Math.max(0, Math.min(255, value))).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}
function hexToRgba(hex, alpha) {
  const rgb = hexToRgb(hex);
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

export { AccentThemes, AlertColors, AlertsNWSClass, BackgroundGradient, BorderGradients, CloudCoverTrend, ENSEMBLE_MODELS, ENSEMBLE_POP_THRESHOLD_MM, FEET_TO_METERS, ForecastGenerator, HEAVY_RAIN_THRESHOLD, INCHES_TO_MM, INVALID_DOUBLE, INVALID_DOUBLE_VALUE, INVALID_INT, INVALID_INT_VALUE, LIGHT_RAIN_THRESHOLD, MILES_TO_KM, MODERATE_RAIN_THRESHOLD, MainWeatherInfo, NativeScriptHttpAdapter, OpenMeteoModelType, ROLocationManager, RONetworkManager, RainOwlsMain, SPIPPIN_LOCATION, STEADY_TEMP_THRESHOLD, SolarCalculator, TEST_LOCATIONS, ThemeColors, WeatherColors, WeatherNWSClass, WeatherOMClass, WeatherPeriodAggregator, WeatherSYNClass, WeatherValueType, WeathercomRadarService, WinterPrecipType, analyzeSynopticPrecipitation, analyzeWinterPrecipType, apparentTemperatureBOM, arrayCelsiusToFahrenheit, arrayFahrenheitToCelsius, arrayFeetToMeters, arrayInchesToMillimeters, arrayKilometersPerHourToMilesPerHour, arrayKilometersToMiles, arrayKilometersToMilesAsInt, arrayMetersToFeet, arrayMilesPerHourToKilometersPerHour, arrayMilesToKilometers, arrayMilesToKilometersAsInt, arrayMillimetersToInches, calculateEstimatedUVIndex, cardinalDirection, celsiusToFahrenheit, clearCodes, cloudyCodes, colorForTemperature, colorsForTemperatureValues, convertNwsToWmo, convertSynopticConditionToWmo2 as convertSynopticConditionToWmo, convertSynopticWnumToWeatherCode, createDefaultExtremes, createInvalidFirebaseCurrentWeather, createInvalidFirebaseExtremes, createInvalidFirebaseExtremesValues, createInvalidNWSAlertProperties, createInvalidNWSAlerts, createInvalidNWSAlertsFeature, createInvalidNWSForecastHourlyGrid, createInvalidNWSWeatherPoints, createInvalidNWSWeatherProperties, createInvalidOpenMeteoCurrent, createInvalidOpenMeteoCurrentUnits, createInvalidOpenMeteoData, createInvalidOpenMeteoHourly, createInvalidSynopticPrecipitationModel, createInvalidSynopticStation, createInvalidSynopticSummary, createInvalidSynopticSummaryModel, decodeSynopticWnum, defaultLocation, ensembleAutoTemperatureLevel, ensembleMatrix, ensemblePeriods, ensemblePrecipitationAgreement, ensembleRequestUrl, ensembleStatistics, ensembleSurfaceContext, ensembleTemperatureSeries, ensembleWindowSummary, ensembleWindowTotals, ensembleWinterWindow, extractShortDayNameFromTimestamp, fahrenheitToCelsius, feetToMeters, fogCodes, freezingRainCodes, frostCodes, generateUUID, getAlertColor, getBackgroundGradient, getBorderGradient, getCloudCoverFromWMO, getDegreesFromCardinal, getDewPoint, getDistance, getDistanceMiles, getWMOCodeFromNWSWeatherGrid, getWMOCodeFromNWSWeatherIcon, getWeatherIconName, gradientColorsForTemperatures, hailShowersCodes, hazeyCodes, hexToRgb, hexToRgba, icePelletsCodes, inchesToMillimeters, invalidDoubleValue, invalidIntValue, isLocationInUnitedStates, isPrecipitationWeatherCode, isWeatherValueValid, kilometersPerHourToMilesPerHour, kilometersToMiles, kilometersToMilesAsInt, lightRainCodes, lightSnowCodes, metersToFeet, milesPerHourToKilometersPerHour, milesToKilometers, milesToKilometersAsInt, millimetersToInches, networkManager, normalizeEnsemble, partialFogCodes, partlyCloudyCodes, precipPopThreshold, rainCodes, rainShowersCodes, rainSnowCodes, rainSnowShowersCodes, rgbColorForTemperature, rgbColorsForTemperatureValues, rgbGradientColorsForTemperatures, rgbToHex2 as rgbToHex, roundArrayToPlaces, roundToNearestFive, roundToPlaces, scatteredClouds, snowCodes, snowShowersCodes, stationPressureToSeaLevelPressure, thunderCodes, thunderStormChanceCodes, thunderStormCodes, transformSynopticPrecipitationResponse, transformSynopticResponse, transformSynopticSummary, validateCurrentWeatherObservation, weatherDescription };
